import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAssociation } from '../contexts/AssociationContext';
import { SchoolYearProvider, useSchoolYear } from '../contexts/SchoolYearContext';
import { SchoolYearSelector } from './SchoolYearSelector';
import { AssociationSettings } from './AssociationSettings';
import { ApiServiceAdmin } from '../services/ApiServiceAdmin.js';
import { GLOBAL_STYLES, GLOBAL_CONSTANTS, GLOBAL_UTILS } from '../styles/globalStyles';
import { COUNTRIES, Country, DEFAULT_COUNTRY_CODE, getCountryByCode, formatPhoneForStorage, parseStoredPhone } from '../constants/countries';
import { validateUsername, validatePhone, validateEmail, validatePassword } from '../utils/validation';
import { FiRefreshCw, FiRotateCcw } from 'react-icons/fi';
import { MdEdit } from 'react-icons/md';
import { AiOutlineDelete } from 'react-icons/ai';
import { ImCross } from 'react-icons/im';
import PasswordInput from './shared/PasswordInput';
import QuranTrackingSection from './shared/QuranTrackingSection';
import MessageItem, { MessageItemData } from './shared/MessageItem';
import { getParameterName } from '../locales/index';
import { getTimezoneOptions } from '../utils/timezones';
import { formatParamValue } from '../utils/paramDisplay';
import { formatDateInTz, formatTimeInTz, formatShortDateInTz, todayInTz } from '../utils/dateTime';
import { extractRepeatNewHifzFromRecord } from '../utils/quranRecordMapper';
import ExamScoresTable from './ExamScoresTable';
import SearchableSelect from './shared/SearchableSelect';
//import { BsXCircle } from 'react-icons/bs';
//import { HiOutlinePencil, HiOutlineTrash, HiOutlineXCircle } from 'react-icons/hi';

// Composants d'icônes typés pour éviter les erreurs TypeScript avec react-icons
// تعديل - Icône Edit moderne depuis Heroicons - hérite de la couleur du texte
const EditIcon: React.FC<{ size?: number; style?: React.CSSProperties }> = ({ size = 14, style }) => {
  const IconComponent = MdEdit as any;
  return <IconComponent size={size} style={{ display: 'inline-block', verticalAlign: 'middle', color: 'currentColor', ...style }} />;
};

// حذف - Icône Delete moderne depuis Heroicons - hérite de la couleur du texte
const DeleteIcon: React.FC<{ size?: number; style?: React.CSSProperties }> = ({ size = 14, style }) => {
  const IconComponent = AiOutlineDelete as any;
  return <IconComponent size={size} style={{ display: 'inline-block', verticalAlign: 'middle', color: 'currentColor', ...style }} />;
};

const RefreshIcon: React.FC<{ size?: number; style?: React.CSSProperties; animate?: boolean }> = ({ size = 14, style, animate = false }) => {
  const IconComponent = FiRefreshCw as any;
  return <IconComponent size={size} style={{ display: 'inline-block', verticalAlign: 'middle', color: 'currentColor', animation: animate ? 'spin 1s linear infinite' : undefined, ...style }} />;
};

// Réinitialisation - Icône RotateCcw pour la réinitialisation des filtres
const ResetIcon: React.FC<{ size?: number; style?: React.CSSProperties }> = ({ size = 14, style }) => {
  const IconComponent = FiRotateCcw as any;
  return <IconComponent size={size} style={{ display: 'inline-block', verticalAlign: 'middle', color: 'currentColor', ...style }} />;
};

// تعطيل - Icône Disable moderne depuis Heroicons - hérite de la couleur du texte
const XCircleIcon: React.FC<{ size?: number; style?: React.CSSProperties }> = ({ size = 10, style }) => {
  const IconComponent = ImCross as any;
  return <IconComponent size={size} style={{ display: 'inline-block', verticalAlign: 'middle', color: 'currentColor', ...style }} />;
};


interface AdminDashboardProps {
  onBackHome: () => void;
  onAuthError?: () => void;
  currentLanguage?: string;
  translations?: {
    ar?: any;
    fr?: any;
    en?: any;
  };
}

interface DashboardStats {
  data?: {
    totalStudents: number;
    totalTeachers: number;
    totalParents: number;
    averageGrade: number;
    unreadMessages: number;
    presentToday?: number;
    absentToday?: number;
    pendingInscriptions?: number;
    newContactMessages?: number;
  };
  success?: boolean;
}

interface Student {
  id: number;
  student_name: string;
  parent_id?: number | null; // parents.id
  parent_user_id?: number | null; // user_id du parent (via JOIN)
  parent_name?: string | null; // Nom du parent (via JOIN)
  parent_phone?: string | null; // Téléphone du parent (via JOIN)
  teacher_name?: string | null; // Nom de l'enseignant (via relation student-teachers)
  phone?: string;
  id_number?: string;
  address?: string;
  level?: string;
  halqa?: string;
  average?: number;
  attendance?: number;
  status: 'active' | 'inactive';
  created_date: string;
  notes?: string;
  gender?: 'Female' | 'Male';
  school_year_id?: number | null;
  school_year_label?: string | null;
}

interface Teacher {
  id: number;
  user_id?: number;
  full_name?: string;
  username?: string;
  email?: string;
  phone?: string;
  specialization?: string;
  experience_years?: number;
  hire_date?: string;
  salary?: number;
  assigned_halqas?: string;
  notes?: string;
  status: 'active' | 'inactive';
  created_date?: string;
  type?: string;
  gender?: 'Female' | 'Male';
  teacher_specializations?: Array<{
    id: number;
    teacher_id: number;
    specialization: string;
    experience_years?: number;
    certification_date?: string;
    status?: string;
  }>;
}

interface Message {
  id: number;
  sender_id?: number;
  sender_name?: string;
  sender_type?: string;
  sent_date?: string;
  sent_time?: string;
  subject?: string;
  content?: string;
  priority?: 'normal' | 'high' | 'urgent';
  recipient_type?: string;
  recipient_ids?: number[] | string | null;
  student_id?: number | null;
  student_name?: string | null;
  student_gender?: 'Male' | 'Female' | null;  // ⭐ NOUVEAU - Genre de l'étudiant
  student_label?: string | null;                // ⭐ NOUVEAU - Label calculé ("الطالب" ou "الطالبة")
  is_reply?: boolean; // Indique si c'est une réponse
  reply_to_message_id?: number | null; // ID du message auquel on répond
}

type PermissionOption = {
  key: string;
  labelAr: string;
  labelFr: string;
  labelEn: string;
};

// Vérifie si un élève est inscrit pour une année donnée.
// Source : historique student_enrollments (enrolled_year_ids), avec repli sur school_year_id.
const isEnrolledForYear = (student: any, yearId: number | null | undefined): boolean => {
  if (yearId == null || !Number.isFinite(Number(yearId))) return false;
  const enrolled = String(student?.enrolled_year_ids || '').split(',').filter(Boolean).map(Number);
  const effective = enrolled.length > 0 ? enrolled : (student?.school_year_id ? [Number(student.school_year_id)] : []);
  return effective.includes(Number(yearId));
};

/**
 * Relations élève → enseignant, chargées en **UNE seule requête** (`GET /api/admin/student-teachers`).
 *
 * ⚠️ Avant : la liste des élèves était enrichie avec `getStudentTeachersByStudent(student.id)`
 * dans un `Promise.all` → **un appel HTTP par élève** (« N+1 ») : ~272 requêtes et ~15 s
 * pour afficher le tableau de bord.
 *
 * @returns `Map<studentId, relation>` (la première relation si l'élève en a plusieurs —
 *          l'API trie déjà par `assigned_date DESC`, comme l'ancien `data[0]`).
 */
async function loadStudentTeacherMap(apiService: any): Promise<Map<number, any>> {
  const map = new Map<number, any>();
  try {
    const res = await apiService.getStudentTeachers();
    if (res?.success && Array.isArray(res.data)) {
      for (const rel of res.data) {
        const sid = Number(rel.student_id ?? rel.studentId);
        if (sid && !map.has(sid)) map.set(sid, rel);
      }
    }
  } catch (err) {
    console.error('❌ ADMIN DASHBOARD - Relations élève→enseignant:', err);
  }
  return map;
}

const AdminDashboardContent: React.FC<AdminDashboardProps> = ({ onBackHome, onAuthError, currentLanguage = 'ar', translations }) => {
  const { association, getName, timezone } = useAssociation();
  const logoUrl = association?.logoUrl ?? '/images/logo.png';
  const isRTL = currentLanguage === 'ar';
  const dateLocale = currentLanguage === 'fr' ? 'fr-MA' : currentLanguage === 'en' ? 'en-US' : 'ar-MA';

  const { selectedYear, currentYear, isReadOnly: schoolYearReadOnly, years, inscriptionYear } = useSchoolYear();
  
  // Fonction de traduction
  const t = (key: string) => {
    if (!translations) return key;
    const langTranslations = translations[currentLanguage as keyof typeof translations];
    if (langTranslations && langTranslations[key]) {
      return langTranslations[key];
    }
    return key;
  };


  // Fonction pour traduire le type de rapport (comme dans ParentDashboard)
  const translateReportType = (subject: string): string => {
    if (!subject) return subject;
    
    // Normaliser le sujet en minuscules pour la comparaison
    const normalizedSubject = subject.toLowerCase().trim();
    
    // Gérer les cas où le sujet commence par "Rapport" ou contient le type
    if (normalizedSubject.includes('rapport') || normalizedSubject.includes('report')) {
      if (normalizedSubject.includes('assignment') || normalizedSubject.includes('واجب') || normalizedSubject.includes('devoir')) {
        return t('reportTypeAssignment');
      }
      if (normalizedSubject.includes('progress') || normalizedSubject.includes('تقدم')) {
        return t('reportTypeProgress');
      }
      if (normalizedSubject.includes('achievement') || normalizedSubject.includes('إنجاز') || normalizedSubject.includes('réussite')) {
        return t('reportTypeAchievement');
      }
      if (normalizedSubject.includes('concern') || normalizedSubject.includes('ملاحظة') || normalizedSubject.includes('note')) {
        return t('reportTypeConcern');
      }
    }
    
    // Mapping direct pour les types purs
    const reportTypeMap: { [key: string]: string } = {
      'assignment': t('reportTypeAssignment'),
      'progress': t('reportTypeProgress'),
      'achievement': t('reportTypeAchievement'),
      'concern': t('reportTypeConcern'),
      'واجب منزلي': t('reportTypeAssignment'),
      'تقرير تقدم': t('reportTypeProgress'),
      'إنجاز متميز': t('reportTypeAchievement'),
      'ملاحظة مهمة': t('reportTypeConcern'),
      'rapport assignment': t('reportTypeAssignment'),
      'rapport progress': t('reportTypeProgress'),
      'rapport achievement': t('reportTypeAchievement'),
      'rapport concern': t('reportTypeConcern')
    };
    
    // Chercher une correspondance exacte
    if (reportTypeMap[normalizedSubject] || reportTypeMap[subject]) {
      return reportTypeMap[normalizedSubject] || reportTypeMap[subject];
    }
    
    // Si aucune correspondance, retourner le sujet original
    return subject;
  };

  // Fonction pour traduire la priorité
  const translatePriority = (priority: string): string => {
    const priorityMap: { [key: string]: string } = {
      'normal': currentLanguage === 'ar' ? 'عادية' : currentLanguage === 'fr' ? 'Normale' : 'Normal',
      'high': currentLanguage === 'ar' ? 'عالية' : currentLanguage === 'fr' ? 'Élevée' : 'High',
      'urgent': currentLanguage === 'ar' ? 'عاجلة' : currentLanguage === 'fr' ? 'Urgente' : 'Urgent'
    };
    return priorityMap[priority.toLowerCase()] || priority;
  };


  // Fonction pour nettoyer le contenu
  const getCleanedContent = (content: string | null | undefined): string => {
    if (!content) return '';
    return GLOBAL_UTILS.cleanText(content);
  };

  // Fonction pour obtenir le titre de l'expéditeur
  const getSenderTitle = (senderType: string | null | undefined): string => {
    if (!senderType) return '';
    const titleMap: { [key: string]: string } = {
      'teacher': t('senderTeacher'),
      'admin': t('senderAdmin'),
      'parent': t('senderParent'),
      'معلم': t('senderTeacher'),
      'مدير': t('senderAdmin'),
      'ولي أمر': t('senderParent')
    };
    return titleMap[senderType.toLowerCase()] || '';
  };

  // Fonction pour gérer le clic sur un message (expansion/réduction)
  const handleMessageClick = (messageId: number, e?: React.MouseEvent) => {
    // Empêcher la propagation si on clique sur le bouton de réponse
    if (e && (e.target as HTMLElement).closest('button')) {
      return;
    }
    
    if (expandedMessageId === messageId) {
      // Si on clique sur le même message, le fermer
      setExpandedMessageId(null);
    } else {
      // Sinon, ouvrir ce message
      setExpandedMessageId(messageId);
    }
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [quranRecords, setQuranRecords] = useState<any[]>([]);
  const [parents, setParents] = useState<any[]>([]);
  const [messages, setMessages] = useState<(Message & { priority?: 'normal' | 'high' | 'urgent'; sent_time?: string })[]>([]);
  const [admins, setAdmins] = useState<any[]>([]);
  // Filtres pour le tableau des étudiants
  const [studentFilters, setStudentFilters] = useState({
    studentName: '',
    parentName: '',
    teacherName: '',
    level: '',
    halqa: '',
    status: '',
    gender: '',
    schoolYearId: '' // '' = année du système, 'all' = toutes les années, sinon id numérique
  });
  // Filtre pour le tableau des parents
  const [parentFilter, setParentFilter] = useState('');
  // Mode d'affichage mobile pour les parents
  const [parentMobileViewMode, setParentMobileViewMode] = useState<'cards' | 'table'>('table');
  // Détail parent sélectionné
  const [selectedParentDetail, setSelectedParentDetail] = useState<any>(null);
  const [parentDetailChildren, setParentDetailChildren] = useState<any[]>([]);
  const [parentDetailLoading, setParentDetailLoading] = useState(false);
  const [parentReenrollMode, setParentReenrollMode] = useState(false);
  const [parentReenrollSelection, setParentReenrollSelection] = useState<number[]>([]);
  // Détail étudiant sélectionné
  const [selectedStudentDetail, setSelectedStudentDetail] = useState<any>(null);
  const [showAddAdminForm, setShowAddAdminForm] = useState(false);
  const [showEditAdminForm, setShowEditAdminForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<any>(null);
  const [newAdmin, setNewAdmin] = useState({
    fullName: '',
    username: '',
    password: '',
    phone: '',
    email: '',
    permissions: ['students', 'teachers', 'parents', 'admins', 'communication', 'grades', 'finances'],
    notes: '',
    status: 'active',
    gender: 'Male' as 'Female' | 'Male'
  });
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState('');
  const [isSubmittingAdmin, setIsSubmittingAdmin] = useState(false);
  const [adminSendEmail, setAdminSendEmail] = useState(true);
  const [adminSendSms, setAdminSendSms] = useState(false);
  const [adminSendWhatsApp, setAdminSendWhatsApp] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentSection, setCurrentSection] = useState('dashboard');
  const [dashboardTab, setDashboardTab] = useState<'activities' | 'attendance' | 'attendance-by-date'>('activities'); // Onglet actif dans le dashboard
  const [attendanceData, setAttendanceData] = useState<any>(null); // Données d'attendance pour l'onglet
  // ✅ Bouton « Rafraîchir » du tableau de bord : relance le chargement complet
  // (KPI + listes élèves/enseignants/parents + messages + matières + enregistrements Coran)
  // sans recharger la page (pas besoin de Ctrl+Shift+R).
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null);

  const [todayHasSession, setTodayHasSession] = useState<boolean>(true); // false = aucune séance Coran aujourd'hui
  const [attendanceTeacherFilter, setAttendanceTeacherFilter] = useState<string>('all'); // Filtre professeur pour l'onglet attendance
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState<'all' | 'present' | 'absent'>('all'); // Filtre statut pour l'onglet attendance
  const [attendanceStartDate, setAttendanceStartDate] = useState<string>(''); // Date de début pour le filtre attendance
  const [attendanceEndDate, setAttendanceEndDate] = useState<string>(''); // Date de fin pour le filtre attendance
  const [attendanceByDateData, setAttendanceByDateData] = useState<any>(null); // Données d'attendance par date
  const [attendanceByDateLoading, setAttendanceByDateLoading] = useState(false); // Loading pour attendance par date
  const [attendanceByDateTeacherFilter, setAttendanceByDateTeacherFilter] = useState<string>('all'); // Filtre professeur pour attendance par date
  const [currentAdminPermissions, setCurrentAdminPermissions] = useState<string[]>([]);
  const [currentAdminName, setCurrentAdminName] = useState<string>('');
  const [teachersSubSection, setTeachersSubSection] = useState<'manage' | 'quranTracking' | 'studentProgress'>('manage'); // Sous-section dans الأساتذة
  const [adminsSubSection, setAdminsSubSection] = useState<'adminsList' | 'parameters'>('adminsList'); // Sous-section dans المديرون
  const [financialSubSection, setFinancialSubSection] = useState<'expenses' | 'studentFees' | 'teacherSalaries'>('expenses'); // Sous-section dans الإدارة المالية
  const [monthlyContributions, setMonthlyContributions] = useState<any[]>([]);
  const [loadingContributions, setLoadingContributions] = useState(false);
  const [showEditContributionForm, setShowEditContributionForm] = useState(false);
  const [editingContribution, setEditingContribution] = useState<any>(null);
  const [editingContributionValues, setEditingContributionValues] = useState<{
    annual_membership: string;
    september: string;
    october: string;
    november: string;
    december: string;
    january: string;
    february: string;
    march: string;
    april: string;
    may: string;
    june: string;
    july: string;
    august: string;
  }>({
    annual_membership: '',
    september: '',
    october: '',
    november: '',
    december: '',
    january: '',
    february: '',
    march: '',
    april: '',
    may: '',
    june: '',
    july: '',
    august: ''
  });
  const [isSubmittingContribution, setIsSubmittingContribution] = useState(false);
  const [contributionFilters, setContributionFilters] = useState({
    academic_year: '',
    student_id: '',
    parent_id: '',
    status: ''
  });
  // États pour les filtres de recherche avec dropdown (comme dans متابعة حفظ القرآن)
  const [studentFeesStudentSearchText, setStudentFeesStudentSearchText] = useState<string>('');
  const [showStudentFeesStudentDropdown, setShowStudentFeesStudentDropdown] = useState<boolean>(false);
  const [studentFeesParentSearchText, setStudentFeesParentSearchText] = useState<string>('');
  const [showStudentFeesParentDropdown, setShowStudentFeesParentDropdown] = useState<boolean>(false);
  
  // Colonnes fixes visibles dans le tableau des étudiants
  const [visibleColumns] = useState<{[key: string]: boolean}>({
    studentName: true,
    parentName: true,
    phone: false,
    gender: true,
    teacherName: true,
    level: false,
    halqa: true,
    status: true,
    actions: true
  });
  
  // État pour le mode d'affichage sur mobile (cartes ou tableau)
  const [mobileViewMode, setMobileViewMode] = useState<'cards' | 'table'>('table');
  
  // Synchroniser les champs de recherche avec les filtres
  React.useEffect(() => {
    if (contributionFilters.student_id) {
      const selectedStudent = students.find(s => s.id.toString() === contributionFilters.student_id);
      if (selectedStudent && !showStudentFeesStudentDropdown) {
        setStudentFeesStudentSearchText(selectedStudent.student_name || '');
      }
    } else {
      if (!showStudentFeesStudentDropdown) {
        setStudentFeesStudentSearchText('');
      }
    }
  }, [contributionFilters.student_id, students, showStudentFeesStudentDropdown]);
  
  React.useEffect(() => {
    if (contributionFilters.parent_id) {
      const selectedParent = parents.find(p => {
        const parentValue = p.user_id || p.id;
        return parentValue.toString() === contributionFilters.parent_id;
      });
      if (selectedParent && !showStudentFeesParentDropdown) {
        setStudentFeesParentSearchText(selectedParent.full_name || selectedParent.fullName || '');
      }
    } else {
      if (!showStudentFeesParentDropdown) {
        setStudentFeesParentSearchText('');
      }
    }
  }, [contributionFilters.parent_id, parents, showStudentFeesParentDropdown]);
  
  // Initialiser les dates d'attendance : plage de l'année scolaire sélectionnée, ou début du mois → aujourd'hui
  React.useEffect(() => {
    if (!attendanceStartDate || !attendanceEndDate) {
      if (selectedYear?.start_date && selectedYear?.end_date) {
        setAttendanceStartDate(selectedYear.start_date.slice(0, 10));
        setAttendanceEndDate(selectedYear.end_date.slice(0, 10));
      } else {
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const startDateStr = `${firstDayOfMonth.getFullYear()}-${String(firstDayOfMonth.getMonth() + 1).padStart(2, '0')}-${String(firstDayOfMonth.getDate()).padStart(2, '0')}`;
        const endDateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        if (!attendanceStartDate) setAttendanceStartDate(startDateStr);
        if (!attendanceEndDate) setAttendanceEndDate(endDateStr);
      }
    }
  }, [attendanceStartDate, attendanceEndDate, selectedYear]);
  
  // Charger automatiquement les données d'attendance par date lors de l'ouverture de l'onglet
  React.useEffect(() => {
    const loadAttendanceByDate = async () => {
      if (
        dashboardTab === 'attendance-by-date' && 
        attendanceStartDate && 
        attendanceEndDate && 
        !attendanceByDateData &&
        !attendanceByDateLoading
      ) {
        setAttendanceByDateLoading(true);
        try {
          const response = await apiService.getAttendanceByDate({
            startDate: attendanceStartDate,
            endDate: attendanceEndDate,
            school_year_id: selectedYear?.id,
          });
          
          if (response.success && response.data) {
            setAttendanceByDateData(response.data);
          }
        } catch (error) {
          console.error('❌ Erreur lors du chargement automatique:', error);
        } finally {
          setAttendanceByDateLoading(false);
        }
      }
    };
    
    loadAttendanceByDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardTab, attendanceStartDate, attendanceEndDate, attendanceByDateData, attendanceByDateLoading]);
  
  // États pour les salaires des enseignants
  const [teacherSalaries, setTeacherSalaries] = useState<any[]>([]);
  const [loadingSalaries, setLoadingSalaries] = useState(false);
  const [showEditSalaryForm, setShowEditSalaryForm] = useState(false);
  const [editingSalary, setEditingSalary] = useState<any>(null);
  const [editingSalaryValues, setEditingSalaryValues] = useState<{
    september: string;
    october: string;
    november: string;
    december: string;
    january: string;
    february: string;
    march: string;
    april: string;
    may: string;
    june: string;
    july: string;
    august: string;
  }>({
    september: '',
    october: '',
    november: '',
    december: '',
    january: '',
    february: '',
    march: '',
    april: '',
    may: '',
    june: '',
    july: '',
    august: ''
  });
  const [isSubmittingSalary, setIsSubmittingSalary] = useState(false);
  // États pour les enregistrements financiers (المداخيل و المصاريف)
  const [financialRecords, setFinancialRecords] = useState<any[]>([]);
  const [loadingFinancialRecords, setLoadingFinancialRecords] = useState(false);
  const [showAddFinancialRecordForm, setShowAddFinancialRecordForm] = useState(false);
  const [showEditFinancialRecordForm, setShowEditFinancialRecordForm] = useState(false);
  const [editingFinancialRecord, setEditingFinancialRecord] = useState<any>(null);
  const [isSubmittingFinancialRecord, setIsSubmittingFinancialRecord] = useState(false);
  const [newFinancialRecord, setNewFinancialRecord] = useState<{
    category_name: string;
    category_type: 'revenue' | 'expense';
    display_order: number;
    september: string;
    october: string;
    november: string;
    december: string;
    january: string;
    february: string;
    march: string;
    april: string;
    may: string;
    june: string;
    july: string;
    august: string;
  }>({
    category_name: '',
    category_type: 'revenue',
    display_order: 1,
    september: '',
    october: '',
    november: '',
    december: '',
    january: '',
    february: '',
    march: '',
    april: '',
    may: '',
    june: '',
    july: '',
    august: ''
  });
  const [editingFinancialRecordValues, setEditingFinancialRecordValues] = useState<{
    category_name: string;
    category_type: 'revenue' | 'expense' | 'total_revenue' | 'total_expense';
    display_order: number;
    september: string;
    october: string;
    november: string;
    december: string;
    january: string;
    february: string;
    march: string;
    april: string;
    may: string;
    june: string;
    july: string;
    august: string;
  }>({
    category_name: '',
    category_type: 'revenue',
    display_order: 1,
    september: '',
    october: '',
    november: '',
    december: '',
    january: '',
    february: '',
    march: '',
    april: '',
    may: '',
    june: '',
    july: '',
    august: ''
  });

  // Constante centralisée pour les permissions disponibles
  const AVAILABLE_PERMISSIONS: PermissionOption[] = [
    { key: 'students', labelAr: 'الطلاب', labelFr: 'Gestion des Étudiants', labelEn: 'Student Management' },
    { key: 'teachers', labelAr: 'الأساتذة', labelFr: 'Gestion des Enseignants', labelEn: 'Teacher Management' },
    { key: 'parents', labelAr: 'أولياء الأمور', labelFr: 'Gestion des Parents', labelEn: 'Parent Management' },
    { key: 'admins', labelAr: 'المديرين', labelFr: 'Gestion des Administrateurs', labelEn: 'Admin Management' },
    { key: 'communication', labelAr: 'التواصل', labelFr: 'Gestion de la Communication', labelEn: 'Communication Management' },
    { key: 'grades', labelAr: 'نقط الإختبارات', labelFr: 'Gestion des Notes', labelEn: 'Grade Management' },
    { key: 'finances', labelAr: 'المالية', labelFr: 'Gestion Financière', labelEn: 'Financial Management' }
  ];

  // Fonction pour convertir les permissions arabes en clés anglaises
  const normalizePermission = (permission: string): string => {
    if (!permission) return permission;
    
    const permissionMap: { [key: string]: string } = {
      'الطلاب': 'students',
      'نقط الإختبارات': 'grades',
      'التواصل': 'communication',
      'الأساتذة': 'teachers',
      'أولياء الأمور': 'parents',
      'المديرون': 'admins',
      'المديرين': 'admins', // Variante acceptée
      'الإدارة المالية': 'finances',
      'finances': 'finances',
      // Mappings pour les clés anglaises (garder tel quel)
      'students': 'students',
      'grades': 'grades',
      'communication': 'communication',
      'teachers': 'teachers',
      'parents': 'parents',
      'admins': 'admins'
    };
    
    // Nettoyer la permission (supprimer les espaces et guillemets)
    const cleaned = permission.trim().replace(/^["']|["']$/g, '');
    const normalized = permissionMap[cleaned] || cleaned;
    
    return normalized;
  };

  // Fonctions helper pour vérifier les permissions
  const canModify = (permission: string): boolean => {
    // Vérifier si l'admin a la permission de modification/création
    // Les permissions valides sont: 'students', 'grades', 'communication', 'teachers', 'parents', 'admins'
    return currentAdminPermissions.includes(permission);
  };
  
  const canEditFinances = canModify('finances') && !schoolYearReadOnly;

  const canViewSection = (permission: string): boolean => {
    // Pour la section "المديرين", vérifier la permission
    if (permission === 'admins') {
      return currentAdminPermissions.includes('admins');
    }
    // Pour la section "الإدارة المالية", tous les admins peuvent voir en lecture seule
    // Les boutons d'ajout/modification seront masqués avec canEditFinances
    if (permission === 'financial' || permission === 'finances') {
      return true; // Visible en lecture seule pour tous
    }
    // Pour les autres sections, tous les admins peuvent voir (lecture)
    return true;
  };
  const [selectedTeacherFilter, setSelectedTeacherFilter] = useState<string>('all'); // Filtre pour le professeur dans متابعة القرآن
  const [selectedStudentFilter, setSelectedStudentFilter] = useState<string>('all'); // Filtre pour l'étudiant dans متابعة القرآن
  const [studentProgressStudentSearchText, setStudentProgressStudentSearchText] = useState<string>(''); // Texte de recherche pour le filtre étudiant dans تقدم الطلاب
  const [showStudentProgressDropdown, setShowStudentProgressDropdown] = useState<boolean>(false); // Afficher le dropdown pour le filtre étudiant dans تقدم الطلاب

  // États pour les paramètres
  const [parameters, setParameters] = useState<any[]>([]);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [showAddParameterForm, setShowAddParameterForm] = useState(false);
  const [showEditParameterForm, setShowEditParameterForm] = useState(false);
  const [editingParameter, setEditingParameter] = useState<any>(null);
  const [newParameter, setNewParameter] = useState({
    name: '',
    value: '',
    description: ''
  });

  // États pour le sélecteur de pays par défaut (indicatif téléphonique)
  const [defaultCountry, setDefaultCountry] = useState<Country>(getCountryByCode(DEFAULT_COUNTRY_CODE));
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [savingCountry, setSavingCountry] = useState(false);

  // États pour les pickers d'indicatif dans les formulaires
  const [teacherPhoneCountry, setTeacherPhoneCountry] = useState<Country>(getCountryByCode(DEFAULT_COUNTRY_CODE));
  const [parentPhoneCountry, setParentPhoneCountry] = useState<Country>(getCountryByCode(DEFAULT_COUNTRY_CODE));
  const [adminPhoneCountry, setAdminPhoneCountry] = useState<Country>(getCountryByCode(DEFAULT_COUNTRY_CODE));
  const [formPhonePickerOpen, setFormPhonePickerOpen] = useState<'teacher' | 'parent' | 'admin' | null>(null);
  const [formPhoneSearch, setFormPhoneSearch] = useState('');

  // Réinitialiser le filtre étudiant quand le filtre professeur change dans la section تقدم الطلاب
  useEffect(() => {
    if (teachersSubSection === 'studentProgress') {
      setSelectedStudentFilter('all');
      setStudentProgressStudentSearchText('');
      setShowStudentProgressDropdown(false);
    }
  }, [selectedTeacherFilter, teachersSubSection]);

  // Charger les enregistrements financiers et les contributions quand on entre dans la section expenses
  useEffect(() => {
    if (currentSection === 'financial' && financialSubSection === 'expenses') {
      const loadData = async () => {
        setLoadingFinancialRecords(true);
        try {
          // Charger les enregistrements financiers
          const financialResponse = await apiService.getFinancialRecords({});
          if (financialResponse.success) {
            setFinancialRecords(financialResponse.data || []);
          } else {
            console.error('Erreur lors du chargement des enregistrements financiers:', financialResponse.error);
          }

          // Charger les contributions mensuelles pour calculer les totaux
          const contributionsResponse = await apiService.getMonthlyContributions({});
          if (contributionsResponse.success) {
            setMonthlyContributions(contributionsResponse.data || []);
          } else {
            console.error('Erreur lors du chargement des contributions:', contributionsResponse.error);
          }

          // Charger les salaires des enseignants pour calculer les totaux
          const salariesResponse = await apiService.getTeacherSalaries({});
          if (salariesResponse.success) {
            setTeacherSalaries(salariesResponse.data || []);
          } else {
            console.error('Erreur lors du chargement des salaires:', salariesResponse.error);
          }
        } catch (error) {
          console.error('Erreur:', error);
        } finally {
          setLoadingFinancialRecords(false);
        }
      };
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection, financialSubSection, selectedYear?.id]);

  // Charger les paramètres quand on entre dans la sous-section parameters de admins
  useEffect(() => {
    if (currentSection === 'admins' && adminsSubSection === 'parameters' && parameters.length === 0 && !loadingParameters) {
      fetchParameters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection, adminsSubSection]);

  // Fonction pour vérifier si un enregistrement est calculé (non stocké en DB)
  const isCalculatedRecord = (record: any): boolean => {
    return record.id === null || 
           record.category_name === 'Total Expenses' ||
           record.category_name === 'Total Revenues' ||
           record.category_name === 'رواتب الأساتذة' ||
           record.category_name === 'رسوم الطلاب';
  };

  // Fonction pour calculer "رواتب الأساتذة" depuis teacherSalaries
  const calculateTeacherSalariesRecord = (salaries: any[]): any => {
    const months = ['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'];
    const totals: any = {
      september: 0, october: 0, november: 0, december: 0,
      january: 0, february: 0, march: 0, april: 0,
      may: 0, june: 0, july: 0, august: 0,
      total_amount: 0
    };

    salaries.forEach(salary => {
      months.forEach(month => {
        totals[month] += parseFloat(salary[month] || 0) || 0;
      });
      totals.total_amount += parseFloat(salary.total_amount || 0) || 0;
    });

    return {
      id: null,
      category_name: 'رواتب الأساتذة',
      category_type: 'expense',
      display_order: 1,
      ...totals,
      description: 'Total des salaires des enseignants',
      status: 'active'
    };
  };

  // Fonction pour calculer "رسوم الطلاب" depuis monthlyContributions
  const calculateStudentFeesRecord = (contributions: any[]): any => {
    const months = ['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'];
    const totals: any = {
      september: 0, october: 0, november: 0, december: 0,
      january: 0, february: 0, march: 0, april: 0,
      may: 0, june: 0, july: 0, august: 0,
      total_amount: 0
    };

    contributions.forEach(contribution => {
      months.forEach(month => {
        totals[month] += parseFloat(contribution[month] || 0) || 0;
      });
      // Ajouter annual_membership au total_amount
      totals.total_amount += parseFloat(contribution.annual_membership || 0) || 0;
    });

    // Calculer total_amount (somme des mois + annual_membership)
    months.forEach(month => {
      totals.total_amount += totals[month];
    });

    return {
      id: null,
      category_name: 'رسوم الطلاب',
      category_type: 'revenue',
      display_order: 1,
      ...totals,
      description: 'Total des cotisations mensuelles et annuelles des étudiants',
      status: 'active'
    };
  };

  // Fonction pour calculer "Total Expenses"
  const calculateTotalExpenses = (expenseRecords: any[], teacherSalariesRecord: any): any => {
    const months = ['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'];
    const totals: any = {
      september: 0, october: 0, november: 0, december: 0,
      january: 0, february: 0, march: 0, april: 0,
      may: 0, june: 0, july: 0, august: 0,
      total_amount: 0
    };

    // Somme des dépenses (excluant total_expense)
    expenseRecords.forEach(record => {
      if (record.category_type === 'expense' && record.category_type !== 'total_expense') {
        months.forEach(month => {
          totals[month] += parseFloat(record[month] || 0) || 0;
        });
        totals.total_amount += parseFloat(record.total_amount || 0) || 0;
      }
    });

    // Ajouter رواتب الأساتذة
    if (teacherSalariesRecord) {
      months.forEach(month => {
        totals[month] += parseFloat(teacherSalariesRecord[month] || 0) || 0;
      });
      totals.total_amount += parseFloat(teacherSalariesRecord.total_amount || 0) || 0;
    }

    return {
      id: null,
      category_name: 'Total Expenses',
      category_type: 'total_expense',
      display_order: 0,
      ...totals,
      description: 'Automatically calculated total of all expenses',
      status: 'active'
    };
  };

  // Fonction pour calculer "Total Revenues"
  const calculateTotalRevenues = (revenueRecords: any[], studentFeesRecord: any): any => {
    const months = ['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'];
    const totals: any = {
      september: 0, october: 0, november: 0, december: 0,
      january: 0, february: 0, march: 0, april: 0,
      may: 0, june: 0, july: 0, august: 0,
      total_amount: 0
    };

    // Somme des recettes (excluant total_revenue)
    revenueRecords.forEach(record => {
      if (record.category_type === 'revenue' && record.category_type !== 'total_revenue') {
        months.forEach(month => {
          totals[month] += parseFloat(record[month] || 0) || 0;
        });
        totals.total_amount += parseFloat(record.total_amount || 0) || 0;
      }
    });

    // Ajouter رسوم الطلاب
    if (studentFeesRecord) {
      months.forEach(month => {
        totals[month] += parseFloat(studentFeesRecord[month] || 0) || 0;
      });
      totals.total_amount += parseFloat(studentFeesRecord.total_amount || 0) || 0;
    }

    return {
      id: null,
      category_name: 'Total Revenues',
      category_type: 'total_revenue',
      display_order: 0,
      ...totals,
      description: 'Automatically calculated total of all revenues',
      status: 'active'
    };
  };

  // Séparer les enregistrements financiers en recettes et dépenses, et calculer les sous-totaux
  const { incomeRecords, expenseRecords, teacherSalariesRecord, studentFeesRecord, incomeTotals, expenseTotals } = useMemo(() => {
    const income: any[] = [];
    const expense: any[] = [];
    const months = ['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'];
    
    // Filtrer les enregistrements (exclure total_revenue, total_expense, et les lignes calculées car elles seront calculées)
    financialRecords.forEach(record => {
      // Exclure les lignes calculées qui seront recalculées
      if (record.category_name === 'رواتب الأساتذة' || record.category_name === 'رسوم الطلاب') {
        return; // Ignorer ces lignes car elles seront calculées
      }
      if (record.category_type === 'revenue' && record.category_type !== 'total_revenue') {
        income.push(record);
      } else if (record.category_type === 'expense' && record.category_type !== 'total_expense') {
        expense.push(record);
      }
    });

    // Calculer les lignes calculées
    const teacherSalariesCalc = calculateTeacherSalariesRecord(teacherSalaries);
    const studentFeesCalc = calculateStudentFeesRecord(monthlyContributions);

    // Ajouter les lignes calculées aux listes
    const allIncomeRecords = [...income, studentFeesCalc];
    const allExpenseRecords = [...expense, teacherSalariesCalc];

    // Trier par display_order ASC, puis par id en cas d'égalité
    allIncomeRecords.sort((a, b) => {
      const orderA = a.display_order ?? 999;
      const orderB = b.display_order ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      // Les lignes calculées (id: null) doivent venir après les lignes réelles
      if (a.id === null && b.id !== null) return 1;
      if (a.id !== null && b.id === null) return -1;
      return (a.id || 0) - (b.id || 0);
    });

    allExpenseRecords.sort((a, b) => {
      const orderA = a.display_order ?? 999;
      const orderB = b.display_order ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      // Les lignes calculées (id: null) doivent venir après les lignes réelles
      if (a.id === null && b.id !== null) return 1;
      if (a.id !== null && b.id === null) return -1;
      return (a.id || 0) - (b.id || 0);
    });

    // Calculer les totaux
    const totalExpensesCalc = calculateTotalExpenses(expense, teacherSalariesCalc);
    const totalRevenuesCalc = calculateTotalRevenues(income, studentFeesCalc);

    // Calculer les totaux pour l'affichage (incluant toutes les lignes)
    const incomeTotalsCalc: { [key: string]: number } = {};
    const expenseTotalsCalc: { [key: string]: number } = {};

    months.forEach(month => {
      incomeTotalsCalc[month] = parseFloat(totalRevenuesCalc[month] || 0) || 0;
      expenseTotalsCalc[month] = parseFloat(totalExpensesCalc[month] || 0) || 0;
    });
    incomeTotalsCalc.total_amount = parseFloat(totalRevenuesCalc.total_amount || 0) || 0;
    expenseTotalsCalc.total_amount = parseFloat(totalExpensesCalc.total_amount || 0) || 0;

    return {
      incomeRecords: allIncomeRecords,
      expenseRecords: allExpenseRecords,
      teacherSalariesRecord: teacherSalariesCalc,
      studentFeesRecord: studentFeesCalc,
      incomeTotals: incomeTotalsCalc as { [key: string]: number },
      expenseTotals: expenseTotalsCalc as { [key: string]: number }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialRecords, monthlyContributions, teacherSalaries]);

  // Charger les salaires des enseignants quand on entre dans la section teacherSalaries
  useEffect(() => {
    if (currentSection === 'financial' && financialSubSection === 'teacherSalaries') {
      const loadSalaries = async () => {
        setLoadingSalaries(true);
        try {
          const response = await apiService.getTeacherSalaries({});
          if (response.success) {
            setTeacherSalaries(response.data || []);
          } else {
            console.error('Erreur lors du chargement des salaires:', response.error);
          }
        } catch (error) {
          console.error('Erreur:', error);
        } finally {
          setLoadingSalaries(false);
        }
      };
      loadSalaries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection, financialSubSection, selectedYear?.id]);

  // Charger les contributions mensuelles quand on entre dans la section studentFees
  useEffect(() => {
    if (currentSection === 'financial' && financialSubSection === 'studentFees') {
      const loadContributions = async () => {
        setLoadingContributions(true);
        try {
          console.log('🔍 AdminDashboard - Chargement des contributions avec filtres:', contributionFilters);
          console.log('🔍 AdminDashboard - parent_id dans les filtres:', contributionFilters.parent_id);
          console.log('🔍 AdminDashboard - Parents disponibles:', parents.length);
          const response = await apiService.getMonthlyContributions(contributionFilters);
          console.log('🔍 AdminDashboard - Réponse API:', response);
          if (response.success) {
            console.log('✅ AdminDashboard - Contributions chargées:', response.data?.length || 0);
            setMonthlyContributions(response.data || []);
          } else {
            console.error('❌ AdminDashboard - Erreur lors du chargement des contributions:', response.error);
            // Afficher un message d'erreur plus détaillé
            if (response.error) {
              console.error('❌ Détails de l\'erreur:', {
                error: response.error,
                filters: contributionFilters
              });
            }
          }
        } catch (error) {
          console.error('❌ AdminDashboard - Exception lors du chargement des contributions:', error);
          console.error('❌ Stack trace:', error instanceof Error ? error.stack : 'N/A');
        } finally {
          setLoadingContributions(false);
        }
      };
      loadContributions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection, financialSubSection, contributionFilters, selectedYear?.id]);
  const [showAddStudentForm, setShowAddStudentForm] = useState(false);
  const [showEditStudentForm, setShowEditStudentForm] = useState(false);
  const [showAddParentForm, setShowAddParentForm] = useState(false);
  const [showEditParentForm, setShowEditParentForm] = useState(false);
  const [editingParent, setEditingParent] = useState<any>(null);
  const [newParent, setNewParent] = useState({
    fullName: '',
    username: '',
    password: '',
    confirmPassword: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    status: 'active',
    gender: 'Male' as 'Female' | 'Male'
  });
  const [parentSendEmail, setParentSendEmail] = useState(true);
  const [parentSendSms, setParentSendSms] = useState(false);
  const [parentSendWhatsApp, setParentSendWhatsApp] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [newStudent, setNewStudent] = useState({
    student_name: '',
    parent_id: '', // user_id du parent sélectionné
    teacher_id: '', // user_id du teacher sélectionné
    level: '',
    halqa: '',
    status: 'active',
    notes: '',
    gender: 'Male' as 'Female' | 'Male',
    school_year_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  
  // États pour la sélection d'enseignant
  const [currentStudentTeacherRelation, setCurrentStudentTeacherRelation] = useState<any>(null); // Relation existante lors de l'édition
  
  // Communication states
  const [communicationTab, setCommunicationTab] = useState<'messages' | 'emails' | 'inscription-requests' | 'contact-messages'>('messages');
  const [messagesSubTab, setMessagesSubTab] = useState<'received' | 'sent'>('received');
  const [emailsSubTab, setEmailsSubTab] = useState<'sent' | 'received'>('sent');

  // États onglet Demandes d'inscription
  const [inscriptionItems, setInscriptionItems] = useState<any[]>([]);
  const [pendingInscriptionsCount, setPendingInscriptionsCount] = useState<number>(0);
  const [newContactMessagesCount, setNewContactMessagesCount] = useState<number>(0);
  const [inscriptionLoading, setInscriptionLoading] = useState(false);
  const [inscriptionSearch, setInscriptionSearch] = useState('');
  const [inscriptionFilterStatus, setInscriptionFilterStatus] = useState('');
  const [inscriptionFilterSchoolYear, setInscriptionFilterSchoolYear] = useState<string>('current');
  const [inscriptionSelected, setInscriptionSelected] = useState<any | null>(null);
  const [inscriptionDetail, setInscriptionDetail] = useState<any | null>(null);
  const [inscriptionDetailLoading, setInscriptionDetailLoading] = useState(false);
  const [inscriptionAdminNotes, setInscriptionAdminNotes] = useState('');
  const [inscriptionActionLoading, setInscriptionActionLoading] = useState(false);
  const [inscriptionProvisionLoading, setInscriptionProvisionLoading] = useState(false);
  const [inscriptionToLink, setInscriptionToLink] = useState<number | null>(null);

  // États modals de confirmation de suppression
  const [deleteParentModal, setDeleteParentModal] = useState<{ parent: any; childrenCount: number } | null>(null);
  const [deleteParentInput, setDeleteParentInput] = useState('');
  const [deleteParentLoading, setDeleteParentLoading] = useState(false);
  const [deleteStudentModal, setDeleteStudentModal] = useState<{ student: any } | null>(null);
  const [deleteStudentInput, setDeleteStudentInput] = useState('');
  const [deleteStudentLoading, setDeleteStudentLoading] = useState(false);
  const [deleteStudentCheck, setDeleteStudentCheck] = useState<any | null>(null);
  const [deleteStudentCheckLoading, setDeleteStudentCheckLoading] = useState(false);

  // États onglet Messages de contact
  const [contactItems, setContactItems] = useState<any[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactFilterStatus, setContactFilterStatus] = useState('');
  const [contactSelected, setContactSelected] = useState<any | null>(null);
  const [contactDetail, setContactDetail] = useState<any | null>(null);
  const [contactDetailLoading, setContactDetailLoading] = useState(false);
  const [contactActionLoading, setContactActionLoading] = useState(false);
  const [contactReplyText, setContactReplyText] = useState('');
  const [contactShowReply, setContactShowReply] = useState(false);
  const [expandedMessageId, setExpandedMessageId] = useState<number | null>(null);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);
  const [messageToReplyTo, setMessageToReplyTo] = useState<Message | null>(null);
  const [isReplyMode, setIsReplyMode] = useState(false);
  const [newMessage, setNewMessage] = useState({
    recipientType: 'all',
    subject: '',
    content: '',
    priority: 'normal',
    selectedClass: ''
  });
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [showSpecificUsers, setShowSpecificUsers] = useState(false);
  const [showClassSelection, setShowClassSelection] = useState(false);
  
  // Email states
  const [sentEmails, setSentEmails] = useState<any[]>([]);
  const [receivedEmails, setReceivedEmails] = useState<any[]>([]);
  const [sentEmailsLoading, setSentEmailsLoading] = useState(false);
  const [receivedEmailsLoading, setReceivedEmailsLoading] = useState(false);
  const [sentEmailsError, setSentEmailsError] = useState<string | null>(null);
  const [receivedEmailsError, setReceivedEmailsError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_sentEmailsPagination, setSentEmailsPagination] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_receivedEmailsPagination, setReceivedEmailsPagination] = useState<any>({});
  const [openedSentEmailId, setOpenedSentEmailId] = useState<number | string | null>(null);
  const [openedReceivedEmailId, setOpenedReceivedEmailId] = useState<number | string | null>(null);
  const [sentMessages, setSentMessages] = useState<any[]>([]);
  const isLoadingRef = useRef(false);

  // États pour les نقط الإختبارات (test scores)
  const [testScores, setTestScores] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  
  // Debug: Log des matières quand elles changent
  useEffect(() => {
    console.log('🔍 AdminDashboard - subjects state changed:', subjects);
    console.log('🔍 AdminDashboard - subjects length:', subjects.length);
    if (subjects.length > 0) {
      console.log('🔍 AdminDashboard - first subject:', subjects[0]);
    }
  }, [subjects]);
  const [showAddTestScoreForm, setShowAddTestScoreForm] = useState(false);
  const [newTestScore, setNewTestScore] = useState({
    studentId: '',
    subjectId: '',
    grade: '',
    date: todayInTz(timezone)
  });
  const [isSubmittingTestScore, setIsSubmittingTestScore] = useState(false);
  const [testScoreError, setTestScoreError] = useState<string | null>(null);

  // États pour les enseignants
  const [showAddTeacherForm, setShowAddTeacherForm] = useState(false);
  const [showEditTeacherForm, setShowEditTeacherForm] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<any | null>(null);
  const [newTeacher, setNewTeacher] = useState({
    fullName: '',
    username: '',
    password: '',
    confirmPassword: '',
    phone: '',
    email: '',
    specialization: '',
    assigned_halqas: '', // Changé de assigned_halqas array vers assigned_halqas string
    experience_years: 0,
    certification_date: '',
    notes: '',
    status: 'active',
    gender: 'Male' as 'Female' | 'Male'
  });
  const [isSubmittingTeacher, setIsSubmittingTeacher] = useState(false);
  const [teacherError, setTeacherError] = useState<string | null>(null);
  const [teacherSendEmail, setTeacherSendEmail] = useState(true);
  const [teacherSendSms, setTeacherSendSms] = useState(false);
  const [teacherSendWhatsApp, setTeacherSendWhatsApp] = useState(false);
  const [showInactiveTeachers, setShowInactiveTeachers] = useState(false); // filtre : afficher aussi les enseignants inactifs (décoché par défaut)
  
  // États pour la responsivité
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileExpandedGroup, setMobileExpandedGroup] = useState<string | null>(null);

  // États pour le menu déroulant au survol
  const [hoveredNavKey, setHoveredNavKey] = useState<string | null>(null);
  const [hoveredSubNavKey, setHoveredSubNavKey] = useState<string | null>(null);
  const navLeaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearNavLeaveTimer = () => { if (navLeaveTimer.current) clearTimeout(navLeaveTimer.current); };
  const scheduleNavClose = () => { clearNavLeaveTimer(); navLeaveTimer.current = setTimeout(() => setHoveredNavKey(null), 200); };
  
  const apiService = ApiServiceAdmin.getInstance();
  
  // Enregistrer le handler d'erreur d'authentification
  useEffect(() => {
    if (onAuthError && apiService && typeof apiService.setAuthErrorHandler === 'function') {
      apiService.setAuthErrorHandler(onAuthError);
    }
  }, [onAuthError, apiService]);
  
  // Gestion du redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Fermer le menu mobile si on passe en mode desktop
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Conversion des statuts pour l'affichage
  const getDisplayStatus = (status: string) => {
    if (status === 'active') return t('active');
    if (status === 'inactive') return t('inactive');
    return status;
  };

  // Filtre du tableau « Gestion des enseignants » :
  // les enseignants inactifs sont masqués tant que la case « غير نشط » n'est pas cochée.
  const visibleTeachers = React.useMemo(
    () =>
      (Array.isArray(teachers) ? teachers : []).filter(
        (teacher: any) => showInactiveTeachers || teacher.status !== 'inactive'
      ),
    [teachers, showInactiveTeachers]
  );

  // Fonction pour filtrer les étudiants
  const filteredStudents = React.useMemo(() => {
    if (!Array.isArray(students)) return [];
    
    return students.filter(student => {
      // Filtre par nom d'étudiant (recherche exacte du nom sélectionné)
      if (studentFilters.studentName) {
        // Comparaison exacte du nom
        if (student.student_name !== studentFilters.studentName) {
        return false;
      }
      }
      
      // Filtre par nom de parent (recherche exacte du nom sélectionné)
      if (studentFilters.parentName) {
        if (student.parent_name !== studentFilters.parentName) {
        return false;
        }
      }
      
      // Filtre par nom d'enseignant
      if (studentFilters.teacherName && 
          !(student.teacher_name || '').toLowerCase().includes(studentFilters.teacherName.toLowerCase())) {
        return false;
      }
      
      // Filtre par niveau
      if (studentFilters.level && student.level !== studentFilters.level) {
        return false;
      }
      
      // Filtre par حلقة
      if (studentFilters.halqa && student.halqa !== studentFilters.halqa) {
        return false;
      }
      
      // Filtre par statut
      if (studentFilters.status) {
        const studentStatusStr = student.status as string;
        const studentStatus = studentStatusStr === 'active' || studentStatusStr === 'نشط' || studentStatusStr === '1' ? 'active' : 'inactive';
        const filterStatusStr = studentFilters.status as string;
        const filterStatus = filterStatusStr === 'active' || filterStatusStr === 'نشط' ? 'active' : 'inactive';
        if (studentStatus !== filterStatus) {
          return false;
        }
      }
      
      // Filtre par الجنس (gender)
      if (studentFilters.gender) {
        // Normaliser les valeurs de gender pour la comparaison
        // Le champ gender peut être 'Male', 'Female', 'ذكر', 'أنثى', ou null/undefined
        const studentGenderRaw = student.gender;
        // Convertir en string pour permettre les comparaisons avec d'autres formats
        const studentGenderStr = studentGenderRaw ? String(studentGenderRaw) : null;
        let studentGender: string | null = null;
        
        if (studentGenderStr) {
          const normalized = studentGenderStr.toLowerCase().trim();
          if (normalized === 'female' || normalized === 'أنثى' || normalized === 'f') {
            studentGender = 'Female';
          } else if (normalized === 'male' || normalized === 'ذكر' || normalized === 'm') {
            studentGender = 'Male';
          } else if (studentGenderStr === 'Female' || studentGenderStr === 'Male') {
            // Valeurs exactes déjà normalisées
            studentGender = studentGenderStr;
          }
        }
        
        const filterGender = studentFilters.gender;
        
        // Si le filtre est défini mais que l'étudiant n'a pas de gender correspondant, exclure
        // Si studentGender est null (pas de gender défini), exclure aussi
        if (!studentGender || studentGender !== filterGender) {
          return false;
        }
      }
      
      // Filtre par année scolaire
      // ''   => suit l'année sélectionnée dans le bandeau « Année scolaire (système) »
      // 'all' => toutes les années
      // autre => id numérique d'une année précise
      const yearFilter = studentFilters.schoolYearId;
      if (yearFilter !== 'all') {
        const targetYearId = yearFilter
          ? parseInt(yearFilter, 10)
          : (selectedYear?.id ?? currentYear?.id ?? null);
        if (targetYearId && Number.isFinite(targetYearId)) {
          if (!isEnrolledForYear(student, targetYearId)) {
            return false;
          }
        }
      }
      
      return true;
    });
  }, [students, studentFilters, selectedYear?.id, currentYear?.id]);

  // Fonction utilitaire pour convertir les statuts
  const convertTeacherStatus = (teacher: any) => {
    let status = teacher.status;
    if (status === 'نشط') status = 'active';
    if (status === 'غير نشط') status = 'inactive';
    if (status === '1') status = 'active';
    if (status === '0') status = 'inactive';
    // Normaliser le champ full_name (peut venir du backend comme fullName, full_name, name, ou depuis users.full_name)
    // Si les données viennent d'un JOIN avec users, le full_name peut être dans users.full_name
    // Le backend peut aussi retourner les données directement depuis la table users avec type='teacher'
    let full_name = teacher.full_name 
      || teacher.fullName 
      || teacher.name 
      || (teacher.user && teacher.user.full_name)
      || (teacher.users && teacher.users.full_name);
    
    // Si full_name est toujours vide, vérifier si c'est une donnée de la table users directement
    if (!full_name) {
      // Si teacher.type === 'teacher', c'est probablement une donnée de la table users
      if (teacher.type === 'teacher' && teacher.full_name === undefined) {
        // Les données peuvent être directement dans l'objet teacher si c'est un JOIN
        full_name = '';
      }
    }
    
    // Log pour déboguer si le nom est toujours vide après tous les essais
    if (!full_name && teacher.id) {
      console.warn('⚠️ Enseignant sans nom:', { 
        id: teacher.id, 
        user_id: teacher.user_id,
        type: teacher.type,
        teacher: teacher,
        availableKeys: Object.keys(teacher)
      });
    }
    
    return { ...teacher, status, full_name: full_name || '' };
  };

  // Fonction utilitaire pour convertir les statuts des parents
  const convertParentStatus = (parent: any) => {
    let status = parent.status;
    if (status === 'نشط') status = 'active';
    if (status === 'غير نشط') status = 'inactive';
    if (status === '1') status = 'active';
    if (status === '0') status = 'inactive';
    return { ...parent, status };
  };

  // Fonction pour nettoyer le texte et corriger l'encodage
  const cleanText = (text: string) => {
    if (!text) return 'غير محدد';
    
    // Décoder les entités HTML et corriger l'encodage
    let cleanedText = text
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .replace(/&apos;/g, "'")
      .replace(/&#39;/g, "'");
    
    // Essayer de décoder les caractères UTF-8 mal encodés
    try {
      // Si le texte contient des caractères mal encodés, essayer de les corriger
      if (cleanedText.includes('??????')) {
        // Remplacer les caractères mal encodés par des caractères arabes par défaut
        cleanedText = cleanedText.replace(/[?]+/g, 'غير محدد');
      }
      
      // Décoder les entités numériques HTML
      cleanedText = cleanedText.replace(/&#(\d+);/g, (match, dec) => {
        return String.fromCharCode(dec);
      });
      
      // Décoder les entités hexadécimales HTML
      cleanedText = cleanedText.replace(/&#x([0-9A-Fa-f]+);/g, (match, hex) => {
        return String.fromCharCode(parseInt(hex, 16));
      });
      
    } catch (error) {
      console.warn('Erreur lors du nettoyage du texte:', error);
    }
    
    return cleanedText;
  };

  // Fonction de formatage des dates (utilise GLOBAL_UTILS)
  const formatDate = (dateString: string) => {
    if (!dateString) return 'غير محدد';
    return GLOBAL_UTILS.formatDate(dateString) || 'غير محدد';
  };

  // Fonction de navigation
  const navigateToSection = async (section: string) => {
    setCurrentSection(section);
    
    // Si on navigue vers la section "students", recharger les enseignants et les étudiants avec leurs relations
    if (section === 'students') {
      try {
        // Recharger les enseignants
        const teachersResponse = await apiService.getTeachers();
        if (teachersResponse && teachersResponse.success && Array.isArray(teachersResponse.data)) {
          const convertedTeachers = teachersResponse.data.map(convertTeacherStatus);
          setTeachers(convertedTeachers);
        }
        
        // Recharger les étudiants avec leurs relations
        await handleRefreshStudents();
      } catch (error) {
        console.error('Erreur lors du rechargement des données pour la section students:', error);
      }
    }
  };

  // Fonction pour valider les données d'un parent
  const validateParentData = (data: any) => {
    const errors: string[] = [];

    if (!data.fullName || data.fullName.trim() === '') {
      errors.push('الاسم الكامل مطلوب');
    }

    const usernameErr = validateUsername(data.username);
    if (usernameErr) errors.push(usernameErr);

    const pwdErr = validatePassword(data.password, data.confirmPassword);
    if (pwdErr) errors.push(pwdErr);

    const phoneErr = validatePhone(data.phone);
    if (phoneErr) errors.push(phoneErr);

    // Email optionnel : un email est envoyé à l'admin lors de création/modification
    // mais le parent n'est pas obligé d'avoir une adresse email
    const emailErr = validateEmail(data.email, false);
    if (emailErr) errors.push(emailErr);

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  };

  // Fonction pour valider les champs
  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!newStudent.student_name.trim()) {
      errors.student_name = 'اسم الطالب مطلوب';
    }
    
    // Validation obligatoire de parent_id (doit être un nombre valide > 0)
    if (!newStudent.parent_id || newStudent.parent_id.trim() === '' || parseInt(newStudent.parent_id) <= 0) {
      errors.parent_id = 'يجب اختيار ولي الأمر. الطالب يجب أن يكون مرتبطاً بولي أمر.';
    }
    
    // المستوى optionnel (non obligatoire)
    // if (!newStudent.level.trim()) {
    //   errors.level = 'المستوى مطلوب';
    // }
    
    if (!newStudent.halqa.trim()) {
      errors.halqa = 'الحلقة مطلوبة';
    }
    
    if (!newStudent.status.trim()) {
      errors.status = 'الحالة مطلوبة';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fonction pour ajouter un étudiant
  const handleAddStudent = async () => {
    // Nettoyer les erreurs précédentes
    setFormError(null);
    setFieldErrors({});
    
    // Valider le formulaire
    if (!validateForm()) {
      setFormError('يرجى تصحيح الأخطاء في النموذج');
      return;
    }

    setIsSubmitting(true);
    try {
      // Préparer les données (parent_id est maintenant OBLIGATOIRE)
      const parentId = parseInt(newStudent.parent_id);
      if (!parentId || parentId <= 0) {
        setFormError('يجب اختيار ولي أمر صالح. الطالب يجب أن يكون مرتبطاً بولي أمر.');
        setIsSubmitting(false);
        return;
      }
      
      const studentData: any = {
        student_name: newStudent.student_name,
        parent_id: parentId, // OBLIGATOIRE - toujours inclus
        level: newStudent.level,
        halqa: newStudent.halqa,
        status: newStudent.status,
        notes: newStudent.notes || '',
        average: 0.00,
        attendance: 0.00,
        gender: newStudent.gender || 'Male',
        ...(newStudent.school_year_id ? { school_year_id: parseInt(newStudent.school_year_id) } : {})
      };
      
      const response = await apiService.createStudent(studentData);
      
      if (response.success) {
        const createdStudentId = response.data?.id || response.data?.student_id;
        
        // Si un enseignant a été sélectionné, créer la relation étudiant-enseignant
        if (newStudent.teacher_id && newStudent.teacher_id.trim() !== '' && createdStudentId) {
          try {
            const relationData = {
              student_id: createdStudentId,
              teacher_id: parseInt(newStudent.teacher_id),
              subject: null, // Peut être ajouté plus tard
              notes: null
            };
            const relationResponse = await apiService.addStudentTeacherRelation(relationData);
            if (!relationResponse.success) {
              console.warn('Étudiant créé mais relation avec enseignant échouée:', relationResponse.error);
              // On continue quand même, l'étudiant a été créé
            }
          } catch (error) {
            console.error('Erreur lors de la création de la relation étudiant-enseignant:', error);
            // On continue quand même, l'étudiant a été créé
          }
        }
        
        // Recharger les enseignants pour s'assurer qu'ils sont à jour
        let loadedTeachers: Teacher[] = [];
        try {
          const teachersResponse = await apiService.getTeachers();
          if (teachersResponse && teachersResponse.success && Array.isArray(teachersResponse.data)) {
            const convertedTeachers = teachersResponse.data.map(convertTeacherStatus);
            loadedTeachers = convertedTeachers;
            setTeachers(convertedTeachers);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des enseignants:', error);
        }
        
        // Charger les parents avant de rafraîchir les étudiants pour pouvoir enrichir avec parent_name
        let loadedParentsList: any[] = [];
        try {
          const parentsResponse = await apiService.getParents();
          if (parentsResponse && parentsResponse.success && Array.isArray(parentsResponse.data)) {
            const convertedParents = parentsResponse.data.map(convertParentStatus);
            loadedParentsList = convertedParents;
            setParents(convertedParents);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des parents:', error);
        }
        
        // Rafraîchir la liste des étudiants et enrichir avec les noms des enseignants et des parents
        const studentsData = await apiService.getStudents();
        if (studentsData.success && Array.isArray(studentsData.data)) {
          // ✅ Une SEULE requête pour toutes les relations élève→enseignant (avant : 1 appel par élève)
          const relationsByStudent = await loadStudentTeacherMap(apiService);
          // Enrichir les données des étudiants avec les noms des enseignants et des parents
          const studentsWithTeachers = studentsData.data.map((student: Student) => {
              let enrichedStudent = { ...student };
              
              // Enrichir avec le nom du parent si parent_name n'est pas disponible
              if (!enrichedStudent.parent_name && (enrichedStudent.parent_id || enrichedStudent.parent_user_id)) {
                const parentIdToSearch = enrichedStudent.parent_user_id || enrichedStudent.parent_id;
                const foundParent = loadedParentsList.find(p => 
                  (p.user_id === parentIdToSearch) || (p.id === parentIdToSearch)
                );
                if (foundParent) {
                  enrichedStudent.parent_name = foundParent.full_name || foundParent.fullName || null;
                  enrichedStudent.parent_user_id = foundParent.user_id || foundParent.id || null;
                }
              }
              
              // Enseignant : lecture directe de la table chargée en une requête (plus de N+1)
              const relation = relationsByStudent.get(Number(student.id));
              if (relation) {
                const teacher = loadedTeachers.find(t => (t.id === relation.teacher_id) || (t.user_id === relation.teacher_id));
                if (teacher) {
                  enrichedStudent.teacher_name = teacher.full_name || 'غير محدد';
                }
              }
              
              if (!enrichedStudent.teacher_name) {
                enrichedStudent.teacher_name = null;
              }
              
              return enrichedStudent;
          });
          setStudents(studentsWithTeachers);
        }
        
        // Réinitialiser le formulaire
        setNewStudent({
          student_name: '',
          parent_id: '',
          teacher_id: '',
          level: '',
          halqa: '',
          status: 'active',
          notes: '',
          gender: 'Male',
          school_year_id: currentYear?.id?.toString() || selectedYear?.id?.toString() || ''
        });
        
        setShowAddStudentForm(false);
        setFormError(null);
        setFieldErrors({});
        alert(t('studentAddedSuccess'));
      } else {
        // ✅ Analyser l'erreur pour donner des détails (incluant les nouveaux messages d'erreur backend)
        let errorMessage = t('studentAddError');
        let errorDetails = '';
        
        if (response.error) {
          // ✅ Gérer les nouveaux messages d'erreur du backend concernant parent_id
          if (response.error.includes('parent_id') || response.error.includes('parent') || response.error.includes('ولي الأمر')) {
            errorMessage = response.error; // Utiliser le message du backend directement
            errorDetails = '• يجب اختيار ولي أمر صالح\n• الطالب يجب أن يكون مرتبطاً بولي أمر';
          } else if (response.error.includes('student_name') || response.error.includes('name')) {
            errorMessage = 'خطأ في اسم الطالب';
            errorDetails = '• قد يكون الاسم موجود مسبقاً\n• تأكد من كتابة الاسم بشكل صحيح\n• تجنب استخدام رموز خاصة';
          } else if (response.error.includes('phone') || response.error.includes('téléphone')) {
            errorMessage = 'خطأ في رقم الهاتف';
            errorDetails = '• تأكد من أن الرقم يحتوي على أرقام فقط\n• مثال صحيح: 0123456789\n• تجنب المسافات أو الرموز الخاصة';
          } else if (response.error.includes('id_number') || response.error.includes('carte')) {
            errorMessage = 'خطأ في رقم البطاقة';
            errorDetails = '• قد يكون الرقم موجود مسبقاً\n• تأكد من أن الرقم يحتوي على 5 أرقام على الأقل\n• مثال صحيح: 12345';
          } else if (response.error.includes('database') || response.error.includes('base de données')) {
            errorMessage = 'خطأ في قاعدة البيانات';
            errorDetails = '• مشكلة في الاتصال بقاعدة البيانات\n• يرجى المحاولة مرة أخرى\n• إذا استمرت المشكلة، اتصل بالدعم الفني';
          } else if (response.error.includes('validation') || response.error.includes('validation')) {
            errorMessage = 'خطأ في التحقق من البيانات';
            errorDetails = '• تأكد من ملء جميع الحقول المطلوبة\n• تحقق من صحة البيانات المدخلة\n• تجنب استخدام رموز خاصة';
          } else {
            errorMessage = 'خطأ في الخادم';
            errorDetails = `• تفاصيل الخطأ: ${response.error}\n• يرجى المحاولة مرة أخرى\n• إذا استمرت المشكلة، اتصل بالدعم الفني`;
          }
        }
        
        setFormError(`${errorMessage}\n\n${errorDetails}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'étudiant:', error);
      setFormError('خطأ في الاتصال بالخادم\n\n• تحقق من اتصال الإنترنت\n• يرجى المحاولة مرة أخرى\n• إذا استمرت المشكلة، اتصل بالدعم الفني');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour modifier un étudiant
  const handleEditStudent = async (student: Student) => {
    setEditingStudent(student);
    
    console.log('🔍 handleEditStudent - Étudiant sélectionné:', {
      id: student.id,
      student_name: student.student_name,
      parent_id: student.parent_id,
      parent_user_id: student.parent_user_id
    });
    console.log('🔍 handleEditStudent - Parents disponibles:', parents.map(p => ({ id: p.id, user_id: p.user_id, full_name: p.full_name || p.fullName })));
    
    // Récupérer le parent_user_id directement depuis l'étudiant (parent_user_id est le user_id du parent)
    // Après les modifications du schéma, parent_id référence maintenant directement users.id (pas parents.id)
    let parentId = '';
    if (student.parent_user_id) {
      parentId = student.parent_user_id.toString();
      console.log('✅ handleEditStudent - Utilisation de parent_user_id:', parentId);
    } else if (student.parent_id) {
      // parent_id est maintenant directement users.id, chercher le parent par user_id
      const foundParent = parents.find(p => (p.user_id === student.parent_id) || (p.id === student.parent_id));
      if (foundParent) {
        // Utiliser user_id en priorité (c'est maintenant la référence directe)
        parentId = (foundParent.user_id || foundParent.id)?.toString() || '';
        console.log('✅ handleEditStudent - Parent trouvé:', { foundParent, parentId });
      } else {
        // Si pas trouvé, utiliser directement parent_id (c'est déjà users.id maintenant)
        parentId = student.parent_id.toString();
        console.log('⚠️ handleEditStudent - Parent non trouvé dans la liste, utilisation directe de parent_id:', parentId);
      }
    } else {
      console.log('⚠️ handleEditStudent - Aucun parent_id ou parent_user_id trouvé');
    }
    
    console.log('🔍 handleEditStudent - parentId final:', parentId);
    
    // Charger les relations étudiant-enseignant existantes
    let teacherId = '';
    let currentRelation = null;
    try {
      const teachersResponse = await apiService.getStudentTeachersByStudent(student.id);
      if (teachersResponse.success && Array.isArray(teachersResponse.data) && teachersResponse.data.length > 0) {
        // Prendre le premier enseignant (on suppose qu'un étudiant n'a qu'un enseignant principal pour l'instant)
        currentRelation = teachersResponse.data[0];
        // teacher_user_id est le user_id de l'enseignant
        if (currentRelation.teacher_user_id) {
          teacherId = currentRelation.teacher_user_id.toString();
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des relations étudiant-enseignant:', error);
    }
    
    setCurrentStudentTeacherRelation(currentRelation);
    
    // Normaliser le niveau pour s'assurer qu'il correspond aux valeurs du select
    let normalizedLevel = '';
    if (student.level) {
      const levelTrimmed = student.level.trim();
      // Liste des niveaux valides
      const validLevels = ['الروض', 'الأول', 'الثاني', 'الثالث', 'الرابع', 'الخامس', 'السادس', 'الإعدادي', 'الثانوي'];
      if (validLevels.includes(levelTrimmed)) {
        normalizedLevel = levelTrimmed;
      } else {
        // Essayer de mapper si la valeur est différente
        console.warn('⚠️ handleEditStudent - Valeur de level non reconnue:', levelTrimmed);
      }
    }
    
    // Normaliser la halqa de la même manière
    let normalizedHalqa = '';
    if (student.halqa) {
      const halqaTrimmed = student.halqa.trim();
      const validHalqas = ['حلقة الحفظ بالمصحف', 'حلقة الحفظ بالسمع'];
      if (validHalqas.includes(halqaTrimmed)) {
        normalizedHalqa = halqaTrimmed;
      } else {
        console.warn('⚠️ handleEditStudent - Valeur de halqa non reconnue:', halqaTrimmed);
      }
    }
    
    // Normaliser le status pour s'assurer qu'il correspond aux valeurs du select
    let normalizedStatus = 'active'; // Valeur par défaut
    if (student.status) {
      const statusTrimmed = student.status.trim();
      const statusLower = statusTrimmed.toLowerCase();
      // Mapper les différentes valeurs possibles vers les valeurs du select
      // Vérifier d'abord les valeurs en arabe (sans toLowerCase)
      if (statusTrimmed === 'نشط' || statusLower === 'active') {
        normalizedStatus = 'active';
      } else if (statusTrimmed === 'غير نشط' || statusLower === 'inactive') {
        normalizedStatus = 'inactive';
      } else {
        console.warn('⚠️ handleEditStudent - Valeur de status non reconnue:', student.status, '- Utilisation de "active" par défaut');
        normalizedStatus = 'active';
      }
    }
    
    console.log('🔍 handleEditStudent - Normalisation du status:', {
      original_status: student.status,
      normalized_status: normalizedStatus
    });
        
        setNewStudent({
      student_name: student.student_name || '',
      parent_id: parentId,
      teacher_id: teacherId,
      level: normalizedLevel,
      halqa: normalizedHalqa,
      status: normalizedStatus,
          notes: student.notes || '',
          gender: (student.gender as 'Female' | 'Male') || 'Male',
          school_year_id: student.school_year_id?.toString() || currentYear?.id?.toString() || selectedYear?.id?.toString() || ''
        });
    
    console.log('🔍 handleEditStudent - newStudent après initialisation:', {
      parent_id: parentId,
      teacher_id: teacherId,
      student_name: student.student_name
    });
    
    setShowEditStudentForm(true);
    setFormError(null);
    setFieldErrors({});
  };

  // Fonction pour supprimer un étudiant — ouvre le modal et charge les vrais comptes
  const handleDeleteStudent = async (studentId: number) => {
    const student = students.find(s => s.id === studentId);
    if (!student) return;
    setDeleteStudentInput('');
    setDeleteStudentCheck(null);
    setDeleteStudentModal({ student });
    setDeleteStudentCheckLoading(true);
    try {
      const res = await apiService.checkStudentDeletion(studentId);
      if (res.success) setDeleteStudentCheck(res.data);
    } finally {
      setDeleteStudentCheckLoading(false);
    }
  };

  // Suppression effective après confirmation dans le modal
  const confirmDeleteStudent = async () => {
    if (!deleteStudentModal) return;
    setDeleteStudentLoading(true);
    try {
      const response = await apiService.deleteStudent(deleteStudentModal.student.id);
      if (response.success) {
        const studentsData = await apiService.getStudents();
        if (studentsData.success && Array.isArray(studentsData.data)) {
          setStudents(studentsData.data);
        }
        setDeleteStudentModal(null);
        setSelectedStudentDetail(null);
      } else {
        alert('خطأ في حذف الطالب: ' + (response.message || response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    } finally {
      setDeleteStudentLoading(false);
    }
  };

  // Passer un élève en statut "inactif" (quand la suppression est bloquée par des données)
  const handleSetStudentInactive = async () => {
    if (!deleteStudentModal) return;
    setDeleteStudentLoading(true);
    try {
      const response = await apiService.updateStudent(deleteStudentModal.student.id, { status: 'inactive' });
      if (response.success) {
        const studentsData = await apiService.getStudents();
        if (studentsData.success && Array.isArray(studentsData.data)) {
          setStudents(studentsData.data);
        }
        setDeleteStudentModal(null);
        setSelectedStudentDetail(null);
      } else {
        alert('خطأ في تحديث الحالة: ' + (response.message || response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    } finally {
      setDeleteStudentLoading(false);
    }
  };

  // Fonction pour sauvegarder les modifications
  const handleUpdateStudent = async () => {
    if (!editingStudent) return;


    setFormError(null);
    setFieldErrors({});
    
    if (!validateForm()) {
      setFormError('يرجى تصحيح الأخطاء في النموذج');
      return;
    }
    setIsSubmitting(true);
    
    try {
      // Préparer les données (parent_id est maintenant directement dans l'étudiant)
      const studentData: any = {
        student_name: newStudent.student_name,
        level: newStudent.level,
        halqa: newStudent.halqa,
        status: newStudent.status,
        notes: newStudent.notes || '',
        average: 0.00,
        attendance: 0.00,
        gender: newStudent.gender || 'Male',
        ...(newStudent.school_year_id ? { school_year_id: parseInt(newStudent.school_year_id) } : {})
      };
      
      // Ajouter parent_id si un parent est sélectionné
      if (newStudent.parent_id && newStudent.parent_id.trim() !== '') {
        studentData.parent_id = parseInt(newStudent.parent_id);
      } else {
        studentData.parent_id = null;
      }
      
      const response = await apiService.updateStudent(editingStudent.id, studentData);
      
      if (response.success) {
        // Gérer la relation étudiant-enseignant
        const newTeacherId = newStudent.teacher_id && newStudent.teacher_id.trim() !== '' ? parseInt(newStudent.teacher_id) : null;
        const oldTeacherId = currentStudentTeacherRelation?.teacher_user_id ? parseInt(currentStudentTeacherRelation.teacher_user_id.toString()) : null;
        
        try {
          // Cas 1: Si une relation existe déjà
          if (currentStudentTeacherRelation) {
            // Cas 1a: L'enseignant a changé ou a été retiré
            if (newTeacherId !== oldTeacherId) {
              if (newTeacherId) {
                // Modifier la relation existante avec le nouvel enseignant
                await apiService.updateStudentTeacherRelation(currentStudentTeacherRelation.id, {
                  teacher_id: newTeacherId
                });
              } else {
                // Supprimer la relation existante
                await apiService.deleteStudentTeacherRelation(currentStudentTeacherRelation.id);
              }
            }
            // Si newTeacherId === oldTeacherId, on ne fait rien (même enseignant)
            } else {
            // Cas 2: Pas de relation existante
            if (newTeacherId) {
              // Créer une nouvelle relation
              await apiService.addStudentTeacherRelation({
                student_id: editingStudent.id,
                teacher_id: newTeacherId,
                subject: null,
                notes: null
              });
            }
            // Si pas de relation existante et pas de nouvel enseignant, on ne fait rien
          }
        } catch (error) {
          console.error('Erreur lors de la gestion de la relation étudiant-enseignant:', error);
          // On continue quand même, l'étudiant a été mis à jour
          }
        
        // Charger les enseignants pour enrichir les données
        let loadedTeachers: Teacher[] = [];
        try {
          const teachersResponse = await apiService.getTeachers();
          if (teachersResponse && teachersResponse.success && Array.isArray(teachersResponse.data)) {
            const convertedTeachers = teachersResponse.data.map(convertTeacherStatus);
            loadedTeachers = convertedTeachers;
            setTeachers(convertedTeachers);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des enseignants:', error);
        }
        
        // Charger les parents avant de rafraîchir les étudiants pour pouvoir enrichir avec parent_name
        let loadedParentsList: any[] = [];
        try {
          const parentsResponse = await apiService.getParents();
          if (parentsResponse && parentsResponse.success && Array.isArray(parentsResponse.data)) {
            const convertedParents = parentsResponse.data.map(convertParentStatus);
            loadedParentsList = convertedParents;
            setParents(convertedParents);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des parents:', error);
        }
        
        // Rafraîchir la liste complète des étudiants depuis l'API pour avoir les données à jour (notamment parent_name)
        const studentsData = await apiService.getStudents();
        if (studentsData.success && Array.isArray(studentsData.data)) {
          // ✅ Une SEULE requête pour toutes les relations élève→enseignant (avant : 1 appel par élève)
          const relationsByStudent = await loadStudentTeacherMap(apiService);
          // Enrichir les données des étudiants avec les noms des enseignants et des parents
          const studentsWithTeachers = studentsData.data.map((student: Student) => {
              let enrichedStudent = { ...student };
              
              // Enrichir avec le nom du parent si parent_name n'est pas disponible
              if (!enrichedStudent.parent_name && (enrichedStudent.parent_id || enrichedStudent.parent_user_id)) {
                const parentIdToSearch = enrichedStudent.parent_user_id || enrichedStudent.parent_id;
                const foundParent = loadedParentsList.find(p => 
                  (p.user_id === parentIdToSearch) || (p.id === parentIdToSearch)
                );
                if (foundParent) {
                  enrichedStudent.parent_name = foundParent.full_name || foundParent.fullName || null;
                  enrichedStudent.parent_user_id = foundParent.user_id || foundParent.id || null;
                }
              }
              
              const relation = relationsByStudent.get(Number(student.id));
              if (relation) {
                const teacher = loadedTeachers.find(t => (t.id === relation.teacher_id) || (t.user_id === relation.teacher_id));
                if (teacher) {
                  enrichedStudent.teacher_name = teacher.full_name || 'غير محدد';
                }
              }
              
              if (!enrichedStudent.teacher_name) {
                enrichedStudent.teacher_name = null;
              }
              
              return enrichedStudent;
          });
          setStudents(studentsWithTeachers);
        } else {
          // Si le rafraîchissement échoue, mettre à jour localement avec les données de la réponse
          // Mais toujours essayer de charger le nom de l'enseignant et du parent pour l'étudiant modifié
          const updatedStudentId = editingStudent.id;
          let teacherName: string | null = null;
          let parentName: string | null = null;
          
          try {
            const teacherRelation = await apiService.getStudentTeachersByStudent(updatedStudentId);
            if (teacherRelation.success && teacherRelation.data && teacherRelation.data.length > 0) {
              const relation = teacherRelation.data[0];
              const teacher = loadedTeachers.find(t => (t.id === relation.teacher_id) || (t.user_id === relation.teacher_id));
              if (teacher) {
                teacherName = teacher.full_name || 'غير محدد';
              }
            }
          } catch (error) {
            console.error(`Erreur lors du chargement de l'enseignant pour l'étudiant ${updatedStudentId}:`, error);
          }
          
          // Chercher le nom du parent
          const updatedStudentData = response.data;
          if (updatedStudentData && (updatedStudentData.parent_id || updatedStudentData.parent_user_id)) {
            const parentIdToSearch = updatedStudentData.parent_user_id || updatedStudentData.parent_id;
            const foundParent = loadedParentsList.find(p => 
              (p.user_id === parentIdToSearch) || (p.id === parentIdToSearch)
            );
            if (foundParent) {
              parentName = foundParent.full_name || foundParent.fullName || null;
            }
          }
          
          const updatedStudents = students.map(student => 
            student.id === updatedStudentId 
              ? { 
                  ...student, 
                  ...response.data,
                  teacher_name: teacherName || student.teacher_name,
                  parent_name: parentName || student.parent_name,
                  parent_user_id: updatedStudentData?.parent_user_id || updatedStudentData?.parent_id || student.parent_user_id
                }
              : student
          );
          setStudents(updatedStudents);
        }
        
        // Réinitialiser les états
        setCurrentStudentTeacherRelation(null);
        
        setShowEditStudentForm(false);
        setEditingStudent(null);
        setFormError(null);
        setFieldErrors({});
        alert(t('studentUpdatedSuccess'));
      } else {
        setFormError(response.error || t('studentUpdateError'));
      }
    } catch (error) {
      console.error('Erreur lors de la modification de l\'étudiant:', error);
      setFormError('خطأ في الاتصال بالخادم');
    } finally {
      setIsSubmitting(false);
    }
  };


  // Fonctions pour gérer les enseignants
  const handleAddTeacher = async () => {
    setTeacherError(null);

    // Validation des champs requis - créer une liste des champs manquants
    const missingFields: string[] = [];
    
    if (!newTeacher.fullName || newTeacher.fullName.trim() === '') {
      missingFields.push('الاسم الكامل');
    }
    const usernameErrT = validateUsername(newTeacher.username);
    if (usernameErrT) { setTeacherError(usernameErrT); return; }
    if (!newTeacher.username || newTeacher.username.trim() === '') {
      missingFields.push('اسم المستخدم');
    }
    if (!newTeacher.password || newTeacher.password.trim() === '') {
      missingFields.push('كلمة المرور');
    }
    if (!newTeacher.phone || newTeacher.phone.trim() === '') {
      missingFields.push('رقم الهاتف');
    }
    if (!newTeacher.email || newTeacher.email.trim() === '') {
      missingFields.push('البريد الإلكتروني');
    }
    if (!newTeacher.specialization || newTeacher.specialization.trim() === '') {
      missingFields.push('التخصص');
    }

    // Si des champs manquent, afficher un message précis
    if (missingFields.length > 0) {
      setTeacherError(`يرجى ملء الحقول التالية: ${missingFields.join('، ')}`);
      return;
    }

    const phoneErrT = validatePhone(newTeacher.phone);
    if (phoneErrT) { setTeacherError(phoneErrT); return; }

    const emailErrT = validateEmail(newTeacher.email);
    if (emailErrT) { setTeacherError(emailErrT); return; }

    const pwdErrT = validatePassword(newTeacher.password, newTeacher.confirmPassword);
    if (pwdErrT) { setTeacherError(pwdErrT); return; }

    // Vérifier l'unicité du username
    const teacherUsernameExists = teachers.some((t: any) =>
      t.username && t.username.toLowerCase() === newTeacher.username.trim().toLowerCase()
    );
    if (teacherUsernameExists) {
      setTeacherError('اسم المستخدم موجود بالفعل. يرجى اختيار اسم مستخدم آخر.');
      return;
    }

    // Validation de la spécialisation
    if (!GLOBAL_CONSTANTS.SPECIALIZATIONS.includes(newTeacher.specialization)) {
      setTeacherError(`التخصص غير صحيح. التخصصات المسموحة: ${GLOBAL_CONSTANTS.SPECIALIZATIONS.join(', ')}`);
      return;
    }

    // Validation des halqas assignées si fournies
    if (newTeacher.assigned_halqas) {
      const halqas = newTeacher.assigned_halqas ? newTeacher.assigned_halqas.split(',').map(s => s.trim()) : [];
      const invalidHalqas = halqas.filter(halqa => !GLOBAL_CONSTANTS.MANAGED_CIRCLES.includes(halqa));
      
      if (invalidHalqas.length > 0) {
        setTeacherError(`الحلقات غير صحيحة: ${invalidHalqas.join(', ')}. الحلقات المسموحة: ${GLOBAL_CONSTANTS.MANAGED_CIRCLES.join(', ')}`);
        return;
      }
    }

    setIsSubmittingTeacher(true);
    
    try {
      const teacherData = {
        userType: 'teacher' as const,
        fullName: newTeacher.fullName,
        username: newTeacher.username,
        password: newTeacher.password,
        phone: formatPhoneForStorage(newTeacher.phone, teacherPhoneCountry, defaultCountry),
        email: newTeacher.email,
        specialization: newTeacher.specialization,
        assigned_halqas: newTeacher.assigned_halqas,
        experience_years: newTeacher.experience_years,
        certification_date: newTeacher.certification_date,
        notes: newTeacher.notes,
        gender: (newTeacher.gender || 'Male').toLowerCase(),
        send_email: teacherSendEmail,
        send_sms: teacherSendSms,
        send_whatsapp: teacherSendWhatsApp,
      };

      const response = await apiService.createUnifiedUser(teacherData);

      if (response.success) {
        await handleRefreshTeachers();

        // Ouvrir les liens SMS / WhatsApp si activés (avant reset du formulaire)
        const storedPhone = formatPhoneForStorage(newTeacher.phone, teacherPhoneCountry, defaultCountry);
        const waPhone = storedPhone.replace(/[^\d]/g, '');
        const credMsg = `بيانات الدخول:\nاسم المستخدم: ${newTeacher.username}\nكلمة المرور: ${newTeacher.password}`;
        if (teacherSendSms && newTeacher.phone.trim()) {
          window.open(`sms:${storedPhone}?body=${encodeURIComponent(credMsg)}`, '_blank');
        }
        if (teacherSendWhatsApp && newTeacher.phone.trim()) {
          window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(credMsg)}`, '_blank');
        }

        alert('تم إضافة الأستاذ بنجاح');

        setShowAddTeacherForm(false);
        setNewTeacher({
          fullName: '',
          username: '',
          password: '',
          confirmPassword: '',
          phone: '',
          email: '',
          specialization: '',
          assigned_halqas: '',
          experience_years: 0,
          certification_date: '',
          notes: '',
          status: 'active',
          gender: 'Male'
        });
        setTeacherSendEmail(true); setTeacherSendSms(false); setTeacherSendWhatsApp(false);
      } else {
        setTeacherError(response.error || 'خطأ في إضافة الأستاذ');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'enseignant:', error);
      setTeacherError('خطأ في الاتصال بالخادم');
    } finally {
      setIsSubmittingTeacher(false);
    }
  };

  const handleEditTeacher = (teacher: any) => {
    console.log('🔍 handleEditTeacher - Enseignant sélectionné:', teacher);
    console.log('🔍 handleEditTeacher - Spécialisations disponibles:', teacher.teacher_specializations);
    console.log('🔍 handleEditTeacher - Halqas assignés:', teacher.assigned_halqas);
    
    setEditingTeacher(teacher);
    
    // Extraire les spécialisations depuis teacher_specializations
    let specializations = [];
    if (teacher.teacher_specializations && Array.isArray(teacher.teacher_specializations)) {
      specializations = teacher.teacher_specializations.map((spec: any) => spec.specialization);
    }
    
    // Utiliser la première spécialisation comme spécialisation principale
    const mainSpecialization = specializations.length > 0 ? specializations[0] : (teacher.specialization || '');
    
    // Les autres spécialisations deviennent des halqas
    const additionalHalqas = specializations.slice(1);
    
    // Gérer assigned_halqas selon son type (string ou array)
    let existingHalqas = [];
    if (teacher.assigned_halqas) {
      if (typeof teacher.assigned_halqas === 'string') {
        existingHalqas = teacher.assigned_halqas.split(',').map((s: string) => s.trim()).filter((s: string) => s);
      } else if (Array.isArray(teacher.assigned_halqas)) {
        existingHalqas = teacher.assigned_halqas.filter((s: string) => s && s.trim());
      }
    }
    
    const allHalqas = [...existingHalqas, ...additionalHalqas];
    
    console.log('🔍 handleEditTeacher - Spécialisations extraites:', specializations);
    console.log('🔍 handleEditTeacher - Spécialisation principale:', mainSpecialization);
    console.log('🔍 handleEditTeacher - Halqas supplémentaires:', additionalHalqas);
    console.log('🔍 handleEditTeacher - Tous les halqas:', allHalqas);
    
    const { country: tPhoneCountry, localNumber: tLocalPhone } = parseStoredPhone(teacher.phone || '', defaultCountry);
    setTeacherPhoneCountry(tPhoneCountry);
    setNewTeacher({
      fullName: teacher.full_name || '',
      username: teacher.username || '',
      password: '', // Champ vide pour permettre la modification optionnelle
      confirmPassword: '', // Plus utilisé
      phone: tLocalPhone,
      email: teacher.email || '',
      specialization: mainSpecialization,
      assigned_halqas: allHalqas.join(', '),
      experience_years: teacher.experience_years || 0,
      certification_date: teacher.certification_date || '',
      notes: teacher.notes || '',
      status: teacher.status || 'active',
      gender: (teacher.gender as 'Female' | 'Male') || 'Male'
    });
    setTeacherSendEmail(true); setTeacherSendSms(false); setTeacherSendWhatsApp(false);
    setShowEditTeacherForm(true);
  };

  const handleUpdateTeacher = async () => {
    if (!editingTeacher) return;
    
    setTeacherError(null);
    
    if (!newTeacher.fullName || !newTeacher.username || !newTeacher.phone || !newTeacher.email || !newTeacher.specialization) {
      setTeacherError('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    const usernameErrTE = validateUsername(newTeacher.username);
    if (usernameErrTE) { setTeacherError(usernameErrTE); return; }

    const phoneErrTE = validatePhone(newTeacher.phone);
    if (phoneErrTE) { setTeacherError(phoneErrTE); return; }

    const emailErrTE = validateEmail(newTeacher.email);
    if (emailErrTE) { setTeacherError(emailErrTE); return; }

    const pwdErrTE = validatePassword(newTeacher.password, newTeacher.confirmPassword, false);
    if (pwdErrTE) { setTeacherError(pwdErrTE); return; }

    // Validation de la spécialisation
    if (!GLOBAL_CONSTANTS.SPECIALIZATIONS.includes(newTeacher.specialization)) {
      setTeacherError(`التخصص غير صحيح. التخصصات المسموحة: ${GLOBAL_CONSTANTS.SPECIALIZATIONS.join(', ')}`);
      return;
    }

    // Validation des halqas assignées si fournies
    if (newTeacher.assigned_halqas) {
      const halqas = newTeacher.assigned_halqas.split(',').map(s => s.trim()).filter(s => s);
      const invalidHalqas = halqas.filter(halqa => !GLOBAL_CONSTANTS.MANAGED_CIRCLES.includes(halqa));
      
      if (invalidHalqas.length > 0) {
        setTeacherError(`الحلقات غير صحيحة: ${invalidHalqas.join(', ')}. الحلقات المسموحة: ${GLOBAL_CONSTANTS.MANAGED_CIRCLES.join(', ')}`);
      return;
      }
    }

    setIsSubmittingTeacher(true);
    try {
      const teacherData: any = {
        userType: 'teacher' as const,
        fullName: newTeacher.fullName,
        username: newTeacher.username,
        phone: formatPhoneForStorage(newTeacher.phone, teacherPhoneCountry, defaultCountry),
        email: newTeacher.email,
        specialization: newTeacher.specialization,
        assigned_halqas: newTeacher.assigned_halqas,
        experience_years: newTeacher.experience_years,
        certification_date: newTeacher.certification_date,
        notes: newTeacher.notes,
        status: newTeacher.status || 'active',
        gender: (newTeacher.gender || 'Male').toLowerCase(),
        send_email: teacherSendEmail,
        send_sms: teacherSendSms,
        send_whatsapp: teacherSendWhatsApp,
      };

      if (newTeacher.password && newTeacher.password.trim() !== '') {
        teacherData.password = newTeacher.password;
      }

      const response = await apiService.updateUnifiedUser(editingTeacher.id, teacherData);

      if (response.success) {
        await handleRefreshTeachers();

        // Ouvrir les liens SMS / WhatsApp si activés (avant reset du formulaire)
        const storedPhone = formatPhoneForStorage(newTeacher.phone, teacherPhoneCountry, defaultCountry);
        const waPhone = storedPhone.replace(/[^\d]/g, '');
        const credLines = [`بيانات الدخول:\nاسم المستخدم: ${newTeacher.username}`];
        if (newTeacher.password && newTeacher.password.trim()) credLines.push(`كلمة المرور: ${newTeacher.password}`);
        const credMsg = credLines.join('\n');
        if (teacherSendSms && newTeacher.phone.trim()) {
          window.open(`sms:${storedPhone}?body=${encodeURIComponent(credMsg)}`, '_blank');
        }
        if (teacherSendWhatsApp && newTeacher.phone.trim()) {
          window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(credMsg)}`, '_blank');
        }

        setShowEditTeacherForm(false);
        setEditingTeacher(null);
        setNewTeacher({
          fullName: '',
          username: '',
          password: '',
          confirmPassword: '',
          phone: '',
          email: '',
          specialization: '',
          assigned_halqas: '',
          experience_years: 0,
          certification_date: '',
          notes: '',
          status: 'active',
          gender: 'Male'
        });
        setTeacherSendEmail(true); setTeacherSendSms(false); setTeacherSendWhatsApp(false);
        alert('تم تحديث بيانات الأستاذ بنجاح');
      } else {
        setTeacherError(response.error || 'خطأ في تحديث الأستاذ');
      }
    } catch (error) {
      console.error('Erreur lors de la modification de l\'enseignant:', error);
      setTeacherError('خطأ في الاتصال بالخادم');
    } finally {
      setIsSubmittingTeacher(false);
    }
  };



  // ==================== ADMINS MANAGEMENT FUNCTIONS ====================

  // Ajouter un administrateur
  const handleAddAdmin = async () => {
    // Validation des données
    if (!newAdmin.fullName || !newAdmin.username || !newAdmin.password) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    const usernameErrA = validateUsername(newAdmin.username);
    if (usernameErrA) { alert(usernameErrA); return; }

    const pwdErrA = validatePassword(newAdmin.password, adminPasswordConfirm);
    if (pwdErrA) { alert(pwdErrA); return; }

    const emailErrA = validateEmail(newAdmin.email);
    if (emailErrA) { alert(emailErrA); return; }

    const phoneErrA = validatePhone(newAdmin.phone);
    if (phoneErrA) { alert(phoneErrA); return; }

    if (newAdmin.permissions.length === 0) {
      alert('يرجى اختيار صلاحية واحدة على الأقل');
      return;
    }

    // Vérifier si le username existe déjà dans la liste des admins chargés
    const usernameExists = admins.some((admin: any) => 
      admin.username && admin.username.toLowerCase() === newAdmin.username.toLowerCase()
    );
    if (usernameExists) {
      alert('اسم المستخدم موجود بالفعل. يرجى اختيار اسم مستخدم آخر.');
      return;
    }

    setIsSubmittingAdmin(true);
    try {
      // Préparer les données à envoyer
      const adminData: any = { ...newAdmin };
      
      // Nettoyer les champs vides (ne pas envoyer email/phone si vides ou null)
      if (!adminData.email || adminData.email.trim() === '') {
        delete adminData.email;
      } else {
        adminData.email = adminData.email.trim();
      }
      
      if (!adminData.phone || adminData.phone.trim() === '') {
        delete adminData.phone;
      } else {
        adminData.phone = formatPhoneForStorage(adminData.phone.trim(), adminPhoneCountry, defaultCountry);
      }
      
      // Nettoyer aussi les autres champs
      if (adminData.notes && adminData.notes.trim() === '') {
        delete adminData.notes;
      }
      
      // S'assurer que le username est nettoyé
      adminData.username = adminData.username.trim();
      
      // Envoyer les permissions telles quelles (tableau)
      // Plus besoin de convertir en 'all', on envoie toujours le tableau complet
      
      console.log('🔍 handleAddAdmin - Données à envoyer:', adminData);
      const response = await (apiService as any).createUnifiedUser({
        ...adminData,
        userType: 'admin',
        send_email: adminSendEmail,
        send_sms: adminSendSms,
        send_whatsapp: adminSendWhatsApp,
      });
      
      if (response.success) {
        // Ouvrir les liens SMS / WhatsApp si activés (avant reset du formulaire)
        if (newAdmin.phone) {
          const storedPhone = formatPhoneForStorage(newAdmin.phone, adminPhoneCountry, defaultCountry);
          const waPhone = storedPhone.replace(/[^\d]/g, '');
          const credMsg = `بيانات الدخول:\nاسم المستخدم: ${newAdmin.username}\nكلمة المرور: ${newAdmin.password}`;
          if (adminSendSms) {
            window.open(`sms:${storedPhone}?body=${encodeURIComponent(credMsg)}`, '_blank');
          }
          if (adminSendWhatsApp) {
            window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(credMsg)}`, '_blank');
          }
        }

        // Ajouter le nouvel administrateur à la liste
        const updatedAdmins = [...admins, response.data];
        setAdmins(updatedAdmins);
        
        // Réinitialiser le formulaire
        setNewAdmin({
          fullName: '',
          username: '',
          password: '',
          phone: '',
          email: '',
          permissions: ['students', 'teachers', 'parents', 'admins', 'communication', 'grades', 'finances'],
          notes: '',
          status: 'active',
          gender: 'Male'
        });
        setAdminSendEmail(true);
        setAdminSendSms(false);
        setAdminSendWhatsApp(false);
        setAdminPasswordConfirm('');
        setShowAddAdminForm(false);
        alert('تم إضافة المدير بنجاح');
      } else {
        alert('خطأ في إضافة المدير: ' + (response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    } finally {
      setIsSubmittingAdmin(false);
    }
  };

  // Modifier un administrateur
  const handleEditAdmin = (admin: any) => {
    // Normaliser les permissions
    let adminPermissions = admin.permissions;
    if (typeof adminPermissions === 'string') {
      // Si c'est une chaîne avec des permissions séparées par des virgules, normaliser les permissions arabes
      adminPermissions = adminPermissions.split(',').map((p: string) => normalizePermission(p.trim()));
    } else if (Array.isArray(adminPermissions)) {
      // Normaliser les permissions arabes en clés anglaises
      adminPermissions = adminPermissions.map((p: string) => normalizePermission(p.trim()));
    } else {
      adminPermissions = ['students', 'teachers', 'parents', 'admins', 'communication', 'grades', 'finances'];
    }
    
    setEditingAdmin(admin);
    const { country: aPhoneCountry, localNumber: aLocalPhone } = parseStoredPhone(admin.phone || '', defaultCountry);
    setAdminPhoneCountry(aPhoneCountry);
    setNewAdmin({
      fullName: admin.full_name || admin.fullName || '',
      username: admin.username || '',
      password: '', // Champ vide pour permettre la modification optionnelle
      phone: aLocalPhone,
      email: admin.email || '',
      permissions: adminPermissions,
      notes: admin.notes || '',
      status: admin.status || 'active',
      gender: (admin.gender as 'Female' | 'Male') || 'Male'
    });
    setAdminPasswordConfirm(''); // Réinitialiser le champ de confirmation
    setShowEditAdminForm(true);
  };

  // Mettre à jour un administrateur
  const handleUpdateAdmin = async () => {
    if (!editingAdmin) return;
    
    if (!newAdmin.fullName || !newAdmin.username) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    const usernameErrAE = validateUsername(newAdmin.username);
    if (usernameErrAE) { alert(usernameErrAE); return; }

    const emailErrAE = validateEmail(newAdmin.email);
    if (emailErrAE) { alert(emailErrAE); return; }

    const phoneErrAE = validatePhone(newAdmin.phone);
    if (phoneErrAE) { alert(phoneErrAE); return; }

    if (newAdmin.permissions.length === 0) {
      alert('يرجى اختيار صلاحية واحدة على الأقل');
      return;
    }

    const pwdErrAE = validatePassword(newAdmin.password, adminPasswordConfirm, false);
    if (pwdErrAE) { alert(pwdErrAE); return; }

    setIsSubmittingAdmin(true);
    try {
      // Préparer les données à envoyer (ne pas envoyer le mot de passe s'il est vide)
      const adminData: any = { ...newAdmin };
      if (!adminData.password || adminData.password.trim() === '') {
        delete adminData.password;
      }
      if (adminData.phone && adminData.phone.trim()) {
        adminData.phone = formatPhoneForStorage(adminData.phone.trim(), adminPhoneCountry, defaultCountry);
      }

      const response = await (apiService as any).updateUnifiedUser(editingAdmin.id, {
        ...adminData,
        userType: 'admin',
        send_email: adminSendEmail,
        send_sms: adminSendSms,
        send_whatsapp: adminSendWhatsApp,
      });
      
      if (response.success) {
        // Mettre à jour l'administrateur dans la liste locale pour que le statut soit immédiatement visible
        const updatedAdmins = admins.map(admin => 
          admin.id === editingAdmin.id 
            ? { ...admin, ...response.data, status: newAdmin.status || admin.status }
            : admin
        );
        setAdmins(updatedAdmins);

        // Ouvrir les liens SMS / WhatsApp si activés (avant reset du formulaire)
        if (newAdmin.phone) {
          const storedPhone = formatPhoneForStorage(newAdmin.phone, adminPhoneCountry, defaultCountry);
          const waPhone = storedPhone.replace(/[^\d]/g, '');
          const credLines = [`بيانات الدخول:\nاسم المستخدم: ${newAdmin.username}`];
          if (newAdmin.password && newAdmin.password.trim()) credLines.push(`كلمة المرور: ${newAdmin.password}`);
          const credMsg = credLines.join('\n');
          if (adminSendSms) {
            window.open(`sms:${storedPhone}?body=${encodeURIComponent(credMsg)}`, '_blank');
          }
          if (adminSendWhatsApp) {
            window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(credMsg)}`, '_blank');
          }
        }
        
        setShowEditAdminForm(false);
        setEditingAdmin(null);
        setAdminSendEmail(true);
        setAdminSendSms(false);
        setAdminSendWhatsApp(false);
        alert('تم تحديث بيانات المدير بنجاح');
      } else {
        alert('خطأ في تحديث بيانات المدير: ' + (response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    } finally {
      setIsSubmittingAdmin(false);
    }
  };

  // ==================== MONTHLY CONTRIBUTIONS MANAGEMENT FUNCTIONS ====================

  // Ouvrir le formulaire d'édition d'une contribution
  const handleEditContribution = (contribution: any) => {
    setEditingContribution(contribution);
    setEditingContributionValues({
      annual_membership: (contribution.annual_membership || contribution.annualMembership || 0).toString(),
      september: (contribution.september || 0).toString(),
      october: (contribution.october || 0).toString(),
      november: (contribution.november || 0).toString(),
      december: (contribution.december || 0).toString(),
      january: (contribution.january || 0).toString(),
      february: (contribution.february || 0).toString(),
      march: (contribution.march || 0).toString(),
      april: (contribution.april || 0).toString(),
      may: (contribution.may || 0).toString(),
      june: (contribution.june || 0).toString(),
      july: (contribution.july || 0).toString(),
      august: (contribution.august || 0).toString()
    });
    setShowEditContributionForm(true);
  };

  // Mettre à jour une contribution
  const handleUpdateContribution = async () => {
    if (!editingContribution) return;

    setIsSubmittingContribution(true);
    try {
      // Préparer les données à envoyer
      const contributionData: any = {
        annual_membership: parseFloat(editingContributionValues.annual_membership) || 0,
        september: parseFloat(editingContributionValues.september) || 0,
        october: parseFloat(editingContributionValues.october) || 0,
        november: parseFloat(editingContributionValues.november) || 0,
        december: parseFloat(editingContributionValues.december) || 0,
        january: parseFloat(editingContributionValues.january) || 0,
        february: parseFloat(editingContributionValues.february) || 0,
        march: parseFloat(editingContributionValues.march) || 0,
        april: parseFloat(editingContributionValues.april) || 0,
        may: parseFloat(editingContributionValues.may) || 0,
        june: parseFloat(editingContributionValues.june) || 0,
        july: parseFloat(editingContributionValues.july) || 0,
        august: parseFloat(editingContributionValues.august) || 0
      };

      // Récupérer les valeurs avant modification pour comparer
      const oldValues = {
        annual_membership: parseFloat(editingContribution.annual_membership || editingContribution.annualMembership || 0) || 0,
        september: parseFloat(editingContribution.september || 0) || 0,
        october: parseFloat(editingContribution.october || 0) || 0,
        november: parseFloat(editingContribution.november || 0) || 0,
        december: parseFloat(editingContribution.december || 0) || 0,
        january: parseFloat(editingContribution.january || 0) || 0,
        february: parseFloat(editingContribution.february || 0) || 0,
        march: parseFloat(editingContribution.march || 0) || 0,
        april: parseFloat(editingContribution.april || 0) || 0,
        may: parseFloat(editingContribution.may || 0) || 0,
        june: parseFloat(editingContribution.june || 0) || 0,
        july: parseFloat(editingContribution.july || 0) || 0,
        august: parseFloat(editingContribution.august || 0) || 0
      };

      const response = await apiService.updateMonthlyContribution(editingContribution.id, contributionData);

      if (response.success) {
        // Mettre à jour la contribution dans l'état local
        setMonthlyContributions(monthlyContributions.map(c => 
          c.id === editingContribution.id 
            ? { ...c, ...contributionData }
            : c
        ));

        // Détecter les changements et envoyer un message au parent
        const changes: string[] = [];
        const monthNames: { [key: string]: string } = {
          annual_membership: t('annualEnrollment'),
          september: t('september'),
          october: t('october'),
          november: t('november'),
          december: t('december'),
          january: t('january'),
          february: t('february'),
          march: t('march'),
          april: t('april'),
          may: t('may'),
          june: t('june'),
          july: t('july'),
          august: t('august')
        };

        // Vérifier chaque champ pour détecter les changements
        Object.keys(contributionData).forEach(key => {
          const oldValue = oldValues[key as keyof typeof oldValues];
          const newValue = contributionData[key];
          if (oldValue !== newValue) {
            const fieldName = monthNames[key] || key;
            changes.push(`${fieldName}: ${oldValue.toFixed(2)} → ${newValue.toFixed(2)} دج`);
          }
        });

        // Si des changements ont été détectés, envoyer un message au parent
        if (changes.length > 0) {
          try {
            // Trouver le parent_user_id via student_id
            let parentUserId: number | null = null;
            const studentId = editingContribution.student_id;
            
            if (studentId) {
              const student = students.find(s => s.id === studentId);
              if (student && student.parent_user_id) {
                parentUserId = student.parent_user_id;
              }
            }

            // Si on n'a pas trouvé via student, essayer via contribution directement
            if (!parentUserId && editingContribution.parent_user_id) {
              parentUserId = editingContribution.parent_user_id;
            }

            if (parentUserId) {
              const studentName = editingContribution.student_name || 'الطالب';
              const messageContent = `تم تحديث رسوم الطالب ${studentName}:\n\n${changes.join('\n')}\n\nشكراً لكم.`;
              
              // Obtenir les informations de l'admin actuel
              const currentUser = apiService.getCurrentUser();
              const senderId = currentUser?.id || currentUser?.user_id || 1;
              const senderName = currentAdminName || 'المدير العام';
              
              const messageResponse = await apiService.sendMessage({
                sender_id: senderId,
                sender_name: senderName,
                recipient_type: 'specific',
                recipient_ids: [parentUserId],
                subject: 'تحديث رسوم الطالب',
                content: messageContent,
                priority: 'normal'
              });

              if (!messageResponse.success) {
                console.warn('⚠️ Échec de l\'envoi du message au parent:', messageResponse.error);
              } else {
                console.log('✅ Message envoyé au parent avec succès');
              }
            } else {
              console.warn('⚠️ Impossible de trouver le parent_user_id pour envoyer le message');
            }
          } catch (messageError) {
            console.error('❌ Erreur lors de l\'envoi du message au parent:', messageError);
            // Ne pas bloquer la mise à jour si l'envoi du message échoue
          }
        }

        setShowEditContributionForm(false);
        setEditingContribution(null);
        alert('تم تحديث بيانات الرسوم بنجاح');
      } else {
        alert(response.error || 'خطأ في تحديث بيانات الرسوم');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la contribution:', error);
      alert('خطأ في تحديث بيانات الرسوم');
    } finally {
      setIsSubmittingContribution(false);
    }
  };

  // Fonctions pour gérer les salaires des enseignants
  const handleEditSalary = (salary: any) => {
    setEditingSalary(salary);
    setEditingSalaryValues({
      september: (salary.september || 0).toString(),
      october: (salary.october || 0).toString(),
      november: (salary.november || 0).toString(),
      december: (salary.december || 0).toString(),
      january: (salary.january || 0).toString(),
      february: (salary.february || 0).toString(),
      march: (salary.march || 0).toString(),
      april: (salary.april || 0).toString(),
      may: (salary.may || 0).toString(),
      june: (salary.june || 0).toString(),
      july: (salary.july || 0).toString(),
      august: (salary.august || 0).toString()
    });
    setShowEditSalaryForm(true);
  };

  const handleUpdateSalary = async () => {
    if (!editingSalary) return;

    setIsSubmittingSalary(true);
    try {
      const salaryData: any = {
        september: parseFloat(editingSalaryValues.september) || 0,
        october: parseFloat(editingSalaryValues.october) || 0,
        november: parseFloat(editingSalaryValues.november) || 0,
        december: parseFloat(editingSalaryValues.december) || 0,
        january: parseFloat(editingSalaryValues.january) || 0,
        february: parseFloat(editingSalaryValues.february) || 0,
        march: parseFloat(editingSalaryValues.march) || 0,
        april: parseFloat(editingSalaryValues.april) || 0,
        may: parseFloat(editingSalaryValues.may) || 0,
        june: parseFloat(editingSalaryValues.june) || 0,
        july: parseFloat(editingSalaryValues.july) || 0,
        august: parseFloat(editingSalaryValues.august) || 0
      };

      const response = await apiService.updateTeacherSalary(editingSalary.id, salaryData);

      if (response.success) {
        // Mettre à jour le salaire dans l'état local
        setTeacherSalaries(teacherSalaries.map(s => 
          s.id === editingSalary.id 
            ? { ...s, ...salaryData }
            : s
        ));

        alert(t('updateSuccess') || 'تم التحديث بنجاح');
        setShowEditSalaryForm(false);
        setEditingSalary(null);
        setIsSubmittingSalary(false);
      } else {
        alert(response.error || 'Erreur lors de la mise à jour du salaire');
        setIsSubmittingSalary(false);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du salaire');
      setIsSubmittingSalary(false);
    }
  };

  // ==================== FINANCIAL RECORDS MANAGEMENT FUNCTIONS ====================

  // Ajouter un enregistrement financier
  const handleAddFinancialRecord = async () => {
    if (!newFinancialRecord.category_name || newFinancialRecord.category_name.trim() === '') {
      alert('يرجى إدخال اسم الفئة');
      return;
    }

    setIsSubmittingFinancialRecord(true);
    try {
      // Calculer le total_amount (somme de tous les mois)
      const totalAmount = [
        parseFloat(newFinancialRecord.september) || 0,
        parseFloat(newFinancialRecord.october) || 0,
        parseFloat(newFinancialRecord.november) || 0,
        parseFloat(newFinancialRecord.december) || 0,
        parseFloat(newFinancialRecord.january) || 0,
        parseFloat(newFinancialRecord.february) || 0,
        parseFloat(newFinancialRecord.march) || 0,
        parseFloat(newFinancialRecord.april) || 0,
        parseFloat(newFinancialRecord.may) || 0,
        parseFloat(newFinancialRecord.june) || 0,
        parseFloat(newFinancialRecord.july) || 0,
        parseFloat(newFinancialRecord.august) || 0
      ].reduce((sum, val) => sum + val, 0);

      const recordData = {
        category_name: newFinancialRecord.category_name.trim(),
        category_type: newFinancialRecord.category_type,
        display_order: newFinancialRecord.display_order || 1,
        september: parseFloat(newFinancialRecord.september) || 0,
        october: parseFloat(newFinancialRecord.october) || 0,
        november: parseFloat(newFinancialRecord.november) || 0,
        december: parseFloat(newFinancialRecord.december) || 0,
        january: parseFloat(newFinancialRecord.january) || 0,
        february: parseFloat(newFinancialRecord.february) || 0,
        march: parseFloat(newFinancialRecord.march) || 0,
        april: parseFloat(newFinancialRecord.april) || 0,
        may: parseFloat(newFinancialRecord.may) || 0,
        june: parseFloat(newFinancialRecord.june) || 0,
        july: parseFloat(newFinancialRecord.july) || 0,
        august: parseFloat(newFinancialRecord.august) || 0,
        total_amount: totalAmount,
        description: null
      };

      const response = await apiService.addFinancialRecord(recordData);
      
      if (response.success) {
        // Rafraîchir la liste
        const refreshResponse = await apiService.getFinancialRecords({});
        if (refreshResponse.success) {
          setFinancialRecords(refreshResponse.data || []);
        }
        setShowAddFinancialRecordForm(false);
        setNewFinancialRecord({
          category_name: '',
          category_type: 'revenue',
          display_order: 1,
          september: '',
          october: '',
          november: '',
          december: '',
          january: '',
          february: '',
          march: '',
          april: '',
          may: '',
          june: '',
          july: '',
          august: ''
        });
        alert('تم إضافة السجل المالي بنجاح');
      } else {
        alert('خطأ في إضافة السجل المالي: ' + (response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    } finally {
      setIsSubmittingFinancialRecord(false);
    }
  };

  // Mettre à jour un enregistrement financier
  const handleUpdateFinancialRecord = async () => {
    if (!editingFinancialRecord) return;
    
    // Protéger les lignes calculées contre la modification
    if (isCalculatedRecord(editingFinancialRecord)) {
      alert('لا يمكن تعديل هذه السجلات. يتم حسابها تلقائياً.');
      return;
    }
    
    if (!editingFinancialRecordValues.category_name || editingFinancialRecordValues.category_name.trim() === '') {
      alert('يرجى إدخال اسم الفئة');
      return;
    }

    setIsSubmittingFinancialRecord(true);
    try {
      // Calculer le total_amount (somme de tous les mois)
      const totalAmount = [
        parseFloat(editingFinancialRecordValues.september) || 0,
        parseFloat(editingFinancialRecordValues.october) || 0,
        parseFloat(editingFinancialRecordValues.november) || 0,
        parseFloat(editingFinancialRecordValues.december) || 0,
        parseFloat(editingFinancialRecordValues.january) || 0,
        parseFloat(editingFinancialRecordValues.february) || 0,
        parseFloat(editingFinancialRecordValues.march) || 0,
        parseFloat(editingFinancialRecordValues.april) || 0,
        parseFloat(editingFinancialRecordValues.may) || 0,
        parseFloat(editingFinancialRecordValues.june) || 0,
        parseFloat(editingFinancialRecordValues.july) || 0,
        parseFloat(editingFinancialRecordValues.august) || 0
      ].reduce((sum, val) => sum + val, 0);

      const recordData = {
        category_name: editingFinancialRecordValues.category_name.trim(),
        category_type: editingFinancialRecordValues.category_type,
        display_order: editingFinancialRecordValues.display_order || 1,
        september: parseFloat(editingFinancialRecordValues.september) || 0,
        october: parseFloat(editingFinancialRecordValues.october) || 0,
        november: parseFloat(editingFinancialRecordValues.november) || 0,
        december: parseFloat(editingFinancialRecordValues.december) || 0,
        january: parseFloat(editingFinancialRecordValues.january) || 0,
        february: parseFloat(editingFinancialRecordValues.february) || 0,
        march: parseFloat(editingFinancialRecordValues.march) || 0,
        april: parseFloat(editingFinancialRecordValues.april) || 0,
        may: parseFloat(editingFinancialRecordValues.may) || 0,
        june: parseFloat(editingFinancialRecordValues.june) || 0,
        july: parseFloat(editingFinancialRecordValues.july) || 0,
        august: parseFloat(editingFinancialRecordValues.august) || 0,
        total_amount: totalAmount,
        description: null
      };

      const response = await apiService.updateFinancialRecord(editingFinancialRecord.id, recordData);
      
      if (response.success) {
        // Rafraîchir la liste
        const refreshResponse = await apiService.getFinancialRecords({});
        if (refreshResponse.success) {
          setFinancialRecords(refreshResponse.data || []);
        }
        setShowEditFinancialRecordForm(false);
        setEditingFinancialRecord(null);
        alert('تم تحديث السجل المالي بنجاح');
      } else {
        alert('خطأ في تحديث السجل المالي: ' + (response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    } finally {
      setIsSubmittingFinancialRecord(false);
    }
  };

  // Supprimer un administrateur
  const handleDeleteAdmin = async (adminId: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المدير؟')) {
      return;
    }

    try {
      const response = await apiService.deleteAdmin(adminId);
      
      if (response.success) {
        // Supprimer l'administrateur de la liste
        const updatedAdmins = admins.filter(admin => admin.id !== adminId);
        setAdmins(updatedAdmins);
        alert('تم حذف المدير بنجاح');
      } else {
        alert('خطأ في حذف المدير: ' + (response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    }
  };

  // Rafraîchir la liste des administrateurs
  const handleRefreshAdmins = async () => {
    try {
      const adminsResponse = await apiService.getAdmins();
      if (!adminsResponse.success || !Array.isArray(adminsResponse.data)) {
        alert('Erreur lors du rafraîchissement: ' + (adminsResponse.error || 'Erreur inconnue'));
        return;
      }

      // Convertir les statuts des administrateurs
      const convertedAdmins = adminsResponse.data.map(convertAdminStatus);
      setAdmins(convertedAdmins);
    } catch (error) {
      alert('Erreur lors du rafraîchissement: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    }
  };

  // Convertir le statut d'un administrateur
  const convertAdminStatus = (admin: any) => {
    let status = admin.status;
    if (status === 'نشط') status = 'active';
    if (status === 'غير نشط') status = 'inactive';
    if (status === '1') status = 'active';
    if (status === '0') status = 'inactive';
    
    // Gérer les permissions
    let permissions = admin.permissions;
    
    if (typeof permissions === 'string') {
      // Si c'est une chaîne avec des permissions séparées par des virgules, normaliser les permissions arabes
      permissions = permissions.split(',').map((p: string) => normalizePermission(p.trim()));
    } else if (Array.isArray(permissions)) {
      // Normaliser les permissions arabes en clés anglaises
      permissions = permissions.map((p: string) => normalizePermission(p.trim()));
    } else {
      permissions = ['students', 'teachers', 'parents', 'admins', 'communication', 'grades', 'finances'];
    }
    
    return { ...admin, status, permissions };
  };

  // ==================== PARENTS MANAGEMENT FUNCTIONS ====================

  // Ajouter un parent
  const handleAddParent = async () => {
    // Validation des données
    const validation = validateParentData(newParent);
    if (!validation.isValid) {
      alert('خطأ في البيانات:\n' + validation.errors.join('\n'));
      return;
    }

    // Vérifier l'unicité du username
    const parentUsernameExists = parents.some((p: any) =>
      p.username && p.username.toLowerCase() === newParent.username.trim().toLowerCase()
    );
    if (parentUsernameExists) {
      alert('اسم المستخدم موجود بالفعل. يرجى اختيار اسم مستخدم آخر.');
      return;
    }

    try {
      const response = await apiService.createUnifiedUser({
        userType: 'parent',
        fullName: newParent.fullName,
        username: newParent.username,
        password: newParent.password,
        phone: formatPhoneForStorage(newParent.phone, parentPhoneCountry, defaultCountry),
        email: newParent.email,
        address: newParent.address,
        notes: newParent.notes,
        status: newParent.status,
        gender: newParent.gender,
        send_email: parentSendEmail,
        send_sms: parentSendSms,
        send_whatsapp: parentSendWhatsApp,
      });

      if (response.success) {
        // Ouvrir les liens SMS / WhatsApp si activés (avant reset du formulaire)
        const storedPhone = formatPhoneForStorage(newParent.phone, parentPhoneCountry, defaultCountry);
        const waPhone = storedPhone.replace(/[^\d]/g, '');
        const credMsg = `بيانات الدخول:\nاسم المستخدم: ${newParent.username}\nكلمة المرور: ${newParent.password}`;
        if (parentSendSms && newParent.phone.trim()) {
          window.open(`sms:${storedPhone}?body=${encodeURIComponent(credMsg)}`, '_blank');
        }
        if (parentSendWhatsApp && newParent.phone.trim()) {
          window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(credMsg)}`, '_blank');
        }

        setParents([...parents, response.data]);
        const createdUserId = response.data?.user_id || response.data?.id;
        if (inscriptionToLink && createdUserId) {
          try {
            const linkRes = await apiService.linkInscriptionUser(inscriptionToLink, createdUserId);
            if (linkRes.success) {
              const detail = await apiService.getInscriptionRequestById(inscriptionToLink);
              if (detail.success) {
                setInscriptionDetail(detail.data);
                setInscriptionAdminNotes(detail.data.admin_notes || '');
                setInscriptionItems((prev: any[]) => prev.map((i: any) => i.id === inscriptionToLink ? { ...i, ...detail.data } : i));
              }
              alert('تم تسجيل ولي الأمر وربط الطلب بنجاح');
            } else {
              alert('تم إنشاء الحساب لكن فشل ربط الطلب: ' + (linkRes.error || linkRes.message || ''));
            }
          } catch {
            alert('تم إنشاء الحساب لكن حدث خطأ أثناء الربط');
          }
          setInscriptionToLink(null);
        } else {
          alert('تم إضافة ولي الأمر بنجاح');
        }
        setNewParent({
          fullName: '',
          username: '',
          password: '',
          confirmPassword: '',
          phone: '',
          email: '',
          address: '',
          notes: '',
          status: 'active',
          gender: 'Male'
        });
        setParentSendEmail(true);
        setParentSendSms(false);
        setParentSendWhatsApp(false);
        setShowAddParentForm(false);
      } else {
        alert('خطأ في إضافة ولي الأمر: ' + (response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    }
  };

  // Modifier un parent
  const handleParentDetailOpen = async (parent: any) => {
    const parentId = parent.id || parent.user_id;
    // Toggle : clic sur le même parent → ferme le panneau
    if (selectedParentDetail && (selectedParentDetail.id || selectedParentDetail.user_id) === parentId) {
      setSelectedParentDetail(null);
      setParentDetailChildren([]);
      return;
    }
    setSelectedParentDetail(parent);
    setParentDetailChildren([]);
    setParentDetailLoading(true);
    try {
      const adminService = ApiServiceAdmin.getInstance();
      const res = await adminService.getParentStudents(parentId);
      if (res.success && Array.isArray(res.data)) {
        setParentDetailChildren(res.data);
      } else {
        console.error('getParentStudents failed:', res);
      }
    } catch (err) {
      console.error('handleParentDetailOpen error:', err);
    } finally {
      setParentDetailLoading(false);
    }
  };

  const handleParentReenroll = async () => {
    const targetYearId = inscriptionYear?.id ?? currentYear?.id ?? null;
    if (parentReenrollSelection.length === 0 || !targetYearId) return;
    const parentId = selectedParentDetail?.id || selectedParentDetail?.user_id;
    if (!parentId) return;
    try {
      const res = await apiService.reenrollParentStudents(parentId, {
        student_ids: parentReenrollSelection,
        school_year_id: targetYearId,
      });
      if (res.success) {
        setParentReenrollMode(false);
        setParentReenrollSelection([]);
        await handleParentDetailOpen(selectedParentDetail);
      } else {
        alert(res.error || 'Erreur réinscription');
      }
    } catch (err) {
      alert('Erreur réinscription');
    }
  };

  const handleEditParent = (parent: any) => {
    setEditingParent(parent);
    const { country: pPhoneCountry, localNumber: pLocalPhone } = parseStoredPhone(parent.phone || '', defaultCountry);
    setParentPhoneCountry(pPhoneCountry);
    setNewParent({
      fullName: parent.full_name || parent.fullName || '',
      username: parent.username || '',
      password: '',
      confirmPassword: '',
      phone: pLocalPhone,
      email: parent.email || '',
      address: parent.address || '',
      notes: parent.notes || '',
      status: parent.status || 'active',
      gender: (parent.gender as 'Female' | 'Male') || 'Male'
    });
    setParentSendEmail(true);
    setParentSendSms(false);
    setParentSendWhatsApp(false);
    setShowEditParentForm(true);
  };

  // Mettre à jour un parent
  const handleUpdateParent = async () => {
    if (!editingParent) return;

    if (!newParent.fullName || !newParent.phone) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    const usernameErrP = validateUsername(newParent.username);
    if (usernameErrP) { alert(usernameErrP); return; }

    const phoneErrP = validatePhone(newParent.phone);
    if (phoneErrP) { alert(phoneErrP); return; }

    // Email optionnel : un email est envoyé à l'admin lors de création/modification
    const emailErrP = validateEmail(newParent.email, false);
    if (emailErrP) { alert(emailErrP); return; }

    const pwdErrP = validatePassword(newParent.password, newParent.confirmPassword, false);
    if (pwdErrP) { alert(pwdErrP); return; }

    try {
      const parentData: any = {
        userType: 'parent' as const,
        fullName: newParent.fullName,
        username: newParent.username,
        phone: formatPhoneForStorage(newParent.phone, parentPhoneCountry, defaultCountry),
        email: newParent.email,
        address: newParent.address,
        notes: newParent.notes,
        status: newParent.status || 'active',
        gender: newParent.gender || 'Male',
        send_email: parentSendEmail,
        send_sms: parentSendSms,
        send_whatsapp: parentSendWhatsApp,
      };

      if (newParent.password && newParent.password.trim() !== '') {
        parentData.password = newParent.password;
      }

      const response = await apiService.updateUnifiedUser(editingParent.id, parentData);
      
      if (response.success) {
        // Mettre à jour le parent dans la liste locale avec toutes les données modifiées
        const updatedParents = parents.map(parent => {
          if (parent.id === editingParent.id) {
            // Utiliser les données du formulaire directement car c'est ce que l'utilisateur a modifié
            // et compléter avec les données de la réponse API si nécessaire
            return {
              ...parent,
              ...response.data,
              // Mapper explicitement tous les champs du formulaire vers les propriétés du parent
              full_name: newParent.fullName,
              fullName: newParent.fullName,
              username: newParent.username,
              phone: newParent.phone,
              email: newParent.email,
              address: newParent.address || '',
              notes: newParent.notes || '',
              status: newParent.status || 'active',
              id: parent.id,
              user_id: parent.user_id || response.data.user_id || parent.user_id,
              created_date: parent.created_date || response.data.created_date || parent.created_date
            };
          }
          return parent;
        });
        setParents(updatedParents);

        // Ouvrir les liens SMS / WhatsApp si activés (avant reset du formulaire)
        const storedPhone = formatPhoneForStorage(newParent.phone, parentPhoneCountry, defaultCountry);
        const waPhone = storedPhone.replace(/[^\d]/g, '');
        const credLines = [`بيانات الدخول:\nاسم المستخدم: ${newParent.username}`];
        if (newParent.password && newParent.password.trim()) credLines.push(`كلمة المرور: ${newParent.password}`);
        const credMsg = credLines.join('\n');
        if (parentSendSms && newParent.phone.trim()) {
          window.open(`sms:${storedPhone}?body=${encodeURIComponent(credMsg)}`, '_blank');
        }
        if (parentSendWhatsApp && newParent.phone.trim()) {
          window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(credMsg)}`, '_blank');
        }

        setShowEditParentForm(false);
        setEditingParent(null);
        setNewParent({
          fullName: '',
          username: '',
          password: '',
          confirmPassword: '',
          phone: '',
          email: '',
          address: '',
          notes: '',
          status: 'active',
          gender: 'Male'
        });
        setParentSendEmail(true);
        setParentSendSms(false);
        setParentSendWhatsApp(false);
        alert('تم تحديث بيانات ولي الأمر بنجاح');
            } else {
        alert('خطأ في تحديث ولي الأمر: ' + (response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    }
  };

  // Supprimer un parent — ouvre le modal de confirmation
  const handleDeleteParent = (parentId: number) => {
    const parent = parents.find(p => p.id === parentId);
    if (!parent) return;
    const childrenCount = students.filter(s => s.parent_id === parentId || s.parent_user_id === parentId).length;
    setDeleteParentInput('');
    setDeleteParentModal({ parent, childrenCount });
  };

  // Suppression effective après confirmation dans le modal
  const confirmDeleteParent = async () => {
    if (!deleteParentModal) return;
    setDeleteParentLoading(true);
    try {
      const response = await apiService.deleteParent(deleteParentModal.parent.id);
      if (response.success) {
        setParents(parents.filter(p => p.id !== deleteParentModal.parent.id));
        setDeleteParentModal(null);
        setSelectedParentDetail(null);
      } else {
        alert('خطأ في حذف ولي الأمر: ' + (response.message || response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    } finally {
      setDeleteParentLoading(false);
    }
  };

  // Passer un parent en statut "inactif" (quand la suppression est bloquée par des enfants)
  const handleSetParentInactive = async () => {
    if (!deleteParentModal) return;
    setDeleteParentLoading(true);
    try {
      const response = await apiService.updateUnifiedUser(deleteParentModal.parent.id, {
        userType: 'parent' as const,
        status: 'inactive'
      });
      if (response.success) {
        await handleRefreshParents();
        setDeleteParentModal(null);
        setSelectedParentDetail(null);
      } else {
        alert('خطأ في تحديث الحالة: ' + (response.message || response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      alert('خطأ في الاتصال بالخادم: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    } finally {
      setDeleteParentLoading(false);
    }
  };

  // Rafraîchir la liste des parents
  const handleRefreshParents = async () => {
    try {
      const parentsResponse = await apiService.getParents();
      if (!parentsResponse.success || !Array.isArray(parentsResponse.data)) {
        alert('Erreur lors du rafraîchissement: ' + (parentsResponse.error || 'Erreur inconnue'));
        return;
      }

      // Convertir les statuts des parents
      const convertedParents = parentsResponse.data.map(convertParentStatus);
      setParents(convertedParents);
    } catch (error) {
      alert('Erreur lors du rafraîchissement: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    }
  };

  // ==================== PARAMETERS MANAGEMENT FUNCTIONS ====================

  // Charger tous les paramètres
  const fetchParameters = async () => {
    setLoadingParameters(true);
    try {
      const response = await apiService.getParameters();
      if (response.success && Array.isArray(response.data)) {
        setParameters(response.data);
        // Initialiser le pays par défaut depuis les paramètres
        const countryParam = response.data.find((p: any) => p.name === 'default_country');
        if (countryParam?.value) {
          setDefaultCountry(getCountryByCode(countryParam.value));
        }
      } else {
        console.error('Erreur lors du chargement des paramètres:', response.error);
        alert(t('unknownError') + ': ' + (response.error || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      alert(t('unknownError') + ': ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    } finally {
      setLoadingParameters(false);
    }
  };

  // Sauvegarder le pays par défaut
  const handleDefaultCountryChange = async (country: Country) => {
    setDefaultCountry(country);
    setCountryPickerOpen(false);
    setCountrySearch('');
    setSavingCountry(true);
    try {
      await apiService.updateParameter('default_country', { value: country.code });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du pays par défaut:', error);
    } finally {
      setSavingCountry(false);
    }
  };

  // Rafraîchir la liste des paramètres
  const handleRefreshParameters = async () => {
    try {
      const response = await apiService.getParameters();
      if (!response.success || !Array.isArray(response.data)) {
        alert('Erreur lors du rafraîchissement: ' + (response.error || 'Erreur inconnue'));
        return;
      }
      setParameters(response.data);
    } catch (error) {
      alert('Erreur lors du rafraîchissement: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    }
  };

  // Créer un nouveau paramètre
  const handleCreateParameter = async () => {
    // Validation
    if (!newParameter.name || !newParameter.value) {
      alert(t('parameterNameRequired') + '\n' + t('parameterValueRequired'));
      return;
    }

    try {
      const response = await apiService.createParameter(newParameter);
      
      if (response.success) {
        setParameters([...parameters, response.data]);
        setNewParameter({ name: '', value: '', description: '' });
        setShowAddParameterForm(false);
        alert(t('parameterCreated'));
      } else {
        alert((response.error || t('unknownError')));
      }
    } catch (error) {
      alert(t('unknownError') + ': ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    }
  };

  // Modifier un paramètre (ouvrir le formulaire)
  const handleEditParameter = (parameter: any) => {
    setEditingParameter(parameter);
    setNewParameter({
      name: parameter.name || '',
      value: parameter.value || '',
      description: parameter.description || ''
    });
    setShowEditParameterForm(true);
  };

  // Mettre à jour un paramètre
  const handleUpdateParameter = async () => {
    if (!editingParameter) return;
    
    // Validation
    if (!newParameter.value) {
      alert(t('parameterValueRequired'));
      return;
    }

    try {
      const updateData = {
        value: newParameter.value,
        description: newParameter.description
      };

      const response = await apiService.updateParameter(editingParameter.name, updateData);
      
      if (response.success) {
        // Mettre à jour le paramètre dans la liste locale
        const updatedParameters = parameters.map(param => 
          param.name === editingParameter.name ? response.data : param
        );
        setParameters(updatedParameters);
        setNewParameter({ name: '', value: '', description: '' });
        setEditingParameter(null);
        setShowEditParameterForm(false);
        alert(t('parameterUpdated'));
      } else {
        alert((response.error || t('unknownError')));
      }
    } catch (error) {
      alert(t('unknownError') + ': ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    }
  };


  // Réinitialiser le formulaire des paramètres
  const resetParameterForm = () => {
    setNewParameter({ name: '', value: '', description: '' });
    setEditingParameter(null);
    setShowAddParameterForm(false);
    setShowEditParameterForm(false);
  };

  // Charger les emails envoyés
  const loadSentEmails = useCallback(async () => {
    setSentEmailsLoading(true);
    setSentEmailsError(null);
    try {
      const response = await apiService.getSentEmails({ limit: 50, status: 'sent' });
      if (response.success) {
        setSentEmails(response.data || []);
        setSentEmailsPagination(response.pagination || {});
      } else {
        setSentEmailsError(response.error || 'Erreur lors du chargement des emails envoyés');
      }
    } catch (error) {
      setSentEmailsError(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      setSentEmailsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger les emails reçus
  const loadReceivedEmails = useCallback(async () => {
    setReceivedEmailsLoading(true);
    setReceivedEmailsError(null);
    try {
      const response = await apiService.getReceivedEmails({ limit: 50 });
      if (response.success) {
        setReceivedEmails(response.data || []);
        setReceivedEmailsPagination(response.pagination || {});
      } else {
        setReceivedEmailsError(response.error || 'Erreur lors du chargement des emails reçus');
      }
    } catch (error) {
      setReceivedEmailsError(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      setReceivedEmailsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Synchroniser les emails reçus depuis Resend
  const syncReceivedEmails = useCallback(async () => {
    setReceivedEmailsLoading(true);
    setReceivedEmailsError(null);
    try {
      console.log('🔄 Synchronisation des emails depuis Resend...');
      const syncResponse = await apiService.syncReceivedEmails(50);
      
      if (syncResponse.success) {
        console.log('✅ Synchronisation réussie:', syncResponse.data);
        // Recharger les emails après synchronisation
        await loadReceivedEmails();
        // Afficher un message de succès
        setReceivedEmailsError(null);
      } else {
        console.error('❌ Erreur de synchronisation:', syncResponse.error);
        setReceivedEmailsError(syncResponse.error || 'Erreur lors de la synchronisation des emails');
      }
    } catch (error) {
      console.error('❌ Exception lors de la synchronisation:', error);
      setReceivedEmailsError(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      setReceivedEmailsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadReceivedEmails]);

  // Fonction helper pour extraire le nom de l'étudiant depuis le contenu
  const extractStudentNameFromContent = (content: string): { studentName: string | null; cleanContent: string } => {
    if (!content) return { studentName: null, cleanContent: content || '' };
    
    let cleanContent = GLOBAL_UTILS.cleanText(content);
    let studentName = null;
    
    // Chercher le pattern avec les différentes formes inclusives
    const studentLabelPatterns = [
      /الطالب\/ـة:\s*([^\n]+)/i,       // Arabe: الطالب/ـة
      /الطالب\/الطالبة:\s*([^\n]+)/i,  // Arabe: الطالب/الطالبة (ancien format)
      /الطالب\(ة\):\s*([^\n]+)/i,      // Arabe: الطالب(ة)
      /الطالب:\s*([^\n]+)/i,           // Arabe: الطالب (ancien format)
      /Student\(s\):\s*([^\n]+)/i,     // Anglais: Student(s)
      /Student:\s*([^\n]+)/i,          // Anglais: Student (ancien format)
      /Étudiant\(e\):\s*([^\n]+)/i,    // Français: Étudiant(e)
      /Étudiant:\s*([^\n]+)/i          // Français: Étudiant (ancien format)
    ];
    
    for (const pattern of studentLabelPatterns) {
      const match = cleanContent.match(pattern);
      if (match && match[1]) {
        studentName = match[1].trim();
        // Retirer le nom de l'étudiant du contenu pour l'affichage
        cleanContent = cleanContent.replace(pattern, '').trim();
        // Retirer les sauts de ligne multiples au début
        cleanContent = cleanContent.replace(/^\n+/, '').trim();
        break;
      }
    }
    
    return { studentName, cleanContent };
  };

  // Fonction pour charger les messages reçus
  const loadReceivedMessages = useCallback(async () => {
    try {
      const messagesData = await apiService.getMessages();
      if (messagesData && messagesData.success && Array.isArray(messagesData.data)) {
        // Créer un Set des IDs des admins pour une vérification rapide
        const adminIds = new Set<number | string>();
        admins.forEach((admin: any) => {
          if (admin.id) {
            adminIds.add(Number(admin.id));
            adminIds.add(String(admin.id));
          }
          if (admin.user_id) {
            adminIds.add(Number(admin.user_id));
            adminIds.add(String(admin.user_id));
          }
        });
        
        // Filtrer les messages selon la règle :
        // 1. Exclure tous les messages dont sender_id est de type 'admin' (messages envoyés par la direction)
        // 2. Inclure tous les messages restants où recipient_type = 'admin'
        const receivedMessages = messagesData.data.filter((message: any) => {
          const senderId = message.sender_id;
          
          // Exclure les messages envoyés par un admin (vérifier si sender_id est dans la liste des admins)
          if (senderId) {
            const senderIdNum = Number(senderId);
            const senderIdStr = String(senderId);
            if (adminIds.has(senderIdNum) || adminIds.has(senderIdStr)) {
              return false;
            }
          }
          
          // Inclure uniquement les messages où recipient_type === 'admin' (insensible à la casse et aux espaces)
          const recipientType = message.recipient_type ? String(message.recipient_type).trim().toLowerCase() : '';
          return recipientType === 'admin';
        });
        
        // Mapper les messages pour extraire le nom de l'étudiant depuis le contenu
        const mappedMessages = receivedMessages.map((message: any) => {
          const { studentName, cleanContent } = extractStudentNameFromContent(message.content || '');
          return {
            ...message,
            content: cleanContent,
            student_id: message.student_id || null,
            student_name: studentName || message.student_name || null,
            priority: message.priority || 'normal',
            sent_time: message.sent_time || null,
            sender_type: message.sender_type || null,
            is_reply: message.reply_to_message_id !== null && message.reply_to_message_id !== undefined,
            reply_to_message_id: message.reply_to_message_id || null
          };
        });
        setMessages(mappedMessages as Message[]);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des messages reçus:', error);
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiService, admins]);

  // Initialiser le filtre année inscription à l'année courante dès que years est chargé
  useEffect(() => {
    if (years.length > 0 && inscriptionFilterSchoolYear === 'current') {
      const current = years.find(y => y.is_current);
      if (current) setInscriptionFilterSchoolYear(String(current.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years]);

  // Charger les données quand on change d'onglet communication
  useEffect(() => {
    if (currentSection === 'communication') {
      if (communicationTab === 'messages') {
        loadReceivedMessages();
      } else if (communicationTab === 'emails') {
        if (emailsSubTab === 'sent') loadSentEmails();
        else loadReceivedEmails();
      } else if (communicationTab === 'inscription-requests') {
        loadInscriptionRequests({ school_year_id: inscriptionFilterSchoolYear as any });
      } else if (communicationTab === 'contact-messages') {
        loadContactRequests();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communicationTab, emailsSubTab, currentSection, loadSentEmails, loadReceivedEmails, loadReceivedMessages]);

  const loadInscriptionRequests = async (params: { search?: string; status?: string; school_year_id?: number | 'current' | '' } = {}) => {
    setInscriptionLoading(true);
    try {
      const syId = params.school_year_id ?? inscriptionFilterSchoolYear;
      const resolvedSyId = syId === 'current'
        ? years.find(y => y.is_current)?.id
        : syId === '' || syId === undefined ? undefined
        : typeof syId === 'number' ? syId
        : parseInt(String(syId), 10) || undefined;
      const res = await apiService.getInscriptionRequests({
        limit: 50,
        search: params.search,
        status: params.status,
        school_year_id: resolvedSyId,
      });
      if (res.success) {
        const items = Array.isArray(res.data) ? res.data : [];
        setInscriptionItems(items);
      }
    } catch {}
    setInscriptionLoading(false);
  };

  const loadContactRequests = async (params: { search?: string; status?: string } = {}) => {
    setContactLoading(true);
    try {
      const res = await apiService.getContactRequests({ limit: 50, ...params });
      if (res.success) setContactItems(Array.isArray(res.data) ? res.data : []);
    } catch {}
    setContactLoading(false);
  };

  const updateInscriptionStatus = async (id: number, status: string) => {
    setInscriptionActionLoading(true);
    try {
      const res = await apiService.updateInscriptionStatus(id, status, inscriptionAdminNotes);
      if (res.success) {
        setInscriptionItems(prev => prev.map((i: any) => i.id === id ? { ...i, status, admin_notes: inscriptionAdminNotes } : i));
        if (inscriptionSelected?.id === id) setInscriptionSelected((p: any) => ({ ...p, status, admin_notes: inscriptionAdminNotes }));
        if (inscriptionDetail?.id === id) setInscriptionDetail((p: any) => ({ ...p, status, admin_notes: inscriptionAdminNotes }));
      }
    } catch {}
    setInscriptionActionLoading(false);
  };

  const openInscriptionDetail = async (item: any) => {
    // Re-clic sur le même item → fermer le détail
    if (inscriptionSelected?.id === item.id) {
      setInscriptionSelected(null);
      setInscriptionDetail(null);
      setInscriptionAdminNotes('');
      return;
    }
    setInscriptionSelected(item);
    setInscriptionAdminNotes(item.admin_notes || '');
    setInscriptionDetail(null);
    setInscriptionDetailLoading(true);
    try {
      const res = await apiService.getInscriptionRequestById(item.id);
      const detail = res.success ? res.data : item;
      setInscriptionDetail(detail);
      setInscriptionAdminNotes(detail.admin_notes || '');
    } catch {
      setInscriptionDetail(item);
    } finally {
      setInscriptionDetailLoading(false);
    }
  };

  const provisionStudents = async (inscriptionId: number) => {
    setInscriptionProvisionLoading(true);
    try {
      const provisionRes = await apiService.provisionInscriptionStudents(inscriptionId);
      if (provisionRes.success) {
        const res = await apiService.getInscriptionRequestById(inscriptionId);
        if (res.success) {
          setInscriptionDetail(res.data);
          setInscriptionAdminNotes(res.data.admin_notes || '');
          setInscriptionItems((prev: any[]) => prev.map((i: any) => i.id === inscriptionId ? { ...i, status: res.data.status, school_year_id: res.data.school_year_id } : i));
        }
      }
    } catch {}
    setInscriptionProvisionLoading(false);
  };


  const updateContactStatusFn = async (id: number, status: string, reply?: string) => {
    setContactActionLoading(true);
    try {
      const res = await apiService.updateContactStatus(id, status, reply);
      if (res.success) {
        setContactItems(prev => prev.map((i: any) => i.id === id ? { ...i, status, ...(reply ? { reply_message: reply } : {}) } : i));
        if (contactSelected?.id === id) setContactSelected((p: any) => ({ ...p, status, ...(reply ? { reply_message: reply } : {}) }));
        if (contactDetail?.id === id) setContactDetail((p: any) => ({ ...p, status, ...(reply ? { reply_message: reply } : {}) }));
      }
    } catch {}
    setContactActionLoading(false);
  };

  const openContactDetail = async (item: any) => {
    // Re-clic sur le même item → fermer le détail
    if (contactSelected?.id === item.id) {
      setContactSelected(null);
      setContactDetail(null);
      setContactShowReply(false);
      setContactReplyText('');
      return;
    }
    setContactSelected(item);
    setContactReplyText('');
    setContactShowReply(false);
    setContactDetail(null);
    setContactDetailLoading(true);
    if (item.status === 'new') {
      updateContactStatusFn(item.id, 'read');
    }
    try {
      const res = await apiService.getContactRequestById(item.id);
      setContactDetail(res.success ? res.data : item);
    } catch {
      setContactDetail(item);
    } finally {
      setContactDetailLoading(false);
    }
  };

  // Fonction pour extraire le texte sans HTML
  // Supporte deux signatures :
  // 1. getPlainText(content, maxLength) - pour les messages simples
  // 2. getPlainText(htmlContent, textContent, maxLength) - pour les emails
  const getPlainText = (
    htmlContentOrContent: string | null | undefined, 
    textContentOrMaxLength?: string | null | number, 
    maxLength?: number
  ): string => {
    // Si le deuxième paramètre est un nombre, c'est la signature simple (content, maxLength)
    if (typeof textContentOrMaxLength === 'number') {
      const content = htmlContentOrContent;
      const maxLen = textContentOrMaxLength;
      if (!content) return '';
      const text = GLOBAL_UTILS.cleanText(content);
      if (text.length <= maxLen) return text;
      return text.substring(0, maxLen) + '...';
    }
    
    // Sinon, c'est la signature complète (htmlContent, textContent, maxLength)
    const htmlContent = htmlContentOrContent;
    const textContent = textContentOrMaxLength as string | null | undefined;
    const maxLen = maxLength || 80;
    
    if (textContent) {
      const cleaned = textContent.trim().replace(/\s+/g, ' ');
      return cleaned.length > maxLen ? cleaned.substring(0, maxLen) + '...' : cleaned;
    }
    if (htmlContent) {
      // Supprimer les balises <style> et leur contenu
      let text = htmlContent.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
      // Supprimer les balises <script> et leur contenu
      text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
      // Supprimer toutes les balises HTML
      text = text.replace(/<[^>]*>/g, '');
      // Remplacer les entités HTML
      text = text.replace(/&nbsp;/g, ' ')
                  .replace(/&amp;/g, '&')
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>')
                  .replace(/&quot;/g, '"')
                  .replace(/&#39;/g, "'")
                  .replace(/&apos;/g, "'");
      // Nettoyer les espaces multiples et les sauts de ligne
      text = text.replace(/\s+/g, ' ').trim();
      return text.length > maxLen ? text.substring(0, maxLen) + '...' : text;
    }
    return 'لا يوجد محتوى';
  };

  // Gestionnaire pour toggle l'ouverture d'un email envoyé
  const handleToggleSentEmail = (emailId: number | string) => {
    if (openedSentEmailId === emailId) {
      setOpenedSentEmailId(null);
    } else {
      setOpenedSentEmailId(emailId);
    }
  };

  // Gestionnaire pour toggle l'ouverture d'un email reçu
  const handleToggleReceivedEmail = (emailId: number | string) => {
    if (openedReceivedEmailId === emailId) {
      setOpenedReceivedEmailId(null);
    } else {
      setOpenedReceivedEmailId(emailId);
    }
  };

  // ==================== PARENT-STUDENT RELATIONS FUNCTIONS ====================
  // ⚠️ SUPPRIMÉ : Les relations parent-étudiant sont maintenant gérées directement via students.parent_id


  // Fonction pour rafraîchir la liste des étudiants
  // Fonction pour réinitialiser tous les filtres
  const handleResetFilters = () => {
    setStudentFilters({
      studentName: '',
      parentName: '',
      teacherName: '',
      level: '',
      halqa: '',
      status: '',
      gender: '',
      schoolYearId: ''
    });
  };

  const handleRefreshStudents = async () => {
    try {
      // Charger d'abord les enseignants pour pouvoir enrichir les données des étudiants
      const teachersResponse = await apiService.getTeachers();
      let loadedTeachers: Teacher[] = [];
      if (teachersResponse && teachersResponse.success && Array.isArray(teachersResponse.data)) {
        const convertedTeachers = teachersResponse.data.map(convertTeacherStatus);
        loadedTeachers = convertedTeachers;
        setTeachers(convertedTeachers);
      }
      
      // Charger les parents pour pouvoir enrichir avec parent_name
      let loadedParentsList: any[] = [];
      try {
        const parentsResponse = await apiService.getParents();
        if (parentsResponse && parentsResponse.success && Array.isArray(parentsResponse.data)) {
          const convertedParents = parentsResponse.data.map(convertParentStatus);
          loadedParentsList = convertedParents;
          setParents(convertedParents);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des parents:', error);
      }
      
      // Charger les étudiants
      const studentsResponse = await apiService.getStudents();
      if (!studentsResponse.success || !Array.isArray(studentsResponse.data)) {
        alert('Erreur lors du rafraîchissement: ' + (studentsResponse.error || 'Erreur inconnue'));
        return;
      }

      // Enrichir les données des étudiants avec les noms des enseignants et des parents
      const relationsByStudent = await loadStudentTeacherMap(apiService); // ✅ 1 seule requête (avant : 1 par élève)
      const studentsWithTeachers = studentsResponse.data.map((student: Student) => {
          let enrichedStudent = { ...student };
          
          // Enrichir avec le nom du parent si parent_name n'est pas disponible
          if (!enrichedStudent.parent_name && (enrichedStudent.parent_id || enrichedStudent.parent_user_id)) {
            const parentIdToSearch = enrichedStudent.parent_user_id || enrichedStudent.parent_id;
            const foundParent = loadedParentsList.find(p => 
              (p.user_id === parentIdToSearch) || (p.id === parentIdToSearch)
            );
            if (foundParent) {
              enrichedStudent.parent_name = foundParent.full_name || foundParent.fullName || null;
              enrichedStudent.parent_user_id = foundParent.user_id || foundParent.id || null;
            }
          }
          
          const relation = relationsByStudent.get(Number(student.id));
          if (relation) {
            const teacher = loadedTeachers.find(t => (t.id === relation.teacher_id) || (t.user_id === relation.teacher_id));
            if (teacher) {
              enrichedStudent.teacher_name = teacher.full_name || 'غير محدد';
            }
          }
          
          if (!enrichedStudent.teacher_name) {
            enrichedStudent.teacher_name = null;
          }
          
          return enrichedStudent;
      });
      
      setStudents(studentsWithTeachers);
    } catch (error) {
      alert('Erreur lors du rafraîchissement: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    }
  };

  // Fonction pour rafraîchir la liste des enseignants
  const handleRefreshTeachers = async () => {
    try {
      // Récupérer les enseignants (le backend retourne maintenant directement assigned_halqas)
      const teachersResponse = await apiService.getTeachers();
      if (!teachersResponse.success || !Array.isArray(teachersResponse.data)) {
        alert('Erreur lors du rafraîchissement: ' + (teachersResponse.error || 'Erreur inconnue'));
        return;
      }

      console.log('🔍 DEBUG REFRESH - Données reçues du backend:', teachersResponse.data);
      teachersResponse.data.forEach((teacher: Teacher, index: number) => {
        console.log(`🔍 DEBUG REFRESH - Enseignant ${index + 1}:`, {
          id: teacher.id,
          full_name: teacher.full_name,
          assigned_halqas: teacher.assigned_halqas,
          type: typeof teacher.assigned_halqas
        });
      });

      // Convertir les statuts des enseignants
      const convertedTeachers = teachersResponse.data.map(convertTeacherStatus);
      setTeachers(convertedTeachers);
      // Supprimer l'alerte pour éviter le message "Liste rafraîchie"
      // alert(`Liste rafraîchie: ${teachersResponse.data.length} enseignants trouvés`);
    } catch (error) {
      alert('Erreur lors du rafraîchissement: ' + (error instanceof Error ? error.message : 'Erreur inconnue'));
    }
  };

  // Fonctions pour gérer les نقط الإختبارات (test scores)
  const handleAddTestScore = async () => {
    // Vérifier la permission de grades (clé backend inchangée)
    if (!canModify('grades')) {
      alert('ليس لديك صلاحية لإضافة نقطة اختبار');
      return;
    }
    
    setTestScoreError(null);
    
    console.log('🔍 AdminDashboard - Début handleAddTestScore');
    console.log('🔍 AdminDashboard - newTestScore:', newTestScore);
    console.log('🔍 AdminDashboard - subjects disponibles:', subjects);
    
    if (!newTestScore.studentId || !newTestScore.subjectId || !newTestScore.grade) {
      setTestScoreError('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    // Vérifier que la matière sélectionnée existe dans la liste
    const subjectId = parseInt(newTestScore.subjectId);
    const selectedSubject = subjects.find(s => s.id === subjectId);
    
    if (!selectedSubject) {
      console.error('❌ AdminDashboard - Matière introuvable:', subjectId);
      console.error('❌ AdminDashboard - Matières disponibles:', subjects.map(s => ({ id: s.id, name: s.name })));
      setTestScoreError(`المادة المحددة (ID: ${subjectId}) غير موجودة. يرجى إعادة تحميل الصفحة أو اختيار مادة أخرى.`);
      
      // Essayer de recharger les matières
      try {
        console.log('🔄 AdminDashboard - Tentative de rechargement des matières...');
        const subjectsData = await apiService.getSubjects();
        if (subjectsData && subjectsData.success && Array.isArray(subjectsData.data)) {
          setSubjects(subjectsData.data);
          console.log('✅ AdminDashboard - Matières rechargées:', subjectsData.data.length);
          // Vérifier à nouveau après rechargement
          const updatedSubject = subjectsData.data.find((s: any) => s.id === subjectId);
          if (!updatedSubject) {
            setTestScoreError(`المادة المحددة (ID: ${subjectId}) غير موجودة في النظام. يرجى اختيار مادة أخرى.`);
            return;
          }
        } else {
          setTestScoreError('فشل في تحميل قائمة المواد. يرجى إعادة تحميل الصفحة.');
          return;
        }
      } catch (error) {
        console.error('❌ AdminDashboard - Erreur lors du rechargement des matières:', error);
        setTestScoreError('فشل في تحميل قائمة المواد. يرجى إعادة تحميل الصفحة.');
        return;
      }
    }

    setIsSubmittingTestScore(true);
    try {
      const testScoreData = {
        student_id: parseInt(newTestScore.studentId),
        subject_id: parseInt(newTestScore.subjectId),
        grade: parseFloat(newTestScore.grade),
        date: newTestScore.date,
        school_year_id: selectedYear?.id ?? null
      };
      
      console.log('🔍 AdminDashboard - Données préparées:', testScoreData);
      console.log('🔍 AdminDashboard - Matière sélectionnée:', selectedSubject);
      
      const response = await apiService.createGrade(testScoreData);
      
      console.log('🔍 AdminDashboard - Réponse API:', response);
      
      if (response.success) {
        // Recharger les نقط الإختبارات depuis l'API
        const testScoresData = await apiService.getGrades(undefined, selectedYear?.id);
        if (testScoresData.success && Array.isArray(testScoresData.data)) {
          setTestScores(testScoresData.data);
        }
        
        setShowAddTestScoreForm(false);
        setNewTestScore({
          studentId: '',
          subjectId: '',
          grade: '',
          date: todayInTz(timezone)
        });
        alert('تم إضافة نقطة الاختبار بنجاح');
      } else {
        console.log('🔍 AdminDashboard - Erreur API:', response.error);
        setTestScoreError(response.error || 'خطأ في إضافة نقطة الاختبار');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la نقطة اختبار:', error);
      setTestScoreError('خطأ في الاتصال بالخادم');
    } finally {
      setIsSubmittingTestScore(false);
    }
  };


  const handleDeleteTestScore = async (gradeId: number) => {
    if (!window.confirm('هل أنت متأكد من حذف نقطة الاختبار هذه؟')) {
      return;
    }
    
    try {
      const response = await apiService.deleteGrade(gradeId);
      
      if (response.success) {
        // Recharger les نقط الإختبارات depuis l'API
        const testScoresData = await apiService.getGrades(undefined, selectedYear?.id);
        if (testScoresData.success && Array.isArray(testScoresData.data)) {
          setTestScores(testScoresData.data);
        }
        alert('تم حذف نقطة الاختبار بنجاح');
      } else {
        alert('خطأ في حذف نقطة الاختبار: ' + (response.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la نقطة اختبار:', error);
      alert('خطأ في الاتصال بالخادم');
    }
  };

  // Fonction pour répondre à un message
  const handleReplyToMessage = (message: Message) => {
    setMessageToReplyTo(message);
    setIsReplyMode(true);
    setShowSendMessageModal(true);
    
    // En mode réponse, on doit toujours envoyer uniquement à l'expéditeur du message
    const senderType = message.sender_type?.toLowerCase() || '';
    const recipientType = 'specific'; // Toujours 'specific' en mode réponse
    
    // Identifier l'étudiant correspondant à l'expéditeur pour la sélection
    if (senderType === 'parent' || senderType === 'ولي الأمر') {
      // Pour un parent, utiliser student_id pour trouver le parent
      if (message.student_id) {
        const student = students.find(s => s.id === message.student_id);
        if (student) {
          setSelectedUsers([message.student_id]); // Utiliser student_id pour le mapping
        }
      }
    } else if (senderType === 'teacher' || senderType === 'معلم') {
      // Pour un enseignant, trouver l'étudiant associé ou utiliser sender_id
      // Si le message a un student_id, l'utiliser pour trouver l'enseignant
      if (message.student_id) {
        // Trouver un étudiant lié à cet enseignant
        const student = students.find(s => s.id === message.student_id);
        if (student) {
          setSelectedUsers([message.student_id]);
        }
      } else if (message.sender_id) {
        // Si pas de student_id, essayer de trouver un étudiant via l'enseignant
        // Pour l'instant, on utilisera le premier étudiant trouvé lié à cet enseignant
        // Note: Cette logique peut nécessiter une amélioration selon la structure des données
        const firstStudent = students.find(s => s.id);
        if (firstStudent) {
          setSelectedUsers([firstStudent.id]);
        }
      }
    } else {
      // Pour les autres types (admin, etc.), utiliser student_id si disponible
      if (message.student_id) {
        setSelectedUsers([message.student_id]);
      }
    }
    
    // Préfixer le sujet avec un symbole de réponse (↩) SANS traduction
    let replySubject = message.subject || '';
    if (!replySubject.toLowerCase().startsWith('re:') && !replySubject.toLowerCase().startsWith('↩') && !replySubject.startsWith('↩')) {
      replySubject = `↩ ${replySubject}`;
    } else {
      replySubject = replySubject.replace(/^(رد:|re:)\s*/i, '↩ ');
    }
    
    // Construire le message avec les informations du message original formatées
    const studentLabel = message.student_label || 
      GLOBAL_UTILS.getStudentLabel(message.student_gender) ||
      (message.student_id ? GLOBAL_UTILS.getStudentLabel(students.find(s => s.id === message.student_id)?.gender) : 'الطالب/ـة');
    
    const messageLines: string[] = [
      '',
      '',
      '',
      `│ ${t('from')}: ${message.sender_name || ''}${message.sender_type ? ` (${getSenderTitle(message.sender_type)})` : ''}`
    ];
    
    if (message.student_name) {
      messageLines.push(`│ ${studentLabel}: ${message.student_name}`);
    }
    
    messageLines.push(`│ ${t('messageSubject')}: ${message.subject || ''}`);
    
    const messageDate = message.sent_date || '';
    const messageTime = message.sent_time || '';
    messageLines.push(`│ ${t('date')}: ${messageDate ? GLOBAL_UTILS.formatDate(messageDate) : ''}${messageTime ? ` ${messageTime}` : ''}`);
    
    messageLines.push('│');
    
    const content = message.content || '';
    const contentLines = content.split('\n').map((line) => `│ ${line}`);
    messageLines.push(...contentLines);
    
    const formattedOriginalMessage = messageLines.join('\n');
    
    setNewMessage({
      recipientType: recipientType,
      subject: replySubject,
      content: formattedOriginalMessage,
      priority: message.priority || 'normal',
      selectedClass: ''
    });
    
    // Afficher automatiquement la sélection des utilisateurs en mode réponse
    setShowSpecificUsers(true);
    setShowClassSelection(false);
  };

  // Fonctions de communication
  const handleSendMessage = async () => {
    // Vérifier la permission de communication
    if (!canModify('communication')) {
      alert('ليس لديك صلاحية لإرسال رسالة');
      return;
    }
    
    setMessageError(null);
    
    // Log de début d'envoi
    const sendLogStart = {
      action: 'send_message',
      component: 'AdminDashboard',
      timestamp: new Date().toISOString(),
      message_data: {
        subject: newMessage.subject,
        content: newMessage.content?.substring(0, 100) + (newMessage.content?.length > 100 ? '...' : ''),
        content_length: newMessage.content?.length || 0,
        recipient_type: newMessage.recipientType,
        priority: newMessage.priority,
        selected_class: newMessage.selectedClass
      }
    };
    console.log('📤 ADMIN DASHBOARD - handleSendMessage - Début:', JSON.stringify(sendLogStart, null, 2));
    
    if (!newMessage.subject || !newMessage.content) {
      setMessageError('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    if (!newMessage.recipientType) {
      setMessageError('يرجى اختيار نوع المستقبلين');
      return;
    }

    let recipientIds: number[] = [];
    let recipientCount = 0;
    let studentIdsForDetails: number[] = []; // Pour les détails dans les logs

    // En mode réponse, le destinataire est toujours l'expéditeur du message original (pas selectedUsers)
    if (isReplyMode && messageToReplyTo) {
      const senderType = messageToReplyTo.sender_type?.toLowerCase() || '';
      if (senderType === 'parent' || senderType === 'ولي الأمر') {
        if (messageToReplyTo.student_id) {
          const student = students.find(s => s.id === messageToReplyTo.student_id);
          if (student) {
            const userId = student.parent_user_id || student.parent_id;
            if (userId != null && userId > 0) {
              recipientIds = [userId];
              recipientCount = 1;
              studentIdsForDetails = [messageToReplyTo.student_id];
            }
          }
        }
        if (recipientIds.length === 0 && messageToReplyTo.sender_id) {
          recipientIds = [messageToReplyTo.sender_id];
          recipientCount = 1;
          studentIdsForDetails = messageToReplyTo.student_id ? [messageToReplyTo.student_id] : [];
        }
      } else if (senderType === 'teacher' || senderType === 'معلم') {
        if (messageToReplyTo.sender_id) {
          recipientIds = [messageToReplyTo.sender_id];
          recipientCount = 1;
          studentIdsForDetails = messageToReplyTo.student_id ? [messageToReplyTo.student_id] : [];
        }
      } else if (messageToReplyTo.sender_id) {
        recipientIds = [messageToReplyTo.sender_id];
        recipientCount = 1;
        studentIdsForDetails = messageToReplyTo.student_id ? [messageToReplyTo.student_id] : [];
      }
      if (recipientIds.length === 0) {
        setMessageError('لا يمكن تحديد مستلم الرد. يرجى المحاولة مرة أخرى.');
        return;
      }
    } else if (newMessage.recipientType === 'all') {
      // Pour "all", récupérer tous les IDs d'utilisateurs (parents) des étudiants
      studentIdsForDetails = students.map(s => s.id);
      const userIds = students
        .map(s => s.parent_user_id || s.parent_id)
        .filter((id): id is number => id !== null && id !== undefined && id > 0);
      // Dédupliquer les IDs d'utilisateurs
      recipientIds = Array.from(new Set(userIds));
      recipientCount = recipientIds.length;
    } else if (newMessage.recipientType === 'admin') {
      // Pour "admin", recipient_ids est optionnel
      // Si selectedUsers est fourni, mapper vers des IDs d'utilisateurs (parents/teachers)
      // Sinon, recipient_ids sera NULL et tous les admins recevront le message
      if (selectedUsers.length > 0) {
        const selectedStudents = students.filter(s => selectedUsers.includes(s.id));
        studentIdsForDetails = selectedUsers;
        const userIds = selectedStudents
          .map(s => s.parent_user_id || s.parent_id)
          .filter((id): id is number => id !== null && id !== undefined && id > 0);
        // Dédupliquer les IDs d'utilisateurs
        recipientIds = Array.from(new Set(userIds));
        recipientCount = recipientIds.length;
      } else {
        // Aucun destinataire supplémentaire, tous les admins recevront le message
        recipientIds = [];
        recipientCount = 0;
      }
    } else if (newMessage.recipientType === 'specific') {
      if (selectedUsers.length === 0) {
        setMessageError('يرجى اختيار مستخدم واحد على الأقل');
        return;
      }
      // selectedUsers contient des IDs d'étudiants, les mapper vers des IDs d'utilisateurs (parents)
      studentIdsForDetails = selectedUsers;
      
      // Log pour déboguer le mapping
      const selectedStudents = students.filter(s => selectedUsers.includes(s.id));
      console.log('🔍 ADMIN DASHBOARD - Mapping étudiants vers utilisateurs:', {
        selected_student_ids: selectedUsers,
        selected_students: selectedStudents.map(s => ({
          student_id: s.id,
          student_name: s.student_name,
          parent_user_id: s.parent_user_id,
          parent_id: s.parent_id,
          note: 'parent_user_id ou parent_id devrait correspondre à un ID dans la table users'
        }))
      });
      
      const userIds = selectedStudents
        .map(s => s.parent_user_id || s.parent_id)
        .filter((id): id is number => id !== null && id !== undefined && id > 0);
      
      console.log('🔍 ADMIN DASHBOARD - IDs d\'utilisateurs extraits:', {
        raw_user_ids: selectedStudents.map(s => ({ 
          student_id: s.id, 
          student_name: s.student_name,
          parent_user_id: s.parent_user_id, 
          parent_id: s.parent_id,
          mapped_id: s.parent_user_id || s.parent_id
        })),
        filtered_user_ids: userIds,
        warning: 'Ces IDs doivent exister dans la table users pour être valides'
      });
      
      // Dédupliquer les IDs d'utilisateurs
      recipientIds = Array.from(new Set(userIds));
      recipientCount = recipientIds.length;
      
      console.log('🔍 ADMIN DASHBOARD - IDs d\'utilisateurs finaux (AVANT validation):', {
        recipient_ids: recipientIds,
        recipient_count: recipientCount,
        note: 'Ces IDs seront validés contre la liste des utilisateurs disponibles'
      });
      
      if (recipientIds.length === 0) {
        const errorMsg = 'لا يوجد أولياء أمور مرتبطين بالطلاب المحددين. يرجى التأكد من أن الطلاب المحددين لديهم أولياء أمور مرتبطين.';
        setMessageError(errorMsg);
        console.error('❌ ADMIN DASHBOARD - Aucun parent trouvé pour les étudiants:', selectedUsers);
        return;
      }
    } else if (newMessage.recipientType === 'class') {
      if (!newMessage.selectedClass) {
        setMessageError('يرجى اختيار حلقة');
        return;
      }
      // Filtrer les étudiants par حلقة
      const classStudents = students.filter(s => s.halqa === newMessage.selectedClass);
      studentIdsForDetails = classStudents.map(s => s.id);
      
      if (classStudents.length === 0) {
        setMessageError('لا يوجد طلاب في هذه الحلقة');
        return;
      }
      
      // Mapper les IDs d'étudiants vers les IDs d'utilisateurs (parents)
      const userIds = classStudents
        .map(s => s.parent_user_id || s.parent_id)
        .filter((id): id is number => id !== null && id !== undefined && id > 0);
      // Dédupliquer les IDs d'utilisateurs
      recipientIds = Array.from(new Set(userIds));
      recipientCount = recipientIds.length;
      
      if (recipientIds.length === 0) {
        setMessageError('لا يوجد أولياء أمور مرتبطين بطلاب هذه الحلقة');
        return;
      }
    }

    setIsSubmittingMessage(true);
    try {
      // Récupérer les utilisateurs existants depuis la base de données
      const usersResponse = await apiService.getUsers();
      let availableUserIds: number[] = [];
      
      if (usersResponse.success && Array.isArray(usersResponse.data)) {
        availableUserIds = usersResponse.data.map((user: any) => user.id).filter((id: any): id is number => typeof id === 'number' && id > 0);
      } else {
        availableUserIds = [1, 2, 3, 4, 5]; // Fallback
      }
      
      console.log('🔍 ADMIN DASHBOARD - Utilisateurs disponibles (depuis getUsers()):', {
        total_users: availableUserIds.length,
        user_ids: availableUserIds,
        recipient_ids_before_validation: recipientIds,
        note: 'Ces IDs proviennent de la table users via apiService.getUsers()'
      });
      
      // VALIDATION CRITIQUE : Filtrer les recipient_ids pour ne garder que ceux qui existent dans users
      if (newMessage.recipientType === 'specific' && recipientIds.length > 0) {
        const validRecipientIds = recipientIds.filter(id => availableUserIds.includes(id));
        const invalidRecipientIds = recipientIds.filter(id => !availableUserIds.includes(id));
        
        console.log('🔍 ADMIN DASHBOARD - Validation des recipient_ids:', {
          total_recipient_ids: recipientIds.length,
          valid_recipient_ids: validRecipientIds,
          invalid_recipient_ids: invalidRecipientIds,
          valid_count: validRecipientIds.length,
          invalid_count: invalidRecipientIds.length,
          diagnostic: {
            message: 'Les IDs invalides sont des parent_user_id ou parent_id référencés dans students mais qui n\'existent pas dans users',
            possible_causes: [
              'Le parent a été supprimé de users mais pas de students',
              'Incohérence dans les données (parent_id pointe vers un ID inexistant)',
              'Le mapping entre students.parent_id et users.id est incorrect'
            ]
          }
        });
        
        // Pour chaque ID invalide, trouver l'étudiant concerné
        if (invalidRecipientIds.length > 0) {
          const problematicStudents = students.filter(s => {
            const studentParentId = s.parent_user_id || s.parent_id;
            return studentParentId && invalidRecipientIds.includes(studentParentId);
          });
          
          console.log('🔍 ADMIN DASHBOARD - Étudiants avec des IDs de parents invalides:', {
            invalid_parent_ids: invalidRecipientIds,
            affected_students: problematicStudents.map(s => ({
              student_id: s.id,
              student_name: s.student_name,
              parent_user_id: s.parent_user_id,
              parent_id: s.parent_id,
              problematic_id: s.parent_user_id || s.parent_id
            })),
            recommendation: 'Vérifier dans la base de données si ces IDs existent dans la table users, ou corriger les références dans students'
          });
        }
        
        if (invalidRecipientIds.length > 0) {
          console.warn('⚠️ ADMIN DASHBOARD - Certains recipient_ids n\'existent pas dans users:', invalidRecipientIds);
          
          // Récupérer les noms des étudiants concernés pour le message d'erreur
          const affectedStudents = students.filter(s => {
            const studentParentId = s.parent_user_id || s.parent_id;
            return studentParentId && invalidRecipientIds.includes(studentParentId);
          });
          
          if (validRecipientIds.length === 0) {
            // Aucun ID valide, erreur fatale
            const errorMsg = `لا يمكن إرسال الرسالة: لا يوجد أولياء أمور صالحين للطلاب المحددين. يرجى التأكد من أن الطلاب المحددين لديهم أولياء أمور مرتبطين في النظام.${affectedStudents.length > 0 ? ` الطلاب المتأثرون: ${affectedStudents.map(s => s.student_name).join(', ')}` : ''}`;
            setMessageError(errorMsg);
            setIsSubmittingMessage(false);
            return;
          } else {
            // Certains IDs sont valides, continuer avec ceux-ci et avertir l'utilisateur
            console.warn('⚠️ ADMIN DASHBOARD - Continuation avec les IDs valides uniquement');
            recipientIds = validRecipientIds;
            recipientCount = validRecipientIds.length;
            
            // Afficher un avertissement à l'utilisateur
            const warningMsg = `تحذير: بعض أولياء الأمور المحددين غير موجودين في النظام. سيتم إرسال الرسالة إلى ${validRecipientIds.length} ولي أمر فقط.${affectedStudents.length > 0 ? ` الطلاب المتأثرون: ${affectedStudents.map(s => s.student_name).join(', ')}` : ''}`;
            alert(warningMsg);
          }
        } else {
          // Tous les IDs sont valides
          console.log('✅ ADMIN DASHBOARD - Tous les recipient_ids sont valides');
        }
      }
      
      // Récupérer les détails des destinataires pour les logs
      // Utiliser studentIdsForDetails pour les détails (IDs d'étudiants)
      let recipientDetails: any[] = [];
      if (newMessage.recipientType === 'specific') {
        // Pour "specific", récupérer les noms des étudiants sélectionnés et leurs parents
        recipientDetails = students
          .filter(s => studentIdsForDetails.includes(s.id))
          .map(s => ({
            student_id: s.id,
            student_name: s.student_name || 'غير محدد',
            parent_user_id: s.parent_user_id || s.parent_id || null,
            parent_name: s.parent_name || 'غير محدد',
            phone: s.phone || 'غير محدد',
            halqa: s.halqa || 'غير محدد'
          }));
      } else if (newMessage.recipientType === 'class') {
        // Pour "class", récupérer les étudiants de la classe et leurs parents
        const classStudents = students.filter(s => s.halqa === newMessage.selectedClass);
        recipientDetails = classStudents.map(s => ({
          student_id: s.id,
          student_name: s.student_name || 'غير محدد',
          parent_user_id: s.parent_user_id || s.parent_id || null,
          parent_name: s.parent_name || 'غير محدد',
          phone: s.phone || 'غير محدد',
          halqa: s.halqa || 'غير محدد'
        }));
      } else if (newMessage.recipientType === 'admin') {
        // Pour "admin", récupérer les détails des destinataires supplémentaires si fournis
        if (selectedUsers.length > 0) {
          recipientDetails = students
            .filter(s => studentIdsForDetails.includes(s.id))
            .map(s => ({
              student_id: s.id,
              student_name: s.student_name || 'غير محدد',
              parent_user_id: s.parent_user_id || s.parent_id || null,
              parent_name: s.parent_name || 'غير محدد',
              phone: s.phone || 'غير محدد',
              halqa: s.halqa || 'غير محدد'
            }));
        } else {
          recipientDetails = [{
            type: 'admin',
            description: 'جميع المديرين',
            additional_recipients: 0
          }];
        }
      } else if (newMessage.recipientType === 'all') {
        // Pour "all", juste le nombre total
        recipientDetails = [{
          type: 'all',
          students_count: students.length,
          users_count: recipientIds.length,
          description: 'جميع أولياء الأمور'
        }];
      }
      
      // Préparer les données pour l'API backend
      let messageData: any = {
        sender_id: availableUserIds[0] || 1, // Utiliser le premier ID disponible
        sender_name: 'المدير العام',
        recipient_type: newMessage.recipientType,
        subject: newMessage.subject,
        content: newMessage.content,
        priority: newMessage.priority
      };
      
      // Gestion de recipient_ids selon le type
      if (newMessage.recipientType === 'specific') {
        // Pour "specific", recipient_ids est obligatoire
        // Utiliser les recipientIds validés (filtrés pour ne garder que ceux qui existent dans users)
        messageData.recipient_ids = recipientIds;
        messageData.recipient_count = recipientCount;
        
        // Vérification finale : s'assurer qu'on a au moins un ID valide
        if (messageData.recipient_ids.length === 0) {
          setMessageError('لا يمكن إرسال الرسالة: لا يوجد مستلمين صالحين.');
          setIsSubmittingMessage(false);
          return;
        }
      } else if (newMessage.recipientType === 'admin') {
        // Pour "admin", recipient_ids est optionnel
        // Si recipientIds est fourni et non vide, l'inclure (tous les admins + ces IDs)
        // Sinon, ne pas inclure recipient_ids (sera NULL, tous les admins seulement)
        if (recipientIds.length > 0) {
          messageData.recipient_ids = recipientIds;
          messageData.recipient_count = recipientCount;
        }
        // Si recipientIds est vide, ne pas inclure recipient_ids dans le payload
      } else {
        // Pour "all", "class", on ne passe pas recipient_ids
        // Le backend déterminera les destinataires selon recipient_type
        messageData.recipient_count = recipientCount; // Pour information seulement
      }
      
      // Ajouter les détails des destinataires pour les logs API
      if (recipientDetails.length > 0) {
        messageData.recipient_details = recipientDetails;
      }
      if (newMessage.selectedClass) {
        messageData.selected_class = newMessage.selectedClass;
      }
      
      // Log avant envoi avec détails complets du mapping
      const sendLogBefore = {
        action: 'send_message',
        component: 'AdminDashboard',
        timestamp: new Date().toISOString(),
        message_data: {
          sender_id: messageData.sender_id,
          sender_name: messageData.sender_name,
          recipient_type: messageData.recipient_type,
          subject: messageData.subject,
          content: messageData.content?.substring(0, 100) + (messageData.content?.length > 100 ? '...' : ''),
          content_length: messageData.content?.length || 0,
          priority: messageData.priority,
          recipient_ids: messageData.recipient_ids,
          recipient_ids_type: messageData.recipient_ids?.map((id: number) => ({ id, type: typeof id })) || [],
          recipient_count: messageData.recipient_count,
          recipient_details: recipientDetails,
          available_user_ids: availableUserIds,
          mapping_info: {
            student_ids_for_details: studentIdsForDetails,
            recipient_ids_mapped: recipientIds,
            note: 'recipient_ids doit contenir des IDs d\'utilisateurs (users.id), pas des IDs d\'étudiants (students.id)'
          }
        }
      };
      console.log('📤 ADMIN DASHBOARD - handleSendMessage - Avant envoi:', JSON.stringify(sendLogBefore, null, 2));
      
      // Vérification supplémentaire : s'assurer que recipient_ids contient bien des nombres
      if (newMessage.recipientType === 'specific' && messageData.recipient_ids) {
        const invalidIds = messageData.recipient_ids.filter((id: any) => typeof id !== 'number' || id <= 0 || !Number.isInteger(id));
        if (invalidIds.length > 0) {
          console.error('❌ ADMIN DASHBOARD - recipient_ids invalides détectés:', invalidIds);
          setMessageError('خطأ في تحديد هويات المستلمين. يرجى المحاولة مرة أخرى.');
          return;
        }
        console.log('✅ ADMIN DASHBOARD - recipient_ids validés:', {
          count: messageData.recipient_ids.length,
          ids: messageData.recipient_ids,
          all_numbers: messageData.recipient_ids.every((id: number) => typeof id === 'number' && id > 0 && Number.isInteger(id))
        });
      }
      
      // Essayer avec les IDs d'utilisateurs disponibles
      let success = false;
      for (let i = 0; i < availableUserIds.length; i++) {
        messageData.sender_id = availableUserIds[i];
        
        const response = await apiService.sendMessage(messageData);
        
        // Log après réponse
        const sendLogAfter = {
          action: 'send_message',
          component: 'AdminDashboard',
          timestamp: new Date().toISOString(),
          response: {
            success: response.success,
            message_id: response.data?.id || null,
            recipients_count: response.data?.recipients_count || response.data?.recipient_count || null,
            error: response.error || null,
            error_message: response.error || null
          }
        };
        console.log('📤 ADMIN DASHBOARD - handleSendMessage - Après réponse:', JSON.stringify(sendLogAfter, null, 2));
        
        if (!response.success) {
          // Gérer les erreurs de validation du backend
          const errorMessage = response.error || 'Erreur lors de l\'envoi du message';
          setMessageError(errorMessage);
          alert(errorMessage);
          return;
        }
        
        if (response.success) {
          // Fonction helper pour extraire le nom de l'étudiant depuis le contenu
          const extractStudentName = (content: string): { studentName: string | null; cleanContent: string } => {
            if (!content) return { studentName: null, cleanContent: content || '' };
            
            let cleanContent = GLOBAL_UTILS.cleanText(content);
            let studentName = null;
            
            // Chercher le pattern avec les différentes formes inclusives
            const studentLabelPatterns = [
              /الطالب\/ـة:\s*([^\n]+)/i,       // Arabe: الطالب/ـة
              /الطالب\/الطالبة:\s*([^\n]+)/i,  // Arabe: الطالب/الطالبة (ancien format)
              /الطالب\(ة\):\s*([^\n]+)/i,      // Arabe: الطالب(ة)
              /الطالب:\s*([^\n]+)/i,           // Arabe: الطالب (ancien format)
              /Student\(s\):\s*([^\n]+)/i,     // Anglais: Student(s)
              /Student:\s*([^\n]+)/i,          // Anglais: Student (ancien format)
              /Étudiant\(e\):\s*([^\n]+)/i,    // Français: Étudiant(e)
              /Étudiant:\s*([^\n]+)/i          // Français: Étudiant (ancien format)
            ];
            
            for (const pattern of studentLabelPatterns) {
              const match = cleanContent.match(pattern);
              if (match && match[1]) {
                studentName = match[1].trim();
                // Retirer le nom de l'étudiant du contenu pour l'affichage
                cleanContent = cleanContent.replace(pattern, '').trim();
                // Retirer les sauts de ligne multiples au début
                cleanContent = cleanContent.replace(/^\n+/, '').trim();
                break;
              }
            }
            
            return { studentName, cleanContent };
          };
          
          const messageContent = response.data.content || newMessage.content;
          const { studentName, cleanContent } = extractStudentName(messageContent);
          
          // Utiliser recipients_count du backend si disponible, sinon utiliser la valeur calculée localement
          const backendRecipientCount = response.data.recipients_count || response.data.recipient_count;
          const finalRecipientCount = backendRecipientCount !== undefined ? backendRecipientCount : recipientCount;
          
          // Ajouter le message à la liste des messages envoyés
          const newSentMessage = {
            id: response.data.id || Date.now(),
            senderId: response.data.sender_id || messageData.sender_id,
            senderName: response.data.sender_name || 'المدير العام',
            recipientIds: recipientIds, // IDs d'utilisateurs (pour le backend)
            studentIds: studentIdsForDetails, // IDs d'étudiants (pour l'affichage)
            recipientType: response.data.recipient_type || newMessage.recipientType,
            subject: response.data.subject || newMessage.subject,
            content: cleanContent,
            priority: response.data.priority || newMessage.priority,
            sentDate: response.data.sent_date || todayInTz(timezone),
            sentTime: response.data.sent_time || formatTimeInTz(new Date(), timezone, 'ar-SA'),
            recipientCount: finalRecipientCount,
            // Ajouter les informations des destinataires (utiliser studentIdsForDetails pour l'affichage)
            recipientNames: getRecipientNames(newMessage.recipientType, studentIdsForDetails),
            recipientDetails: getRecipientDetails(newMessage.recipientType, studentIdsForDetails),
            student_id: response.data.student_id || null,
            student_name: studentName || response.data.student_name || null
          };
          
          setSentMessages(prev => [newSentMessage, ...prev]);
          
          setShowSendMessageModal(false);
          setMessageToReplyTo(null);
          setIsReplyMode(false);
          setNewMessage({
            recipientType: 'all',
            subject: '',
            content: '',
            priority: 'normal',
            selectedClass: ''
          });
          setSelectedUsers([]);
          setShowSpecificUsers(false);
          setShowClassSelection(false);
          alert(`تم إرسال الرسالة إلى ${finalRecipientCount} مستخدم بنجاح!`);
          success = true;
          break; // Succès, sortir de la boucle
        }
      }
      
      // Si aucun expéditeur n'existe, utiliser une simulation côté frontend
      if (!success) {
        
        // Simuler un délai d'envoi
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Ajouter le message à la liste des messages envoyés
        const newSentMessage = {
          id: Date.now(),
          senderId: 1,
          senderName: 'المدير العام',
          recipientIds: recipientIds, // IDs d'utilisateurs (pour le backend)
          studentIds: studentIdsForDetails, // IDs d'étudiants (pour l'affichage)
          recipientType: newMessage.recipientType,
          subject: newMessage.subject,
          content: newMessage.content,
          priority: newMessage.priority,
          sentDate: todayInTz(timezone),
          sentTime: formatTimeInTz(new Date(), timezone, 'ar-SA'),
          recipientCount: recipientCount,
          recipientNames: getRecipientNames(newMessage.recipientType, studentIdsForDetails),
          recipientDetails: getRecipientDetails(newMessage.recipientType, studentIdsForDetails)
        };
        
        setSentMessages(prev => [newSentMessage, ...prev]);
        
        setShowSendMessageModal(false);
        setMessageToReplyTo(null);
        setIsReplyMode(false);
        setNewMessage({
          recipientType: 'all',
          subject: '',
          content: '',
          priority: 'normal',
          selectedClass: ''
        });
        setSelectedUsers([]);
        setShowSpecificUsers(false);
        setShowClassSelection(false);
        alert(`تم إرسال الرسالة إلى ${recipientCount} مستخدم بنجاح! (محاكاة - لا يوجد expéditeur في قاعدة البيانات)`);
      }
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setMessageError('خطأ في الاتصال بالخادم');
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  const getRecipientNames = (recipientType: string, recipientIds: number[]) => {
    if (recipientType === 'all') {
      return 'جميع أولياء الأمور';
    } else if (recipientType === 'class') {
      return `حلقة ${newMessage.selectedClass}`;
    } else if (recipientType === 'specific') {
      // Trouver les noms des étudiants sélectionnés
      const selectedStudents = students.filter(student => recipientIds.includes(student.id));
      if (selectedStudents.length > 0) {
        return selectedStudents.map(student => student.student_name).join(', ');
      }
      return `${recipientIds.length} مستخدم محدد`;
    }
    return 'غير محدد';
  };

  const getRecipientDetails = (recipientType: string, recipientIds: number[]) => {
    if (recipientType === 'all') {
      return {
        type: 'all',
        count: students.length,
        description: 'جميع الطلاب'
      };
    } else if (recipientType === 'admin') {
      const hasExtraRecipients = recipientIds && recipientIds.length > 0;
      return {
        type: 'admin',
        count: hasExtraRecipients ? recipientIds.length : 0,
        description: hasExtraRecipients 
          ? `جميع المديرين + ${recipientIds.length} مستلم إضافي`
          : 'جميع المديرين',
        students: []
      };
    } else if (recipientType === 'class') {
      const classStudents = students.filter(student => student.halqa === newMessage.selectedClass);
      return {
        type: 'class',
        count: classStudents.length,
        description: `حلقة ${newMessage.selectedClass}`,
        students: classStudents.map(s => s.student_name)
      };
    } else if (recipientType === 'specific') {
      const selectedStudents = students.filter(student => recipientIds.includes(student.id));
      return {
        type: 'specific',
        count: selectedStudents.length,
        description: 'مستخدمين محددين',
        students: selectedStudents.map(s => s.student_name)
      };
    }
    return {
      type: 'unknown',
      count: 0,
      description: 'غير محدد'
    };
  };

  const applyTemplate = (templateType: string) => {
    const templates = {
      absence: {
        subject: 'إشعار غياب',
        content: 'نود إعلامكم بغياب ابنكم/ابنتكم اليوم. يرجى التواصل معنا في حالة وجود أي استفسارات.'
      },
      meeting: {
        subject: 'دعوة اجتماع',
        content: 'نود دعوتكم لحضور اجتماع أولياء الأمور المقرر في [التاريخ]. ننتظر مشاركتكم الفعالة.'
      },
      congratulation: {
        subject: 'تهنئة بالإنجاز',
        content: 'نهنئكم بالإنجاز المتميز لابنكم/ابنتكم. نفخر بهذا التقدم ونتمنى المزيد من النجاح.'
      },
      announcement: {
        subject: 'إعلان مهم',
        content: 'نود إعلامكم بإعلان مهم يخص المدرسة. يرجى الاطلاع على التفاصيل المرفقة.'
      }
    };

    const template = templates[templateType as keyof typeof templates];
    if (template) {
      setNewMessage(prev => ({
        ...prev,
        subject: template.subject,
        content: template.content
      }));
    }
  };

  // Fonction pour détecter le type de message et retourner les couleurs appropriées





  // Charger les données du dashboard
  const loadDashboardData = async () => {
    if (isLoadingRef.current) {
      return;
    }
    isLoadingRef.current = true;
    try {
      setLoading(true);
      setError(null);
      
      // Vérifier l'authentification
      console.log('🔍 AUTH DEBUG - Vérification de l\'authentification...');
      const currentUser = apiService.getCurrentUser();
      console.log('🔍 AUTH DEBUG - Utilisateur actuel:', currentUser);
      
      if (!currentUser) {
        console.log('❌ AUTH DEBUG - Aucun utilisateur trouvé');
        setError('Vous devez être connecté pour accéder au dashboard');
        return;
      }
      
      // Récupérer le nom de l'admin connecté
      const adminName = currentUser.full_name || currentUser.fullName || currentUser.username || '';
      setCurrentAdminName(adminName);
      
      // Extraire les permissions directement depuis currentUser (retournées par le backend)
      let currentPermissions: string[] = [];
      if (currentUser.permissions) {
        let permArray = currentUser.permissions;
        if (typeof permArray === 'string') {
          try {
            permArray = JSON.parse(permArray);
          } catch {
            permArray = [];
          }
        }
        if (Array.isArray(permArray)) {
          currentPermissions = permArray.map((p: string) => normalizePermission(String(p).trim())).filter(Boolean);
        }
      }
      setCurrentAdminPermissions(currentPermissions);
      
      // S'assurer que le token est défini dans ApiService
      const token = localStorage.getItem('app_token');
      console.log('🔍 AUTH DEBUG - Token depuis localStorage:', token ? 'Présent' : 'Absent');
      
      if (token) {
        apiService.setAuthToken(token);
        console.log('✅ AUTH DEBUG - Token défini dans ApiService');
      } else {
        console.log('❌ AUTH DEBUG - Token manquant');
        setError('Vous devez être connecté pour accéder aux données. Veuillez vous reconnecter.');
        return;
      }
      
      // Charger les données du dashboard
      const dashboardData = await apiService.getDashboardData(selectedYear?.id);

      // Lire les compteurs KPI directement depuis la réponse du dashboard
      if (dashboardData?.success && dashboardData?.data) {
        const pending = dashboardData.data.pendingInscriptions;
        const newContact = dashboardData.data.newContactMessages;
        if (pending != null) setPendingInscriptionsCount(Number(pending));
        if (newContact != null) setNewContactMessagesCount(Number(newContact));
      }
      
      // Charger les statistiques de présence/absence pour aujourd'hui
      const today = new Date();
      // ✅ Jour « aujourd'hui » dans le fuseau de l'ASSOCIATION (et non celui du navigateur)
      const todayStr = todayInTz(timezone);
      
      console.log('🔍 ADMIN DASHBOARD - loadDashboardData - Date du jour:', {
        today: today,
        todayISO: today.toISOString(),
        todayStr: todayStr,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      
      // ✅ Périmètre = élèves INSCRITS pour l'année consultée.
      // Si l'année n'est pas (encore) résolue côté frontend — SchoolYearContext asynchrone —
      // on laisse le BACKEND appliquer son défaut (année ACTIVE, cf. resolveRequestSchoolYear
      // dans routes/quran-records.js) : l'appel est donc TOUJOURS fait, et le périmètre du KPI
      // reste celui de la carte « إجمالي الطلاب » (jamais « toutes années »).
      const attendanceResponse = await apiService.getAttendance({
        startDate: todayStr,
        endDate: todayStr,
        school_year_id: selectedYear?.id ?? currentYear?.id ?? undefined
      });
      
      console.log('🔍 ADMIN DASHBOARD - loadDashboardData - Réponse attendance:', {
        success: attendanceResponse.success,
        error: attendanceResponse.error,
        data: attendanceResponse.data,
        present: attendanceResponse.data?.present,
        absent: attendanceResponse.data?.absent,
        total: attendanceResponse.data?.total
      });
      
      // ✅ Jour sans séance (aucun enregistrement Coran sur la période) : l'API signalerait
      // tous les élèves comme « absents » → on affiche 0/0 + mention « aucune séance ».
      const sessionDay = attendanceResponse.success ? (attendanceResponse.data?.sessionDay !== false) : true;
      setTodayHasSession(sessionDay);

      // Calculer les présents et absents
      // Utiliser le total de l'API d'attendance qui connaît déjà le nombre total d'étudiants
      const presentToday = attendanceResponse.success && sessionDay ? (attendanceResponse.data?.present || 0) : 0;
      const totalFromAttendance = attendanceResponse.success ? (attendanceResponse.data?.total || 0) : 0;
      // Pour le dashboard admin, utiliser directement la valeur absent de l'API
      // car l'API calcule déjà correctement: absent = total - present
      const absentToday = attendanceResponse.success && sessionDay ? (attendanceResponse.data?.absent || 0) : 0;
      
      console.log('🔍 ADMIN DASHBOARD - Calcul des absents:', {
        totalFromAttendance: totalFromAttendance,
        presentToday: presentToday,
        absentFromAPI: absentToday,
        totalStudentsFromDashboard: dashboardData.success ? (dashboardData.data?.totalStudents || 0) : 0
      });
      
      // Stocker les données d'attendance pour l'onglet
      if (attendanceResponse.success && attendanceResponse.data) {
        setAttendanceData(attendanceResponse.data);
      }
      
      if (dashboardData.success && dashboardData.data) {
        // Fusionner les données d'attendance avec les données du dashboard
        const updatedDashboardData = {
          ...dashboardData,
          data: {
            ...dashboardData.data,
            presentToday: presentToday,
            absentToday: absentToday
          }
        };
        setStats(updatedDashboardData);
      } else if (attendanceResponse.success) {
        // Si le dashboard n'a pas de données mais l'attendance oui, créer un objet stats minimal
        setStats({
          success: true,
          data: {
            totalStudents: totalFromAttendance,
            totalTeachers: 0,
            totalParents: 0,
            averageGrade: 0,
            unreadMessages: 0,
            presentToday: presentToday,
            absentToday: absentToday
          }
        });
      }

      // ✅ Auto-réparation : si /api/dashboard/data n'a pas répondu (base ou API momentanément
      // indisponible), une seule relance automatique évite un affichage figé/incomplet
      // (type « Total étudiants = 272 » alors que le périmètre attendu est l'année active).
      if (dashboardData.success) {
        dashboardRetryRef.current = 0;
      } else if (dashboardRetryRef.current < 1) {
        dashboardRetryRef.current += 1;
        console.warn('⚠️ KPI : /api/dashboard/data indisponible — nouvelle tentative dans 3 s');
        setTimeout(() => { loadDashboardData(); }, 3000);
      }

      // Tester d'abord la connexion
      console.log('🔍 loadDashboardData - Test de connexion...');
      const connectionTest = await apiService.testConnection();
      console.log('🔍 loadDashboardData - Résultat du test de connexion:', connectionTest);
      
      if (!connectionTest.success) {
        console.log('❌ loadDashboardData - Connexion échouée:', connectionTest.error);
        alert(`Erreur de connexion au serveur: ${connectionTest.error}`);
        return;
      }
      
      // Charger les enseignants d'abord (nécessaire pour enrichir les données des étudiants)
      console.log('🔍 loadDashboardData - Chargement des enseignants...');
      let loadedTeachers: Teacher[] = [];
      try {
        const teachersResponse = await apiService.getTeachers();
        console.log('🔍 loadDashboardData - Réponse enseignants complète:', teachersResponse);
        
        if (teachersResponse && teachersResponse.success && Array.isArray(teachersResponse.data)) {
          console.log('✅ loadDashboardData - Enseignants chargés:', teachersResponse.data.length);
          console.log('✅ loadDashboardData - Premier enseignant:', teachersResponse.data[0]);
          // Convertir les statuts des enseignants
          const convertedTeachers = teachersResponse.data.map(convertTeacherStatus);
          loadedTeachers = convertedTeachers;
          setTeachers(convertedTeachers);
        } else {
          console.log('❌ loadDashboardData - Erreur lors du chargement des enseignants');
          console.log('🔍 loadDashboardData - Détails de l\'erreur:', teachersResponse?.error || 'Réponse invalide');
          setTeachers([]);
          
          // Afficher l'erreur à l'utilisateur
          if (teachersResponse?.error) {
            alert(`Erreur lors du chargement des enseignants: ${teachersResponse.error}`);
          }
        }
      } catch (error) {
        console.error('❌ loadDashboardData - Exception lors du chargement des enseignants:', error);
        setTeachers([]);
      }

      // Charger les parents AVANT les étudiants pour pouvoir enrichir les données des étudiants
      // Charger les parents
      console.log('🔍 loadDashboardData - Chargement des parents...');
      let loadedParentsList: any[] = [];
      try {
        const parentsResponse = await apiService.getParents();
        console.log('🔍 loadDashboardData - Réponse parents complète:', parentsResponse);
        
        if (parentsResponse && parentsResponse.success && Array.isArray(parentsResponse.data)) {
          console.log('✅ loadDashboardData - Parents chargés:', parentsResponse.data.length);
          console.log('✅ loadDashboardData - Premier parent:', parentsResponse.data[0]);
          // Convertir les statuts des parents
          const convertedParents = parentsResponse.data.map(convertParentStatus);
          loadedParentsList = convertedParents;
          setParents(convertedParents);
        } else {
          console.log('❌ loadDashboardData - Erreur lors du chargement des parents');
          console.log('🔍 loadDashboardData - Détails de l\'erreur:', parentsResponse?.error || 'Réponse invalide');
          setParents([]);
        }
      } catch (error) {
        console.error('❌ loadDashboardData - Exception lors du chargement des parents:', error);
        setParents([]);
      }

      // ⚠️ SUPPRIMÉ : Les relations parent-étudiant sont maintenant gérées directement via students.parent_id
      // Plus besoin de charger les relations séparément, les données sont déjà dans students (parent_id, parent_name, etc.)

      // Charger les étudiants APRÈS les parents pour pouvoir enrichir avec les noms des parents
      const studentsData = await apiService.getStudents();
      
      console.log('🔍 loadDashboardData - Réponse étudiants:', studentsData);
      if (studentsData.success && Array.isArray(studentsData.data) && studentsData.data.length > 0) {
        console.log('🔍 loadDashboardData - Premier étudiant:', studentsData.data[0]);
      }
      
      if (studentsData.success && Array.isArray(studentsData.data)) {
        // Charger les relations étudiant-enseignant pour enrichir les données
        const relationsByStudent = await loadStudentTeacherMap(apiService); // ✅ 1 seule requête (avant : 1 par élève)
        const studentsWithTeachers = studentsData.data.map((student: Student) => {
            // Enrichir avec le nom du parent si parent_name n'est pas disponible
            let enrichedStudent = { ...student };
            
            // Si parent_name n'est pas disponible, chercher dans la liste des parents chargés
            if (!enrichedStudent.parent_name && (enrichedStudent.parent_id || enrichedStudent.parent_user_id)) {
              const parentIdToSearch = enrichedStudent.parent_user_id || enrichedStudent.parent_id;
              const foundParent = loadedParentsList.find(p => 
                (p.user_id === parentIdToSearch) || (p.id === parentIdToSearch)
              );
              if (foundParent) {
                enrichedStudent.parent_name = foundParent.full_name || foundParent.fullName || null;
                enrichedStudent.parent_user_id = foundParent.user_id || foundParent.id || null;
                console.log(`✅ loadDashboardData - Parent trouvé pour étudiant ${student.id}:`, foundParent.full_name || foundParent.fullName);
              } else {
                console.log(`⚠️ loadDashboardData - Parent non trouvé pour étudiant ${student.id}, parent_id: ${enrichedStudent.parent_id}, parent_user_id: ${enrichedStudent.parent_user_id}`);
              }
            }
            
            // Enseignant : lecture directe de la table chargée en une requête (plus de N+1)
            const relation = relationsByStudent.get(Number(student.id));
            if (relation) {
              const teacher = loadedTeachers.find(t => (t.id === relation.teacher_id) || (t.user_id === relation.teacher_id));
              if (teacher) {
                enrichedStudent.teacher_name = teacher.full_name || 'غير محدد';
              }
            }
            
            if (!enrichedStudent.teacher_name) {
              enrichedStudent.teacher_name = null;
            }
            
            return enrichedStudent;
        });
        console.log('🔍 loadDashboardData - Étudiants enrichis (premier):', studentsWithTeachers[0]);
        setStudents(studentsWithTeachers);
      } else {
        setStudents([]);
      }

      // Charger la liste des administrateurs (utilisée pour le filtrage des messages)
      let currentAdminsList: any[] = [];
      try {
        const adminsResponse = await apiService.getAdmins();
        if (adminsResponse && adminsResponse.success && Array.isArray(adminsResponse.data)) {
          const convertedAdmins = adminsResponse.data.map(convertAdminStatus);
          currentAdminsList = convertedAdmins;
          setAdmins(convertedAdmins);
        } else {
          setAdmins([]);
        }
      } catch (error) {
        console.error('❌ loadDashboardData - Erreur chargement admins:', error);
        setAdmins([]);
      }

      // Charger les messages
      const receiveLogStart = {
        action: 'receive_messages',
        component: 'AdminDashboard',
        timestamp: new Date().toISOString(),
        route: '/api/admin/messages',
        method: 'GET'
      };
      console.log('📥 ADMIN DASHBOARD - loadDashboardData - Début réception:', JSON.stringify(receiveLogStart, null, 2));
      
      try {
        const messagesData = await apiService.getMessages();
        
        const receiveLogResponse = {
          action: 'receive_messages',
          component: 'AdminDashboard',
          timestamp: new Date().toISOString(),
          response: {
            success: messagesData?.success || false,
            messages_count: messagesData?.data ? (Array.isArray(messagesData.data) ? messagesData.data.length : 0) : 0,
            error: messagesData?.error || null
          }
        };
        console.log('📥 ADMIN DASHBOARD - loadDashboardData - Réponse:', JSON.stringify(receiveLogResponse, null, 2));
        
        if (messagesData && messagesData.success && Array.isArray(messagesData.data)) {
          // Créer un Set des IDs des admins pour une vérification rapide
          // Utiliser la liste des admins chargée dans cette fonction pour vérifier si sender_id est un admin
          const adminIds = new Set<number | string>();
          currentAdminsList.forEach((admin: any) => {
            if (admin.id) {
              adminIds.add(Number(admin.id));
              adminIds.add(String(admin.id)); // Ajouter aussi en string pour la compatibilité
            }
            if (admin.user_id) {
              adminIds.add(Number(admin.user_id));
              adminIds.add(String(admin.user_id)); // Ajouter aussi en string pour la compatibilité
            }
          });
          
          // Filtrer les messages selon la règle :
          // 1. Exclure tous les messages dont sender_id est de type 'admin' (messages envoyés par la direction)
          // 2. Inclure tous les messages restants où recipient_type = 'admin'
          const receivedMessages = messagesData.data.filter((message: any) => {
            const senderId = message.sender_id;
            
            // Exclure les messages envoyés par un admin (vérifier si sender_id est dans la liste des admins)
            // Vérifier à la fois en nombre et en string pour la compatibilité
            if (senderId) {
              const senderIdNum = Number(senderId);
              const senderIdStr = String(senderId);
              if (adminIds.has(senderIdNum) || adminIds.has(senderIdStr)) {
                return false;
              }
            }
            
            // Inclure uniquement les messages où recipient_type === 'admin' (insensible à la casse et aux espaces)
            const recipientType = message.recipient_type ? String(message.recipient_type).trim().toLowerCase() : '';
            return recipientType === 'admin';
          });
          
          // Mapper les messages pour extraire le nom de l'étudiant depuis le contenu
          const mappedMessages = receivedMessages.map((message: any) => {
            const { studentName, cleanContent } = extractStudentNameFromContent(message.content || '');
            return {
              ...message,
              content: cleanContent,
              student_id: message.student_id || null,
              student_name: studentName || message.student_name || null,
              priority: message.priority || 'normal',
              sent_time: message.sent_time || null,
              sender_type: message.sender_type || null,
              is_reply: message.reply_to_message_id !== null && message.reply_to_message_id !== undefined,
              reply_to_message_id: message.reply_to_message_id || null
            };
          });
          setMessages(mappedMessages as Message[]);
          
          const receiveLogSuccess = {
            action: 'receive_messages',
            component: 'AdminDashboard',
            timestamp: new Date().toISOString(),
            result: {
              total_messages: messagesData.data.length,
              filtered_messages: receivedMessages.length,
              messages_loaded: receivedMessages.length,
              first_message_id: receivedMessages[0]?.id || null,
              first_message_subject: receivedMessages[0]?.subject?.substring(0, 50) || null
            }
          };
          console.log('✅ ADMIN DASHBOARD - loadDashboardData - Messages chargés:', JSON.stringify(receiveLogSuccess, null, 2));
          
          // Charger les messages envoyés (messages où sender_type === 'admin' ou sender_id est dans la liste des admins)
          const adminIdsForSent = new Set<number | string>();
          currentAdminsList.forEach((admin: any) => {
            if (admin.id) {
              adminIdsForSent.add(Number(admin.id));
              adminIdsForSent.add(String(admin.id));
            }
            if (admin.user_id) {
              adminIdsForSent.add(Number(admin.user_id));
              adminIdsForSent.add(String(admin.user_id));
            }
          });
          
          // Filtrer les messages envoyés par l'admin
          const sentMessagesList = messagesData.data.filter((message: any) => {
            const senderId = message.sender_id;
            const senderType = message.sender_type?.toLowerCase() || '';
            
            // Inclure si sender_type est 'admin' ou si sender_id est dans la liste des admins
            if (senderType === 'admin' || senderType === 'مدير') {
              return true;
            }
            if (senderId) {
              const senderIdNum = Number(senderId);
              const senderIdStr = String(senderId);
              if (adminIdsForSent.has(senderIdNum) || adminIdsForSent.has(senderIdStr)) {
                return true;
              }
            }
            return false;
          });
          
          // Mapper les messages envoyés
          const mappedSentMessages = sentMessagesList.map((msg: any) => ({
            ...msg,
            recipientCount: msg.recipients_count || msg.recipient_count || 0,
            sentDate: msg.sent_date || msg.created_date || '',
            sentTime: msg.sent_time || '',
            sender_type: 'admin',
            is_reply: msg.reply_to_message_id !== null && msg.reply_to_message_id !== undefined,
            reply_to_message_id: msg.reply_to_message_id || null
          }));
          setSentMessages(mappedSentMessages);
        } else {
          const receiveLogError = {
            action: 'receive_messages',
            component: 'AdminDashboard',
            timestamp: new Date().toISOString(),
            error: messagesData?.error || 'Réponse invalide'
          };
          console.error('❌ ADMIN DASHBOARD - loadDashboardData - Erreur:', JSON.stringify(receiveLogError, null, 2));
          setMessages([]);
          setSentMessages([]);
        }
      } catch (error: any) {
        const receiveLogException = {
          action: 'receive_messages',
          component: 'AdminDashboard',
          timestamp: new Date().toISOString(),
          exception: {
            message: error?.message || 'Erreur inconnue',
            type: error?.constructor?.name || 'Error',
            stack: error?.stack || null
          }
        };
        console.error('❌ ADMIN DASHBOARD - loadDashboardData - Exception:', JSON.stringify(receiveLogException, null, 2));
        setMessages([]);
        setSentMessages([]);
      }

      // Charger les نقط الإختبارات (test scores)
      const testScoresData = await apiService.getGrades(undefined, selectedYear?.id);
      
      if (testScoresData.success && Array.isArray(testScoresData.data)) {
        setTestScores(testScoresData.data);
      } else {
        setTestScores([]);
      }

      // Charger les subjects (matières)
      console.log('🔍 loadDashboardData - Chargement des matières...');
      try {
        const subjectsData = await apiService.getSubjects();
        console.log('🔍 loadDashboardData - Réponse matières complète:', subjectsData);
        
        if (subjectsData && subjectsData.success && Array.isArray(subjectsData.data)) {
          console.log('✅ loadDashboardData - Matières chargées:', subjectsData.data.length);
          console.log('✅ loadDashboardData - Première matière:', subjectsData.data[0]);
          setSubjects(subjectsData.data);
        } else {
          console.log('❌ loadDashboardData - Erreur lors du chargement des matières');
          console.log('🔍 loadDashboardData - Détails de l\'erreur:', subjectsData?.error || 'Réponse invalide');
          setSubjects([]);
        }
      } catch (error) {
        console.error('❌ loadDashboardData - Exception lors du chargement des matières:', error);
        setSubjects([]);
      }

      // Charger les enregistrements Coran
      console.log('🔍 loadDashboardData - Chargement des enregistrements Coran...');
      try {
        // 1. Récupérer les enregistrements Coran (sans les données ns_*)
        const quranRecordsResponse = await apiService.getAllQuranRecords();
        console.log('🔍 loadDashboardData - Réponse enregistrements Coran complète:', quranRecordsResponse);
        
        // 2. Récupérer toutes les sessions suivantes
        let nextSessionsMap = new Map();
        try {
          const nextSessionsResponse = await apiService.getAllNextSessions();
          if (nextSessionsResponse.success && nextSessionsResponse.data) {
            nextSessionsResponse.data.forEach((nextSession: any) => {
              const studentId = nextSession.studentId || nextSession.student_id;
              if (studentId) {
                nextSessionsMap.set(studentId, nextSession);
              }
            });
            console.log('✅ loadDashboardData - Sessions suivantes récupérées:', nextSessionsMap.size);
          }
        } catch (nextSessionError) {
          console.warn('⚠️ loadDashboardData - Erreur lors de la récupération des sessions suivantes:', nextSessionError);
        }
        
        if (quranRecordsResponse && quranRecordsResponse.success && Array.isArray(quranRecordsResponse.data)) {
          console.log('✅ loadDashboardData - Enregistrements Coran chargés:', quranRecordsResponse.data.length);
          // 3. Combiner les données : mapper les enregistrements Coran avec leurs sessions suivantes
          const mappedRecords = (quranRecordsResponse.data || []).map((record: any) => {
            const studentId = record.student_id || record.studentId;
            const nextSession = nextSessionsMap.get(studentId) || null;
            const totalHizbValue = record.total_hizb ?? record.totalHizb ?? null;
            
            return {
              ...record,
              id: record.id,
              studentId: studentId,
              total_hizb: totalHizbValue,
              totalHizb: totalHizbValue,
              // ✅ آخر جلسة (Last Session) - depuis quran_records
              newMemorization: GLOBAL_UTILS.cleanText(record.new_memorization || record.newMemorization || ''),
              review: GLOBAL_UTILS.cleanText(record.review || ''),
              // ✅ الجلسة القادمة (Next Session) - depuis quran_next_session
              assignment: nextSession ? GLOBAL_UTILS.cleanText(nextSession.nextHalaqaNewHifzText || nextSession.assignment || '') : GLOBAL_UTILS.cleanText(record.assignment || ''),
              note: GLOBAL_UTILS.cleanText((record.note || '').trim()),
              date: record.modifiedDate || record.modified_date || record.date || record.createdDate || record.created_date || '',
              modifiedDate: record.modifiedDate || record.modified_date || record.date || record.createdDate || record.created_date || '',
              createdDate: record.createdDate || record.created_date || record.modifiedDate || record.modified_date || record.date || '',
              studentName: GLOBAL_UTILS.cleanText(record.student_name || record.studentName || ''),
              teacher_id: record.teacher_id || record.teacherId || 0,
              teacherId: record.teacher_id || record.teacherId || 0,
              evalNewHifz: record.evalNewHifz,
              evalOldHifzMurajah: record.evalOldHifzMurajah,
              // ✅ Champs du nouveau modèle depuis quran_records
              newHifzTxt: GLOBAL_UTILS.cleanText(record.newHifzTxt || record.new_memorization || ''),
              oldHifzMurajahTxt: GLOBAL_UTILS.cleanText(record.oldHifzMurajahTxt || record.review || ''),
              newHifzMurajahText: GLOBAL_UTILS.cleanText(record.newHifzMurajahText || ''),
              // ✅ Champs الجلسة القادمة depuis quran_next_session
              nextHalaqaNewHifzText: nextSession ? GLOBAL_UTILS.cleanText(nextSession.nextHalaqaNewHifzText || '') : '',
              nextHalaqaOldHifzMurajahText: nextSession ? GLOBAL_UTILS.cleanText(nextSession.nextHalaqaOldHifzMurajahText || '') : '',
              nextHalaqaNewHifzMurajahText: nextSession ? GLOBAL_UTILS.cleanText(nextSession.nextHalaqaNewHifzMurajahText || '') : '',
              nextHalaqaNewHifzRange: nextSession ? (nextSession.nextHalaqaNewHifzRange || null) : null,
              nextHalaqaOldHifzMurajahRange: nextSession ? (nextSession.nextHalaqaOldHifzMurajahRange || null) : null,
              nextHalaqaNewHifzMurajahRange: nextSession ? (nextSession.nextHalaqaNewHifzMurajahRange || null) : null,
              // ✅ Compteur de répétitions - depuis quran_records uniquement
              repeatNewHifz: extractRepeatNewHifzFromRecord(record)
            };
          });
          setQuranRecords(mappedRecords);
          console.log('✅ loadDashboardData - Enregistrements Coran mappés:', mappedRecords.length);
        } else {
          console.log('❌ loadDashboardData - Erreur lors du chargement des enregistrements Coran');
          console.log('🔍 loadDashboardData - Détails de l\'erreur:', quranRecordsResponse?.error || 'Réponse invalide');
          setQuranRecords([]);
        }
      } catch (error) {
        console.error('❌ loadDashboardData - Exception lors du chargement des enregistrements Coran:', error);
        setQuranRecords([]);
      }
      
      // ✅ Heure du dernier chargement RÉUSSI (chargement initial, changement d'année ou clic sur « تحديث »)
      setLastRefreshAt(new Date());
    } catch (error) {
      console.error('❌ Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Fonction pour rafraîchir les enregistrements Coran
  const refreshQuranRecords = React.useCallback(async () => {
    try {
      // 1. Récupérer les enregistrements Coran (sans les données ns_*)
      const quranRecordsResponse = await apiService.getAllQuranRecords();
      
      // 2. Récupérer toutes les sessions suivantes
      let nextSessionsMap = new Map();
      try {
        const nextSessionsResponse = await apiService.getAllNextSessions();
        if (nextSessionsResponse.success && nextSessionsResponse.data) {
          // Créer un Map pour un accès rapide (studentId -> nextSession)
          nextSessionsResponse.data.forEach((nextSession: any) => {
            const studentId = nextSession.studentId || nextSession.student_id;
            if (studentId) {
              nextSessionsMap.set(studentId, nextSession);
            }
          });
          console.log('✅ AdminDashboard - Sessions suivantes récupérées:', nextSessionsMap.size);
        }
      } catch (nextSessionError) {
        console.warn('⚠️ AdminDashboard - Erreur lors de la récupération des sessions suivantes:', nextSessionError);
        // Ne pas faire échouer toute l'opération si les sessions suivantes ne peuvent pas être récupérées
      }
      
      if (quranRecordsResponse && quranRecordsResponse.success && Array.isArray(quranRecordsResponse.data)) {
        // 3. Combiner les données : mapper les enregistrements Coran avec leurs sessions suivantes
        const mappedRecords = (quranRecordsResponse.data || []).map((record: any) => {
          const studentId = record.student_id || record.studentId;
          const nextSession = nextSessionsMap.get(studentId) || null;
          
          return {
            ...record,
            id: record.id,
            studentId: studentId,
            totalHizb: record.total_hizb || record.totalHizb || 0,
            // ✅ آخر جلسة (Last Session) - depuis quran_records
            newMemorization: GLOBAL_UTILS.cleanText(record.new_memorization || record.newMemorization || ''),
            review: GLOBAL_UTILS.cleanText(record.review || ''),
            // ✅ الجلسة القادمة (Next Session) - depuis quran_next_session
            assignment: nextSession ? GLOBAL_UTILS.cleanText(nextSession.nextHalaqaNewHifzText || nextSession.assignment || '') : GLOBAL_UTILS.cleanText(record.assignment || ''),
            note: GLOBAL_UTILS.cleanText((record.note || '').trim()),
            date: record.date || record.created_date || '',
            studentName: GLOBAL_UTILS.cleanText(record.student_name || record.studentName || ''),
            teacher_id: record.teacher_id || record.teacherId || 0,
            teacherId: record.teacher_id || record.teacherId || 0,
            evalNewHifz: record.evalNewHifz,
            evalOldHifzMurajah: record.evalOldHifzMurajah,
            // ✅ Champs du nouveau modèle depuis quran_records
            newHifzTxt: GLOBAL_UTILS.cleanText(record.newHifzTxt || record.new_memorization || ''),
            oldHifzMurajahTxt: GLOBAL_UTILS.cleanText(record.oldHifzMurajahTxt || record.review || ''),
            newHifzMurajahText: GLOBAL_UTILS.cleanText(record.newHifzMurajahText || ''),
            // ❌ Les champs nextHalaqa* ne doivent plus être dans quranRecords
            // Ils sont maintenant récupérés directement depuis quran_next_session dans QuranTrackingSection
            // ✅ Compteur de répétitions - depuis quran_records uniquement
            repeatNewHifz: extractRepeatNewHifzFromRecord(record)
          };
        });
        
        setQuranRecords(mappedRecords);
        return true;
      } else {
        console.error('❌ AdminDashboard - refreshQuranRecords - Erreur:', quranRecordsResponse?.error);
        return false;
      }
    } catch (error) {
      console.error('❌ AdminDashboard - refreshQuranRecords - Exception:', error);
      return false;
    }
  }, [apiService]);

  // Charger automatiquement les enregistrements Quran quand on accède à la section متابعة حفظ القرآن
  useEffect(() => {
    if (currentSection === 'teachers' && teachersSubSection === 'quranTracking') {
      refreshQuranRecords();
    }
  }, [currentSection, teachersSubSection, refreshQuranRecords]);

  // Mémoriser les callbacks pour QuranTrackingSection
  const handleRefreshStats = React.useCallback(async () => {
    // TODO: Implémenter si nécessaire
  }, []);

  const handleGetStudents = React.useCallback(async () => {
    // ✅ Ne renvoyer que les élèves inscrits pour l'année de référence
    // (année sélectionnée pour la consultation, sinon année courante du système).
    // L'API filtre via student_enrollments (school_year_id).
    const yearId = selectedYear?.id ?? currentYear?.id ?? null;
    const response = await apiService.getStudents(yearId ? { school_year_id: yearId } : {});
    return response;
  }, [apiService, selectedYear?.id, currentYear?.id]);

  const handleAddQuranRecord = React.useCallback(async (record: any) => {
    try {
      // Utiliser le teacher_id du filtre sélectionné, ou le teacher_id du record s'il est fourni
      const teacherId = record.teacherId || (selectedTeacherFilter !== 'all' ? parseInt(selectedTeacherFilter) : null);
      if (!teacherId) {
        return { success: false, error: 'يجب اختيار أستاذ لإضافة السجل' };
      }
      
      const recordWithTeacher = {
        ...record,
        teacherId: teacherId
      };
      
      // Ne pas faire de refresh ici - onRecordAdded le fera une seule fois
      const result = await apiService.addQuranRecord(recordWithTeacher);
      return result;
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout de l\'enregistrement Coran:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'ajout du سجل القرآن'
      };
    }
  }, [apiService, selectedTeacherFilter]);

  const handleCreateOrUpdateNextSession = React.useCallback(async (studentId: number, sessionData: any) => {
    try {
      const result = await apiService.createOrUpdateNextSession(studentId, sessionData);
      return result;
    } catch (error) {
      console.error('❌ Erreur lors de la création/mise à jour de la session suivante:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création/mise à jour de la session suivante'
      };
    }
  }, [apiService]);

  const handleUpdateRepeatNewHifz = React.useCallback(async (recordId: number, repeatNewHifz: number) => {
    try {
      const result = await apiService.updateRepeatNewHifz(recordId, repeatNewHifz);
      return result;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de repeatNewHifz:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de repeatNewHifz'
      };
    }
  }, [apiService]);

  // Callback pour récupérer la session suivante d'un étudiant
  const handleGetNextSessionForStudent = React.useCallback(async (studentId: number) => {
    try {
      return await apiService.getNextSessionForStudent(studentId);
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de la session suivante:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération de la session suivante'
      };
    }
  }, [apiService]);

  // ✅ NOUVEAU : Récupérer les IDs des étudiants assignés à un professeur (via student_teachers)
  const handleGetStudentIdsByTeacher = React.useCallback(async (teacherId: number) => {
    try {
      // Essayer d'abord via la table student_teachers
      const response = await apiService.getStudentTeachersByTeacher(teacherId);
      
      if (response.success && response.data && response.data.length > 0) {
        // Extraire les student_id de la réponse
        const studentIds = response.data.map((relation: any) => relation.student_id).filter((id: any) => id !== undefined && id !== null);
        return {
          success: true,
          data: studentIds
        };
      }
      
      // Fallback: Si pas de relation dans student_teachers, utiliser teacher_name
      const selectedTeacher = teachers.find(t => {
        const tUserId = (t as any).user_id || t.id;
        return tUserId === teacherId || parseInt(String(tUserId)) === teacherId || t.id === teacherId;
      });
      
      if (selectedTeacher) {
        const teacherName = selectedTeacher.full_name || selectedTeacher.username;
        const studentIds = students
          .filter(student => student.teacher_name === teacherName)
          .map(student => student.id);
        
        if (studentIds.length > 0) {
          return {
            success: true,
            data: studentIds
          };
        }
      }
      
      return {
        success: false,
        error: 'Aucune relation trouvée'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des étudiants du professeur'
      };
    }
  }, [apiService, teachers, students]);

  const handleUpdateQuranRecord = React.useCallback(async (id: number, record: any) => {
    try {
      // Utiliser le teacher_id du filtre sélectionné, ou le teacher_id du record s'il est fourni
      const teacherId = record.teacherId || (selectedTeacherFilter !== 'all' ? parseInt(selectedTeacherFilter) : null);
      if (!teacherId) {
        return { success: false, error: 'يجب اختيار أستاذ لتعديل السجل' };
      }
      
      const recordWithTeacher = {
        ...record,
        teacherId: teacherId
      };
      
      // Ne pas faire de refresh ici - onRecordUpdated le fera une seule fois
      const result = await apiService.updateQuranRecord(id, recordWithTeacher);
      return result;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de l\'enregistrement Coran:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour du سجل القرآن'
      };
    }
  }, [apiService, selectedTeacherFilter]);

  // Année effectivement chargée dans le tableau de bord (détection du changement d'année consultée)
  const dashboardLoadedYearRef = React.useRef<number | null | undefined>(undefined);

  // Compteur de relance automatique des KPI (max 1) quand /api/dashboard/data échoue
  const dashboardRetryRef = React.useRef(0);

  // ✅ Rafraîchissement manuel : relance loadDashboardData() et mémorise l'heure
  const handleRefreshDashboard = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      dashboardRetryRef.current = 0;
      await loadDashboardData();
      setLastRefreshAt(new Date());
      console.log('🔄 ADMIN DASHBOARD - rafraîchissement manuel terminé');
    } catch (error) {
      console.error('❌ ADMIN DASHBOARD - rafraîchissement manuel:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const yearId = selectedYear?.id ?? currentYear?.id ?? null;

    if (!isInitialized) {
      dashboardLoadedYearRef.current = yearId;
      loadDashboardData();
      setIsInitialized(true);
      return;
    }

    // ✅ Recharger les KPI (et les listes) quand l'année scolaire consultée change
    if (dashboardLoadedYearRef.current !== yearId) {
      dashboardLoadedYearRef.current = yearId;
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, selectedYear?.id, currentYear?.id]);

  // ✅ Cohérence des KPI : présent + absent doit égaler le total des élèves du périmètre
  // affiché (carte « إجمالي الطلاب »). Sinon l'API d'assiduité et /api/dashboard/data ne
  // visent pas le même périmètre (ex. « الغائبون اليوم = 263 » pour 88 élèves) → on
  // n'affiche pas une valeur fausse.
  const kpiPerimeterMismatch = React.useMemo(() => {
    if (!todayHasSession) return false;               // jour sans séance → 0/0 légitime
    const total = stats?.data?.totalStudents;
    const present = stats?.data?.presentToday ?? 0;
    const absent = stats?.data?.absentToday ?? 0;
    if (total == null) return false;
    return present + absent !== total;
  }, [stats, todayHasSession]);

  // Mémoriser les étudiants filtrés pour QuranTrackingSection
  const filteredStudentsForQuran = React.useMemo(() => {
    // Filtrer les étudiants par professeur si un professeur est sélectionné
    let filteredStudents = students;
    if (selectedTeacherFilter !== 'all') {
      const teacherId = parseInt(selectedTeacherFilter);
      
      // Trouver le professeur sélectionné
      const selectedTeacher = teachers.find(t => {
        const tUserId = (t as any).user_id || t.id;
        return tUserId === teacherId || parseInt(String(tUserId)) === teacherId || t.id === teacherId;
      });
      
      if (selectedTeacher) {
        const teacherName = selectedTeacher.full_name || selectedTeacher.username;
        
        // Filtrer par teacher_name
        filteredStudents = students.filter(student => {
          return student.teacher_name === teacherName;
        });
      }
    }
    
    // ✅ Année de référence : année sélectionnée (consultation) sinon année courante du système
    const quranYearId = selectedYear?.id ?? currentYear?.id ?? null;

    // ✅ Périmètre d'affichage : uniquement les élèves INSCRITS pour l'année de référence.
    // Le suivi de mémorisation reste longitudinal (les sessions précédentes traversent les années),
    // mais seuls les élèves inscrits cette année apparaissent dans la page.
    if (quranYearId && Number.isFinite(Number(quranYearId))) {
      filteredStudents = filteredStudents.filter(student => isEnrolledForYear(student, quranYearId));
    }

    // Enrichir les étudiants avec leurs noms depuis les enregistrements si nécessaire
    return filteredStudents.map(student => {
      // Trouver le premier enregistrement pour cet étudiant pour récupérer le nom si manquant
      const firstRecord = quranRecords.find(r => 
        (r.student_id === student.id) || (r.studentId === student.id)
      );
      const studentName = student.student_name || firstRecord?.studentName || firstRecord?.student_name || `طالب ${student.id}`;
      return {
        ...student,
        student_name: studentName,
        // Ajouter studentName pour compatibilité avec QuranTrackingSection
        studentName: studentName
      } as Student & { studentName: string }; // Type assertion pour permettre studentName
    });
  }, [students, quranRecords, selectedTeacherFilter, teachers, selectedYear?.id, currentYear?.id]);

  // Mémoriser les enregistrements Quran filtrés
  const filteredQuranRecords = React.useMemo(() => {
    // Filtrer les enregistrements en fonction du professeur sélectionné
    if (selectedTeacherFilter === 'all') {
      return quranRecords;
    }
    
    // Le selectedTeacherFilter contient l'ID de la table users
    const userIdToMatch = parseInt(selectedTeacherFilter);
    
    const filtered = quranRecords.filter(record => {
      // teacher_id dans quran_records doit correspondre à users.id
      const recordTeacherId = record.teacher_id || record.teacherId;
      
      // Comparaison directe avec l'ID de la table users
      return recordTeacherId === userIdToMatch || 
             parseInt(String(recordTeacherId)) === userIdToMatch;
    });
    
    return filtered;
  }, [quranRecords, selectedTeacherFilter]);

  // Filtrer les contributions pour exclure celles des étudiants inactifs
  const filteredContributions = React.useMemo(() => {
    return monthlyContributions.filter(contribution => {
      // Si la contribution a un student_id, vérifier le statut de l'étudiant
      if (contribution.student_id) {
        const student = students.find(s => s.id === contribution.student_id);
        if (student) {
          const studentStatusStr = student.status as string;
          const studentStatus = studentStatusStr === 'active' || studentStatusStr === 'نشط' || studentStatusStr === '1';
          return studentStatus;
        }
      }
      // Si pas de student_id, essayer de trouver l'étudiant par nom
      if (contribution.student_name) {
        const student = students.find(s => s.student_name === contribution.student_name);
        if (student) {
          const studentStatusStr = student.status as string;
          const studentStatus = studentStatusStr === 'active' || studentStatusStr === 'نشط' || studentStatusStr === '1';
          return studentStatus;
        }
      }
      // Si pas de student_id ou étudiant non trouvé, inclure la contribution
      // (pour éviter de cacher des données si l'étudiant n'est pas trouvé)
      return true;
    });
  }, [monthlyContributions, students]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8f9fa',
        fontFamily: 'Cairo, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #2E7D32',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <div style={{ fontSize: '1.2rem', color: '#2E7D32' }}>Chargement du Dashboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8f9fa',
        fontFamily: 'Cairo, sans-serif'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '20px' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>❌</div>
          <h2 style={{ color: '#dc3545', marginBottom: '10px' }}>Erreur</h2>
          <p style={{ color: '#6c757d', marginBottom: '20px' }}>{error}</p>
            <button
              onClick={loadDashboardData}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2E7D32',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
              cursor: 'pointer',
              marginRight: '10px'
              }}
            >
              <RefreshIcon size={16} style={{ marginLeft: '4px' }} />
              Réessayer
            </button>
            <button
              onClick={onBackHome}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              🏠 Retour à l'accueil
            </button>
        </div>
      </div>
    );
  }

  
  // Rendu du menu de navigation élégant et responsive
  const renderNavigation = () => (
    <nav style={{
      backgroundColor: '#fafafa',
      borderBottom: '1px solid #e0e0e0',
      marginBottom: '30px',
      padding: '0'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: windowWidth < 768 ? '0 15px' : '0 30px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          height: windowWidth < 768 ? 'auto' : '70px',
          minHeight: windowWidth < 768 ? '60px' : '70px',
          padding: windowWidth < 768 ? '12px 0' : '0',
          flexWrap: windowWidth < 768 ? 'wrap' : 'nowrap',
          gap: windowWidth < 768 ? '12px' : '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            <div style={{
              width: windowWidth < 768 ? '50px' : '65px',
              height: windowWidth < 768 ? '50px' : '65px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              backgroundColor: 'white',
            }}>
              <img 
                src={logoUrl}
                alt={getName(currentLanguage)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '6px'
                }}
              />
            </div>
            {windowWidth >= 768 && (
              <div>
                <div style={{ fontSize: '1.2rem', fontWeight: '400' }}>جمعية الفلاح</div>
                <div style={{ 
                  fontSize: '0.9rem', 
                  color: '#757575', 
                  fontWeight: '300',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>{t('adminDashboard')}</div>
                {currentAdminName && (
                  <div style={{ 
                    fontSize: '0.85rem', 
                    color: '#2E7D32', 
                    fontWeight: '500',
                    marginTop: '2px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>{currentAdminName}</div>
                )}
              </div>
            )}
          </div>
          {windowWidth < 768 ? (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                padding: '8px 12px',
                backgroundColor: 'transparent',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '20px',
                marginLeft: 'auto'
              }}
            >
              ☰
            </button>
          ) : (
            <div style={{
              display: 'flex',
              gap: isRTL ? (windowWidth < 1200 ? '1px' : '2px') : '4px',
              flexWrap: isRTL ? (windowWidth < 1200 ? 'wrap' : 'nowrap') : 'nowrap',
              alignItems: 'center'
            }}>
              {/* 1. Tableau de bord */}
              <button
                onClick={() => navigateToSection('dashboard')}
                style={{
                  padding: windowWidth < 1024 ? '10px 16px' : (isRTL ? (windowWidth < 1200 ? '10px 18px' : '12px 24px') : '10px 18px'),
                  backgroundColor: currentSection === 'dashboard' ? '#e8f5e8' : 'transparent',
                  color: currentSection === 'dashboard' ? '#2e7d32' : '#666666',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: windowWidth < 1024 ? '14px' : (isRTL ? (windowWidth < 1200 ? '15px' : '18px') : '14px'),
                  fontWeight: '500', transition: 'all 0.2s ease',
                  borderBottom: currentSection === 'dashboard' ? '2px solid #4caf50' : '2px solid transparent',
                  whiteSpace: 'nowrap'
                }}
              >
                {t('dashboard')}
              </button>

              {/* 2. Notes d'examens */}
              <button
                onClick={() => navigateToSection('grades')}
                style={{
                  padding: windowWidth < 1024 ? '10px 16px' : (isRTL ? (windowWidth < 1200 ? '10px 18px' : '12px 24px') : '10px 18px'),
                  backgroundColor: currentSection === 'grades' ? '#f3e5f5' : 'transparent',
                  color: currentSection === 'grades' ? '#7b1fa2' : '#666666',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: windowWidth < 1024 ? '14px' : (isRTL ? (windowWidth < 1200 ? '15px' : '18px') : '14px'),
                  fontWeight: '500', transition: 'all 0.2s ease',
                  borderBottom: currentSection === 'grades' ? '2px solid #9c27b0' : '2px solid transparent',
                  whiteSpace: 'nowrap'
                }}
              >
                {t('grades')}
              </button>

              {/* 3. Messages & Communication — dropdown au survol */}
              <div
                style={{ position: 'relative' }}
                onMouseEnter={() => { clearNavLeaveTimer(); setHoveredNavKey('communication'); }}
                onMouseLeave={scheduleNavClose}
              >
              <button
                  onClick={() => { navigateToSection('communication'); setCommunicationTab('messages'); setMessagesSubTab('received'); }}
                style={{
                  padding: windowWidth < 1024 ? '10px 16px' : (isRTL ? (windowWidth < 1200 ? '10px 18px' : '12px 24px') : '10px 18px'),
                  backgroundColor: currentSection === 'communication' ? '#fff3e0' : 'transparent',
                  color: currentSection === 'communication' ? '#f57c00' : '#666666',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: windowWidth < 1024 ? '14px' : (isRTL ? (windowWidth < 1200 ? '15px' : '18px') : '14px'),
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  borderBottom: currentSection === 'communication' ? '2px solid #ff9800' : '2px solid transparent',
                    whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center', gap: '4px'
                }}
              >
                  {t('communication')} <span style={{ fontSize: '9px', opacity: 0.6 }}>▾</span>
              </button>
                {hoveredNavKey === 'communication' && (
                  <div
                    onMouseEnter={() => { clearNavLeaveTimer(); setHoveredNavKey('communication'); }}
                    onMouseLeave={scheduleNavClose}
                    style={{ position: 'absolute', top: '100%', left: isRTL ? 'auto' : 0, right: isRTL ? 0 : 'auto', minWidth: '220px', backgroundColor: '#fff', border: '1px solid #ffe0b2', borderTop: '2px solid #ff9800', borderRadius: '0 0 8px 8px', padding: '0', zIndex: 200, overflow: 'hidden' }}
                  >
                    {[
                      { key: 'messages', label: t('messages') || 'الرسائل' },
                      { key: 'emails', label: t('emails') || 'البريد الإلكتروني' },
                      { key: 'inscription-requests', label: t('inscriptionRequests') || 'طلبات التسجيل' },
                      { key: 'contact-messages', label: t('contactMessages') || 'رسائل التواصل' },
                    ].map((sub, idx, arr) => (
              <button
                        key={sub.key}
                        onMouseEnter={() => setHoveredSubNavKey(sub.key)}
                        onMouseLeave={() => setHoveredSubNavKey(null)}
                        onClick={() => { navigateToSection('communication'); setCommunicationTab(sub.key as typeof communicationTab); setHoveredNavKey(null); }}
                        style={{ display: 'block', width: '100%', padding: '10px 16px', background: (currentSection === 'communication' && communicationTab === sub.key) ? '#fff3e0' : hoveredSubNavKey === sub.key ? '#fffaf5' : 'linear-gradient(180deg,#ffffff 0%,#f9f9f9 100%)', color: (currentSection === 'communication' && communicationTab === sub.key) ? '#f57c00' : '#444', border: 'none', borderBottom: idx < arr.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none', boxShadow: idx < arr.length - 1 ? '0 2px 3px rgba(0,0,0,0.04)' : 'none', cursor: 'pointer', fontSize: '13px', fontWeight: (currentSection === 'communication' && communicationTab === sub.key) ? '600' : '400', textAlign: isRTL ? 'right' : 'left', transition: 'background 0.15s ease' }}
                      >
                        {sub.label}
              </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 4. Élèves & Parents — dropdown au survol */}
              <div
                style={{ position: 'relative' }}
                onMouseEnter={() => { clearNavLeaveTimer(); setHoveredNavKey('studentsParents'); }}
                onMouseLeave={scheduleNavClose}
              >
              <button
                  onClick={() => navigateToSection('students')}
                style={{
                  padding: windowWidth < 1024 ? '10px 16px' : (isRTL ? (windowWidth < 1200 ? '10px 18px' : '12px 24px') : '10px 18px'),
                    backgroundColor: (currentSection === 'students' || currentSection === 'parents') ? '#e3f2fd' : 'transparent',
                    color: (currentSection === 'students' || currentSection === 'parents') ? '#1565c0' : '#666666',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: windowWidth < 1024 ? '14px' : (isRTL ? (windowWidth < 1200 ? '15px' : '18px') : '14px'),
                    fontWeight: '500', transition: 'all 0.2s ease',
                    borderBottom: (currentSection === 'students' || currentSection === 'parents') ? '2px solid #2196f3' : '2px solid transparent',
                    whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  {t('studentsParents') || 'Élèves & Parents'} <span style={{ fontSize: '9px', opacity: 0.6 }}>▾</span>
              </button>
                {hoveredNavKey === 'studentsParents' && (
                  <div
                    onMouseEnter={() => { clearNavLeaveTimer(); setHoveredNavKey('studentsParents'); }}
                    onMouseLeave={scheduleNavClose}
                    style={{ position: 'absolute', top: '100%', left: isRTL ? 'auto' : 0, right: isRTL ? 0 : 'auto', minWidth: '180px', backgroundColor: '#fff', border: '1px solid #bbdefb', borderTop: '2px solid #2196f3', borderRadius: '0 0 8px 8px', padding: '0', zIndex: 200, overflow: 'hidden' }}
                  >
                    {[
                      { key: 'students', label: t('students') || 'Élèves' },
                      { key: 'parents', label: t('parents') || 'Parents' },
                    ].map((sub, idx, arr) => (
                <button
                        key={sub.key}
                        onMouseEnter={() => setHoveredSubNavKey(sub.key)}
                        onMouseLeave={() => setHoveredSubNavKey(null)}
                        onClick={() => { navigateToSection(sub.key); setHoveredNavKey(null); }}
                        style={{ display: 'block', width: '100%', padding: '10px 16px', background: currentSection === sub.key ? '#e3f2fd' : hoveredSubNavKey === sub.key ? '#f0f7ff' : 'linear-gradient(180deg,#ffffff 0%,#f9f9f9 100%)', color: currentSection === sub.key ? '#1565c0' : '#444', border: 'none', borderBottom: idx < arr.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none', boxShadow: idx < arr.length - 1 ? '0 2px 3px rgba(0,0,0,0.04)' : 'none', cursor: 'pointer', fontSize: '13px', fontWeight: currentSection === sub.key ? '600' : '400', textAlign: isRTL ? 'right' : 'left', transition: 'background 0.15s ease' }}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 5. Enseignants — dropdown au survol */}
              <div
                style={{ position: 'relative' }}
                onMouseEnter={() => { clearNavLeaveTimer(); setHoveredNavKey('teachers'); }}
                onMouseLeave={scheduleNavClose}
              >
                <button
                  onClick={() => { navigateToSection('teachers'); setTeachersSubSection('manage'); }}
                  style={{
                    padding: windowWidth < 1024 ? '10px 16px' : (isRTL ? (windowWidth < 1200 ? '10px 18px' : '12px 24px') : '10px 18px'),
                    backgroundColor: currentSection === 'teachers' ? '#e0f2f1' : 'transparent',
                    color: currentSection === 'teachers' ? '#00695c' : '#666666',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: windowWidth < 1024 ? '14px' : (isRTL ? (windowWidth < 1200 ? '15px' : '18px') : '14px'),
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    borderBottom: currentSection === 'teachers' ? '2px solid #009688' : '2px solid transparent',
                    whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  {t('teachers')} <span style={{ fontSize: '9px', opacity: 0.6 }}>▾</span>
                </button>
                {hoveredNavKey === 'teachers' && (
                  <div
                    onMouseEnter={() => { clearNavLeaveTimer(); setHoveredNavKey('teachers'); }}
                    onMouseLeave={scheduleNavClose}
                    style={{ position: 'absolute', top: '100%', left: isRTL ? 'auto' : 0, right: isRTL ? 0 : 'auto', minWidth: '230px', backgroundColor: '#fff', border: '1px solid #b2dfdb', borderTop: '2px solid #009688', borderRadius: '0 0 8px 8px', padding: '0', zIndex: 200, overflow: 'hidden' }}
                  >
                    {[
                      { key: 'manage' as const, label: t('manageTeachers') },
                      { key: 'quranTracking' as const, label: t('quranTracking') },
                      { key: 'studentProgress' as const, label: t('studentProgress') },
                    ].map((sub, idx, arr) => (
                      <button
                        key={sub.key}
                        onMouseEnter={() => setHoveredSubNavKey(sub.key)}
                        onMouseLeave={() => setHoveredSubNavKey(null)}
                        onClick={() => { navigateToSection('teachers'); setTeachersSubSection(sub.key); setHoveredNavKey(null); }}
                        style={{ display: 'block', width: '100%', padding: '10px 16px', background: (currentSection === 'teachers' && teachersSubSection === sub.key) ? '#e0f2f1' : hoveredSubNavKey === sub.key ? '#f5fffe' : 'linear-gradient(180deg,#ffffff 0%,#f9f9f9 100%)', color: (currentSection === 'teachers' && teachersSubSection === sub.key) ? '#00695c' : '#444', border: 'none', borderBottom: idx < arr.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none', boxShadow: idx < arr.length - 1 ? '0 2px 3px rgba(0,0,0,0.04)' : 'none', cursor: 'pointer', fontSize: '13px', fontWeight: (currentSection === 'teachers' && teachersSubSection === sub.key) ? '600' : '400', textAlign: isRTL ? 'right' : 'left', transition: 'background 0.15s ease' }}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 6. Administration — dropdown combinant financial + admins */}
              {(canViewSection('finances') || canViewSection('admins')) && (
                <div
                  style={{ position: 'relative' }}
                  onMouseEnter={() => { clearNavLeaveTimer(); setHoveredNavKey('administration'); }}
                  onMouseLeave={scheduleNavClose}
                >
                <button
                    onClick={() => {
                      if (canViewSection('finances')) { navigateToSection('financial'); setFinancialSubSection('expenses'); }
                      else { navigateToSection('admins'); setAdminsSubSection('adminsList'); }
                    }}
                  style={{
                    padding: windowWidth < 1024 ? '10px 16px' : (isRTL ? (windowWidth < 1200 ? '10px 18px' : '12px 24px') : '10px 18px'),
                      backgroundColor: (currentSection === 'financial' || currentSection === 'admins' || currentSection === 'association') ? '#ede7f6' : 'transparent',
                      color: (currentSection === 'financial' || currentSection === 'admins' || currentSection === 'association') ? '#5e35b1' : '#666666',
                      border: 'none', borderRadius: '6px', cursor: 'pointer',
                      fontSize: windowWidth < 1024 ? '14px' : (isRTL ? (windowWidth < 1200 ? '15px' : '18px') : '14px'),
                      fontWeight: '500', transition: 'all 0.2s ease',
                      borderBottom: (currentSection === 'financial' || currentSection === 'admins' || currentSection === 'association') ? '2px solid #5e35b1' : '2px solid transparent',
                      whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    {t('administration') || 'Administration'} <span style={{ fontSize: '9px', opacity: 0.6 }}>▾</span>
                </button>
                  {hoveredNavKey === 'administration' && (
                    <div
                      onMouseEnter={() => { clearNavLeaveTimer(); setHoveredNavKey('administration'); }}
                      onMouseLeave={scheduleNavClose}
                      style={{ position: 'absolute', top: '100%', left: isRTL ? 'auto' : 0, right: isRTL ? 0 : 'auto', minWidth: '220px', backgroundColor: '#fff', border: '1px solid #d1c4e9', borderTop: '2px solid #5e35b1', borderRadius: '0 0 8px 8px', padding: '0', zIndex: 200, overflow: 'hidden' }}
                    >
                      {[
                        ...(canViewSection('finances') ? [{ key: 'financial-expenses', label: t('financialManagement') || 'Gestion financière', action: () => { navigateToSection('financial'); setFinancialSubSection('expenses'); setHoveredNavKey(null); }, isActive: currentSection === 'financial' }] : []),
                        ...(canViewSection('admins') ? [
                          { key: 'admins-list', label: t('adminsList') || 'المديرون', action: () => { navigateToSection('admins'); setAdminsSubSection('adminsList'); setHoveredNavKey(null); }, isActive: currentSection === 'admins' && adminsSubSection === 'adminsList' },
                          { key: 'association-settings', label: t('parameters') || 'الإعدادات', action: () => { navigateToSection('association'); setHoveredNavKey(null); }, isActive: currentSection === 'association' || (currentSection === 'admins' && adminsSubSection === 'parameters') },
                        ] : []),
                      ].map((sub, idx, arr) => (
                        <button
                          key={sub.key}
                          onMouseEnter={() => setHoveredSubNavKey(sub.key)}
                          onMouseLeave={() => setHoveredSubNavKey(null)}
                          onClick={sub.action}
                          style={{ display: 'block', width: '100%', padding: '10px 16px', background: sub.isActive ? '#ede7f6' : hoveredSubNavKey === sub.key ? '#f9f7ff' : 'linear-gradient(180deg,#ffffff 0%,#f9f9f9 100%)', color: sub.isActive ? '#5e35b1' : '#444', border: 'none', borderBottom: idx < arr.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none', boxShadow: idx < arr.length - 1 ? '0 2px 3px rgba(0,0,0,0.04)' : 'none', cursor: 'pointer', fontSize: '13px', fontWeight: sub.isActive ? '600' : '400', textAlign: isRTL ? 'right' : 'left', transition: 'background 0.15s ease' }}
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div style={{ width: '1px', height: '30px', backgroundColor: '#e0e0e0', margin: '0 10px' }}></div>
              <button
                onClick={onBackHome}
                style={{
                  padding: windowWidth < 1024 ? '10px 16px' : (isRTL ? (windowWidth < 1200 ? '10px 18px' : '12px 24px') : '10px 18px'),
                  backgroundColor: 'transparent',
                  color: '#d32f2f',
                  border: '1px solid #ffcdd2',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: windowWidth < 1024 ? '14px' : (isRTL ? (windowWidth < 1200 ? '15px' : '18px') : '14px'),
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                {t('logout')}
              </button>
            </div>
          )}
        </div>
        {/* Drawer mobile — overlay + panneau latéral */}
        {windowWidth < 768 && isMobileMenuOpen && (
          <>
            {/* Overlay */}
            <div onClick={() => setIsMobileMenuOpen(false)}
              style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1000 }} />
            {/* Panneau */}
            <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '280px', backgroundColor: 'white', zIndex: 1001, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)', overflowY: 'auto' }}>
              {/* En-tête */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#FAFAFA' }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '15px', color: '#1F2937' }}>{currentAdminName || t('admin')}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{t('administrationBoard')}</div>
                </div>
                <button onClick={() => setIsMobileMenuOpen(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6B7280', padding: '4px', lineHeight: 1 }}>✕</button>
              </div>

              {/* Navigation — même structure que le menu desktop */}
              <div style={{ flex: 1, padding: '10px 10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>

                {/* Style helper réutilisable */}
                {(() => {
                  const btnDirect = (active: boolean, activeColor: string, activeText: string) => ({
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    padding: '11px 14px', backgroundColor: active ? activeColor : 'transparent',
                    color: active ? activeText : '#374151', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontSize: '14px', fontWeight: active ? '600' : '400',
                    textAlign: (isRTL ? 'right' : 'left') as 'right' | 'left', outline: 'none'
                  });
                  const btnGroup = (active: boolean, activeColor: string, activeText: string) => ({
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                    padding: '11px 14px', backgroundColor: active ? activeColor : 'transparent',
                    color: active ? activeText : '#374151', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontSize: '14px', fontWeight: active ? '600' : '400',
                    textAlign: (isRTL ? 'right' : 'left') as 'right' | 'left', outline: 'none'
                  });
                  const btnSub = (active: boolean, activeColor: string, activeText: string) => ({
                    width: '100%', padding: '8px 12px', background: active ? activeColor : 'transparent',
                    color: active ? activeText : '#6B7280', border: 'none', borderRadius: '6px',
                    cursor: 'pointer', fontSize: '13px', fontWeight: active ? '600' : '400',
                    textAlign: (isRTL ? 'right' : 'left') as 'right' | 'left', outline: 'none'
                  });
                  const chevron = (open: boolean, color: string) => (
                    <span style={{ fontSize: '10px', color, transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', lineHeight: 1 }}>▼</span>
                  );
                  const subList = (borderColor: string, children: React.ReactNode) => (
                    <div style={{ marginTop: '2px', marginRight: '14px', borderRight: `2px solid ${borderColor}`, paddingRight: '6px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      {children}
                    </div>
                  );

                  return (
                    <>
                      {/* لوحة التحكم */}
                      <button onClick={() => { navigateToSection('dashboard'); setIsMobileMenuOpen(false); }}
                        style={btnDirect(currentSection === 'dashboard', '#e8f5e8', '#2E7D32')}>
                        <span>🏠</span>{t('dashboard')}
            </button>

                      {/* نقط الإختبارات */}
                      <button onClick={() => { navigateToSection('grades'); setIsMobileMenuOpen(false); }}
                        style={btnDirect(currentSection === 'grades', '#F3E5F5', '#7B1FA2')}>
                        <span>📊</span>{t('grades')}
            </button>

                      {/* التواصل */}
                      {(() => {
                        const isActive = currentSection === 'communication';
                        const isOpen = mobileExpandedGroup === 'communication' || isActive;
                        return (
                          <div>
                            <button onClick={() => {
                              setMobileExpandedGroup(mobileExpandedGroup === 'communication' ? null : 'communication');
                            }} style={btnGroup(isActive, '#FFF3E0', '#F57C00')}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>💬</span>{t('communication') || 'Communication'}</span>
                              {chevron(isOpen, isActive ? '#F57C00' : '#9CA3AF')}
            </button>
                            {isOpen && subList('#FFB74D', [
                              { key: 'messages', label: t('messages') || 'الرسائل' },
                              { key: 'emails', label: t('emails') || 'البريد الإلكتروني' },
                              { key: 'inscription-requests', label: t('inscriptionRequests') || 'طلبات التسجيل' },
                              { key: 'contact-messages', label: t('contactMessages') || 'رسائل التواصل' },
                            ].map(sub => (
                              <button key={sub.key} onClick={() => { navigateToSection('communication'); setCommunicationTab(sub.key as typeof communicationTab); setMobileExpandedGroup('communication'); setIsMobileMenuOpen(false); }}
                                style={btnSub(isActive && communicationTab === sub.key, '#FFF3E0', '#E65100')}>
                                {sub.label}
            </button>
                            )))}
                          </div>
                        );
                      })()}

                      {/* الطلاب وأولياء الأمور */}
                      {(() => {
                        const isActive = currentSection === 'students' || currentSection === 'parents';
                        const isOpen = mobileExpandedGroup === 'students-parents' || isActive;
                        return (
                          <div>
                            <button onClick={() => {
                              setMobileExpandedGroup(mobileExpandedGroup === 'students-parents' ? null : 'students-parents');
                            }} style={btnGroup(isActive, '#E3F2FD', '#1565C0')}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>👨‍👧</span>{t('studentsParents') || 'الطلاب وأولياء الأمور'}</span>
                              {chevron(isOpen, isActive ? '#1565C0' : '#9CA3AF')}
                            </button>
                            {isOpen && subList('#90CAF9', [
                              { section: 'students', label: t('students') },
                              { section: 'parents', label: t('parents') },
                            ].map(sub => (
                              <button key={sub.section} onClick={() => { navigateToSection(sub.section as any); setMobileExpandedGroup('students-parents'); setIsMobileMenuOpen(false); }}
                                style={btnSub(currentSection === sub.section, '#E3F2FD', '#1565C0')}>
                                {sub.label}
                              </button>
                            )))}
                          </div>
                        );
                      })()}

                      {/* الأساتذة — accordéon */}
                      {(() => {
                        const isActive = currentSection === 'teachers';
                        const isOpen = mobileExpandedGroup === 'teachers' || isActive;
                        const subItems = [
                          { key: 'manage' as const, label: t('manageTeachers') },
                          { key: 'quranTracking' as const, label: t('quranTracking') },
                          { key: 'studentProgress' as const, label: t('studentProgress') },
                        ];
                        return (
                          <div>
                            <button onClick={() => {
                              setMobileExpandedGroup(mobileExpandedGroup === 'teachers' ? null : 'teachers');
                            }} style={btnGroup(isActive, '#E0F2F1', '#00695C')}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>📚</span>{t('teachers')}</span>
                              {chevron(isOpen, isActive ? '#00695C' : '#9CA3AF')}
                            </button>
                            {isOpen && subList('#80CBC4', subItems.map(sub => (
                              <button key={sub.key} onClick={() => { navigateToSection('teachers'); setTeachersSubSection(sub.key); setMobileExpandedGroup('teachers'); setIsMobileMenuOpen(false); }}
                                style={btnSub(isActive && teachersSubSection === sub.key, '#E0F2F1', '#00695C')}>
                                {sub.label}
                              </button>
                            )))}
                          </div>
                        );
                      })()}

                      {/* الإدارة */}
                      {(canViewSection('finances') || canViewSection('admins')) && (() => {
                        const isActive = currentSection === 'admins' || currentSection === 'financial' || currentSection === 'association';
                        const isOpen = mobileExpandedGroup === 'admin-group' || isActive;
                        type AdminSubItem =
                          | { type: 'financial'; section: 'financial'; subSection: 'expenses'; label: string }
                          | { type: 'admins'; section: 'admins'; subSection: 'adminsList'; label: string }
                          | { type: 'association'; section: 'association'; subSection: null; label: string };
                        const subItems: AdminSubItem[] = [
                          ...(canViewSection('finances') ? [{ type: 'financial' as const, section: 'financial' as const, subSection: 'expenses' as const, label: t('financialManagement') || 'الإدارة المالية' }] : []),
                          ...(canViewSection('admins') ? [
                            { type: 'admins' as const, section: 'admins' as const, subSection: 'adminsList' as const, label: t('adminsList') || 'المديرون' },
                            { type: 'association' as const, section: 'association' as const, subSection: null, label: t('parameters') || 'الإعدادات' },
                          ] : []),
                        ];
                        const isSubActive = (item: AdminSubItem) => {
                          if (item.type === 'financial') return currentSection === 'financial';
                          if (item.type === 'association') return currentSection === 'association';
                          return currentSection === 'admins' && adminsSubSection === item.subSection;
                        };
                        return (
                          <div>
                            <button onClick={() => {
                              setMobileExpandedGroup(mobileExpandedGroup === 'admin-group' ? null : 'admin-group');
                            }} style={btnGroup(isActive, '#ede7f6', '#5e35b1')}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><span>⚙️</span>{t('administration') || 'الإدارة'}</span>
                              {chevron(isOpen, isActive ? '#5e35b1' : '#9CA3AF')}
                            </button>
                            {isOpen && subList('#d1c4e9', subItems.map(sub => (
                              <button key={`${sub.section}-${sub.subSection ?? 'root'}`}
                                onClick={() => {
                                  navigateToSection(sub.section as any);
                                  if (sub.type === 'financial') setFinancialSubSection(sub.subSection as any);
                                  else if (sub.type === 'admins') setAdminsSubSection(sub.subSection as any);
                                  setMobileExpandedGroup('admin-group');
                                  setIsMobileMenuOpen(false);
                                }}
                                style={btnSub(isSubActive(sub), '#ede7f6', '#5e35b1')}>
                                {sub.label}
                              </button>
                            )))}
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>

              {/* Déconnexion */}
              <div style={{ padding: '12px 16px', borderTop: '1px solid #F3F4F6' }}>
                <button onClick={onBackHome}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '11px 16px', backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                  🚪 {t('logout')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  );

  // Fil d'Ariane global — calcule les segments selon la section et le sous-menu actif
  const renderBreadcrumb = () => {
    if (currentSection === 'dashboard') return null;

    type Crumb = { label: string; action?: () => void };
    const crumbs: Crumb[] = [
      { label: t('home') || 'الرئيسية', action: () => navigateToSection('dashboard') },
    ];

    const communicationTabLabel = () => {
      if (communicationTab === 'messages') return t('messages') || 'الرسائل';
      if (communicationTab === 'inscription-requests') return t('inscriptionRequests') || 'طلبات التسجيل';
      if (communicationTab === 'contact-messages') return t('contactMessages') || 'رسائل التواصل';
      if (communicationTab === 'emails') return t('emails') || 'البريد الإلكتروني';
      return t('messages') || 'الرسائل';
    };

    const teachersSubLabel = () => {
      if (teachersSubSection === 'quranTracking') return t('quranTracking');
      if (teachersSubSection === 'studentProgress') return t('studentProgress');
      return t('manageTeachers');
    };

    const financialSubLabel = () => {
      if (financialSubSection === 'expenses') return t('expenses') || 'المداخيل و المصاريف';
      if (financialSubSection === 'studentFees') return t('studentFees') || 'رسوم الطلاب';
      if (financialSubSection === 'teacherSalaries') return t('teacherSalaries') || 'رواتب الأساتذة';
      return t('financialManagement') || 'الإدارة المالية';
    };

    const adminsSubLabel = () => {
      if (adminsSubSection === 'parameters') return t('parameters') || 'الإعدادات';
      return t('adminsList') || 'المديرون';
    };

    switch (currentSection) {
      case 'grades':
        crumbs.push({ label: t('grades') || 'النتائج' });
        break;
      case 'communication':
        crumbs.push({ label: t('communication') || 'Communication', action: () => { navigateToSection('communication'); setCommunicationTab('messages'); setMessagesSubTab('received'); } });
        crumbs.push({ label: communicationTabLabel() });
        break;
      case 'students':
        crumbs.push({ label: t('studentsParents') || 'الطلاب وأولياء الأمور', action: () => navigateToSection('students') });
        crumbs.push({ label: t('students') || 'الطلاب' });
        break;
      case 'parents':
        crumbs.push({ label: t('studentsParents') || 'الطلاب وأولياء الأمور', action: () => navigateToSection('students') });
        crumbs.push({ label: t('parents') || 'أولياء الأمور' });
        break;
      case 'teachers':
        crumbs.push({ label: t('teachers') || 'الأساتذة', action: () => { navigateToSection('teachers'); setTeachersSubSection('manage'); } });
        crumbs.push({ label: teachersSubLabel() });
        break;
      case 'financial':
        crumbs.push({ label: t('administration') || 'الإدارة' });
        crumbs.push({ label: t('financialManagement') || 'الإدارة المالية', action: () => { navigateToSection('financial'); setFinancialSubSection('expenses'); } });
        crumbs.push({ label: financialSubLabel() });
        break;
      case 'admins':
        crumbs.push({ label: t('administration') || 'الإدارة' });
        crumbs.push({ label: adminsSubLabel() });
        break;
      case 'association':
        crumbs.push({ label: t('administration') || 'الإدارة' });
        crumbs.push({ label: isRTL ? 'الإعدادات' : 'Paramètres' });
        break;
      default:
        crumbs.push({ label: currentSection });
    }

    const isMobile = windowWidth < 768;

    const renderSectionActions = (): React.ReactNode => {
      const isMob = windowWidth < 768;
      const actionBtnStyle = (color: string): React.CSSProperties => ({
        backgroundColor: color,
        color: 'white',
        padding: isMob ? '5px 9px' : '5px 13px',
                borderRadius: '6px',
        border: 'none',
        fontSize: isMob ? '12px' : '13px',
                fontWeight: '500',
        cursor: 'pointer',
                transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap' as const,
        lineHeight: 1.4,
      });

      if (currentSection === 'grades') {
        return canModify('grades') ? (
          <button
            style={actionBtnStyle('#9c27b0')}
            onClick={async () => {
              try { const d = await apiService.getSubjects(); if (d?.success && Array.isArray(d.data)) setSubjects(d.data); } catch {}
              setShowAddTestScoreForm(true);
            }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#7b1fa2'; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#9c27b0'; }}
          >➕ {t('addTestScore')}</button>
        ) : null;
      }

      if (currentSection === 'students') {
        return (
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
            <button style={actionBtnStyle('#14b8a6')} onClick={handleRefreshStudents} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#0f766e'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#14b8a6'; }}>
              <RefreshIcon size={13} /><span>{t('refresh')}</span>
            </button>
            {isMob && (
              <button
                title={mobileViewMode === 'cards' ? t('viewTable') : t('viewCards')}
                style={{ ...actionBtnStyle(mobileViewMode === 'cards' ? '#8b5cf6' : '#06b6d4'), padding: '5px 8px' }}
                onClick={() => setMobileViewMode(mobileViewMode === 'cards' ? 'table' : 'cards')}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = mobileViewMode === 'cards' ? '#7c3aed' : '#0891b2'; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = mobileViewMode === 'cards' ? '#8b5cf6' : '#06b6d4'; }}
              >
                {mobileViewMode === 'cards'
                  ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                }
              </button>
            )}
            {canModify('students') && (
              <button style={actionBtnStyle('#4caf50')} onClick={async () => {
                setNewStudent({ student_name: '', parent_id: '', teacher_id: '', level: '', halqa: '', status: 'active', notes: '', gender: 'Male', school_year_id: currentYear?.id?.toString() || selectedYear?.id?.toString() || '' });
                setFormError(null); setFieldErrors({});
                try { const r = await apiService.getTeachers(); if (r?.success && Array.isArray(r.data)) setTeachers(r.data.map(convertTeacherStatus)); } catch {}
                setShowAddStudentForm(true);
              }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#45a049'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#4caf50'; }}>
                ➕ {t('addStudent')}
              </button>
            )}
          </div>
        );
      }

      if (currentSection === 'parents') {
        return (
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
            <button style={actionBtnStyle('#6b7280')} onClick={handleRefreshParents} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#4b5563'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#6b7280'; }}>
              <RefreshIcon size={13} /><span>{t('refresh')}</span>
            </button>
            {isMob && (
              <button
                title={parentMobileViewMode === 'cards' ? 'عرض جدول' : 'عرض بطاقات'}
                style={{ ...actionBtnStyle(parentMobileViewMode === 'cards' ? '#8b5cf6' : '#06b6d4'), padding: '5px 8px' }}
                onClick={() => setParentMobileViewMode(parentMobileViewMode === 'cards' ? 'table' : 'cards')}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = parentMobileViewMode === 'cards' ? '#7c3aed' : '#0891b2'; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = parentMobileViewMode === 'cards' ? '#8b5cf6' : '#06b6d4'; }}
              >
                {parentMobileViewMode === 'cards'
                  ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                }
              </button>
            )}
            {canModify('parents') && (
              <button style={actionBtnStyle('#3b82f6')} onClick={() => setShowAddParentForm(true)} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}>
                ➕ {t('addParent')}
              </button>
            )}
          </div>
        );
      }

      if (currentSection === 'teachers' && teachersSubSection === 'manage') {
        return (
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
            {/* Filtre discret : décoché = les enseignants inactifs sont masqués */}
            <label
              title={t('inactiveFilterHint')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 10px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                backgroundColor: '#fff',
                cursor: 'pointer',
                userSelect: 'none',
                flexShrink: 0
              }}
            >
              <input
                type="checkbox"
                checked={showInactiveTeachers}
                onChange={(e) => setShowInactiveTeachers(e.target.checked)}
                style={{ width: '13px', height: '13px', margin: 0, accentColor: '#9ca3af', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{t('inactive')}</span>
            </label>
            <button style={actionBtnStyle('#6b7280')} onClick={handleRefreshTeachers} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#4b5563'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#6b7280'; }}>
              <RefreshIcon size={13} /><span>{t('refresh')}</span>
            </button>
            {canModify('teachers') && (
              <button style={actionBtnStyle('#14b8a6')} onClick={() => setShowAddTeacherForm(true)} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#0f766e'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#14b8a6'; }}>
                ➕ {t('addTeacher')}
              </button>
            )}
          </div>
        );
      }

      if (currentSection === 'admins' && adminsSubSection === 'adminsList') {
        return (
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
            <button style={actionBtnStyle('#6b7280')} onClick={handleRefreshAdmins} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#4b5563'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#6b7280'; }}>
              <RefreshIcon size={13} /><span>{t('refresh')}</span>
            </button>
            {canModify('admins') && (
              <button style={actionBtnStyle('#6b7280')} onClick={() => { setShowAddAdminForm(true); setAdminPasswordConfirm(''); }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#4b5563'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#6b7280'; }}>
                ➕ إضافة مدير
              </button>
            )}
          </div>
        );
      }

      if (currentSection === 'admins' && adminsSubSection === 'parameters') {
        return (
            <button
            style={{ ...actionBtnStyle('#6b7280'), opacity: loadingParameters ? 0.6 : 1, cursor: loadingParameters ? 'wait' : 'pointer' }}
            onClick={handleRefreshParameters}
            disabled={loadingParameters}
            onMouseOver={(e) => { if (!loadingParameters) e.currentTarget.style.backgroundColor = '#4b5563'; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#6b7280'; }}
          >
            <RefreshIcon size={13} animate={loadingParameters} /><span>{t('refresh')}</span>
            </button>
        );
      }

      return null;
    };

    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 15px' : windowWidth < 1024 ? '0 20px' : '0 30px', marginBottom: isMobile ? '14px' : '18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', rowGap: isMobile ? '10px' : '8px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '20px', padding: isMobile ? '5px 12px' : '6px 16px', fontSize: isMobile ? '12px' : '13px', overflow: 'hidden', flexShrink: 1, minWidth: 0 }}>
          {crumbs.map((crumb, idx) => (
            <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
              {idx > 0 && <span style={{ color: '#94A3B8', fontSize: '15px', lineHeight: 1, flexShrink: 0 }}>»</span>}
              {crumb.action ? (
                <button onClick={crumb.action} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: idx === crumbs.length - 1 ? '#1E293B' : '#3B82F6', fontWeight: idx === crumbs.length - 1 ? '700' : '500', fontSize: 'inherit', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                  {crumb.label}
                </button>
              ) : (
                <span style={{ color: idx === crumbs.length - 1 ? '#1E293B' : '#64748B', fontWeight: idx === crumbs.length - 1 ? '700' : '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {crumb.label}
                </span>
              )}
            </span>
          ))}
      </div>
        {renderSectionActions()}
      </div>
  );
  };

  // Rendu du contenu principal selon la section
  const renderMainContent = () => {
    switch (currentSection) {
      case 'dashboard':
        return (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: windowWidth < 768 ? '0 15px' : windowWidth < 1024 ? '0 20px' : '0 20px'
          }}>

            {/* Carte de bienvenue */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: windowWidth < 768 ? '14px 16px' : '18px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', marginBottom: windowWidth < 768 ? '14px' : '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', borderLeft: isRTL ? '4px solid #2e7d32' : '1px solid #f0f0f0', borderRight: isRTL ? '1px solid #f0f0f0' : '4px solid #2e7d32' }}>
              {/* 🏫 juste avant les 2 lignes + bloc de texte */}
              <div style={{ display: 'flex', alignItems: 'center', gap: windowWidth < 768 ? '10px' : '14px', minWidth: 0 }}>
                <div style={{ fontSize: '28px', lineHeight: 1, flexShrink: 0 }}>🏫</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: windowWidth < 768 ? '15px' : '17px', fontWeight: '700', color: '#1a1a2e' }}>
                    {t('greeting') || 'مرحباً'}، {currentAdminName || t('admin') || 'المدير'} 👋
                  </div>
                  <div style={{ fontSize: windowWidth < 768 ? '12px' : '13px', color: '#6B7280', marginTop: '4px' }}>
                    {t('adminDashboard')} — {formatDateInTz(new Date(), timezone, dateLocale)}
                    {lastRefreshAt && (
                      <span style={{ marginInlineStart: '8px', color: '#9CA3AF' }}>
                        · {t('lastRefresh')} {formatTimeInTz(lastRefreshAt, timezone, dateLocale)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {/* Bouton Actualiser complètement à droite */}
              <div style={{ flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={handleRefreshDashboard}
                  disabled={isRefreshing}
                  title={t('refreshDashboard')}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: windowWidth < 768 ? '6px 8px' : '8px 12px', borderRadius: '8px', border: `1px solid ${isRefreshing ? '#D1D5DB' : '#2E7D32'}`, backgroundColor: isRefreshing ? '#F3F4F6' : '#F1F8E9', color: '#2E7D32', fontSize: windowWidth < 768 ? '11px' : '13px', fontWeight: '600', cursor: isRefreshing ? 'wait' : 'pointer', transition: 'all 0.2s ease' }}
                >
                  <span style={{ fontSize: windowWidth < 768 ? '13px' : '15px' }}>{isRefreshing ? '⏳' : '🔄'}</span>
                  {isRefreshing ? t('refreshing') : t('refresh')}
                </button>
              </div>
            </div>

            {/* Stats Cards */}
        <div style={{
          display: 'grid',
              gridTemplateColumns: windowWidth < 768 ? 'repeat(5, 1fr)' : windowWidth < 1024 ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)',
          gap: windowWidth < 768 ? '8px' : '10px',
              marginBottom: windowWidth < 768 ? '16px' : '20px',
              width: '100%'
        }}>
              {/* إجمالي الطلاب */}
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: windowWidth < 768 ? '8px 4px' : '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', textAlign: 'center', transition: 'all 0.2s ease', borderLeft: isRTL ? '4px solid #2196f3' : '1px solid #f0f0f0', borderRight: isRTL ? '1px solid #f0f0f0' : '4px solid #2196f3' }}>
                <div style={{ fontSize: windowWidth < 768 ? '1.1rem' : '1.75rem', fontWeight: '300', color: '#2196f3', marginBottom: '2px' }}>{stats?.data?.totalStudents != null ? stats.data.totalStudents : '—'}</div>
                <div style={{ color: '#424242', fontSize: windowWidth < 768 ? '9px' : '13px', fontWeight: '400', lineHeight: '1.2' }}>{t('statTotalStudents')}</div>
                </div>

              {/* الحاضرون اليوم */}
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: windowWidth < 768 ? '8px 4px' : '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', textAlign: 'center', transition: 'all 0.2s ease', borderLeft: isRTL ? '4px solid #ff9800' : '1px solid #f0f0f0', borderRight: isRTL ? '1px solid #f0f0f0' : '4px solid #ff9800' }}>
                <div style={{ fontSize: windowWidth < 768 ? '1.1rem' : '1.75rem', fontWeight: '300', color: '#ff9800', marginBottom: '2px' }}>{stats?.data?.presentToday || 0}</div>
                <div style={{ color: '#424242', fontSize: windowWidth < 768 ? '9px' : '13px', fontWeight: '400', lineHeight: '1.2' }}>{t('statPresentToday')}</div>
                {!todayHasSession && (
                  <div style={{ fontSize: windowWidth < 768 ? '8px' : '10px', color: '#9CA3AF', lineHeight: 1.1, marginTop: '2px' }}>{t('noSessionsToday')}</div>
                )}
                </div>

              {/* الغائبون اليوم */}
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: windowWidth < 768 ? '8px 4px' : '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', textAlign: 'center', transition: 'all 0.2s ease', borderLeft: isRTL ? '4px solid #9c27b0' : '1px solid #f0f0f0', borderRight: isRTL ? '1px solid #f0f0f0' : '4px solid #9c27b0' }}>
                <div style={{ fontSize: windowWidth < 768 ? '1.1rem' : '1.75rem', fontWeight: '300', color: '#9c27b0', marginBottom: '2px' }}>{kpiPerimeterMismatch ? '—' : (stats?.data?.absentToday || 0)}</div>
                <div style={{ color: '#424242', fontSize: windowWidth < 768 ? '9px' : '13px', fontWeight: '400', lineHeight: '1.2' }}>{t('statAbsentToday')}</div>
                {kpiPerimeterMismatch && (
                  <div style={{ fontSize: windowWidth < 768 ? '8px' : '10px', color: '#EF4444', lineHeight: 1.1, marginTop: '2px' }}>{t('kpiPerimeterMismatch')}</div>
                )}
                {!todayHasSession && (
                  <div style={{ fontSize: windowWidth < 768 ? '8px' : '10px', color: '#9CA3AF', lineHeight: 1.1, marginTop: '2px' }}>{t('noSessionsToday')}</div>
                )}
              </div>

              {/* طلبات التسجيل المعلقة */}
              <div onClick={() => { navigateToSection('communication'); setCommunicationTab('inscription-requests'); loadInscriptionRequests(); }}
                style={{ backgroundColor: pendingInscriptionsCount > 0 ? '#FEF3C7' : 'white', borderRadius: '8px', padding: windowWidth < 768 ? '8px 4px' : '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderTop: `1px solid ${pendingInscriptionsCount > 0 ? '#FDE68A' : '#f0f0f0'}`, borderBottom: `1px solid ${pendingInscriptionsCount > 0 ? '#FDE68A' : '#f0f0f0'}`, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s ease', position: 'relative', borderLeft: isRTL ? `4px solid ${pendingInscriptionsCount > 0 ? '#F59E0B' : '#D1D5DB'}` : `1px solid ${pendingInscriptionsCount > 0 ? '#FDE68A' : '#f0f0f0'}`, borderRight: isRTL ? `1px solid ${pendingInscriptionsCount > 0 ? '#FDE68A' : '#f0f0f0'}` : `4px solid ${pendingInscriptionsCount > 0 ? '#F59E0B' : '#D1D5DB'}` }}>
                {pendingInscriptionsCount > 0 && <span style={{ position: 'absolute', top: '4px', insetInlineEnd: '4px', backgroundColor: '#EF4444', color: 'white', borderRadius: '10px', fontSize: '9px', fontWeight: '700', padding: '1px 4px', minWidth: '14px' }}>{pendingInscriptionsCount}</span>}
                <div style={{ fontSize: windowWidth < 768 ? '1.1rem' : '1.75rem', fontWeight: '300', color: pendingInscriptionsCount > 0 ? '#D97706' : '#9CA3AF', marginBottom: '2px' }}>{pendingInscriptionsCount}</div>
                <div style={{ color: '#424242', fontSize: windowWidth < 768 ? '9px' : '13px', fontWeight: '400', lineHeight: '1.2' }}>{t('statPendingRequests')}</div>
            </div>

              {/* رسائل التواصل الجديدة */}
              <div onClick={() => { navigateToSection('communication'); setCommunicationTab('contact-messages'); loadContactRequests(); }}
                style={{ backgroundColor: newContactMessagesCount > 0 ? '#D1FAE5' : 'white', borderRadius: '8px', padding: windowWidth < 768 ? '8px 4px' : '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderTop: `1px solid ${newContactMessagesCount > 0 ? '#6EE7B7' : '#f0f0f0'}`, borderBottom: `1px solid ${newContactMessagesCount > 0 ? '#6EE7B7' : '#f0f0f0'}`, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s ease', position: 'relative', borderLeft: isRTL ? `4px solid ${newContactMessagesCount > 0 ? '#10B981' : '#D1D5DB'}` : `1px solid ${newContactMessagesCount > 0 ? '#6EE7B7' : '#f0f0f0'}`, borderRight: isRTL ? `1px solid ${newContactMessagesCount > 0 ? '#6EE7B7' : '#f0f0f0'}` : `4px solid ${newContactMessagesCount > 0 ? '#10B981' : '#D1D5DB'}` }}>
                {newContactMessagesCount > 0 && <span style={{ position: 'absolute', top: '4px', insetInlineEnd: '4px', backgroundColor: '#EF4444', color: 'white', borderRadius: '10px', fontSize: '9px', fontWeight: '700', padding: '1px 4px', minWidth: '14px' }}>{newContactMessagesCount}</span>}
                <div style={{ fontSize: windowWidth < 768 ? '1.1rem' : '1.75rem', fontWeight: '300', color: newContactMessagesCount > 0 ? '#059669' : '#9CA3AF', marginBottom: '2px' }}>{newContactMessagesCount}</div>
                <div style={{ color: '#424242', fontSize: windowWidth < 768 ? '9px' : '13px', fontWeight: '400', lineHeight: '1.2' }}>{t('statNewMessages')}</div>
            </div>
        </div>

            {/* Onglets: الأنشطة الحديثة etالحضور في آخر جلسة */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: windowWidth < 768 ? '16px' : '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0',
              marginBottom: windowWidth < 768 ? '24px' : '40px'
            }}>
              {/* Barre d'onglets */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: windowWidth < 768 ? '16px' : '20px',
                borderBottom: '2px solid #e5e7eb',
                paddingBottom: '8px'
              }}>
                <button
                  onClick={() => setDashboardTab('activities')}
                  style={{
                    padding: windowWidth < 768 ? '8px 12px' : '10px 16px',
                    fontSize: windowWidth < 768 ? '14px' : '16px',
                    fontWeight: '500',
                    color: dashboardTab === 'activities' ? '#2e7d32' : '#757575',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderBottom: dashboardTab === 'activities' ? '2px solid #2e7d32' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '-10px'
                  }}
                >
                  {t('tabActivities')}
                </button>
                <button
                  onClick={() => setDashboardTab('attendance')}
                  style={{
                    padding: windowWidth < 768 ? '8px 12px' : '10px 16px',
                    fontSize: windowWidth < 768 ? '14px' : '16px',
                    fontWeight: '500',
                    color: dashboardTab === 'attendance' ? '#2e7d32' : '#757575',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderBottom: dashboardTab === 'attendance' ? '2px solid #2e7d32' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '-10px'
                  }}
                >
                 {t('tabLastSession')}
                </button>
                <button
                  onClick={() => setDashboardTab('attendance-by-date')}
                  style={{
                    padding: windowWidth < 768 ? '8px 12px' : '10px 16px',
                    fontSize: windowWidth < 768 ? '14px' : '16px',
                    fontWeight: '500',
                    color: dashboardTab === 'attendance-by-date' ? '#2e7d32' : '#757575',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderBottom: dashboardTab === 'attendance-by-date' ? '2px solid #2e7d32' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '-10px'
                  }}
                >
                  {t('tabAttendanceByDate')}
                </button>
              </div>

              {/* Contenu de l'onglet الأنشطة الحديثة */}
              {dashboardTab === 'activities' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: windowWidth < 768 ? '10px' : '12px' }}>
                {/* Messages internes récents */}
                {Array.isArray(messages) && messages.length > 0 ? (
                  messages.slice(0, 3).map((message, index) => (
                    <div key={`message-${index}`} onClick={() => { navigateToSection('communication'); setCommunicationTab('messages'); setMessagesSubTab('received'); }}
                      style={{ display: 'flex', alignItems: windowWidth < 768 ? 'flex-start' : 'center', padding: windowWidth < 768 ? '8px 10px' : '10px 12px', backgroundColor: '#e8f5e8', borderRadius: windowWidth < 768 ? '6px' : '8px', border: '1px solid #c8e6c9', flexWrap: 'nowrap', gap: windowWidth < 768 ? '8px' : '12px', cursor: 'pointer' }}>
                      <div style={{ fontSize: windowWidth < 768 ? '16px' : '20px', color: '#4caf50', flexShrink: 0 }}>💬</div>
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ fontWeight: '500', color: '#424242', marginBottom: '2px', fontSize: windowWidth < 768 ? '13px' : '15px' }}>
                          {t('actMessageFrom')} {message.sender_name || t('actUnknownSender')}
                        </div>
                        <div style={{ fontSize: windowWidth < 768 ? '11px' : '13px', color: '#757575', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {message.content || t('actNoContent')}
                        </div>
                        <div style={{ fontSize: windowWidth < 768 ? '10px' : '11px', color: '#9e9e9e', marginTop: '4px' }}>
                          {message.sent_date ? GLOBAL_UTILS.formatDate(message.sent_date) : t('actUnknownDate')}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', padding: windowWidth < 768 ? '8px 10px' : '10px 12px', backgroundColor: '#f5f5f5', borderRadius: windowWidth < 768 ? '6px' : '8px', border: '1px solid #e0e0e0', gap: windowWidth < 768 ? '8px' : '12px' }}>
                    <div style={{ fontSize: windowWidth < 768 ? '16px' : '20px', color: '#757575', flexShrink: 0 }}>📭</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: '#424242', marginBottom: '2px', fontSize: windowWidth < 768 ? '13px' : '15px' }}>{t('actNoMessages')}</div>
                      <div style={{ fontSize: windowWidth < 768 ? '11px' : '13px', color: '#757575' }}>{t('actNoMessagesDesc')}</div>
                      </div>
                      </div>
                )}

                {/* Demandes d'inscription en attente */}
                {pendingInscriptionsCount > 0 && (
                  <div onClick={() => { navigateToSection('communication'); setCommunicationTab('inscription-requests'); loadInscriptionRequests(); }}
                    style={{ display: 'flex', alignItems: 'center', padding: windowWidth < 768 ? '8px 10px' : '10px 12px', backgroundColor: '#FEF3C7', borderRadius: windowWidth < 768 ? '6px' : '8px', border: '1px solid #FDE68A', gap: windowWidth < 768 ? '8px' : '12px', cursor: 'pointer' }}>
                    <div style={{ fontSize: windowWidth < 768 ? '16px' : '20px', flexShrink: 0 }}>📋</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#92400E', fontSize: windowWidth < 768 ? '13px' : '15px' }}>
                        {pendingInscriptionsCount} {t('actPendingRegistration')}
                    </div>
                      <div style={{ fontSize: windowWidth < 768 ? '11px' : '13px', color: '#B45309' }}>{t('actClickDetails')}</div>
                    </div>
                    <span style={{ backgroundColor: '#F59E0B', color: 'white', borderRadius: '12px', fontSize: '11px', fontWeight: '700', padding: '2px 8px', flexShrink: 0 }}>{pendingInscriptionsCount}</span>
                  </div>
                )}

                {/* Nouveaux messages de contact */}
                {newContactMessagesCount > 0 && (
                  <div onClick={() => { navigateToSection('communication'); setCommunicationTab('contact-messages'); loadContactRequests(); }}
                    style={{ display: 'flex', alignItems: 'center', padding: windowWidth < 768 ? '8px 10px' : '10px 12px', backgroundColor: '#D1FAE5', borderRadius: windowWidth < 768 ? '6px' : '8px', border: '1px solid #6EE7B7', gap: windowWidth < 768 ? '8px' : '12px', cursor: 'pointer' }}>
                    <div style={{ fontSize: windowWidth < 768 ? '16px' : '20px', flexShrink: 0 }}>✉️</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#065F46', fontSize: windowWidth < 768 ? '13px' : '15px' }}>
                        {newContactMessagesCount} {t('actNewContactMessage')}
                      </div>
                      <div style={{ fontSize: windowWidth < 768 ? '11px' : '13px', color: '#059669' }}>{t('actClickReply')}</div>
                    </div>
                    <span style={{ backgroundColor: '#10B981', color: 'white', borderRadius: '12px', fontSize: '11px', fontWeight: '700', padding: '2px 8px', flexShrink: 0 }}>{newContactMessagesCount}</span>
                  </div>
                )}

                {/* Informations sur les étudiants récents */}
                {Array.isArray(students) && students.length > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: windowWidth < 768 ? '8px 10px' : '10px 12px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: windowWidth < 768 ? '6px' : '8px',
                    border: '1px solid #bbdefb',
                    flexWrap: 'nowrap',
                    gap: windowWidth < 768 ? '8px' : '12px'
                  }}>
                    <div style={{ 
                      fontSize: windowWidth < 768 ? '16px' : '20px', 
                      color: '#2196f3',
                      flexShrink: 0
                    }}>👥</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: '500', 
                        color: '#424242',
                        marginBottom: '2px',
                        fontSize: windowWidth < 768 ? '13px' : '15px'
                      }}>
                        {t('actTotalStudents')}
                      </div>
                      <div style={{ 
                        fontSize: windowWidth < 768 ? '11px' : '13px', 
                        color: '#757575'
                      }}>
                        {students.length} {t('actStudentInSystem')}
                      </div>
                    </div>
                    <div style={{ 
                      fontSize: windowWidth < 768 ? '10px' : '11px', 
                      color: '#9e9e9e',
                      flexShrink: 0
                    }}>
                      {t('actNow')}
                    </div>
                  </div>
                )}

                {/* Informations sur les enseignants */}
                {Array.isArray(teachers) && teachers.length > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: windowWidth < 768 ? '8px 10px' : '10px 12px',
                    backgroundColor: '#fff3e0',
                    borderRadius: windowWidth < 768 ? '6px' : '8px',
                    border: '1px solid #ffcc80',
                    flexWrap: 'nowrap',
                    gap: windowWidth < 768 ? '8px' : '12px'
                  }}>
                    <div style={{ 
                      fontSize: windowWidth < 768 ? '16px' : '20px', 
                      color: '#ff9800',
                      flexShrink: 0
                    }}>👨‍🏫</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: '500', 
                        color: '#424242',
                        marginBottom: '2px',
                        fontSize: windowWidth < 768 ? '13px' : '15px'
                      }}>
                        {t('actActiveTeachers')}
                      </div>
                      <div style={{ 
                        fontSize: windowWidth < 768 ? '11px' : '13px', 
                        color: '#757575'
                      }}>
                        {teachers.length} {t('actTeacherInSystem')}
                      </div>
                    </div>
                    <div style={{ 
                      fontSize: windowWidth < 768 ? '10px' : '11px', 
                      color: '#9e9e9e',
                      flexShrink: 0
                    }}>
                      {t('actNow')}
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Contenu de l'ongletالحضور في آخر جلسة  */}
              {dashboardTab === 'attendance' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: windowWidth < 768 ? '10px' : '12px' }}>
                  {attendanceData && attendanceData.sessionDay !== false && attendanceData.records && attendanceData.records.length > 0 ? (
                    <>
                      {/* Compteurs présents / absents */}
                      {(() => {
                        const presentCnt = (attendanceData.records || []).filter((r: any) => r.status === 'present').length;
                        const absentCnt = (attendanceData.records || []).filter((r: any) => r.status === 'absent').length;
                        return (
                          <div style={{ display: 'flex', gap: '8px', marginBottom: windowWidth < 768 ? '8px' : '10px' }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: '8px', padding: windowWidth < 768 ? '8px 10px' : '10px 14px' }}>
                              <span style={{ fontSize: '18px' }}>✅</span>
                              <div>
                                <div style={{ fontSize: windowWidth < 768 ? '18px' : '22px', fontWeight: '700', color: '#065F46', lineHeight: 1 }}>{presentCnt}</div>
                                <div style={{ fontSize: '11px', color: '#059669' }}>{t('actPresent')}</div>
                              </div>
                            </div>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: '8px', padding: windowWidth < 768 ? '8px 10px' : '10px 14px' }}>
                              <span style={{ fontSize: '18px' }}>❌</span>
                              <div>
                                <div style={{ fontSize: windowWidth < 768 ? '18px' : '22px', fontWeight: '700', color: '#991B1B', lineHeight: 1 }}>{absentCnt}</div>
                                <div style={{ fontSize: '11px', color: '#DC2626' }}>{t('actAbsent')}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Filtres */}
                      <div style={{
                        display: 'flex',
                        gap: windowWidth < 768 ? '6px' : '10px',
                        flexWrap: 'wrap',
                        marginBottom: windowWidth < 768 ? '6px' : '10px',
                        padding: windowWidth < 768 ? '8px' : '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: windowWidth < 768 ? '6px' : '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        {/* Filtre par professeur */}
                        <div style={{
                          flex: '1 1 auto',
                          minWidth: windowWidth < 768 ? 'calc(50% - 3px)' : '180px'
                        }}>
                          <label style={{
                            display: 'block',
                            fontSize: windowWidth < 768 ? '11px' : '12px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '4px'
                          }}>
                            الأستاذ:
                          </label>
                          <select
                            value={attendanceTeacherFilter}
                            onChange={(e) => setAttendanceTeacherFilter(e.target.value)}
                            style={{
                              width: '100%',
                              padding: windowWidth < 768 ? '6px 8px' : '8px 12px',
                              borderRadius: windowWidth < 768 ? '6px' : '8px',
                              border: '1px solid #e5e7eb',
                              backgroundColor: 'white',
                              fontSize: windowWidth < 768 ? '12px' : '13px',
                              fontWeight: '500',
                              color: '#374151',
                              cursor: 'pointer',
                              direction: isRTL ? 'rtl' : 'ltr'
                            }}
                          >
                            <option value="all">{t('allTeachersOption')}</option>
                            {Array.isArray(teachers) && teachers.map((teacher: any) => (
                              <option key={teacher.id || teacher.user_id} value={teacher.id?.toString() || teacher.user_id?.toString() || ''}>
                                {teacher.full_name || teacher.fullName || teacher.username || ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Filtre par statut */}
                        <div style={{
                          flex: '1 1 auto',
                          minWidth: windowWidth < 768 ? 'calc(50% - 3px)' : '150px'
                        }}>
                          <label style={{
                            display: 'block',
                            fontSize: windowWidth < 768 ? '11px' : '12px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '4px'
                          }}>
                            الحالة:
                          </label>
                          <select
                            value={attendanceStatusFilter}
                            onChange={(e) => setAttendanceStatusFilter(e.target.value as 'all' | 'present' | 'absent')}
                            style={{
                              width: '100%',
                              padding: windowWidth < 768 ? '6px 8px' : '8px 12px',
                              borderRadius: windowWidth < 768 ? '6px' : '8px',
                              border: '1px solid #e5e7eb',
                              backgroundColor: 'white',
                              fontSize: windowWidth < 768 ? '12px' : '13px',
                              fontWeight: '500',
                              color: '#374151',
                              cursor: 'pointer',
                              direction: isRTL ? 'rtl' : 'ltr'
                            }}
                          >
                            <option value="all">{t('all')}</option>
                            <option value="present">{t('presentFilter')}</option>
                            <option value="absent">{t('absentFilter')}</option>
                          </select>
                        </div>
                      </div>

                      {/* Tableau des étudiants filtrés */}
                      <div style={{
                        maxHeight: windowWidth < 768 ? '350px' : '450px',
                        overflowX: 'auto',
                        overflowY: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: windowWidth < 768 ? '6px' : '8px',
                        WebkitOverflowScrolling: 'touch'
                      }}>
                        {(() => {
                          // Enrichir les données d'attendance avec les informations complètes des étudiants
                          let enrichedRecords = (attendanceData.records || []).map((record: any) => {
                            // Trouver l'étudiant correspondant dans la liste complète
                            const student = students.find((s: Student) => 
                              s.id === record.studentId || 
                              s.student_name === record.studentName
                            );
                            
                            if (student) {
                              return {
                                ...record,
                                // Utiliser les données complètes de l'étudiant
                                studentName: student.student_name || record.studentName,
                                teacherName: student.teacher_name || record.teacherName || '-',
                                halqa: student.halqa || record.halqa || '-',
                                level: student.level || record.level || ''
                              };
                            }
                            
                            // Si l'étudiant n'est pas trouvé, utiliser les données de l'API
                            return {
                              ...record,
                              teacherName: record.teacherName || '-',
                              halqa: record.halqa || '-'
                            };
                          });
                          
                          // Filtrer les records selon les filtres sélectionnés
                          let filteredRecords = enrichedRecords;
                          
                          // Filtre par professeur
                          if (attendanceTeacherFilter !== 'all') {
                            const teacherId = parseInt(attendanceTeacherFilter);
                            // Chercher aussi par nom de professeur si teacherId ne correspond pas
                            const selectedTeacher = teachers.find((t: Teacher) => 
                              (t.id?.toString() === attendanceTeacherFilter) || 
                              (t.user_id?.toString() === attendanceTeacherFilter)
                            );
                            const teacherName = selectedTeacher?.full_name || '';
                            
                            filteredRecords = filteredRecords.filter((record: any) => {
                              // Filtrer par ID si disponible
                              if (record.teacherId === teacherId) return true;
                              // Filtrer par nom si l'ID ne correspond pas
                              if (teacherName && record.teacherName === teacherName) return true;
                              return false;
                            });
                          }
                          
                          // Filtre par statut
                          if (attendanceStatusFilter !== 'all') {
                            filteredRecords = filteredRecords.filter((record: any) => 
                              record.status === attendanceStatusFilter
                            );
                          }
                          
                          if (filteredRecords.length === 0) {
                            return (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: windowWidth < 768 ? '32px' : '48px',
                                textAlign: 'center'
                              }}>
                                <div>
                                  <div style={{
                                    fontSize: windowWidth < 768 ? '18px' : '20px',
                                    color: '#757575',
                                    marginBottom: '8px'
                                  }}>🔍</div>
                                  <div style={{
                                    fontWeight: '500',
                                    color: '#424242',
                                    marginBottom: '4px',
                                    fontSize: windowWidth < 768 ? '14px' : '16px'
                                  }}>
                                    لا توجد نتائج
                                  </div>
                                  <div style={{
                                    fontSize: windowWidth < 768 ? '12px' : '14px',
                                    color: '#757575'
                                  }}>
                                    لا توجد بيانات تطابق الفلاتر المحددة
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          return (
                            <table style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: windowWidth < 768 ? '11px' : '13px',
                              minWidth: windowWidth < 768 ? '450px' : '550px'
                            }}>
                              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                <tr style={{
                                  backgroundColor: '#f3f4f6',
                                  borderBottom: '2px solid #e5e7eb'
                                }}>
                                  <th style={{
                                    padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                    textAlign: isRTL ? 'right' : 'left',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: windowWidth < 768 ? '11px' : '12px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {t('student')}
                                  </th>
                                  <th style={{
                                    padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                    textAlign: isRTL ? 'right' : 'left',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: windowWidth < 768 ? '11px' : '12px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {t('teacher')}
                                  </th>
                                  <th style={{
                                    padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                    textAlign: isRTL ? 'right' : 'left',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: windowWidth < 768 ? '11px' : '12px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {t('studentHalqa')}
                                  </th>
                                  <th style={{
                                    padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: windowWidth < 768 ? '11px' : '12px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {t('status')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredRecords.map((record: any, index: number) => (
                                  <tr
                                    key={`attendance-${record.studentId || index}`}
                                    style={{
                                      backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                                      borderBottom: '1px solid #e5e7eb',
                                      transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = record.status === 'present' ? '#f0fdf4' : '#fef2f2';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f9fafb';
                                    }}
                                  >
                                    <td style={{
                                      padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                      textAlign: isRTL ? 'right' : 'left',
                                      color: '#424242',
                                      fontWeight: '500',
                                      fontSize: windowWidth < 768 ? '11px' : '13px'
                                    }}>
                                      {record.studentName || '-'}
                                    </td>
                                    <td style={{
                                      padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                      textAlign: isRTL ? 'right' : 'left',
                                      color: '#2196f3',
                                      fontWeight: '500',
                                      fontSize: windowWidth < 768 ? '11px' : '13px'
                                    }}>
                                      {record.teacherName || '-'}
                                    </td>
                                    <td style={{
                                      padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                      textAlign: isRTL ? 'right' : 'left',
                                      color: '#757575',
                                      fontSize: windowWidth < 768 ? '11px' : '13px'
                                    }}>
                                      {record.halqa || '-'}
                                    </td>
                                    <td style={{
                                      padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                      textAlign: 'center'
                                    }}>
                                      <span style={{
                                        display: 'inline-block',
                                        padding: windowWidth < 768 ? '3px 8px' : '4px 10px',
                                        borderRadius: '4px',
                                        fontSize: windowWidth < 768 ? '10px' : '11px',
                                        fontWeight: '500',
                                        backgroundColor: record.status === 'present' ? '#dcfce7' : '#fee2e2',
                                        color: record.status === 'present' ? '#166534' : '#dc2626'
                                      }}>
                                        {record.status === 'present' ? 'حاضر' : 'غائب'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          );
                        })()}
                      </div>
                    </>
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: windowWidth < 768 ? '32px' : '48px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: windowWidth < 768 ? '18px' : '20px',
                          color: '#757575',
                          marginBottom: '8px'
                        }}>📋</div>
                        <div style={{
                          fontWeight: '500',
                          color: '#424242',
                          marginBottom: '4px',
                          fontSize: windowWidth < 768 ? '14px' : '16px'
                        }}>
                          لا توجد بيانات حضور/غياب
                        </div>
                        <div style={{
                          fontSize: windowWidth < 768 ? '12px' : '14px',
                          color: '#757575'
                        }}>
                          لم يتم العثور على أي بيانات حضور/غياب لهذا اليوم
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Contenu de l'onglet الحضور حسب التاريخ */}
              {dashboardTab === 'attendance-by-date' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: windowWidth < 768 ? '10px' : '12px' }}>
                  {/* Filtres de dates */}
                  <div style={{
                    display: 'flex',
                    gap: windowWidth < 768 ? '6px' : '10px',
                    flexWrap: 'wrap',
                    marginBottom: windowWidth < 768 ? '6px' : '10px',
                    padding: windowWidth < 768 ? '8px' : '12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: windowWidth < 768 ? '6px' : '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{
                      flex: '1 1 auto',
                      minWidth: windowWidth < 768 ? 'calc(50% - 3px)' : '150px',
                      maxWidth: windowWidth < 768 ? 'calc(50% - 3px)' : '200px'
                    }}>
                      <label style={{
                        display: 'block',
                        fontSize: windowWidth < 768 ? '11px' : '12px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '4px'
                      }}>
                        من تاريخ:
                      </label>
                      <input
                        type="date"
                        value={attendanceStartDate}
                        onChange={(e) => setAttendanceStartDate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: windowWidth < 768 ? '6px 8px' : '8px 12px',
                          borderRadius: windowWidth < 768 ? '6px' : '8px',
                          border: '1px solid #e5e7eb',
                          backgroundColor: 'white',
                          fontSize: windowWidth < 768 ? '12px' : '13px',
                          fontWeight: '500',
                          color: '#374151',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                    
                    <div style={{
                      flex: '1 1 auto',
                      minWidth: windowWidth < 768 ? 'calc(50% - 3px)' : '150px',
                      maxWidth: windowWidth < 768 ? 'calc(50% - 3px)' : '200px'
                    }}>
                      <label style={{
                        display: 'block',
                        fontSize: windowWidth < 768 ? '11px' : '12px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '4px'
                      }}>
                        إلى تاريخ:
                      </label>
                      <input
                        type="date"
                        value={attendanceEndDate}
                        onChange={(e) => setAttendanceEndDate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: windowWidth < 768 ? '6px 8px' : '8px 12px',
                          borderRadius: windowWidth < 768 ? '6px' : '8px',
                          border: '1px solid #e5e7eb',
                          backgroundColor: 'white',
                          fontSize: windowWidth < 768 ? '12px' : '13px',
                          fontWeight: '500',
                          color: '#374151',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                    
                    {/* Filtre par professeur */}
                    <div style={{
                      flex: '1 1 auto',
                      minWidth: windowWidth < 768 ? '100%' : '180px',
                      maxWidth: windowWidth < 768 ? '100%' : '250px'
                    }}>
                      <label style={{
                        display: 'block',
                        fontSize: windowWidth < 768 ? '11px' : '12px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '4px'
                      }}>
                        الأستاذ:
                      </label>
                      <select
                        value={attendanceByDateTeacherFilter}
                        onChange={(e) => setAttendanceByDateTeacherFilter(e.target.value)}
                        style={{
                          width: '100%',
                          padding: windowWidth < 768 ? '6px 8px' : '8px 12px',
                          borderRadius: windowWidth < 768 ? '6px' : '8px',
                          border: '1px solid #e5e7eb',
                          backgroundColor: 'white',
                          fontSize: windowWidth < 768 ? '12px' : '13px',
                          fontWeight: '500',
                          color: '#374151',
                          cursor: 'pointer',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }}
                      >
                        <option value="all">{t('allTeachersOption')}</option>
                        {Array.isArray(teachers) && teachers.map((teacher: any) => (
                          <option key={teacher.id || teacher.user_id} value={teacher.id?.toString() || teacher.user_id?.toString() || ''}>
                            {teacher.full_name || teacher.fullName || teacher.username || ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div style={{
                      flex: windowWidth < 768 ? '1 1 100%' : '0 0 auto',
                      display: 'flex',
                      alignItems: 'flex-end',
                      marginTop: windowWidth < 768 ? '4px' : '0'
                    }}>
                      <button
                        onClick={async () => {
                          console.log('🔄 Chargement attendance:', {
                            startDate: attendanceStartDate,
                            endDate: attendanceEndDate
                          });
                          
                          setAttendanceByDateLoading(true);
                          try {
                            const response = await apiService.getAttendanceByDate({
                              startDate: attendanceStartDate,
                              endDate: attendanceEndDate
                            });
                            
                            if (response.success && response.data) {
                              console.log('✅ Données reçues:', {
                                records: response.data.records?.length || 0,
                                totalDays: response.data.summary?.totalDays,
                                present: response.data.summary?.byStatus?.present,
                                absent: response.data.summary?.byStatus?.absent,
                                firstRecords: response.data.records?.slice(0, 3)
                              });
                              
                              // Log pour vérifier les noms des professeurs
                              const teacherNames = new Set(response.data.records?.map((r: any) => r.teacherName).filter(Boolean));
                              console.log('👥 Professeurs trouvés:', Array.from(teacherNames));
                              
                              setAttendanceByDateData(response.data);
                            } else {
                              console.error('❌ Erreur:', response.error);
                            }
                          } catch (error) {
                            console.error('❌ Exception:', error);
                          } finally {
                            setAttendanceByDateLoading(false);
                          }
                        }}
                        disabled={attendanceByDateLoading}
                        style={{
                          width: '100%',
                          padding: windowWidth < 768 ? '8px 16px' : '9px 20px',
                          backgroundColor: attendanceByDateLoading ? '#9ca3af' : '#2196f3',
                          color: 'white',
                          border: 'none',
                          borderRadius: windowWidth < 768 ? '6px' : '8px',
                          fontSize: windowWidth < 768 ? '12px' : '13px',
                          fontWeight: '600',
                          cursor: attendanceByDateLoading ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: windowWidth < 768 ? '6px' : '8px'
                        }}
                        onMouseOver={(e) => !attendanceByDateLoading && (e.currentTarget.style.backgroundColor = '#1976d2')}
                        onMouseOut={(e) => !attendanceByDateLoading && (e.currentTarget.style.backgroundColor = '#2196f3')}
                      >
                        {attendanceByDateLoading ? 'جاري التحميل...' : '🔄 تحديث'}
                      </button>
                    </div>
                  </div>

                  {/* Résumé des statistiques */}
                  {attendanceByDateData && attendanceByDateData.summary && (
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      marginBottom: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{
                        flex: '1 1 100px',
                        padding: '8px 10px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '6px',
                        border: '1px solid #90caf9'
                      }}>
                        <div style={{ fontSize: '11px', color: '#1565c0', marginBottom: '2px' }}>
                          إجمالي الأيام
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0d47a1' }}>
                          {attendanceByDateData.summary.totalDays}
                        </div>
                      </div>
                      
                      <div style={{
                        flex: '1 1 100px',
                        padding: '8px 10px',
                        backgroundColor: '#e8f5e9',
                        borderRadius: '6px',
                        border: '1px solid #81c784'
                      }}>
                        <div style={{ fontSize: '11px', color: '#2e7d32', marginBottom: '2px' }}>
                          إجمالي الحضور
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1b5e20' }}>
                          {attendanceByDateData.summary.byStatus?.present || 0}
                        </div>
                      </div>
                      
                      <div style={{
                        flex: '1 1 100px',
                        padding: '8px 10px',
                        backgroundColor: '#ffebee',
                        borderRadius: '6px',
                        border: '1px solid #e57373'
                      }}>
                        <div style={{ fontSize: '11px', color: '#c62828', marginBottom: '2px' }}>
                          إجمالي الغياب
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#b71c1c' }}>
                          {attendanceByDateData.summary.byStatus?.absent || 0}
                        </div>
                      </div>
                      
                      <div style={{
                        flex: '1 1 100px',
                        padding: '8px 10px',
                        backgroundColor: '#fff3e0',
                        borderRadius: '6px',
                        border: '1px solid #ffb74d'
                      }}>
                        <div style={{ fontSize: '11px', color: '#e65100', marginBottom: '2px' }}>
                          إجمالي السجلات
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#bf360c' }}>
                          {attendanceByDateData.summary.totalRecords}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tableau des présences par date */}
                  {attendanceByDateLoading ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: windowWidth < 768 ? '30px' : '40px', 
                      color: '#757575',
                      fontSize: windowWidth < 768 ? '13px' : '14px'
                    }}>
                      جاري التحميل...
                    </div>
                  ) : attendanceByDateData && attendanceByDateData.records && attendanceByDateData.records.length > 0 ? (
                    <div style={{
                      maxHeight: windowWidth < 768 ? '350px' : '450px',
                      overflowX: 'auto',
                      overflowY: 'auto',
                      border: '1px solid #e5e7eb',
                      borderRadius: windowWidth < 768 ? '6px' : '8px',
                      WebkitOverflowScrolling: 'touch'
                    }}>
                      {(() => {
                        // Grouper les données par étudiant
                        const studentMap = new Map<number, {
                          studentId: number;
                          studentName: string;
                          teacherName: string;
                          attendance: Record<string, string>;
                        }>();
                        const allDates = new Set<string>();
                        
                        attendanceByDateData.records.forEach((record: any) => {
                          const studentId = record.studentId;
                          const date = record.date;
                          
                          allDates.add(date);
                          
                          if (!studentMap.has(studentId)) {
                            studentMap.set(studentId, {
                              studentId: record.studentId,
                              studentName: record.studentName,
                              teacherName: record.teacherName || '',
                              attendance: {}
                            });
                          }
                          
                          const student = studentMap.get(studentId);
                          if (student) {
                            student.attendance[date] = record.status;
                            // Mettre à jour le nom du professeur si disponible et non vide
                            if (record.teacherName && !student.teacherName) {
                              student.teacherName = record.teacherName;
                            }
                          }
                        });
                        
                        // Convertir les dates en tableau trié
                        const dates: string[] = Array.from(allDates).sort();
                        
                        // Calculer le nombre d'absences pour chaque étudiant
                        let studentsWithAbsences = Array.from(studentMap.values()).map(student => {
                          const totalAbsences = dates.filter((date: string) => 
                            student.attendance[date] === 'absent'
                          ).length;
                          
                          return {
                            ...student,
                            totalAbsences
                          };
                        });
                        
                        // Filtrer par professeur si sélectionné
                        if (attendanceByDateTeacherFilter !== 'all') {
                          // Trouver le nom du professeur sélectionné
                          const selectedTeacher = teachers.find((t: Teacher) => 
                            (t.id?.toString() === attendanceByDateTeacherFilter) || 
                            (t.user_id?.toString() === attendanceByDateTeacherFilter)
                          );
                          const teacherName = selectedTeacher?.full_name || '';
                          
                          studentsWithAbsences = studentsWithAbsences.filter(student => 
                            student.teacherName === teacherName
                          );
                        }
                        
                        // Afficher un message si aucun résultat après filtrage
                        if (studentsWithAbsences.length === 0) {
                          return (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: windowWidth < 768 ? '32px' : '48px',
                              textAlign: 'center'
                            }}>
                              <div>
                                <div style={{
                                  fontSize: windowWidth < 768 ? '18px' : '20px',
                                  color: '#757575',
                                  marginBottom: '8px'
                                }}>🔍</div>
                                <div style={{
                                  fontWeight: '500',
                                  color: '#424242',
                                  marginBottom: '4px',
                                  fontSize: windowWidth < 768 ? '14px' : '16px'
                                }}>
                                  لا توجد نتائج
                                </div>
                                <div style={{
                                  fontSize: windowWidth < 768 ? '12px' : '14px',
                                  color: '#757575'
                                }}>
                                  لا توجد بيانات تطابق الفلاتر المحددة
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: windowWidth < 768 ? '11px' : '13px',
                            minWidth: `${windowWidth < 768 ? 250 : 300 + (dates.length * (windowWidth < 768 ? 60 : 70))}px`
                          }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                              <tr style={{
                                backgroundColor: '#f3f4f6',
                                borderBottom: '2px solid #e5e7eb'
                              }}>
                                <th style={{
                                  padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                  textAlign: isRTL ? 'right' : 'left',
                                  fontWeight: '600',
                                  color: '#374151',
                                  fontSize: windowWidth < 768 ? '11px' : '12px',
                                  backgroundColor: '#f3f4f6',
                                  whiteSpace: 'nowrap',
                                  position: 'sticky',
                                  right: 0,
                                  zIndex: 11,
                                  borderLeft: '2px solid #e5e7eb',
                                  minWidth: windowWidth < 768 ? '80px' : '120px'
                                }}>
                                  {t('student')}
                                </th>
                                <th style={{
                                  padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                  textAlign: isRTL ? 'right' : 'left',
                                  fontWeight: '600',
                                  color: '#374151',
                                  fontSize: windowWidth < 768 ? '11px' : '12px',
                                  backgroundColor: '#f3f4f6',
                                  whiteSpace: 'nowrap',
                                  minWidth: windowWidth < 768 ? '70px' : '100px'
                                }}>
                                  {t('teacher')}
                                </th>
                                {dates.map((date: string) => (
                                  <th key={date} style={{
                                    padding: windowWidth < 768 ? '8px 4px' : '10px 8px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: windowWidth < 768 ? '10px' : '11px',
                                    backgroundColor: '#f3f4f6',
                                    whiteSpace: 'nowrap',
                                    minWidth: windowWidth < 768 ? '50px' : '60px'
                                  }}>
                                    {(() => {
                                      const d = new Date(date);
                                      const day = String(d.getDate()).padStart(2, '0');
                                      const month = String(d.getMonth() + 1).padStart(2, '0');
                                      return `${day}/${month}`;
                                    })()}
                                  </th>
                                ))}
                                <th style={{
                                  padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                  textAlign: 'center',
                                  fontWeight: '600',
                                  color: '#374151',
                                  fontSize: windowWidth < 768 ? '11px' : '12px',
                                  backgroundColor: '#f3f4f6',
                                  whiteSpace: 'nowrap',
                                  borderRight: '2px solid #e5e7eb',
                                  minWidth: windowWidth < 768 ? '50px' : '70px'
                                }}>
                                  {t('totalAbsences')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {studentsWithAbsences.map((student, index) => (
                                <tr
                                  key={student.studentId}
                                  style={{
                                    backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                                    borderBottom: '1px solid #e5e7eb'
                                  }}
                                >
                                  <td style={{
                                    padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                    textAlign: isRTL ? 'right' : 'left',
                                    color: '#424242',
                                    fontWeight: '500',
                                    position: 'sticky',
                                    right: 0,
                                    backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                                    zIndex: 9,
                                    borderLeft: '2px solid #e5e7eb',
                                    fontSize: windowWidth < 768 ? '11px' : '13px'
                                  }}>
                                    {student.studentName}
                                  </td>
                                  <td style={{
                                    padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                    textAlign: isRTL ? 'right' : 'left',
                                    color: '#2196f3',
                                    fontWeight: '500',
                                    fontSize: windowWidth < 768 ? '11px' : '13px'
                                  }}>
                                    {student.teacherName || '-'}
                                  </td>
                                  {dates.map((date: string) => {
                                    const status = student.attendance[date];
                                    return (
                                      <td key={date} style={{
                                        padding: windowWidth < 768 ? '6px 4px' : '8px 6px',
                                        textAlign: 'center'
                                      }}>
                                        {status === 'present' ? (
                                          <span style={{
                                            display: 'inline-block',
                                            width: windowWidth < 768 ? '18px' : '22px',
                                            height: windowWidth < 768 ? '18px' : '22px',
                                            borderRadius: '50%',
                                            backgroundColor: '#dcfce7',
                                            color: '#166534',
                                            lineHeight: windowWidth < 768 ? '18px' : '22px',
                                            fontWeight: 'bold',
                                            fontSize: windowWidth < 768 ? '11px' : '13px'
                                          }}>
                                            ✓
                                          </span>
                                        ) : status === 'absent' ? (
                                          <span style={{
                                            display: 'inline-block',
                                            width: windowWidth < 768 ? '18px' : '22px',
                                            height: windowWidth < 768 ? '18px' : '22px',
                                            borderRadius: '50%',
                                            backgroundColor: '#fee2e2',
                                            color: '#dc2626',
                                            lineHeight: windowWidth < 768 ? '18px' : '22px',
                                            fontWeight: 'bold',
                                            fontSize: windowWidth < 768 ? '11px' : '13px'
                                          }}>
                                            ✗
                                          </span>
                                        ) : (
                                          <span style={{
                                            color: '#9ca3af',
                                            fontSize: windowWidth < 768 ? '9px' : '11px'
                                          }}>
                                            -
                                          </span>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td style={{
                                    padding: windowWidth < 768 ? '8px 6px' : '10px 12px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: windowWidth < 768 ? '12px' : '13px',
                                    color: student.totalAbsences > 0 ? '#dc2626' : '#16a34a',
                                    borderRight: '2px solid #e5e7eb'
                                  }}>
                                    {student.totalAbsences}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      })()}
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: windowWidth < 768 ? '32px' : '48px',
                      textAlign: 'center'
                    }}>
                      <div>
                        <div style={{
                          fontSize: windowWidth < 768 ? '18px' : '20px',
                          color: '#757575',
                          marginBottom: '8px'
                        }}>📅</div>
                        <div style={{
                          fontWeight: '500',
                          color: '#424242',
                          marginBottom: '4px',
                          fontSize: windowWidth < 768 ? '14px' : '16px'
                        }}>
                          لا توجد بيانات حضور
                        </div>
                        <div style={{
                          fontSize: windowWidth < 768 ? '12px' : '14px',
                          color: '#757575'
                        }}>
                          اختر الفترة الزمنية واضغط على "تحديث" لعرض البيانات
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'students':
        return (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: windowWidth < 768 ? '0 15px' : windowWidth < 1024 ? '0 20px' : '0 30px'
          }}>
            {/* Boutons dans le fil d'Ariane (renderBreadcrumb) */}
      <div style={{ display: 'none' }}>
                <button
                  onClick={handleRefreshStudents}
                  style={{
                    backgroundColor: '#14b8a6',
                    color: 'white',
                    padding: windowWidth < 768 ? '10px 14px' : '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: windowWidth < 768 ? '13px' : '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(20, 184, 166, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flex: windowWidth < 768 ? '1' : 'none',
                    justifyContent: 'center'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#0f766e';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#14b8a6';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}>
                  <RefreshIcon size={windowWidth < 768 ? 14 : 16} />
                  <span>{t('refresh')}</span>
                </button>
                {/* Bouton de basculement entre mode cartes et tableau (mobile uniquement) */}
                {windowWidth < 768 && (
                  <button
                    onClick={() => setMobileViewMode(mobileViewMode === 'cards' ? 'table' : 'cards')}
                    title={mobileViewMode === 'cards' ? t('viewTable') : t('viewCards')}
                    style={{
                      backgroundColor: mobileViewMode === 'cards' ? '#8b5cf6' : '#06b6d4',
                      color: 'white',
                      padding: '10px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: mobileViewMode === 'cards' 
                        ? '0 2px 4px rgba(139, 92, 246, 0.2)' 
                        : '0 2px 4px rgba(6, 182, 212, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      height: '40px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = mobileViewMode === 'cards' ? '#7c3aed' : '#0891b2';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = mobileViewMode === 'cards' ? '#8b5cf6' : '#06b6d4';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                    {mobileViewMode === 'cards' ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7"></rect>
                        <rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect>
                        <rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="6" x2="21" y2="6"></line>
                        <line x1="3" y1="12" x2="21" y2="12"></line>
                        <line x1="3" y1="18" x2="21" y2="18"></line>
                      </svg>
                    )}
                  </button>
                )}
                {canModify('students') && (
                  <button 
                    style={{
                      backgroundColor: '#4caf50',
                      color: 'white',
                      padding: windowWidth < 768 ? '10px 14px' : '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: windowWidth < 768 ? '13px' : '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(76, 175, 80, 0.2)',
                      flex: windowWidth < 768 ? '1' : 'none',
                      justifyContent: 'center'
                    }}
                    onClick={async () => {
                      // Réinitialiser tous les champs du formulaire
                      setNewStudent({
                        student_name: '',
                        parent_id: '',
                        teacher_id: '',
                        level: '',
                        halqa: '',
                        status: 'active',
                        notes: '',
                        gender: 'Male',
                        school_year_id: currentYear?.id?.toString() || selectedYear?.id?.toString() || ''
                      });
                      setFormError(null);
                      setFieldErrors({});
                      // Recharger les enseignants avant d'ouvrir le formulaire
                      try {
                        const teachersResponse = await apiService.getTeachers();
                        if (teachersResponse && teachersResponse.success && Array.isArray(teachersResponse.data)) {
                          const convertedTeachers = teachersResponse.data.map(convertTeacherStatus);
                          setTeachers(convertedTeachers);
                        }
                      } catch (error) {
                        console.error('Erreur lors du chargement des enseignants:', error);
                      }
                      setShowAddStudentForm(true);
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#45a049';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#4caf50';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                    ➕ {t('addStudent')}
                  </button>
                )}
            </div>

        {/* Affichage des étudiants */}
        {(() => {
          const isMobile = windowWidth < 768;
          const studentsHasDetail = !isMobile && !!selectedStudentDetail;
          const renderStudentDetailContent = () => {
            if (!selectedStudentDetail) return null;
            return (
              <>
                {/* En-tête du panneau */}
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f9fafb' }}>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '17px', color: '#111827', direction: isRTL ? 'rtl' : 'ltr' }}>
                      {selectedStudentDetail.student_name || 'غير محدد'}
                    </div>
                    <span style={{ fontSize: '12px', padding: '2px 10px', borderRadius: '12px', fontWeight: '600', backgroundColor: selectedStudentDetail.status === 'active' ? '#D1FAE5' : '#FEF3C7', color: selectedStudentDetail.status === 'active' ? '#065F46' : '#92400E' }}>
                      {getDisplayStatus(selectedStudentDetail.status)}
                    </span>
                  </div>
                  <button onClick={() => setSelectedStudentDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', color: '#6b7280', fontSize: '20px', lineHeight: 1 }}>✕</button>
                </div>
                {/* Corps du panneau */}
                <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#059669', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', direction: isRTL ? 'rtl' : 'ltr' }}>{t('infoSectionTitle')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {[
                        { label: t('gender'), value: selectedStudentDetail.gender ? (selectedStudentDetail.gender === 'Female' ? t('female') : t('male')) : null },
                        { label: t('parentPhone'), value: selectedStudentDetail.parent_phone },
                        { label: t('studentParent'), value: selectedStudentDetail.parent_name },
                        { label: t('teacher'), value: selectedStudentDetail.teacher_name },
                        { label: t('studentHalqa'), value: selectedStudentDetail.halqa },
                        { label: t('studentLevel'), value: selectedStudentDetail.level },
                        { label: t('status'), value: selectedStudentDetail.status ? getDisplayStatus(selectedStudentDetail.status) : null },
                      ].filter(row => !!row.value).map((row, i) => (
                        <div key={row.label} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          direction: isRTL ? 'rtl' : 'ltr',
                          padding: '7px 8px',
                          borderRadius: '6px',
                          backgroundColor: i % 2 === 0 ? '#f9fafb' : 'transparent',
                        }}>
                          <span style={{ color: '#9ca3af', fontSize: '12px', whiteSpace: 'nowrap' }}>{row.label}</span>
                          <span style={{ fontWeight: '500', color: '#111827', fontSize: '13px' }}>{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {canModify('students') && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #e5e7eb', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => { handleEditStudent(selectedStudentDetail); setSelectedStudentDetail(null); }}
                        style={{ ...GLOBAL_STYLES.editButton, padding: '8px 16px', fontSize: '13px' }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}
                      >
                        <EditIcon size={15} />
                        <span>{t('edit')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            );
          };
          return (
          <div style={{ display: 'flex', gap: '16px', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            flex: studentsHasDetail ? '0 0 50%' : undefined,
            minWidth: 0,
            width: studentsHasDetail ? undefined : '100%',
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: '75vh'
          }}>
              {/* Affichage en cartes pour mobile */}
              {windowWidth < 768 && mobileViewMode === 'cards' ? (
                <div style={{ padding: '12px' }}>
                  {/* Filtres pour mode cartes mobile */}
                  <div style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '12px',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}>
                      {t('filters')}
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {/* Filtre par nom d'étudiant */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#6b7280',
                          marginBottom: '4px',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }}>
                          {t('studentName')}
                        </label>
                        <SearchableSelect
                          value={studentFilters.studentName}
                          onChange={(value) => setStudentFilters({...studentFilters, studentName: value as string})}
                          options={[
                            { value: '', label: t('all') },
                            ...students.map(student => ({
                              value: student.student_name || '',
                              label: student.student_name || t('notSpecified')
                            }))
                          ]}
                          placeholder={t('all')}
                        />
                      </div>

                      {/* Filtre par parent */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#6b7280',
                          marginBottom: '4px',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }}>
                          {t('studentParent')}
                        </label>
                        <SearchableSelect
                          value={studentFilters.parentName}
                          onChange={(value) => setStudentFilters({...studentFilters, parentName: value as string})}
                          options={[
                            { value: '', label: t('all') },
                            ...Array.from(new Set(students.map(s => s.parent_name).filter(Boolean))).map(parentName => ({
                              value: parentName || '',
                              label: parentName || t('notSpecified')
                            }))
                          ]}
                          placeholder={t('all')}
                        />
                      </div>

                      {/* Filtre par année scolaire */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#6b7280',
                          marginBottom: '4px',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }}>
                          {t('schoolYearSelectLabel') || 'Année scolaire'}
                        </label>
                        <select
                          value={studentFilters.schoolYearId}
                          onChange={(e) => setStudentFilters({...studentFilters, schoolYearId: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '13px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: 'white',
                            direction: isRTL ? 'rtl' : 'ltr',
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="">{t('schoolYearSystemTitle') || 'Année scolaire (système)'}</option>
                          <option value="all">{t('allSchoolYears') || 'Toutes les années'}</option>
                          {years.map((y) => (
                            <option key={y.id} value={String(y.id)}>{y.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Filtres en ligne (Genre, Enseignant, Niveau, etc.) */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {/* Filtre Genre */}
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#6b7280',
                            marginBottom: '4px',
                            direction: isRTL ? 'rtl' : 'ltr'
                          }}>
                            {t('gender')}
                          </label>
                          <select
                            value={studentFilters.gender}
                            onChange={(e) => setStudentFilters({...studentFilters, gender: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              fontSize: '13px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              direction: isRTL ? 'rtl' : 'ltr',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">{t('all')}</option>
                            <option value="Male">{t('male')}</option>
                            <option value="Female">{t('female')}</option>
                          </select>
                        </div>

                        {/* Filtre Statut */}
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#6b7280',
                            marginBottom: '4px',
                            direction: isRTL ? 'rtl' : 'ltr'
                          }}>
                            {t('status')}
                          </label>
                          <select
                            value={studentFilters.status}
                            onChange={(e) => setStudentFilters({...studentFilters, status: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              fontSize: '13px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              direction: isRTL ? 'rtl' : 'ltr',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">{t('all')}</option>
                            <option value="active">{t('active')}</option>
                            <option value="inactive">{t('inactive')}</option>
                          </select>
                        </div>
                      </div>

                      {/* Filtre Enseignant */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#6b7280',
                          marginBottom: '4px',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }}>
                            {t('teacher')}
                          </label>
                          <select
                            value={studentFilters.teacherName}
                            onChange={(e) => setStudentFilters({...studentFilters, teacherName: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '13px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: 'white',
                            direction: isRTL ? 'rtl' : 'ltr',
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="">{t('all')}</option>
                          {Array.from(new Set(students.map(s => s.teacher_name).filter((name): name is string => Boolean(name)))).map((teacherName, index) => (
                            <option key={index} value={teacherName}>
                              {teacherName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Filtres Niveau et Halqa */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {/* Filtre Niveau */}
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#6b7280',
                            marginBottom: '4px',
                            direction: isRTL ? 'rtl' : 'ltr'
                          }}>
                            {t('studentLevel')}
                          </label>
                          <select
                            value={studentFilters.level}
                            onChange={(e) => setStudentFilters({...studentFilters, level: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              fontSize: '13px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              direction: isRTL ? 'rtl' : 'ltr',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">{t('all')}</option>
                            <option value="الروض">الروض</option>
                            <option value="الأول">الأول</option>
                            <option value="الثاني">الثاني</option>
                            <option value="الثالث">الثالث</option>
                            <option value="الرابع">الرابع</option>
                            <option value="الخامس">الخامس</option>
                            <option value="السادس">السادس</option>
                            <option value="الإعدادي">الإعدادي</option>
                            <option value="الثانوي">الثانوي</option>
                          </select>
                        </div>

                        {/* Filtre Halqa */}
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#6b7280',
                            marginBottom: '4px',
                            direction: isRTL ? 'rtl' : 'ltr'
                          }}>
                            {t('studentHalqa')}
                          </label>
                          <select
                            value={studentFilters.halqa}
                            onChange={(e) => setStudentFilters({...studentFilters, halqa: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              fontSize: '13px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              direction: isRTL ? 'rtl' : 'ltr',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">{t('all')}</option>
                            <option value="حلقة الحفظ بالمصحف">حلقة الحفظ بالمصحف</option>
                            <option value="حلقة الحفظ بالسمع">حلقة الحفظ بالسمع</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Liste des cartes */}
                  {Array.isArray(filteredStudents) && filteredStudents.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {filteredStudents.map((student, index) => (
                        <div
                          key={student.id || index}
                          style={{
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            padding: '16px',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          {/* Nom de l'étudiant - En-tête de la carte */}
                          {visibleColumns.studentName && (
                            <div dir="auto" style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#374151',
                              marginBottom: '12px',
                              paddingBottom: '12px',
                              borderBottom: '2px solid #e5e7eb',
                            }}>
                              {student.student_name || t('notSpecified')}
                            </div>
                          )}
                          
                          {/* Informations de l'étudiant */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', direction: isRTL ? 'rtl' : 'ltr' }}>
                            {visibleColumns.parentName && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('studentParent')}:</span>
                                <span style={{ fontSize: '13px', color: '#374151' }}>{student.parent_name || t('notSpecified')}</span>
                              </div>
                            )}
                            
                            
                            {visibleColumns.gender && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('gender')}:</span>
                                <span style={{ fontSize: '13px', color: '#374151' }}>{student.gender === 'Female' ? t('female') : t('male')}</span>
                              </div>
                            )}
                            
                            {visibleColumns.teacherName && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('teacher')}:</span>
                                <span style={{ fontSize: '13px', color: '#374151' }}>{student.teacher_name || t('notSpecified')}</span>
                              </div>
                            )}
                            
                            
                            {visibleColumns.halqa && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('studentHalqa')}:</span>
                                <span style={{ fontSize: '13px', color: '#374151' }}>{student.halqa || t('notSpecified')}</span>
                              </div>
                            )}
                            
                            {visibleColumns.status && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('status')}:</span>
                                <span style={GLOBAL_STYLES.statusBadge(student.status === 'active')}>
                                  {getDisplayStatus(student.status)}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          {visibleColumns.actions && canModify('students') && (
                            <div style={{
                              display: 'flex',
                              gap: '8px',
                              marginTop: '12px',
                              paddingTop: '12px',
                              borderTop: '1px solid #e5e7eb',
                              direction: isRTL ? 'rtl' : 'ltr'
                            }}>
                              <button 
                                onClick={() => handleEditStudent(student)}
                                style={{
                                  ...GLOBAL_STYLES.editButton,
                                  flex: 1,
                                  padding: '10px 16px',
                                  fontSize: '13px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover;
                                }}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}>
                                <EditIcon size={16} />
                                <span>{t('edit')}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '40px 20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                        {Array.isArray(students) && students.length > 0 
                          ? t('noFilterResults') 
                          : t('noStudentsRegistered')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        {Array.isArray(students) && students.length > 0 
                          ? t('tryChangingFilters') 
                          : t('addStudentHint')}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Affichage en tableau pour desktop et mobile (mode tableau) */
                <div>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    minWidth: windowWidth < 768 ? '600px' : 'auto'
                  }}>
                  <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{
                        padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                        fontWeight: '600',
                        color: '#6b7280',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb',
                        whiteSpace: windowWidth < 768 ? 'nowrap' : 'normal',
                        display: visibleColumns.studentName ? 'table-cell' : 'none'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span>{t('studentName')}</span>
                          <SearchableSelect
                            value={studentFilters.studentName}
                            onChange={(value) => setStudentFilters({...studentFilters, studentName: value as string})}
                            options={[
                              { value: '', label: t('all') },
                              ...students.map(student => ({
                                value: student.student_name || '',
                                label: student.student_name || t('notSpecified')
                              }))
                            ]}
                            placeholder={t('all')}
                            compact={true}
                          />
                        </div>
                      </th>
                      <th style={{
                        padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                        fontWeight: '600',
                        color: '#6b7280',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb',
                        display: visibleColumns.parentName ? 'table-cell' : 'none'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span>{t('studentParent')}</span>
                          <SearchableSelect
                            value={studentFilters.parentName}
                            onChange={(value) => setStudentFilters({...studentFilters, parentName: value as string})}
                            options={[
                              { value: '', label: t('all') },
                              ...Array.from(new Set(students.map(s => s.parent_name).filter(Boolean))).map(parentName => ({
                                value: parentName || '',
                                label: parentName || t('notSpecified')
                              }))
                            ]}
                            placeholder={t('all')}
                            compact={true}
                          />
                        </div>
                      </th>
                      <th style={{
                        padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                        fontWeight: '600',
                        color: '#6b7280',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb',
                        display: (visibleColumns.gender && windowWidth >= 768 && !selectedStudentDetail) ? 'table-cell' : 'none'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span>{t('gender')}</span>
                          <select
                            value={studentFilters.gender}
                            onChange={(e) => {
                              const newGender = e.target.value;
                              console.log('🔍 Filtre gender changé:', newGender);
                              setStudentFilters({...studentFilters, gender: newGender});
                            }}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              width: '100%',
                              boxSizing: 'border-box',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="">{t('all')}</option>
                            <option value="Male">{t('male')}</option>
                            <option value="Female">{t('female')}</option>
                          </select>
                        </div>
                      </th>
                      <th style={{
                        padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                        fontWeight: '600',
                        color: '#6b7280',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb',
                        display: (visibleColumns.teacherName && !selectedStudentDetail) ? 'table-cell' : 'none'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span>{t('teacher')}</span>
                          <select
                            value={studentFilters.teacherName}
                            onChange={(e) => setStudentFilters({...studentFilters, teacherName: e.target.value})}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              width: '100%',
                              boxSizing: 'border-box',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="">{t('all')}</option>
                            {Array.from(new Set(students.map(s => s.teacher_name).filter((name): name is string => Boolean(name)))).map((teacherName, index) => (
                              <option key={index} value={teacherName}>
                                {teacherName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </th>
                      <th style={{
                        padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                        fontWeight: '600',
                        color: '#6b7280',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb',
                        display: visibleColumns.halqa ? 'table-cell' : 'none'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span>{t('studentHalqa')}</span>
                          <select
                            value={studentFilters.halqa}
                            onChange={(e) => setStudentFilters({...studentFilters, halqa: e.target.value})}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              width: '100%',
                              boxSizing: 'border-box',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="">{t('all')}</option>
                            <option value="حلقة الحفظ بالمصحف">حلقة الحفظ بالمصحف</option>
                            <option value="حلقة الحفظ بالسمع">حلقة الحفظ بالسمع</option>
                          </select>
                        </div>
                      </th>
                      <th style={{
                        padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                        fontWeight: '600',
                        color: '#6b7280',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb',
                        display: (visibleColumns.status && windowWidth >= 768 && !selectedStudentDetail) ? 'table-cell' : 'none'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span>{t('status')}</span>
                          <select
                            value={studentFilters.status}
                            onChange={(e) => setStudentFilters({...studentFilters, status: e.target.value})}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              width: '100%',
                              boxSizing: 'border-box',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="">{t('all')}</option>
                            <option value="active">{t('active')}</option>
                            <option value="inactive">{t('inactive')}</option>
                          </select>
                        </div>
                      </th>
                      <th style={{
                        padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                        fontWeight: '600',
                        color: '#6b7280',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb',
                        display: selectedStudentDetail ? 'none' : 'table-cell'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}>
                          <span>{t('actions')}</span>
                          <button
                            onClick={handleResetFilters}
                            title={t('resetFilters')}
                            style={{
                              backgroundColor: 'transparent',
                              color: '#f59e0b',
                              border: '1px solid #f59e0b',
                              borderRadius: '4px',
                              padding: '2px 6px',
                              fontSize: '11px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '3px',
                              whiteSpace: 'nowrap',
                              fontWeight: '500',
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#f59e0b';
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#f59e0b';
                            }}
                          >
                            <ResetIcon size={11} />
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: 'white' }}>
                    {Array.isArray(filteredStudents) && filteredStudents.length > 0 ? (
                      filteredStudents.map((student, index) => {
                        const isSelected = selectedStudentDetail && selectedStudentDetail.id === student.id;
                        return (
                        <React.Fragment key={student.id || index}>
                        <tr
                          onClick={() => setSelectedStudentDetail(isSelected ? null : student)}
                          style={{
                            borderBottom: index < filteredStudents.length - 1 ? '1px solid #f3f4f6' : 'none',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#f0fdf4' : 'transparent',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseOver={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = '#f9fafb'; }}
                          onMouseOut={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                        >
                          <td dir="auto" style={{
                            padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                            textAlign: isRTL ? 'right' : 'left',
                            fontSize: windowWidth < 768 ? '13px' : windowWidth < 1024 ? '14px' : '16px',
                            color: '#374151',
                            fontWeight: '500',
                            display: visibleColumns.studentName ? 'table-cell' : 'none'
                          }}>
                            {student.student_name || t('notSpecified')}
                          </td>
                          <td style={{
                            padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                            textAlign: isRTL ? 'right' : 'left',
                            fontSize: windowWidth < 768 ? '12px' : '14px',
                            color: '#6b7280',
                            display: visibleColumns.parentName ? 'table-cell' : 'none'
                          }}>
                            {student.parent_name || t('notSpecified')}
                          </td>
                          <td style={{
                            padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                            textAlign: isRTL ? 'right' : 'left',
                            fontSize: windowWidth < 768 ? '12px' : '14px',
                            color: '#6b7280',
                            display: (visibleColumns.gender && windowWidth >= 768 && !selectedStudentDetail) ? 'table-cell' : 'none'
                          }}>
                            {student.gender === 'Female' ? t('female') : t('male')}
                          </td>
                          <td style={{
                            padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                            textAlign: isRTL ? 'right' : 'left',
                            fontSize: windowWidth < 768 ? '12px' : '14px',
                            color: '#6b7280',
                            display: (visibleColumns.teacherName && !selectedStudentDetail) ? 'table-cell' : 'none'
                          }}>
                            {student.teacher_name || t('notSpecified')}
                          </td>
                          <td style={{
                            padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                            textAlign: isRTL ? 'right' : 'left',
                            fontSize: windowWidth < 768 ? '12px' : '14px',
                            color: '#6b7280',
                            display: visibleColumns.halqa ? 'table-cell' : 'none'
                          }}>
                            {student.halqa || t('notSpecified')}
                          </td>
                          <td style={{
                            padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 16px' : '16px 24px',
                            textAlign: isRTL ? 'right' : 'left',
                            fontSize: windowWidth < 768 ? '12px' : '14px',
                            color: '#6b7280',
                            display: (visibleColumns.status && windowWidth >= 768 && !selectedStudentDetail) ? 'table-cell' : 'none'
                          }}>
                            <span style={GLOBAL_STYLES.statusBadge(student.status === 'active')}>
                              {getDisplayStatus(student.status)}
                            </span>
                          </td>
                          {canModify('students') && (
                            <td style={{ padding: windowWidth < 768 ? '8px' : windowWidth < 1024 ? '10px 12px' : '12px 16px', textAlign: isRTL ? 'right' : 'left', display: selectedStudentDetail ? 'none' : 'table-cell' }}>
                              <div style={{ display: 'flex', gap: '6px', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEditStudent(student); }}
                                  style={{ ...GLOBAL_STYLES.editButton, padding: windowWidth < 768 ? '5px 8px' : '7px 12px', fontSize: windowWidth < 768 ? '11px' : '13px' }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}>
                                  <EditIcon size={windowWidth < 768 ? 13 : 15} />
                                  {windowWidth >= 768 && <span>{t('edit')}</span>}
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                        {isMobile && isSelected && selectedStudentDetail && (
                          <tr>
                            <td colSpan={6} style={{ padding: 0, border: '1px solid #3b82f6', borderTop: 'none', backgroundColor: 'white' }}>
                              {renderStudentDetailContent()}
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={Object.values(visibleColumns).filter(v => v).length || 9} style={{
                          padding: windowWidth < 768 ? '30px 16px' : '40px 24px',
                          textAlign: 'center',
                          color: '#9ca3af',
                          fontSize: windowWidth < 768 ? '14px' : '16px'
                        }}>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: windowWidth < 768 ? '8px' : '12px'
                          }}>
                            <div style={{ fontSize: windowWidth < 768 ? '36px' : '48px' }}>👥</div>
                            <div style={{ fontSize: windowWidth < 768 ? '14px' : '16px' }}>
                              {Array.isArray(students) && students.length > 0 
                                ? t('noFilterResults') 
                                : t('noStudentsRegistered')}
                            </div>
                            <div style={{ fontSize: windowWidth < 768 ? '12px' : '14px', color: '#6b7280' }}>
                              {Array.isArray(students) && students.length > 0 
                                ? t('tryChangingFilters') 
                                : t('addStudentHint')}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}
            </div>

          {/* Panneau de détail de l'étudiant sélectionné - desktop uniquement */}
          {!isMobile && selectedStudentDetail && (
            <div style={{
              flex: '0 0 50%',
              minWidth: 0,
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {renderStudentDetailContent()}
            </div>
          )}
          </div>
          );
        })()}

          </div>
        );


      case 'communication':
        return (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: windowWidth < 768 ? '0 15px' : windowWidth < 1024 ? '0 20px' : '0 30px'
          }}>

            {/* Contenu des onglets */}

            {/* Barre d'onglets internes — الرسائل */}
            {communicationTab === 'messages' && (
              <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #e5e7eb', marginBottom: '20px' }}>
                {([
                  { key: 'received' as const, label: t('receivedMessages') || 'الرسائل المستلمة' },
                  { key: 'sent' as const, label: t('sentMessages') || 'الرسائل المرسلة' },
                ] as const).map(tab => (
              <button
                    key={tab.key}
                    onClick={() => setMessagesSubTab(tab.key)}
                style={{
                      padding: '10px 20px',
                  border: 'none',
                      background: 'transparent',
                      borderBottom: messagesSubTab === tab.key ? '3px solid #f57c00' : '3px solid transparent',
                      color: messagesSubTab === tab.key ? '#f57c00' : '#6b7280',
                      fontWeight: messagesSubTab === tab.key ? '600' : '400',
                      fontSize: windowWidth < 768 ? '13px' : '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >{tab.label}</button>
                ))}
            </div>
            )}

            {/* Section الرسائل المرسلة (Sent Messages) */}
            {communicationTab === 'messages' && messagesSubTab === 'sent' && (
              <div style={GLOBAL_STYLES.dashboard.card}>
                {/* Bouton pour envoyer un message */}
                {canModify('communication') && (
                  <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setShowSendMessageModal(true);
                      }}
                      style={{
                        ...GLOBAL_STYLES.dashboard.button,
                        ...GLOBAL_STYLES.dashboard.primaryButton,
                        padding: windowWidth < 768 ? '10px 16px' : '12px 24px',
                        fontSize: windowWidth < 768 ? '14px' : '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      ✉️ {t('sendMessage')}
                    </button>
                  </div>
                )}

                <div className="messages-container" style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '16px',
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                  paddingRight: '8px'
                }}>
                  {sentMessages.length > 0 ? (
                    sentMessages.map((message) => {
                      const isOpen = expandedMessageId === message.id;
                      
                      // Fonction pour obtenir le genre de l'étudiant
                      const getStudentGender = (studentId: number | null | undefined): 'Male' | 'Female' | null | undefined => {
                        if (!studentId) return null;
                        const student = students.find(s => s.id === studentId);
                        return student?.gender || null;
                      };
                      
                      // Convertir le message en format MessageItemData
                      const messageItemData: MessageItemData = {
                        id: message.id,
                        subject: message.subject || '',
                        content: message.content || '',
                        sender_name: 'الإدارة',
                        sender_type: 'admin',
                        sent_date: message.sentDate || message.sent_date || '',
                        sent_time: message.sentTime || message.sent_time || '',
                        priority: message.priority || 'normal',
                        student_id: message.student_id || null,
                        student_name: message.student_name || null,
                        student_gender: message.student_gender || null,
                        student_label: message.student_label || null,
                        is_reply: message.is_reply || false,
                        reply_to_message_id: message.reply_to_message_id || null
                      };
                      
                      return (
                        <MessageItem
                          key={message.id}
                          message={messageItemData}
                          isOpen={isOpen}
                          onToggle={handleMessageClick}
                          useModernStyle={true}
                          windowWidth={windowWidth}
                          translateReportType={(subject) => subject || ''}
                          translatePriority={translatePriority}
                          getPlainText={getPlainText}
                          getCleanedContent={getCleanedContent}
                          showSenderLabel={false}
                          senderLabel="إلى"
                          getStudentGender={getStudentGender}
                          getStudentLabel={GLOBAL_UTILS.getStudentLabel}
                          formatDate={GLOBAL_UTILS.formatDate}
                          showReplyButton={false}
                          showReplyBadge={false}
                          t={t}
                        />
                      );
                    })
                  ) : (
                    <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px' }}>
                      <p style={{ fontSize: '1.1rem', margin: 0 }}>{t('noSentMessages')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Section الرسائل المستلمة (Received Messages) */}
            {communicationTab === 'messages' && messagesSubTab === 'received' && (
              <div style={GLOBAL_STYLES.dashboard.card}>
                <div className="messages-container" style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '16px',
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                  paddingRight: '8px'
                }}>
                  {messages.filter(msg => {
                    const senderType = msg.sender_type?.toLowerCase() || '';
                    return senderType !== 'admin' && senderType !== 'مدير';
                  }).length > 0 ? (
                    messages.filter(msg => {
                      const senderType = msg.sender_type?.toLowerCase() || '';
                      return senderType !== 'admin' && senderType !== 'مدير';
                    }).map((message) => {
                      const isOpen = expandedMessageId === message.id;
                      
                      // Fonction pour obtenir le genre de l'étudiant
                      const getStudentGender = (studentId: number | null | undefined): 'Male' | 'Female' | null | undefined => {
                        if (!studentId) return null;
                        const student = students.find(s => s.id === studentId);
                        return student?.gender || null;
                      };
                      
                      return (
                        <MessageItem
                          key={message.id}
                          message={message as MessageItemData}
                          isOpen={isOpen}
                          onToggle={handleMessageClick}
                          useModernStyle={true}
                          windowWidth={windowWidth}
                          translateReportType={translateReportType}
                          translatePriority={translatePriority}
                          getPlainText={getPlainText}
                          getCleanedContent={getCleanedContent}
                          showSenderLabel={true}
                          senderLabel={t('from')}
                          getSenderTitle={getSenderTitle}
                          getStudentGender={getStudentGender}
                          getStudentLabel={GLOBAL_UTILS.getStudentLabel}
                          formatDate={GLOBAL_UTILS.formatDate}
                          showReplyButton={true}
                          onReply={(msg) => {
                            // Convertir MessageItemData en Message pour handleReplyToMessage
                            const messageForReply: Message = {
                              id: message.id,
                              sender_id: message.sender_id,
                              sender_name: message.sender_name || '',
                              sender_type: message.sender_type,
                              sent_date: message.sent_date || '',
                              sent_time: message.sent_time || '',
                              subject: message.subject || '',
                              content: message.content || '',
                              priority: message.priority || 'normal',
                              student_id: message.student_id || null,
                              student_name: message.student_name || null,
                              student_gender: message.student_gender || null,
                              student_label: message.student_label || null,
                              is_reply: message.is_reply || false,
                              reply_to_message_id: message.reply_to_message_id || null
                            };
                            handleReplyToMessage(messageForReply);
                          }}
                          showReplyBadge={false}
                          t={t}
                        />
                      );
                    })
                  ) : (
                    <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px' }}>
                      <p style={{ fontSize: '1.1rem', margin: 0 }}>{t('noReceivedMessages')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Barre d'onglets internes — البريد الإلكتروني */}
            {communicationTab === 'emails' && (
              <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #e5e7eb', marginBottom: '20px' }}>
                {([
                  { key: 'sent' as const, label: t('emailsSending') || 'البريد المرسل' },
                  { key: 'received' as const, label: t('emailsReceiving') || 'البريد المستلم' },
                ] as const).map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => { setEmailsSubTab(tab.key); }}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      background: 'transparent',
                      borderBottom: emailsSubTab === tab.key ? '3px solid #f57c00' : '3px solid transparent',
                      color: emailsSubTab === tab.key ? '#f57c00' : '#6b7280',
                      fontWeight: emailsSubTab === tab.key ? '600' : '400',
                      fontSize: windowWidth < 768 ? '13px' : '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >{tab.label}</button>
                ))}
              </div>
            )}

            {communicationTab === 'emails' && emailsSubTab === 'sent' && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: windowWidth < 768 ? '16px' : '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: windowWidth < 768 ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  gap: windowWidth < 768 ? '16px' : '0'
                }}>
                  <h2 style={{
                    fontSize: windowWidth < 768 ? '18px' : '20px',
                    fontWeight: '600',
                    color: '#424242',
                    margin: 0
                  }}>
                    {t('emailsSending')}
                  </h2>
                  <button
                    onClick={loadSentEmails}
                    disabled={sentEmailsLoading}
                    style={{
                      backgroundColor: '#FFE0B2',
                      color: '#E65100',
                      padding: windowWidth < 768 ? '10px 16px' : '12px 24px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: windowWidth < 768 ? '14px' : '16px',
                      fontWeight: '500',
                      cursor: sentEmailsLoading ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: windowWidth < 768 ? '100%' : 'auto',
                      justifyContent: 'center',
                      opacity: sentEmailsLoading ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!sentEmailsLoading) e.currentTarget.style.backgroundColor = '#FFB74D';
                    }}
                    onMouseOut={(e) => {
                      if (!sentEmailsLoading) e.currentTarget.style.backgroundColor = '#FFE0B2';
                    }}
                  >
                    {sentEmailsLoading ? 'جاري التحميل...' : '🔄 تحديث'}
                  </button>
                </div>
                
                {sentEmailsError && (
                  <div style={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    fontSize: '14px'
                  }}>
                    {sentEmailsError}
                  </div>
                )}

                <div style={{ maxHeight: windowWidth < 768 ? '300px' : '500px', overflowY: 'auto' }}>
                  {sentEmailsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#757575' }}>
                      جاري التحميل...
                    </div>
                  ) : sentEmails.length === 0 ? (
                    <p style={{
                      color: '#757575',
                      fontSize: windowWidth < 768 ? '13px' : '14px',
                      textAlign: 'center',
                      fontStyle: 'italic',
                      padding: '40px'
                    }}>
                      لا توجد رسائل بريد إلكتروني مرسلة
                    </p>
                  ) : (
                    sentEmails.map((email, index) => {
                      const emailId = email.id || index;
                      const isOpen = openedSentEmailId === emailId;
                      const emailDate = email.sent_at || email.created_at;
                      const formattedDate = emailDate ? GLOBAL_UTILS.formatDate(emailDate) : 'غير محدد';
                      const formattedTime = emailDate ? GLOBAL_UTILS.formatTime(emailDate) : '';
                      const previewText = getPlainText(email.html_content, email.text_content, 80);
                      
                      return (
                        <div key={emailId} style={{
                          backgroundColor: isOpen ? '#FFF9E6' : '#FAFAFA',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '8px',
                          border: isOpen ? '2px solid #FFE0B2' : '1px solid #E0E0E0',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleToggleSentEmail(emailId)}
                        onMouseEnter={(e) => {
                          if (windowWidth >= 768) {
                            e.currentTarget.style.backgroundColor = isOpen ? '#FFF9E6' : '#F5F5F5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (windowWidth >= 768) {
                            e.currentTarget.style.backgroundColor = isOpen ? '#FFF9E6' : '#FAFAFA';
                          }
                        }}>
                          {/* Ligne compacte avec sujet et début du texte */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            width: '100%'
                          }}>
                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '6px',
                                flexWrap: 'nowrap',
                                overflow: 'hidden'
                              }}>
                                <span style={{
                                  fontSize: windowWidth < 768 ? '13px' : '14px',
                                  fontWeight: '600',
                                  color: '#1f2937',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}>
                                  {email.subject || 'بدون موضوع'}
                                </span>
                                {!isOpen && (
                                  <span style={{
                                    fontSize: windowWidth < 768 ? '11px' : '12px',
                                    color: '#6b7280',
                                    fontWeight: '400',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1
                                  }}>
                                    - {previewText}
                                  </span>
                                )}
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                fontSize: windowWidth < 768 ? '10px' : '11px',
                                color: '#9ca3af',
                                flexWrap: 'wrap'
                              }}>
                                <span style={{ whiteSpace: 'nowrap' }}>إلى: {email.to_email || 'غير محدد'}</span>
                                <span style={{ whiteSpace: 'nowrap' }}>{formattedDate} {formattedTime && formattedTime}</span>
                                <div style={{
                                  backgroundColor: email.status === 'sent' ? '#4caf50' : email.status === 'failed' ? '#f44336' : '#ff9800',
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {email.status === 'sent' ? 'تم الإرسال' : email.status === 'failed' ? 'فشل' : email.status === 'pending' ? 'قيد الانتظار' : email.status || 'غير محدد'}
                                </div>
                              </div>
                            </div>
                            <span style={{
                              fontSize: '16px',
                              color: '#6b7280',
                              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease',
                              flexShrink: 0
                            }}>
                              ▶
                            </span>
                          </div>

                          {/* Contenu complet quand ouvert */}
                          {isOpen && (
                            <div style={{
                              marginTop: '16px',
                              paddingTop: '16px',
                              borderTop: '1px solid #FFE0B2'
                            }}>
                              <div 
                                style={{
                                  fontSize: windowWidth < 768 ? '12px' : '13px',
                                  color: '#374151',
                                  lineHeight: '1.6',
                                  marginBottom: '12px',
                                  wordWrap: 'break-word'
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: email.html_content || email.text_content?.replace(/\n/g, '<br>') || 'لا يوجد محتوى'
                                }}
                              ></div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingTop: '8px',
                                borderTop: '1px solid #FFE0B2',
                                fontSize: windowWidth < 768 ? '11px' : '12px',
                                color: '#6b7280'
                              }}>
                                <span><span style={{ fontWeight: '500' }}>{t('from')}:</span> {email.from_email || '-'}</span>
                                <span>{formattedDate} {formattedTime && formattedTime}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {communicationTab === 'emails' && emailsSubTab === 'received' && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: windowWidth < 768 ? '16px' : '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: windowWidth < 768 ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  gap: windowWidth < 768 ? '16px' : '0'
                }}>
                  <h2 style={{
                    fontSize: windowWidth < 768 ? '18px' : '20px',
                    fontWeight: '600',
                    color: '#424242',
                    margin: 0
                  }}>
                    {t('emailsReceiving')}
                  </h2>
                  <button
                    onClick={syncReceivedEmails}
                    disabled={receivedEmailsLoading}
                    style={{
                      backgroundColor: '#FFE0B2',
                      color: '#E65100',
                      padding: windowWidth < 768 ? '10px 16px' : '12px 24px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: windowWidth < 768 ? '14px' : '16px',
                      fontWeight: '500',
                      cursor: receivedEmailsLoading ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: windowWidth < 768 ? '100%' : 'auto',
                      justifyContent: 'center',
                      opacity: receivedEmailsLoading ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!receivedEmailsLoading) e.currentTarget.style.backgroundColor = '#FFB74D';
                    }}
                    onMouseOut={(e) => {
                      if (!receivedEmailsLoading) e.currentTarget.style.backgroundColor = '#FFE0B2';
                    }}
                  >
                    {receivedEmailsLoading ? 'جاري التحديث...' : '🔄 تحديث و مزامنة'}
                  </button>
                </div>
                
                {receivedEmailsError && (
                  <div style={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    fontSize: '14px'
                  }}>
                    {receivedEmailsError}
                  </div>
                )}

                <div style={{ maxHeight: windowWidth < 768 ? '300px' : '500px', overflowY: 'auto' }}>
                  {receivedEmailsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#757575' }}>
                      جاري التحميل...
                    </div>
                  ) : receivedEmails.length === 0 ? (
                    <p style={{
                      color: '#757575',
                      fontSize: windowWidth < 768 ? '13px' : '14px',
                      textAlign: 'center',
                      fontStyle: 'italic',
                      padding: '40px'
                    }}>
                      لا توجد رسائل بريد إلكتروني مستلمة
                    </p>
                  ) : (
                    receivedEmails.map((email, index) => {
                      const emailId = email.id || email.resend_id || index;
                      const isOpen = openedReceivedEmailId === emailId;
                      const emailDate = email.received_at || email.created_at;
                      const formattedDate = emailDate ? GLOBAL_UTILS.formatDate(emailDate) : 'غير محدد';
                      const formattedTime = emailDate ? GLOBAL_UTILS.formatTime(emailDate) : '';
                      const previewText = getPlainText(email.html_content, email.text_content, 80);
                      
                      return (
                        <div key={emailId} style={{
                          backgroundColor: isOpen ? '#E8F5E9' : '#FAFAFA',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '8px',
                          border: isOpen ? '2px solid #C8E6C9' : '1px solid #E0E0E0',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleToggleReceivedEmail(emailId)}
                        onMouseEnter={(e) => {
                          if (windowWidth >= 768) {
                            e.currentTarget.style.backgroundColor = isOpen ? '#E8F5E9' : '#F5F5F5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (windowWidth >= 768) {
                            e.currentTarget.style.backgroundColor = isOpen ? '#E8F5E9' : '#FAFAFA';
                          }
                        }}>
                          {/* Ligne compacte avec sujet et début du texte */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            width: '100%'
                          }}>
                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '6px',
                                flexWrap: 'nowrap',
                                overflow: 'hidden'
                              }}>
                                <span style={{
                                  fontSize: windowWidth < 768 ? '13px' : '14px',
                                  fontWeight: '600',
                                  color: '#1f2937',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}>
                                  {email.subject || 'بدون موضوع'}
                                </span>
                                {!isOpen && (
                                  <span style={{
                                    fontSize: windowWidth < 768 ? '11px' : '12px',
                                    color: '#6b7280',
                                    fontWeight: '400',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1
                                  }}>
                                    - {previewText}
                                  </span>
                                )}
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                fontSize: windowWidth < 768 ? '10px' : '11px',
                                color: '#9ca3af',
                                flexWrap: 'wrap'
                              }}>
                                <span style={{ whiteSpace: 'nowrap' }}>من: {email.from_email || 'غير محدد'}</span>
                                <span style={{ whiteSpace: 'nowrap' }}>{formattedDate} {formattedTime && formattedTime}</span>
                                <div style={{
                                  backgroundColor: '#4caf50',
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap'
                                }}>
                                  مستلم
                                </div>
                              </div>
                            </div>
                            <span style={{
                              fontSize: '16px',
                              color: '#6b7280',
                              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease',
                              flexShrink: 0
                            }}>
                              ▶
                            </span>
                          </div>

                          {/* Contenu complet quand ouvert */}
                          {isOpen && (
                            <div style={{
                              marginTop: '16px',
                              paddingTop: '16px',
                              borderTop: '1px solid #C8E6C9'
                            }}>
                              <div 
                                style={{
                                  fontSize: windowWidth < 768 ? '12px' : '13px',
                                  color: '#374151',
                                  lineHeight: '1.6',
                                  marginBottom: '12px',
                                  wordWrap: 'break-word'
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: email.html_content || email.text_content?.replace(/\n/g, '<br>') || 'لا يوجد محتوى'
                                }}
                              ></div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingTop: '8px',
                                borderTop: '1px solid #C8E6C9',
                                fontSize: windowWidth < 768 ? '11px' : '12px',
                                color: '#6b7280'
                              }}>
                                <span><span style={{ fontWeight: '500' }}>{t('to')}:</span> {email.to_email || '-'}</span>
                                <span>{formattedDate} {formattedTime && formattedTime}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Onglet Demandes d'inscription */}
            {communicationTab === 'inscription-requests' && (() => {
              const isMobile = windowWidth < 1024;

              const renderInscriptionDetail = (d: any) => {
                if (!d) return null;
                const isDone = ['approved', 'rejected', 'registered'].includes(d.status);
                const hasChildren = Array.isArray(d.children) && d.children.length > 0;
                const hasLinkedUser = d.created_user_id || d.linked_user_id;
                const targetYearId = d.school_year_id;
                const hasNewChildren = hasChildren && d.children.some((c: any) => !c.student_id);
                const hasReenrollToDo = hasChildren && d.children.some((c: any) => c.student_id && Number(c.student_school_year_id) !== Number(targetYearId));
                const canProvision = d.request_type === 'parent' && hasLinkedUser && hasChildren && ['approved', 'registered'].includes(d.status) && (d.status === 'approved' || hasNewChildren || hasReenrollToDo);
                return (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>{d.full_name}</div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>👨‍👧 {t('parent')}</div>
                      </div>
                      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                        backgroundColor: d.status === 'pending' ? '#FEF3C7' : d.status === 'approved' ? '#D1FAE5' : d.status === 'rejected' ? '#FEE2E2' : d.status === 'registered' ? '#DBEAFE' : '#E0E7FF',
                        color: d.status === 'pending' ? '#92400E' : d.status === 'approved' ? '#065F46' : d.status === 'rejected' ? '#991B1B' : d.status === 'registered' ? '#1D4ED8' : '#3730A3'
                      }}>{d.status === 'pending' ? t('pending') : d.status === 'approved' ? t('approved') : d.status === 'rejected' ? t('rejected') : d.status === 'registered' ? t('registered') : t('reviewed')}</span>
                    </div>
                    <div style={{ backgroundColor: '#F9FAFB', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
                        <div style={{ fontWeight: '600', fontSize: '12px', color: '#374151', marginBottom: '6px' }}>{t('applicantInfo')}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#4B5563' }}>
                        {d.phone && <div>📞 {d.phone}</div>}
                        {d.email && <div>✉️ {d.email}</div>}
                        {d.gender && <div>👤 {d.gender === 'male' || d.gender === 'Male' ? t('male') : d.gender === 'female' || d.gender === 'Female' ? t('female') : d.gender}</div>}
                        {d.address && <div>📍 {d.address}</div>}
                        {d.created_at && <div>📅 {formatShortDateInTz(new Date(d.created_at), timezone, dateLocale)}</div>}
                      </div>
                    </div>
                    {hasLinkedUser && (
                      <div style={{ backgroundColor: '#EFF6FF', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
                        <div style={{ fontWeight: '600', fontSize: '12px', color: '#1D4ED8', marginBottom: '4px' }}>🔗 {t('linkedAccount')}</div>
                        <div style={{ fontSize: '13px', color: '#4B5563' }}>ID: {d.created_user_id || d.linked_user_id}</div>
                        {d.linked_username && <div style={{ fontSize: '13px', color: '#4B5563' }}>{d.linked_username}</div>}
                        {d.request_type === 'parent' && (
                          <button onClick={() => {
                            const targetId = (d.created_user_id || d.linked_user_id).toString();
                            setParentFilter(targetId);
                            setSelectedParentDetail(null);
                            setParentDetailChildren([]);
                            navigateToSection('parents').then(() => {
                              const found = parents.find(p => (p.id || p.user_id || '').toString() === targetId);
                              const parentObj = found || {
                                id: parseInt(targetId, 10) || targetId,
                                user_id: parseInt(targetId, 10) || targetId,
                                full_name: d.linked_user_full_name || d.full_name || '',
                                username: d.linked_user_username || '',
                                phone: d.linked_user_phone || d.phone || '',
                                email: d.email || '',
                              };
                              handleParentDetailOpen(parentObj);
                            });
                          }}
                            style={{ marginTop: '8px', width: '100%', padding: '7px', backgroundColor: '#DBEAFE', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                            👨‍👧 {t('viewParent') || 'عرض ملف الوالد في أولياء الأمور'}
                          </button>
                        )}
                      </div>
                    )}
                    {hasChildren && (
                      <div style={{ backgroundColor: '#FEFCE8', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
                        <div style={{ fontWeight: '600', fontSize: '12px', color: '#92400E', marginBottom: '8px' }}>👦 {t('children')} ({d.children.length})</div>
                        {d.children.map((child: any, idx: number) => (
                          <div key={idx} style={{ padding: '7px 9px', marginBottom: idx < d.children.length - 1 ? '6px' : '0', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #FDE68A' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ fontWeight: '700', fontSize: '13px', color: '#1F2937' }}>
                                {idx + 1}. {child.student_name || child.full_name || child.child_name || child.name || (child.first_name ? `${child.first_name} ${child.last_name || ''}`.trim() : null) || `${t('child')} ${idx + 1}`}
                              </span>
                              {child.student_id && <span style={{ fontSize: '11px', fontWeight: '600', color: '#065F46', backgroundColor: '#D1FAE5', padding: '2px 6px', borderRadius: '10px' }}>✓ {t('registered')}</span>}
                            </div>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              {child.gender && <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '10px', backgroundColor: child.gender === 'male' || child.gender === 'Male' ? '#DBEAFE' : '#FCE7F3', color: child.gender === 'male' || child.gender === 'Male' ? '#1D4ED8' : '#9D174D', fontWeight: '500' }}>{child.gender === 'male' || child.gender === 'Male' ? t('male') : t('female')}</span>}
                              {child.level && <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '10px', backgroundColor: '#E0E7FF', color: '#3730A3', fontWeight: '500' }}>📚 {child.level}</span>}
                              {child.halqa && <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '10px', backgroundColor: '#F3F4F6', color: '#374151', fontWeight: '500' }}>🕌 {child.halqa}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {isDone ? (
                      d.admin_notes && (
                        <div style={{ backgroundColor: '#F3F4F6', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
                          <div style={{ fontWeight: '600', fontSize: '12px', color: '#374151', marginBottom: '4px' }}>📋 {t('reviewerNotes')}</div>
                          <div style={{ fontSize: '13px', color: '#4B5563', whiteSpace: 'pre-wrap' }}>{d.admin_notes}</div>
                          {d.reviewed_by_name && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>{t('by')}: {d.reviewed_by_name}</div>}
                        </div>
                      )
                    ) : (
                      <div style={{ marginBottom: '10px' }}>
                        <div style={{ fontWeight: '600', fontSize: '12px', color: '#374151', marginBottom: '5px' }}>📝 {t('adminNotes') || 'Notes administrateur'}</div>
                        <textarea value={inscriptionAdminNotes} onChange={e => setInscriptionAdminNotes(e.target.value)}
                          placeholder={t('adminNotes') || 'Notes administrateur...'}
                          style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', minHeight: '60px', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                    )}
                    {d.status === 'approved' && d.request_type === 'parent' && !hasLinkedUser && (
                      <button onClick={() => {
                        setInscriptionToLink(d.id);
                        setNewParent((prev: any) => ({
                          ...prev,
                          fullName: d.full_name || '',
                          phone: d.phone || '',
                          email: d.email || '',
                          address: d.address || '',
                          notes: d.notes || '',
                          gender: (d.gender === 'Male' || d.gender === 'Female') ? d.gender : 'Male',
                        }));
                        setShowAddParentForm(true);
                      }}
                        style={{ width: '100%', padding: '9px', backgroundColor: '#059669', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px' }}>
                        👤 {t('registerParent') || 'تسجيل ولي أمر'}
                      </button>
                    )}
                    {canProvision && (
                      <button disabled={inscriptionProvisionLoading} onClick={() => provisionStudents(d.id)}
                        style={{ width: '100%', padding: '9px', backgroundColor: '#7C3AED', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: inscriptionProvisionLoading ? 'not-allowed' : 'pointer', marginBottom: '8px', opacity: inscriptionProvisionLoading ? 0.6 : 1 }}>
                        {inscriptionProvisionLoading ? '⏳' : '👦 ' + (t('registerChildren') || 'تسجيل الأبناء')}
                      </button>
                    )}
                    {!isDone && (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {d.status === 'pending' && (
                          <button disabled={inscriptionActionLoading} onClick={() => updateInscriptionStatus(d.id, 'reviewed')}
                            style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer', backgroundColor: '#E0E7FF', color: '#3730A3' }}>
                            🔍 {t('reviewed') || 'Marquer revu'}
                          </button>
                        )}
                        <button disabled={inscriptionActionLoading} onClick={() => updateInscriptionStatus(d.id, 'approved')}
                          style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer', backgroundColor: '#D1FAE5', color: '#065F46' }}>
                          ✅ {t('approved') || 'Approuver'}
                        </button>
                        <button disabled={inscriptionActionLoading} onClick={() => updateInscriptionStatus(d.id, 'rejected')}
                          style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer', backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                          ❌ {t('rejected') || 'Rejeter'}
                        </button>
              </div>
            )}
                  </div>
                );
              };

              const inscriptionHasDetail = !isMobile && !!inscriptionSelected;
              return (
                <div style={{ display: 'flex', gap: '16px', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
                  {/* Carte liste */}
                  <div style={{ flex: inscriptionHasDetail ? 1 : undefined, minWidth: 0, width: inscriptionHasDetail ? undefined : '100%', backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 2px 8px rgba(59,130,246,0.08)', border: '1px solid #E8F0FE', overflow: 'hidden' }}>
                    {/* En-tête coloré */}
                    <div style={{ background: 'linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)', padding: '18px 20px 16px', position: 'relative' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                        <div style={{ textAlign: 'center' }}>
                          <h2 style={{ fontSize: '17px', fontWeight: '700', color: '#ffffff', margin: 0, letterSpacing: '0.01em' }}>
                            {t('inscriptionRequests') || 'Demandes d\'inscription'}
                          </h2>
                          {inscriptionItems.length > 0 && (
                            <div style={{ marginTop: '4px', display: 'inline-flex', alignItems: 'center', gap: '5px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '20px', padding: '2px 10px' }}>
                              <span style={{ fontSize: '12px', fontWeight: '600', color: '#ffffff' }}>{inscriptionItems.length}</span>
                              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)' }}>{t('requests')}</span>
                            </div>
                          )}
                        </div>
                        <button onClick={() => loadInscriptionRequests({ search: inscriptionSearch, status: inscriptionFilterStatus })} disabled={inscriptionLoading}
                          title={t('refresh') || 'Actualiser'}
                          style={{ position: 'absolute', right: 0, background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '50%', width: '34px', height: '34px', cursor: inscriptionLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: inscriptionLoading ? 0.5 : 1, transition: 'background 0.2s' }}>
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M13.5 8A5.5 5.5 0 1 1 8 2.5c1.8 0 3.4.87 4.4 2.2L11 6h3.5V2.5L13.1 3.9A6.5 6.5 0 1 0 14.5 8h-1Z" fill="#ffffff"/></svg>
                        </button>
                      </div>
                    </div>
                    {/* Filtres */}
                    <div style={{ padding: '14px 16px 0', backgroundColor: '#F8FAFF' }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                        <div style={{ position: 'relative', flex: 2, minWidth: '140px' }}>
                          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', pointerEvents: 'none' }}>🔍</span>
                          <input value={inscriptionSearch} onChange={e => setInscriptionSearch(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') loadInscriptionRequests({ search: inscriptionSearch, status: inscriptionFilterStatus }); }}
                            placeholder={t('search') || 'Rechercher...'}
                            style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1px solid #DBEAFE', borderRadius: '10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fff', color: '#374151' }} />
                        </div>
                        <select value={inscriptionFilterStatus} onChange={e => { setInscriptionFilterStatus(e.target.value); loadInscriptionRequests({ search: inscriptionSearch, status: e.target.value }); }}
                          style={{ padding: '8px 10px', border: '1px solid #DBEAFE', borderRadius: '10px', fontSize: '12px', outline: 'none', flex: 1, minWidth: '120px', backgroundColor: '#fff', color: '#374151' }}>
                          <option value="">{t('allStatuses') || 'Tous les statuts'}</option>
                          <option value="pending">{t('pending') || 'En attente'}</option>
                          <option value="reviewed">{t('reviewed') || 'Examiné'}</option>
                          <option value="approved">{t('approved') || 'Approuvé'}</option>
                          <option value="rejected">{t('rejected') || 'Rejeté'}</option>
                          <option value="registered">{t('registered') || 'Inscrit'}</option>
                        </select>
                        <select value={inscriptionFilterSchoolYear} onChange={e => { setInscriptionFilterSchoolYear(e.target.value); loadInscriptionRequests({ search: inscriptionSearch, status: inscriptionFilterStatus, school_year_id: e.target.value as any }); }}
                          style={{ padding: '8px 10px', border: '1px solid #DBEAFE', borderRadius: '10px', fontSize: '12px', outline: 'none', flex: 1, minWidth: '130px', backgroundColor: '#fff', color: '#374151' }}>
                          <option value="">{t('allSchoolYears') || 'Toutes les années'}</option>
                          {years.map(y => (
                            <option key={y.id} value={String(y.id)}>{y.label}{y.is_current ? ` ★` : ''}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Liste */}
                    <div style={{ padding: '0 16px 16px', backgroundColor: '#F8FAFF' }}>
                    {inscriptionLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        <div style={{ fontSize: '28px', marginBottom: '8px' }}>⏳</div>
                        <div style={{ fontSize: '13px' }}>{t('loading') || 'Chargement...'}</div>
                      </div>
                    ) : inscriptionItems.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF', fontSize: '14px' }}>
                        <div style={{ fontSize: '32px', marginBottom: '10px' }}>📭</div>
                        <div>{t('noData') || 'Aucune demande'}</div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', ...(!isMobile ? { maxHeight: '560px', overflowY: 'auto' } : {}) }}>
                        {inscriptionItems.map((item: any) => (
                          <div key={item.id}>
                            {/* Ligne item */}
                            <div onClick={() => openInscriptionDetail(item)}
                              style={{ border: `1.5px solid ${inscriptionSelected?.id === item.id ? '#3B82F6' : '#E8F0FE'}`, borderRadius: inscriptionSelected?.id === item.id && isMobile ? '10px 10px 0 0' : '10px', padding: '11px 13px', cursor: 'pointer', backgroundColor: inscriptionSelected?.id === item.id ? '#EFF6FF' : '#ffffff', transition: 'all 0.15s', boxShadow: inscriptionSelected?.id === item.id ? '0 0 0 3px rgba(59,130,246,0.1)' : '0 1px 3px rgba(0,0,0,0.04)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: item.request_type === 'parent' ? '#DBEAFE' : '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', flexShrink: 0 }}>
                                    {item.request_type === 'parent' ? '👨‍👧' : '📚'}
                                  </div>
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.full_name}</div>
                                    <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                                      {t('parent')}
                                      {item.phone && ` • ${item.phone}`}
                                      {item.school_year_label && <span style={{ marginLeft: '6px', backgroundColor: '#EFF6FF', color: '#3B82F6', borderRadius: '8px', padding: '1px 6px', fontSize: '10px', fontWeight: '600' }}>📅 {item.school_year_label}</span>}
                                    </div>
                                  </div>
                                </div>
                                <span style={{ padding: '3px 9px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0,
                                  backgroundColor: item.status === 'pending' ? '#FEF3C7' : item.status === 'approved' ? '#D1FAE5' : item.status === 'rejected' ? '#FEE2E2' : item.status === 'registered' ? '#DBEAFE' : '#E0E7FF',
                                  color: item.status === 'pending' ? '#92400E' : item.status === 'approved' ? '#065F46' : item.status === 'rejected' ? '#991B1B' : item.status === 'registered' ? '#1D4ED8' : '#3730A3'
                                }}>{item.status === 'pending' ? t('pending') : item.status === 'approved' ? t('approved') : item.status === 'rejected' ? t('rejected') : item.status === 'registered' ? t('registered') : t('reviewed')}</span>
                              </div>
                            </div>
                            {/* Détail inline — mobile uniquement */}
                            {isMobile && inscriptionSelected?.id === item.id && (
                              <div style={{ border: '1px solid #3B82F6', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '14px', backgroundColor: 'white' }}>
                                {inscriptionDetailLoading
                                  ? <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>⏳</div>
                                  : renderInscriptionDetail(inscriptionDetail || inscriptionSelected)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    </div>{/* fin wrapper liste */}
                  </div>

                  {/* Panneau détail — desktop uniquement */}
                  {!isMobile && inscriptionSelected && (
                    <div style={{ flex: 1, backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 2px 8px rgba(59,130,246,0.08)', border: '1px solid #E8F0FE', overflow: 'hidden' }}>
                      <div style={{ background: 'linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)', padding: '14px 18px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#fff' }}>
                          {inscriptionDetail?.full_name || inscriptionSelected?.full_name || (t('details'))}
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', marginTop: '2px' }}>
                          👨‍👧 {t('parent')}
                        </div>
                      </div>
                      <div style={{ padding: '16px' }}>
                        {inscriptionDetailLoading
                          ? <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>⏳</div>
                          : renderInscriptionDetail(inscriptionDetail || inscriptionSelected)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Onglet Messages de contact */}
            {communicationTab === 'contact-messages' && (() => {
              const isMobile = windowWidth < 1024;

              // Rendu du détail d'un message (réutilisé inline mobile + panneau desktop)
              const renderContactDetail = (d: any) => {
                if (!d) return null;
                const isArchived = d.status === 'archived';
                return (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>{d.full_name}</div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '4px', flexWrap: 'wrap', fontSize: '12px', color: '#6B7280' }}>
                          {d.phone && <span>📞 {d.phone}</span>}
                          {d.email && <span>✉️ {d.email}</span>}
                          {d.created_at && <span>📅 {formatShortDateInTz(new Date(d.created_at), timezone, dateLocale)}</span>}
                        </div>
                      </div>
                      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                        backgroundColor: d.status === 'new' ? '#D1FAE5' : d.status === 'replied' ? '#DBEAFE' : d.status === 'archived' ? '#F3F4F6' : '#FEF3C7',
                        color: d.status === 'new' ? '#065F46' : d.status === 'replied' ? '#1D4ED8' : d.status === 'archived' ? '#6B7280' : '#92400E'
                      }}>{d.status === 'new' ? t('statusNew') : d.status === 'read' ? t('statusRead') : d.status === 'replied' ? t('statusReplied') : d.status === 'archived' ? t('statusArchived') : d.status}</span>
                    </div>
                    {d.subject && <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151', marginBottom: '8px' }}>{d.subject}</div>}
                    <div style={{ backgroundColor: '#F9FAFB', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                      {d.message}
                    </div>
                    {d.reply_message && (
                      <div style={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' }}>
                        <div style={{ fontWeight: '600', fontSize: '12px', color: '#065F46', marginBottom: '4px' }}>↩ الرد المُرسل</div>
                        <div style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>{d.reply_message}</div>
                        {d.replied_by_name && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>بقلم: {d.replied_by_name}</div>}
                      </div>
                    )}
                    {!isArchived && (
                      <div>
                        {d.status === 'replied' ? (
                          <button disabled={contactActionLoading} onClick={() => updateContactStatusFn(d.id, 'archived')}
                            style={{ width: '100%', padding: '9px', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                            🗄 {t('archive') || 'Archiver le message'}
                          </button>
                        ) : !contactShowReply ? (
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button onClick={() => setContactShowReply(true)}
                              style={{ padding: '8px 16px', backgroundColor: '#DBEAFE', color: '#1D4ED8', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                              ↩ {t('reply') || 'Répondre'}
                            </button>
                            <button disabled={contactActionLoading} onClick={() => updateContactStatusFn(d.id, 'archived')}
                              style={{ padding: '8px 16px', backgroundColor: '#F3F4F6', color: '#6B7280', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                              🗄 {t('archive') || 'Archiver'}
                            </button>
                          </div>
                        ) : (
                          <div>
                            <textarea value={contactReplyText} onChange={e => setContactReplyText(e.target.value)}
                              placeholder={t('writeReply') || 'Votre réponse...'}
                              style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', minHeight: '90px', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }} />
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                              <button onClick={() => setContactShowReply(false)}
                                style={{ padding: '8px 14px', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                                {t('cancel') || 'Annuler'}
                              </button>
                              <button disabled={contactActionLoading || !contactReplyText.trim()}
                                onClick={() => { if (contactReplyText.trim()) { updateContactStatusFn(d.id, 'replied', contactReplyText.trim()); setContactShowReply(false); setContactReplyText(''); } }}
                                style={{ padding: '8px 16px', backgroundColor: '#10B981', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: contactReplyText.trim() ? 'pointer' : 'not-allowed', opacity: contactReplyText.trim() ? 1 : 0.5 }}>
                                ✉ {t('send') || 'Envoyer'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              const contactHasDetail = !isMobile && !!contactSelected;
              return (
                <div style={{ display: 'flex', gap: '16px', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
                  {/* Carte liste */}
                  <div style={{ flex: contactHasDetail ? 1 : undefined, minWidth: 0, width: contactHasDetail ? undefined : '100%', backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                      <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#065F46', margin: 0 }}>
                        {t('contactMessages') || 'Messages de contact'} {contactItems.length > 0 && <span style={{ fontSize: '13px', fontWeight: '400', color: '#6B7280' }}>({contactItems.length})</span>}
                      </h2>
                      <button onClick={() => loadContactRequests({ search: contactSearch, status: contactFilterStatus })} disabled={contactLoading}
                        title={t('refresh') || 'Actualiser'}
                        style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '50%', width: '32px', height: '32px', cursor: contactLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: contactLoading ? 0.5 : 1 }}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M13.5 8A5.5 5.5 0 1 1 8 2.5c1.8 0 3.4.87 4.4 2.2L11 6h3.5V2.5L13.1 3.9A6.5 6.5 0 1 0 14.5 8h-1Z" fill="#3B82F6"/></svg>
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <input value={contactSearch} onChange={e => setContactSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') loadContactRequests({ search: contactSearch, status: contactFilterStatus }); }}
                        placeholder={t('search') || 'Rechercher...'}
                        style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', outline: 'none', flex: 2, minWidth: '120px', boxSizing: 'border-box' }} />
                      <select value={contactFilterStatus} onChange={e => { setContactFilterStatus(e.target.value); loadContactRequests({ search: contactSearch, status: e.target.value }); }}
                        style={{ padding: '7px 8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '12px', outline: 'none', flex: 1, minWidth: '100px' }}>
                        <option value="">{t('all')}</option>
                        <option value="new">{t('statusNew')}</option>
                        <option value="read">{t('statusRead')}</option>
                        <option value="replied">{t('statusReplied')}</option>
                        <option value="archived">{t('statusArchived')}</option>
                      </select>
                    </div>
                    {contactLoading ? (
                      <div style={{ textAlign: 'center', padding: '30px', color: '#6B7280' }}>⏳</div>
                    ) : contactItems.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '30px', color: '#6B7280', fontSize: '14px' }}>📭 {t('noData') || 'Aucun message'}</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', ...(!isMobile ? { maxHeight: '600px', overflowY: 'auto' } : {}) }}>
                        {contactItems.map((item: any) => (
                          <div key={item.id}>
                            {/* Ligne de l'item */}
                            <div onClick={() => openContactDetail(item)}
                              style={{ border: `1px solid ${contactSelected?.id === item.id ? '#10B981' : '#E5E7EB'}`, borderRadius: contactSelected?.id === item.id && isMobile ? '8px 8px 0 0' : '8px', padding: '10px 12px', cursor: 'pointer',
                                backgroundColor: contactSelected?.id === item.id ? '#ECFDF5' : item.status === 'new' ? '#F0FDF4' : '#FAFAFA', transition: 'all 0.15s' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px' }}>
                                <div style={{ fontWeight: item.status === 'new' ? '700' : '600', fontSize: '14px', color: '#111827' }}>{item.full_name}</div>
                                <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap',
                                  backgroundColor: item.status === 'new' ? '#D1FAE5' : item.status === 'replied' ? '#DBEAFE' : item.status === 'archived' ? '#F3F4F6' : '#FEF3C7',
                                  color: item.status === 'new' ? '#065F46' : item.status === 'replied' ? '#1D4ED8' : item.status === 'archived' ? '#6B7280' : '#92400E'
                                }}>{item.status === 'new' ? t('statusNew') : item.status === 'read' ? t('statusRead') : item.status === 'replied' ? t('statusReplied') : item.status === 'archived' ? t('statusArchived') : item.status}</span>
                              </div>
                              {item.subject && <div style={{ fontSize: '12px', color: '#374151', marginTop: '2px', fontWeight: '500' }}>{item.subject}</div>}
                              <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>{item.message?.substring(0, 80)}{(item.message?.length || 0) > 80 ? '...' : ''}</div>
                            </div>
                            {/* Détail inline — mobile uniquement */}
                            {isMobile && contactSelected?.id === item.id && (
                              <div style={{ border: '1px solid #10B981', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '14px', backgroundColor: 'white' }}>
                                {contactDetailLoading
                                  ? <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>⏳</div>
                                  : renderContactDetail(contactDetail || contactSelected)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Panneau détail — desktop uniquement */}
                  {!isMobile && contactSelected && (
                    <div style={{ flex: 1, backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                      {contactDetailLoading
                        ? <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>⏳</div>
                        : renderContactDetail(contactDetail || contactSelected)}
                    </div>
                  )}
                </div>
              );
            })()}
              </div>
        );

      case 'admins':
        // Masquer la section si pas de permission
        if (!canViewSection('admins')) {
          return (
            <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
              <p>{t('noAccessPermission')}</p>
            </div>
          );
        }
        return (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: windowWidth < 768 ? '0 15px' : windowWidth < 1024 ? '0 20px' : '0 30px'
          }}>

            {/* Contenu selon la sous-section */}
            {adminsSubSection === 'adminsList' ? (
              <div>

            {/* Liste des administrateurs */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: windowWidth < 768 ? '16px' : '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>{t('adminTitle')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb', display: windowWidth < 768 ? 'none' : 'table-cell' }}>{t('username')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb', display: windowWidth < 768 ? 'none' : 'table-cell' }}>{t('gender')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb', display: windowWidth < 768 ? 'none' : 'table-cell' }}>{t('permissions')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb', display: windowWidth < 768 ? 'none' : 'table-cell' }}>{t('createdDate')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>{t('status')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{
                          padding: windowWidth < 768 ? '30px 16px' : '40px 24px',
                          textAlign: 'center',
                          color: '#9ca3af',
                          fontSize: windowWidth < 768 ? '14px' : '16px'
                        }}>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: windowWidth < 768 ? '8px' : '12px'
                          }}>
                            <div style={{ fontSize: windowWidth < 768 ? '36px' : '48px' }}>👨‍💼</div>
                            <div style={{ fontSize: windowWidth < 768 ? '14px' : '16px' }}>{t('noAdminsRegistered')}</div>
                            <div style={{ fontSize: windowWidth < 768 ? '12px' : '14px', color: '#6b7280' }}>
                              اضغط على "إضافة مدير" لإضافة مدير جديد
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      admins.map((admin) => (
                        <tr key={admin.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left' }}>
                            <div>
                              <div dir="auto" style={{ 
                                fontWeight: '600', 
                                color: '#111827', 
                                marginBottom: '4px',
                                fontFamily: 'Cairo, Arial, sans-serif',
                                fontSize: windowWidth < 768 ? '13px' : windowWidth < 1024 ? '14px' : '16px'
                              }}>
                                {cleanText(admin.full_name || admin.fullName || t('notSpecified'))}
                              </div>
                              <div style={{ fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', color: '#6b7280', direction: 'ltr' }}>
                                {admin.phone || t('notSpecified')}
                              </div>
                            </div>
                          </td>
                          <td dir="auto" style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', color: '#374151', fontSize: windowWidth < 768 ? '12px' : '14px', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                            {admin.username || t('notSpecified')}
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', color: '#374151', fontSize: windowWidth < 768 ? '12px' : '14px', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                            {admin.gender === 'Female' ? t('female') : t('male')}
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                              {(() => {
                                // Afficher les permissions telles quelles
                                let permissionsToShow = admin.permissions || [];
                                
                                return permissionsToShow.map((permission: string) => (
                                  <span key={permission} style={{
                                    backgroundColor: '#e5e7eb',
                                    color: '#374151',
                                    padding: windowWidth < 768 ? '2px 6px' : '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: windowWidth < 768 ? '10px' : '12px',
                                    fontWeight: '500'
                                  }}>
                                    {(() => {
                                      const perm = AVAILABLE_PERMISSIONS.find((p: PermissionOption) => p.key === permission);
                                      if (perm) {
                                        return currentLanguage === 'ar' ? perm.labelAr : currentLanguage === 'fr' ? perm.labelFr : perm.labelEn;
                                      }
                                      return permission;
                                    })()}
                                  </span>
                                ));
                              })()}
                            </div>
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', color: '#6b7280', fontSize: windowWidth < 768 ? '11px' : '13px', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                            {formatDate(admin.created_date || admin.createdDate || admin.created_at || admin.createdAt || admin.date_created || admin.dateCreated)}
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left' }}>
                            <span style={GLOBAL_STYLES.statusBadge(admin.status === 'active')}>
                              {getDisplayStatus(admin.status)}
                            </span>
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px' }}>
                            <div style={{ display: 'flex', gap: windowWidth < 768 ? '4px' : '8px', justifyContent: 'center', flexWrap: windowWidth < 768 ? 'wrap' : 'nowrap' }}>
                              {canModify('admins') && (
                                <button
                                  onClick={() => {
                                    handleEditAdmin(admin);
                                  }}
                                  style={{
                                    ...GLOBAL_STYLES.editButton,
                                    padding: windowWidth < 768 ? '6px 10px' : '8px 12px',
                                    fontSize: windowWidth < 768 ? '11px' : '13px'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}>
                                  <EditIcon size={windowWidth < 768 ? 14 : 16} />
                                  <span>{windowWidth < 768 ? '' : 'تعديل'}</span>
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteAdmin(admin.id)}
                                style={{
                                  ...GLOBAL_STYLES.deleteButton,
                                  padding: windowWidth < 768 ? '6px 10px' : '8px 12px',
                                  fontSize: windowWidth < 768 ? '11px' : '13px'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.deleteButtonHover}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.deleteButton.backgroundColor}>
                                <DeleteIcon size={windowWidth < 768 ? 14 : 16} />
                                <span>{windowWidth < 768 ? '' : 'حذف'}</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          // Section الإعدادات (Parameters)
          <div>

            {/* ── Carte : رموز ومفاتيح الإتصال الدولي ── */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: windowWidth < 768 ? '14px 16px' : '16px 24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #e5e7eb',
              borderInlineStart: '4px solid #059669',
              marginBottom: '16px',
              position: 'relative',
              zIndex: countryPickerOpen ? 200 : 1,
              direction: isRTL ? 'rtl' : 'ltr',
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap',
              }}>
                {/* Icône */}
                <div style={{
                  width: '40px', height: '40px', flexShrink: 0,
                  backgroundColor: '#f0fdf4',
                  borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '20px',
                }}>
                  🌍
                </div>

                {/* Texte descriptif */}
                <div style={{ flex: 1, minWidth: '160px' }}>
                  <div style={{
                    fontSize: windowWidth < 768 ? '13px' : '14px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '2px',
                  }}>
                    {currentLanguage === 'ar' ? 'رموز ومفاتيح الإتصال الدولي' : currentLanguage === 'fr' ? 'Codes et indicatifs internationaux' : 'International dialing codes'}
                  </div>
                  <div style={{ fontSize: windowWidth < 768 ? '11px' : '12px', color: '#6b7280' }}>
                    {currentLanguage === 'ar' ? 'رمز الدولة الذي يُستخدم عند إدخال أرقام الهاتف' : currentLanguage === 'fr' ? 'Code pays utilisé lors de la saisie des numéros de téléphone' : 'Country code used when entering phone numbers'}
                  </div>
                </div>

                {/* Sélecteur de pays */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  {/* Bouton déclencheur */}
                  <button
                    onClick={() => { setCountryPickerOpen(v => !v); setCountrySearch(''); }}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '10px',
                      padding: '8px 12px',
                      cursor: savingCountry ? 'wait' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#374151',
                      minWidth: windowWidth < 768 ? '160px' : '200px',
                      opacity: savingCountry ? 0.7 : 1,
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      outline: 'none',
                      direction: 'ltr',
                    }}
                    disabled={savingCountry}
                  >
                    <span style={{ fontSize: '18px' }}>{defaultCountry.flag}</span>
                    <span style={{ fontWeight: '700', color: '#059669', minWidth: '40px' }}>+{defaultCountry.dialCode}</span>
                    <span style={{ flex: 1, color: '#374151', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {defaultCountry.name[currentLanguage as 'ar' | 'fr' | 'en'] || defaultCountry.name.ar}
                    </span>
                    <span style={{ fontSize: '9px', color: '#9ca3af', marginLeft: '4px' }}>{countryPickerOpen ? '▲' : '▼'}</span>
                  </button>

                  {/* Indicateur de sauvegarde */}
                  {savingCountry && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', [isRTL ? 'right' : 'left']: 0, fontSize: '11px', color: '#059669', whiteSpace: 'nowrap' }}>
                      {currentLanguage === 'ar' ? '⏳ جاري الحفظ...' : currentLanguage === 'fr' ? '⏳ Enregistrement...' : '⏳ Saving...'}
                    </div>
                  )}

                  {/* Dropdown */}
                  {countryPickerOpen && (
                    <>
                      <div
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 198 }}
                        onClick={() => { setCountryPickerOpen(false); setCountrySearch(''); }}
                      />
                      <div style={{
                        position: 'absolute',
                        [isRTL ? 'right' : 'left']: 0,
                        top: 'calc(100% + 6px)',
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        width: '300px',
                        maxHeight: '340px',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        zIndex: 199,
                        direction: 'ltr',
                      }}>
                        {/* Barre de recherche */}
                        <div style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #f3f4f6',
                          display: 'flex',
                          flexDirection: isRTL ? 'row-reverse' : 'row',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: '#fafafa',
                        }}>
                          <span style={{ color: '#9ca3af', fontSize: '13px' }}>🔍</span>
                          <input
                            type="text"
                            autoFocus
                            value={countrySearch}
                            onChange={e => setCountrySearch(e.target.value)}
                            placeholder={currentLanguage === 'ar' ? 'بحث...' : currentLanguage === 'fr' ? 'Rechercher...' : 'Search...'}
                            style={{
                              flex: 1,
                              border: 'none',
                              outline: 'none',
                              fontSize: '13px',
                              color: '#111827',
                              backgroundColor: 'transparent',
                              direction: isRTL ? 'rtl' : 'ltr',
                              textAlign: isRTL ? 'right' : 'left',
                            }}
                          />
                          {countrySearch && (
                            <button
                              onClick={() => setCountrySearch('')}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: '18px', lineHeight: 1, padding: 0 }}
                            >×</button>
                          )}
                        </div>

                        {/* Liste des pays */}
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                          {COUNTRIES
                            .filter(c =>
                              !countrySearch.trim() ||
                              c.name.ar.includes(countrySearch) ||
                              c.name.fr.toLowerCase().includes(countrySearch.toLowerCase()) ||
                              c.name.en.toLowerCase().includes(countrySearch.toLowerCase()) ||
                              c.dialCode.includes(countrySearch) ||
                              c.code.toLowerCase().includes(countrySearch.toLowerCase())
                            )
                            .map(c => {
                              const isSelected = c.code === defaultCountry.code;
                              return (
                                <div
                                  key={c.code}
                                  onClick={() => handleDefaultCountryChange(c)}
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '9px 14px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f9fafb',
                                    backgroundColor: isSelected ? '#f0fdf4' : 'transparent',
                                    transition: 'background-color 0.15s',
                                  }}
                                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = isSelected ? '#f0fdf4' : 'transparent'; }}
                                >
                                  <span style={{ fontSize: '18px', flexShrink: 0 }}>{c.flag}</span>
                                  <span style={{ flex: 1, fontSize: '13px', color: isSelected ? '#059669' : '#374151', fontWeight: isSelected ? '600' : '400' }}>
                                    {c.name[currentLanguage as 'ar' | 'fr' | 'en'] || c.name.ar}
                                  </span>
                                  <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '600', minWidth: '42px', textAlign: 'right' }}>+{c.dialCode}</span>
                                  {isSelected && <span style={{ color: '#059669', fontSize: '13px', flexShrink: 0 }}>✓</span>}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <SchoolYearSelector
              labels={{
                schoolYear: t('academicYear'),
                current: t('current'),
                closed: t('closed'),
                newYear: t('schoolYearNew'),
                create: t('schoolYearCreate'),
                cancel: t('schoolYearCancel'),
                creating: t('schoolYearCreating'),
                calendarHint: t('schoolYearCalendarHint'),
                startDate: t('schoolYearStartDate'),
                endDate: t('schoolYearEndDate'),
                closeYear: t('schoolYearClose'),
                closeYearTitle: t('schoolYearCloseTitle'),
                closeYearHelp: t('schoolYearCloseHelp'),
                activateYear: t('schoolYearActivate'),
                confirmClose: t('schoolYearConfirmClose'),
                closing: t('schoolYearClosing'),
                closeError: t('schoolYearCloseError'),
                closeYearNeedNew: t('schoolYearCloseNeedNew'),
              }}
            />

            <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
              {t('parametersGeneralSection')}
            </div>

            {/* Tableau des paramètres généraux */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: windowWidth < 768 ? '16px' : '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>{t('parameterName')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>{t('parameterValue')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>{t('parameterDescription')}</th>
                      {canModify('admins') && (
                        <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: 'center', fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>{t('actions')}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {loadingParameters ? (
                      <tr>
                        <td colSpan={canModify('admins') ? 4 : 3} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                            <RefreshIcon size={20} animate={true} />
                            <span>{t('loading')}</span>
                          </div>
                        </td>
                      </tr>
                    ) : parameters.length === 0 ? (
                      <tr>
                        <td colSpan={canModify('admins') ? 4 : 3} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                          {t('noParameters')}
                        </td>
                      </tr>
                    ) : (
                      parameters.map((parameter, index) => {
                        // Masqué : géré par le sélecteur d'année scolaire dédié
                        if (parameter.name === 'currentSchoolYearId') return null;

                        return (
                        <tr key={parameter.id || index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : '16px', fontSize: windowWidth < 768 ? '12px' : '14px', color: '#1f2937', fontWeight: '500' }}>
                            {getParameterName[currentLanguage as keyof typeof getParameterName]?.(parameter.name) || parameter.name}
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : '16px', fontSize: windowWidth < 768 ? '12px' : '14px', color: '#4b5563', maxWidth: '300px', wordBreak: 'break-word' }}>
                            {formatParamValue(parameter.name, parameter.value, currentLanguage)}
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : '16px', fontSize: windowWidth < 768 ? '12px' : '14px', color: '#6b7280', maxWidth: '250px', wordBreak: 'break-word' }}>
                            {parameter.description || '-'}
                          </td>
                          {canModify('admins') && (
                            <td style={{ padding: windowWidth < 768 ? '12px 8px' : '16px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                <button
                                  onClick={() => handleEditParameter(parameter)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                    color: '#3b82f6',
                                    border: '1px solid rgba(59, 130, 246, 0.2)',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: windowWidth < 768 ? '11px' : '13px',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                                  }}
                                >
                                  <EditIcon size={windowWidth < 768 ? 12 : 14} />
                                  {windowWidth >= 768 && t('editParameter').split(' ')[0]}
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal d'ajout de paramètre */}
            {showAddParameterForm && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                padding: '20px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: windowWidth < 768 ? '20px' : '32px',
                  maxWidth: '600px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}>
                  <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: windowWidth < 768 ? '20px' : '24px', color: '#1f2937' }}>
                    {t('addParameter')}
                  </h2>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      {t('parameterName')} *
                    </label>
                    <input
                      type="text"
                      value={newParameter.name}
                      onChange={(e) => setNewParameter({ ...newParameter, name: e.target.value })}
                      placeholder={t('parameterNamePlaceholder')}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <div style={{ marginTop: '6px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                      {t('parameterNameNote')}
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      {t('parameterValue')} *
                    </label>
                    <textarea
                      value={newParameter.value}
                      onChange={(e) => setNewParameter({ ...newParameter, value: e.target.value })}
                      placeholder={t('parameterValuePlaceholder')}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      {t('parameterDescription')}
                    </label>
                    <textarea
                      value={newParameter.description}
                      onChange={(e) => setNewParameter({ ...newParameter, description: e.target.value })}
                      placeholder={t('parameterDescriptionPlaceholder')}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={resetParameterForm}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    >
                      {t('cancel')}
                    </button>
                    <button
                      onClick={handleCreateParameter}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                    >
                      حفظ
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de modification de paramètre */}
            {showEditParameterForm && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                padding: '20px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: windowWidth < 768 ? '20px' : '32px',
                  maxWidth: '600px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}>
                  <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: windowWidth < 768 ? '20px' : '24px', color: '#1f2937' }}>
                    {t('editParameter')}
                  </h2>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      {t('parameterName')}
                    </label>
                    <input
                      type="text"
                      value={getParameterName[currentLanguage as keyof typeof getParameterName]?.(newParameter.name) || newParameter.name}
                      disabled
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        cursor: 'not-allowed'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      {t('parameterValue')} *
                    </label>
                    {newParameter.name === 'default_country' ? (
                      <select
                        value={newParameter.value}
                        onChange={(e) => setNewParameter({ ...newParameter, value: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          cursor: 'pointer'
                        }}
                      >
                        {newParameter.value && !COUNTRIES.some(c => c.code === newParameter.value) && (
                          <option value={newParameter.value}>{newParameter.value}</option>
                        )}
                        {COUNTRIES.filter((c, i) => COUNTRIES.findIndex(x => x.code === c.code) === i).map(c => (
                          <option key={c.code} value={c.code}>
                            {c.flag} {c.name[currentLanguage as 'ar' | 'fr' | 'en'] || c.name.ar} (+{c.dialCode})
                          </option>
                        ))}
                      </select>
                    ) : newParameter.name === 'default_timezone' ? (
                      <select
                        value={newParameter.value}
                        onChange={(e) => setNewParameter({ ...newParameter, value: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          fontFamily: 'monospace',
                          cursor: 'pointer'
                        }}
                        dir="ltr"
                      >
                        {getTimezoneOptions(newParameter.value).map(tz => (
                          <option key={tz} value={tz}>{tz}</option>
                        ))}
                      </select>
                    ) : (
                      <textarea
                        value={newParameter.value}
                        onChange={(e) => setNewParameter({ ...newParameter, value: e.target.value })}
                        placeholder={t('parameterValuePlaceholder')}
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          resize: 'vertical'
                        }}
                      />
                    )}
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      {t('parameterDescription')}
                    </label>
                    <textarea
                      value={newParameter.description}
                      onChange={(e) => setNewParameter({ ...newParameter, description: e.target.value })}
                      placeholder={t('parameterDescriptionPlaceholder')}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={resetParameterForm}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    >
                      {t('cancel')}
                    </button>
                    <button
                      onClick={handleUpdateParameter}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                    >
                      تحديث
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );

      case 'financial':
        return (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: windowWidth < 768 ? '0 15px' : windowWidth < 1024 ? '0 20px' : '0 30px'
          }}>
            {/* Onglets pour basculer entre المداخيل و المصاريف et رسوم الطلاب */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '20px',
              borderBottom: '2px solid #e5e7eb',
              paddingBottom: '0'
            }}>
              <button
                onClick={() => setFinancialSubSection('expenses')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: financialSubSection === 'expenses' ? '#5e35b1' : 'transparent',
                  color: financialSubSection === 'expenses' ? 'white' : '#6b7280',
                  border: 'none',
                  borderBottom: financialSubSection === 'expenses' ? '3px solid #4527a0' : '3px solid transparent',
                  borderRadius: '8px 8px 0 0',
                  fontSize: '15px',
                  fontWeight: financialSubSection === 'expenses' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  marginBottom: '-2px'
                }}
                onMouseOver={(e) => {
                  if (financialSubSection !== 'expenses') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseOut={(e) => {
                  if (financialSubSection !== 'expenses') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {t('expenses')}
              </button>
              <button
                onClick={() => setFinancialSubSection('studentFees')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: financialSubSection === 'studentFees' ? '#5e35b1' : 'transparent',
                  color: financialSubSection === 'studentFees' ? 'white' : '#6b7280',
                  border: 'none',
                  borderBottom: financialSubSection === 'studentFees' ? '3px solid #4527a0' : '3px solid transparent',
                  borderRadius: '8px 8px 0 0',
                  fontSize: '15px',
                  fontWeight: financialSubSection === 'studentFees' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  marginBottom: '-2px'
                }}
                onMouseOver={(e) => {
                  if (financialSubSection !== 'studentFees') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseOut={(e) => {
                  if (financialSubSection !== 'studentFees') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {t('studentFees')}
              </button>
              <button
                onClick={() => setFinancialSubSection('teacherSalaries')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: financialSubSection === 'teacherSalaries' ? '#5e35b1' : 'transparent',
                  color: financialSubSection === 'teacherSalaries' ? 'white' : '#6b7280',
                  border: 'none',
                  borderBottom: financialSubSection === 'teacherSalaries' ? '3px solid #4527a0' : '3px solid transparent',
                  borderRadius: '8px 8px 0 0',
                  fontSize: '15px',
                  fontWeight: financialSubSection === 'teacherSalaries' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  marginBottom: '-2px'
                }}
                onMouseOver={(e) => {
                  if (financialSubSection !== 'teacherSalaries') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseOut={(e) => {
                  if (financialSubSection !== 'teacherSalaries') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {t('teacherSalaries')}
              </button>
            </div>

            {financialSubSection === 'expenses' ? (
              <>
                <div style={{
                  display: 'flex',
                  flexDirection: windowWidth < 768 ? 'column' : 'row',
                  justifyContent: windowWidth < 768 ? 'flex-start' : 'space-between',
                  alignItems: windowWidth < 768 ? 'flex-start' : 'center',
                  marginBottom: windowWidth < 768 ? '20px' : '30px',
                  gap: windowWidth < 768 ? '16px' : '0'
                }}>
                  <h1 style={{ 
                    color: '#424242', 
                    fontSize: windowWidth < 768 ? '22px' : windowWidth < 1024 ? '24px' : '28px',
                    fontWeight: '600',
                    margin: 0
                  }}>{t('expenses')}</h1>
                  <div style={{ display: 'flex', gap: windowWidth < 768 ? '8px' : '12px', flexWrap: 'wrap', width: windowWidth < 768 ? '100%' : 'auto' }}>
                    <button
                      onClick={async () => {
                        setLoadingFinancialRecords(true);
                        try {
                          const response = await apiService.getFinancialRecords({});
                          if (response.success) {
                            setFinancialRecords(response.data || []);
                          } else {
                            alert(response.error || 'Erreur lors du chargement des enregistrements financiers');
                          }
                        } catch (error) {
                          console.error('Erreur:', error);
                          alert('Erreur lors du chargement des enregistrements financiers');
                        } finally {
                          setLoadingFinancialRecords(false);
                        }
                      }}
                      disabled={loadingFinancialRecords}
                      style={{
                        backgroundColor: loadingFinancialRecords ? '#9ca3af' : '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: windowWidth < 768 ? '10px 14px' : '12px 24px',
                        fontSize: windowWidth < 768 ? '13px' : '14px',
                        cursor: loadingFinancialRecords ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flex: windowWidth < 768 ? '1' : 'none',
                        justifyContent: 'center'
                      }}
                    >
                      <RefreshIcon size={windowWidth < 768 ? 14 : 16} style={{ marginLeft: '4px' }} />
                      {t('refresh')}
                    </button>
                    {canEditFinances && (
                      <button
                        onClick={() => {
                          setNewFinancialRecord({
                            category_name: '',
                            category_type: 'revenue',
                            display_order: 1,
                            september: '',
                            october: '',
                            november: '',
                            december: '',
                            january: '',
                            february: '',
                            march: '',
                            april: '',
                            may: '',
                            june: '',
                            july: '',
                            august: ''
                          });
                          setShowAddFinancialRecordForm(true);
                        }}
                        style={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: windowWidth < 768 ? '10px 14px' : '12px 24px',
                          fontSize: windowWidth < 768 ? '13px' : '14px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flex: windowWidth < 768 ? '1' : 'none',
                          justifyContent: 'center',
                          fontWeight: '600'
                        }}
                      >
                        {'➕ '}{t('addFinancialRecord') || 'إضافة مدخول أو مصروف'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Tableau des enregistrements financiers */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.3s ease'
                }}>
                  {loadingFinancialRecords ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                      <div style={{ fontSize: '18px', marginBottom: '8px' }}>⏳</div>
                      <div>{t('loading')}</div>
                    </div>
                  ) : financialRecords.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#9e9e9e' }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>💰</div>
                      <div style={{ fontSize: '18px' }}>{t('noRecords') || 'لا توجد سجلات'}</div>
                      <div style={{ fontSize: '14px', marginTop: '8px' }}>{t('noFinancialRecords')}</div>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        minWidth: '1200px'
                      }}>
                        <thead style={{ 
                          backgroundColor: '#f9fafb',
                          borderBottom: '2px solid #e5e7eb'
                        }}>
                          <tr>
                            <th style={{
                              padding: windowWidth < 768 ? '12px 6px' : windowWidth < 1024 ? '14px 8px' : '16px 10px',
                              textAlign: 'center',
                              fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '13px',
                              fontWeight: '700',
                              color: '#374151',
                              letterSpacing: '0.5px',
                              borderBottom: '2px solid #e5e7eb',
                              width: windowWidth < 768 ? '50px' : '60px',
                              position: 'sticky',
                              left: isRTL ? 'auto' : 0,
                              right: isRTL ? 0 : 'auto',
                              backgroundColor: '#f9fafb',
                              zIndex: 15,
                              boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                            }}>
                              {t('type') || 'Type'}
                            </th>
                            <th style={{
                              padding: windowWidth < 768 ? '12px 10px' : windowWidth < 1024 ? '14px 14px' : '16px 18px',
                              textAlign: isRTL ? 'right' : 'left',
                              fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '13px',
                              fontWeight: '700',
                              color: '#374151',
                              letterSpacing: '0.5px',
                              position: 'sticky',
                              left: isRTL ? 'auto' : (windowWidth < 768 ? '50px' : '60px'),
                              right: isRTL ? (windowWidth < 768 ? '50px' : '60px') : 'auto',
                              backgroundColor: '#f9fafb',
                              zIndex: 14,
                              borderBottom: '2px solid #e5e7eb',
                              minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                              boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                            }}>
                              {t('category') || 'الفئة'}
                            </th>
                            {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => (
                              <th key={month} style={{
                                padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 10px' : '16px 12px',
                                textAlign: 'center',
                                fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                                fontWeight: '700',
                                color: '#374151',
                                letterSpacing: '0.5px',
                                minWidth: windowWidth < 768 ? '65px' : '75px',
                                borderBottom: '2px solid #e5e7eb'
                              }}>
                                {t(month)}
                              </th>
                            ))}
                            {canEditFinances && (
                              <th style={{
                                padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 10px' : '16px 12px',
                                textAlign: 'center',
                                fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                                fontWeight: '700',
                                color: '#374151',
                                letterSpacing: '0.5px',
                                borderBottom: '2px solid #e5e7eb',
                                position: 'sticky',
                                right: 0,
                                backgroundColor: '#f9fafb',
                                zIndex: 10
                              }}>
                                {t('actions') || 'الإجراءات'}
                              </th>
                            )}
                          </tr>
                        </thead>
                        {/* Ligne de différence (Recettes - Dépenses) */}
                        <tbody>
                          <tr style={{
                            backgroundColor: '#f3f4f6',
                            borderBottom: '2px solid #d1d5db',
                            fontWeight: '700'
                          }}>
                            <td colSpan={2} style={{
                              padding: windowWidth < 768 ? '6px 10px' : windowWidth < 1024 ? '7px 14px' : '8px 18px',
                              fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                              fontWeight: '700',
                              color: '#374151',
                              textAlign: isRTL ? 'right' : 'left'
                            }}>
                              {t('difference') || 'الميزانية(المداخيل - المصاريف)'}
                            </td>
                            {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => {
                              const incomeAmount = incomeTotals[month] || 0;
                              const expenseAmount = expenseTotals[month] || 0;
                              const difference = incomeAmount - expenseAmount;
                              const isPositive = difference >= 0;
                              return (
                                <td key={month} style={{
                                  padding: windowWidth < 768 ? '6px 8px' : windowWidth < 1024 ? '7px 10px' : '8px 12px',
                                  textAlign: 'center',
                                  fontSize: windowWidth < 768 ? '12px' : '13px',
                                  color: isPositive ? '#047857' : '#dc2626',
                                  fontWeight: '700'
                                }}>
                                  {difference !== 0 ? parseFloat(difference.toString()).toFixed(2) : '-'}
                                </td>
                              );
                            })}
                            {/* Pas de colonne actions pour la ligne de différence */}
                            {canEditFinances && (
                              <td style={{
                                padding: windowWidth < 768 ? '6px 8px' : windowWidth < 1024 ? '7px 10px' : '8px 12px',
                                textAlign: 'center',
                                position: 'sticky',
                                right: 0,
                                backgroundColor: '#f3f4f6',
                                zIndex: 5
                              }}>
                                -
                              </td>
                            )}
                          </tr>
                          {/* Section المداخيل (Recettes) */}
                          <>
                            {/* Ligne des totaux des رسوم الطلاب (en premier) */}
                            <tr style={{
                              borderTop: '1px solid #e5e7eb',
                              borderBottom: '1px solid #e5e7eb',
                              backgroundColor: '#f9fafb',
                              fontWeight: '600',
                              borderLeft: '2px solid #d1fae5'
                            }}>
                              {/* Colonne "Type" avec rowSpan pour couvrir toutes les lignes de recettes */}
                              <td 
                                rowSpan={1 + incomeRecords.filter(r => r.category_name !== 'رسوم الطلاب').length + 1}
                                style={{
                                  padding: windowWidth < 768 ? '12px 4px' : windowWidth < 1024 ? '14px 6px' : '16px 8px',
                                  textAlign: 'center',
                                  verticalAlign: 'middle',
                                  fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                  fontWeight: '600',
                                  color: '#047857',
                                  backgroundColor: '#f0fdf4',
                                  borderRight: '1px solid #e5e7eb',
                                  writingMode: 'vertical-rl',
                                  textOrientation: 'mixed',
                                  width: windowWidth < 768 ? '50px' : '60px',
                                  position: 'sticky',
                                  left: isRTL ? 'auto' : 0,
                                  right: isRTL ? 0 : 'auto',
                                  zIndex: 12,
                                  boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                {'Recettes'}
                              </td>
                              <td style={{
                                padding: windowWidth < 768 ? '6px 10px' : windowWidth < 1024 ? '7px 14px' : '8px 18px',
                                position: 'sticky',
                                left: isRTL ? 'auto' : (windowWidth < 768 ? '50px' : '60px'),
                                right: isRTL ? (windowWidth < 768 ? '50px' : '60px') : 'auto',
                                backgroundColor: '#f9fafb',
                                zIndex: 11,
                                minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                                boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                              }}>
                                <div style={{
                                  fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                  fontWeight: '600',
                                  color: '#374151'
                                }}>
                                  {t('studentFees')}
                                </div>
                              </td>
                              {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => {
                                const amount = studentFeesRecord ? (parseFloat(studentFeesRecord[month] || 0) || 0) : 0;
                                return (
                                  <td key={month} style={{
                                    padding: windowWidth < 768 ? '6px 8px' : windowWidth < 1024 ? '7px 10px' : '8px 12px',
                                    textAlign: 'center',
                                    fontSize: windowWidth < 768 ? '12px' : '13px',
                                    color: amount > 0 ? '#047857' : '#9ca3af',
                                    fontWeight: '600'
                                  }}>
                                    {amount > 0 ? parseFloat(amount.toString()).toFixed(2) : '-'}
                                  </td>
                                );
                              })}
                              {/* Pas de bouton تعديل pour رسوم الطلاب */}
                              <td style={{
                                padding: windowWidth < 768 ? '6px 8px' : windowWidth < 1024 ? '7px 10px' : '8px 12px',
                                textAlign: 'center',
                                position: 'sticky',
                                right: 0,
                                backgroundColor: '#f9fafb',
                                zIndex: 5
                              }}>
                                -
                              </td>
                            </tr>
                            {/* Lignes des recettes (exclure رسوم الطلاب car déjà affiché en haut) */}
                            {incomeRecords.filter(record => record.category_name !== 'رسوم الطلاب').map((record, index) => {
                              const isRevenue = record.category_type === 'revenue';
                              const isTotalLine = record.category_type === 'total_revenue';
                              const isCalculated = isCalculatedRecord(record);
                              return (
                                <tr 
                                  key={record.id} 
                                  style={{
                                    borderBottom: '1px solid #e5e7eb',
                                    backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                    borderLeft: isRevenue ? '2px solid #d1fae5' : 'none',
                                    transition: 'all 0.2s ease',
                                    cursor: isTotalLine ? 'default' : 'pointer'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isTotalLine) {
                                      e.currentTarget.style.backgroundColor = '#f9fafb';
                                      e.currentTarget.style.transform = 'scale(1.001)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isTotalLine) {
                                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#fafafa';
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }
                                  }}
                                >
                                  <td style={{
                                    padding: windowWidth < 768 ? '6px 10px' : windowWidth < 1024 ? '7px 14px' : '8px 18px',
                                    position: 'sticky',
                                    left: isRTL ? 'auto' : (windowWidth < 768 ? '50px' : '60px'),
                                    right: isRTL ? (windowWidth < 768 ? '50px' : '60px') : 'auto',
                                    backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                    zIndex: 10,
                                    transition: 'background-color 0.2s ease',
                                    minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                                    boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                                  }}>
                                    <div style={{
                                      fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                      fontWeight: '500',
                                      color: '#424242'
                                    }}>
                                      {record.category_name || 'غير محدد'}
                                    </div>
                                  </td>
                                  {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => {
                                    const amount = record[month] || 0;
                                    return (
                                      <td key={month} style={{
                                        padding: windowWidth < 768 ? '6px 8px' : windowWidth < 1024 ? '7px 10px' : '8px 12px',
                                        textAlign: 'center',
                                        fontSize: windowWidth < 768 ? '12px' : '13px',
                                        color: amount > 0 ? '#047857' : '#9ca3af',
                                        fontWeight: amount > 0 ? '600' : '400',
                                        transition: 'color 0.2s ease'
                                      }}>
                                        {amount > 0 ? parseFloat(amount).toFixed(2) : '-'}
                                      </td>
                                    );
                                  })}
                                  {canEditFinances && (
                                    <td style={{
                                      padding: windowWidth < 768 ? '6px 8px' : windowWidth < 1024 ? '7px 10px' : '8px 12px',
                                      textAlign: 'center',
                                      position: 'sticky',
                                      right: 0,
                                      backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                      zIndex: 5
                                    }}>
                                      {/* Masquer le bouton modifier pour les lignes de totaux et calculées */}
                                      {!isTotalLine && !isCalculated ? (
                                        <button
                                          onClick={() => {
                                            setEditingFinancialRecord(record);
                                            setEditingFinancialRecordValues({
                                              category_name: record.category_name || '',
                                              category_type: record.category_type || 'revenue',
                                              display_order: record.display_order || 1,
                                              september: record.september?.toString() || '',
                                              october: record.october?.toString() || '',
                                              november: record.november?.toString() || '',
                                              december: record.december?.toString() || '',
                                              january: record.january?.toString() || '',
                                              february: record.february?.toString() || '',
                                              march: record.march?.toString() || '',
                                              april: record.april?.toString() || '',
                                              may: record.may?.toString() || '',
                                              june: record.june?.toString() || '',
                                              july: record.july?.toString() || '',
                                              august: record.august?.toString() || ''
                                            });
                                            setShowEditFinancialRecordForm(true);
                                          }}
                                          style={{
                                            backgroundColor: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '6px 12px',
                                            fontSize: windowWidth < 768 ? '11px' : '12px',
                                            cursor: 'pointer',
                                            fontWeight: '500',
                                            transition: 'background-color 0.2s'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#2563eb';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#3b82f6';
                                          }}
                                        >
                                          {t('edit') || 'تعديل'}
                                        </button>
                                      ) : (
                                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          {/* Sous-total des recettes (incluant رسوم الطلاب) */}
                            <tr style={{
                              borderTop: '1px solid #d1fae5',
                              borderBottom: '1px solid #d1fae5',
                              backgroundColor: '#f0fdf4',
                              fontWeight: '700'
                            }}>
                              {/* Pas de <td> pour "Type" car déjà fusionné avec rowSpan de "Recettes" */}
                              <td style={{
                                padding: windowWidth < 768 ? '4px 10px' : windowWidth < 1024 ? '5px 14px' : '6px 18px',
                                position: 'sticky',
                                left: isRTL ? 'auto' : (windowWidth < 768 ? '50px' : '60px'),
                                right: isRTL ? (windowWidth < 768 ? '50px' : '60px') : 'auto',
                                backgroundColor: '#f0fdf4',
                                zIndex: 10,
                                fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                fontWeight: '700',
                                color: '#047857',
                                minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                                boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                              }}>
                                {t('incomeTotal') || 'إجمالي المداخيل'}
                              </td>
                              {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => {
                                const amount = incomeTotals[month] || 0;
                                return (
                                  <td key={month} style={{
                                    padding: windowWidth < 768 ? '4px 8px' : windowWidth < 1024 ? '5px 10px' : '6px 12px',
                                    textAlign: 'center',
                                    fontSize: windowWidth < 768 ? '12px' : '13px',
                                    color: amount > 0 ? '#047857' : '#9ca3af',
                                    fontWeight: '700'
                                  }}>
                                    {amount > 0 ? parseFloat(amount.toString()).toFixed(2) : '-'}
                                  </td>
                                );
                              })}
                              {/* Pas de bouton تعديل pour إجمالي المداخيل */}
                              {canEditFinances && (
                                <td style={{
                                  padding: windowWidth < 768 ? '4px 8px' : windowWidth < 1024 ? '5px 10px' : '6px 12px',
                                  textAlign: 'center',
                                  position: 'sticky',
                                  right: 0,
                                  backgroundColor: '#f0fdf4',
                                  zIndex: 5
                                }}>
                                  -
                                </td>
                              )}
                            </tr>
                          </>

                          {/* Section المصاريف (Dépenses) */}
                          <>
                            {/* Ligne des totaux des رواتب الأساتذة (en premier) */}
                            <tr style={{
                              borderTop: '1px solid #e5e7eb',
                              borderBottom: '1px solid #e5e7eb',
                              backgroundColor: '#f9fafb',
                              fontWeight: '600',
                              borderLeft: '2px solid #fee2e2'
                            }}>
                              {/* Colonne "Type" avec rowSpan pour couvrir toutes les lignes de dépenses */}
                              <td 
                                rowSpan={1 + expenseRecords.filter(r => r.category_name !== 'رواتب الأساتذة').length + 1}
                                style={{
                                  padding: windowWidth < 768 ? '12px 4px' : windowWidth < 1024 ? '14px 6px' : '16px 8px',
                                  textAlign: 'center',
                                  verticalAlign: 'middle',
                                  fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                  fontWeight: '600',
                                  color: '#991b1b',
                                  backgroundColor: '#fef2f2',
                                  borderRight: '1px solid #e5e7eb',
                                  writingMode: 'vertical-rl',
                                  textOrientation: 'mixed',
                                  width: windowWidth < 768 ? '50px' : '60px',
                                  position: 'sticky',
                                  left: isRTL ? 'auto' : 0,
                                  right: isRTL ? 0 : 'auto',
                                  zIndex: 12,
                                  boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                {'Dépenses'}
                              </td>
                              <td style={{
                                padding: windowWidth < 768 ? '6px 10px' : windowWidth < 1024 ? '7px 14px' : '8px 18px',
                                position: 'sticky',
                                left: isRTL ? 'auto' : (windowWidth < 768 ? '50px' : '60px'),
                                right: isRTL ? (windowWidth < 768 ? '50px' : '60px') : 'auto',
                                backgroundColor: '#f9fafb',
                                zIndex: 11,
                                minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                                boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                              }}>
                                <div style={{
                                  fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                  fontWeight: '600',
                                  color: '#374151'
                                }}>
                                  {t('teacherSalaries')}
                                </div>
                              </td>
                              {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => {
                                const amount = teacherSalariesRecord ? (parseFloat(teacherSalariesRecord[month] || 0) || 0) : 0;
                                return (
                                  <td key={month} style={{
                                    padding: windowWidth < 768 ? '6px 8px' : windowWidth < 1024 ? '7px 10px' : '8px 12px',
                                    textAlign: 'center',
                                    fontSize: windowWidth < 768 ? '12px' : '13px',
                                    color: amount > 0 ? '#dc2626' : '#9ca3af',
                                    fontWeight: '600'
                                  }}>
                                    {amount > 0 ? parseFloat(amount.toString()).toFixed(2) : '-'}
                                  </td>
                                );
                              })}
                              {/* Pas de bouton تعديل pour رواتب الأساتذة */}
                              <td style={{
                                padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '13px 10px' : '14px 12px',
                                textAlign: 'center',
                                position: 'sticky',
                                right: 0,
                                backgroundColor: '#f9fafb',
                                zIndex: 5
                              }}>
                                -
                              </td>
                            </tr>
                            {/* Lignes des dépenses (exclure رواتب الأساتذة car déjà affiché en haut) */}
                            {expenseRecords.filter(record => record.category_name !== 'رواتب الأساتذة').length > 0 ? (
                              expenseRecords.filter(record => record.category_name !== 'رواتب الأساتذة').map((record, index) => {
                                const isExpense = record.category_type === 'expense';
                                const isTotalLine = record.category_type === 'total_expense';
                                const isCalculated = isCalculatedRecord(record);
                                return (
                                  <tr 
                                    key={record.id} 
                                    style={{
                                      borderBottom: '1px solid #e5e7eb',
                                      backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                      borderLeft: isExpense ? '2px solid #fee2e2' : 'none',
                                      transition: 'all 0.2s ease',
                                      cursor: isTotalLine ? 'default' : 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isTotalLine) {
                                        e.currentTarget.style.backgroundColor = '#f9fafb';
                                        e.currentTarget.style.transform = 'scale(1.001)';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isTotalLine) {
                                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#fafafa';
                                        e.currentTarget.style.transform = 'scale(1)';
                                      }
                                    }}
                                  >
                                    <td style={{
                                      padding: windowWidth < 768 ? '6px 10px' : windowWidth < 1024 ? '7px 14px' : '8px 18px',
                                      position: 'sticky',
                                      left: isRTL ? 'auto' : (windowWidth < 768 ? '50px' : '60px'),
                                      right: isRTL ? (windowWidth < 768 ? '50px' : '60px') : 'auto',
                                      backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                      zIndex: 10,
                                      transition: 'background-color 0.2s ease',
                                      minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                                      boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                                    }}>
                                      <div style={{
                                        fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                        fontWeight: '500',
                                        color: '#424242'
                                      }}>
                                        {record.category_name || 'غير محدد'}
                                      </div>
                                    </td>
                                    {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => {
                                      const amount = record[month] || 0;
                                      return (
                                        <td key={month} style={{
                                          padding: windowWidth < 768 ? '6px 8px' : windowWidth < 1024 ? '7px 10px' : '8px 12px',
                                          textAlign: 'center',
                                          fontSize: windowWidth < 768 ? '12px' : '13px',
                                          color: amount > 0 ? '#dc2626' : '#9ca3af',
                                          fontWeight: amount > 0 ? '600' : '400',
                                          transition: 'color 0.2s ease'
                                        }}>
                                          {amount > 0 ? parseFloat(amount).toFixed(2) : '-'}
                                        </td>
                                      );
                                    })}
                                    {canEditFinances && (
                                      <td style={{
                                        padding: windowWidth < 768 ? '6px 8px' : windowWidth < 1024 ? '7px 10px' : '8px 12px',
                                        textAlign: 'center',
                                        position: 'sticky',
                                        right: 0,
                                        backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                        zIndex: 5
                                      }}>
                                        {/* Masquer le bouton modifier pour les lignes de totaux et calculées */}
                                        {!isTotalLine && !isCalculated ? (
                                          <button
                                            onClick={() => {
                                              setEditingFinancialRecord(record);
                                              setEditingFinancialRecordValues({
                                                category_name: record.category_name || '',
                                                category_type: record.category_type || 'expense',
                                                display_order: record.display_order || 1,
                                                september: record.september?.toString() || '',
                                                october: record.october?.toString() || '',
                                                november: record.november?.toString() || '',
                                                december: record.december?.toString() || '',
                                                january: record.january?.toString() || '',
                                                february: record.february?.toString() || '',
                                                march: record.march?.toString() || '',
                                                april: record.april?.toString() || '',
                                                may: record.may?.toString() || '',
                                                june: record.june?.toString() || '',
                                                july: record.july?.toString() || '',
                                                august: record.august?.toString() || ''
                                              });
                                              setShowEditFinancialRecordForm(true);
                                            }}
                                            style={{
                                              backgroundColor: '#3b82f6',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '6px',
                                              padding: '6px 12px',
                                              fontSize: windowWidth < 768 ? '11px' : '12px',
                                              cursor: 'pointer',
                                              fontWeight: '500',
                                              transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.backgroundColor = '#2563eb';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.backgroundColor = '#3b82f6';
                                            }}
                                          >
                                            {t('edit') || 'تعديل'}
                                          </button>
                                        ) : (
                                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={14} style={{
                                  padding: windowWidth < 768 ? '20px 8px' : windowWidth < 1024 ? '24px 12px' : '28px 16px',
                                  textAlign: 'center',
                                  color: '#9ca3af',
                                  fontSize: windowWidth < 768 ? '12px' : '14px'
                                }}>
                                  لا توجد مصاريف مسجلة
                                </td>
                              </tr>
                            )}
                            {/* Sous-total des dépenses */}
                            {expenseRecords.filter(record => record.category_name !== 'رواتب الأساتذة').length > 0 && (
                              <tr style={{
                                borderTop: '1px solid #fee2e2',
                                borderBottom: '1px solid #fee2e2',
                                backgroundColor: '#fef2f2',
                                fontWeight: '700'
                              }}>
                                <td style={{
                                  padding: windowWidth < 768 ? '4px 10px' : windowWidth < 1024 ? '5px 14px' : '6px 18px',
                                  position: 'sticky',
                                  left: isRTL ? 'auto' : (windowWidth < 768 ? '50px' : '60px'),
                                  right: isRTL ? (windowWidth < 768 ? '50px' : '60px') : 'auto',
                                  backgroundColor: '#fef2f2',
                                  zIndex: 10,
                                  fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                  fontWeight: '700',
                                  color: '#991b1b',
                                  minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                                  boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                                }}>
                                  {t('expenseTotal') || 'إجمالي المصاريف'}
                                </td>
                                {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => {
                                  const amount = expenseTotals[month] || 0;
                                  return (
                                    <td key={month} style={{
                                      padding: windowWidth < 768 ? '4px 8px' : windowWidth < 1024 ? '5px 10px' : '6px 12px',
                                      textAlign: 'center',
                                      fontSize: windowWidth < 768 ? '12px' : '13px',
                                      color: amount > 0 ? '#991b1b' : '#9ca3af',
                                      fontWeight: '700'
                                    }}>
                                      {amount > 0 ? parseFloat(amount.toString()).toFixed(2) : '-'}
                                    </td>
                                  );
                                })}
                                {/* Pas de bouton تعديل pour إجمالي المصاريف */}
                                {canEditFinances && (
                                  <td style={{
                                    padding: windowWidth < 768 ? '4px 8px' : windowWidth < 1024 ? '5px 10px' : '6px 12px',
                                    textAlign: 'center',
                                    position: 'sticky',
                                    right: 0,
                                    backgroundColor: '#fef2f2',
                                    zIndex: 5
                                  }}>
                                    -
                                  </td>
                                )}
                              </tr>
                            )}
                          </>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : financialSubSection === 'studentFees' ? (
              <>
                <div style={{
                  display: 'flex',
                  flexDirection: windowWidth < 768 ? 'column' : 'row',
                  justifyContent: windowWidth < 768 ? 'flex-start' : 'space-between',
                  alignItems: windowWidth < 768 ? 'flex-start' : 'center',
                  marginBottom: windowWidth < 768 ? '20px' : '30px',
                  gap: windowWidth < 768 ? '16px' : '0'
                }}>
                  <h1 style={{ 
                    color: '#424242', 
                    fontSize: windowWidth < 768 ? '22px' : windowWidth < 1024 ? '24px' : '28px',
                    fontWeight: '600',
                    margin: 0
                  }}>{t('studentFees')}</h1>
                  <div style={{ display: 'flex', gap: windowWidth < 768 ? '8px' : '12px', flexWrap: 'wrap', width: windowWidth < 768 ? '100%' : 'auto' }}>
                    <button
                      onClick={async () => {
                        setLoadingContributions(true);
                        try {
                          const response = await apiService.getMonthlyContributions(contributionFilters);
                          if (response.success) {
                            setMonthlyContributions(response.data || []);
                          } else {
                            alert(response.error || 'Erreur lors du chargement des contributions');
                          }
                        } catch (error) {
                          console.error('Erreur:', error);
                          alert('Erreur lors du chargement des contributions');
                        } finally {
                          setLoadingContributions(false);
                        }
                      }}
                      disabled={loadingContributions}
                      style={{
                        backgroundColor: loadingContributions ? '#9ca3af' : '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: windowWidth < 768 ? '10px 14px' : '12px 24px',
                        fontSize: windowWidth < 768 ? '13px' : '14px',
                        cursor: loadingContributions ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flex: windowWidth < 768 ? '1' : 'none',
                        justifyContent: 'center'
                      }}
                    >
                      <RefreshIcon size={windowWidth < 768 ? 14 : 16} style={{ marginLeft: '4px' }} />
                      {t('refresh')}
                    </button>
                  </div>
                </div>

                {/* Filtres */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: '1px solid #f0f0f0',
                  padding: windowWidth < 768 ? '16px' : '20px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: windowWidth < 768 ? '1fr' : windowWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                    gap: '12px'
                  }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        {GLOBAL_UTILS.getStudentLabel()}
                      </label>
                      <div style={{ position: 'relative', minWidth: '200px' }}>
                        <input
                          type="text"
                          value={studentFeesStudentSearchText}
                          onChange={(e) => {
                            const searchText = e.target.value;
                            setStudentFeesStudentSearchText(searchText);
                            setShowStudentFeesStudentDropdown(true);
                            
                            // Si le champ est vide, sélectionner "all"
                            if (searchText === '') {
                              setContributionFilters({ ...contributionFilters, student_id: '' });
                            }
                          }}
                          onFocus={() => {
                            setShowStudentFeesStudentDropdown(true);
                            // Si un étudiant est sélectionné, afficher son nom dans le champ de recherche
                            if (contributionFilters.student_id) {
                              const selectedStudent = students.find(s => s.id.toString() === contributionFilters.student_id);
                              if (selectedStudent) {
                                setStudentFeesStudentSearchText(selectedStudent.student_name || '');
                              }
                            }
                          }}
                          onBlur={() => {
                            // Délai pour permettre le clic sur un élément de la liste
                            setTimeout(() => {
                              setShowStudentFeesStudentDropdown(false);
                              // Si un étudiant est sélectionné, afficher son nom dans le champ
                              if (contributionFilters.student_id) {
                                const selectedStudent = students.find(s => s.id.toString() === contributionFilters.student_id);
                                if (selectedStudent) {
                                  setStudentFeesStudentSearchText(selectedStudent.student_name || '');
                                } else {
                                  setStudentFeesStudentSearchText('');
                                }
                              } else {
                                setStudentFeesStudentSearchText('');
                              }
                            }, 200);
                          }}
                          placeholder={t('allStudents') || 'جميع الطلاب'}
                        style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: 'white',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#374151',
                          width: '100%',
                            direction: isRTL ? 'rtl' : 'ltr'
                          }}
                        />
                        {showStudentFeesStudentDropdown && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            marginTop: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}>
                            <div
                              onMouseDown={(e) => {
                                e.preventDefault(); // Empêcher le onBlur de fermer le dropdown
                              }}
                              onClick={() => {
                                setContributionFilters({ ...contributionFilters, student_id: '' });
                                setStudentFeesStudentSearchText('');
                                setShowStudentFeesStudentDropdown(false);
                              }}
                              style={{
                                padding: '10px 16px',
                                cursor: 'pointer',
                                backgroundColor: contributionFilters.student_id === '' ? '#f3f4f6' : 'white',
                                direction: isRTL ? 'rtl' : 'ltr',
                                fontSize: '14px',
                                borderBottom: '1px solid #e5e7eb'
                              }}
                              onMouseEnter={(e) => {
                                if (contributionFilters.student_id !== '') {
                                  e.currentTarget.style.backgroundColor = '#f9fafb';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (contributionFilters.student_id !== '') {
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                            >
                              {t('allStudents') || 'جميع الطلاب'}
                            </div>
                            {students
                              .filter(student => {
                                if (!studentFeesStudentSearchText) return true;
                                const name = (student.student_name || '').toLowerCase();
                                return name.includes(studentFeesStudentSearchText.toLowerCase());
                              })
                              .map(student => (
                                <div
                                  key={student.id}
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // Empêcher le onBlur de fermer le dropdown
                                  }}
                                  onClick={() => {
                                    const studentName = student.student_name || '';
                                    setContributionFilters({ ...contributionFilters, student_id: student.id.toString() });
                                    setStudentFeesStudentSearchText(studentName);
                                    setShowStudentFeesStudentDropdown(false);
                                  }}
                                  style={{
                                    padding: '10px 16px',
                                    cursor: 'pointer',
                                    backgroundColor: contributionFilters.student_id === student.id.toString() ? '#f3f4f6' : 'white',
                                    direction: isRTL ? 'rtl' : 'ltr',
                          fontSize: '14px'
                        }}
                                  onMouseEnter={(e) => {
                                    if (contributionFilters.student_id !== student.id.toString()) {
                                      e.currentTarget.style.backgroundColor = '#f9fafb';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (contributionFilters.student_id !== student.id.toString()) {
                                      e.currentTarget.style.backgroundColor = 'white';
                                    }
                                  }}
                                >
                            {student.student_name}
                                </div>
                        ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        {t('parents')}
                      </label>
                      <div style={{ position: 'relative', minWidth: '200px' }}>
                        <input
                          type="text"
                          value={studentFeesParentSearchText}
                        onChange={(e) => {
                            const searchText = e.target.value;
                            setStudentFeesParentSearchText(searchText);
                            setShowStudentFeesParentDropdown(true);
                            
                            // Si le champ est vide, sélectionner "all"
                            if (searchText === '') {
                              setContributionFilters({ ...contributionFilters, parent_id: '' });
                            }
                          }}
                          onFocus={() => {
                            setShowStudentFeesParentDropdown(true);
                            // Si un parent est sélectionné, afficher son nom dans le champ de recherche
                            if (contributionFilters.parent_id) {
                              const selectedParent = parents.find(p => {
                                const parentValue = p.user_id || p.id;
                                return parentValue.toString() === contributionFilters.parent_id;
                              });
                              if (selectedParent) {
                                setStudentFeesParentSearchText(selectedParent.full_name || selectedParent.fullName || '');
                              }
                            }
                          }}
                          onBlur={() => {
                            // Délai pour permettre le clic sur un élément de la liste
                            setTimeout(() => {
                              setShowStudentFeesParentDropdown(false);
                              // Si un parent est sélectionné, afficher son nom dans le champ
                              if (contributionFilters.parent_id) {
                                const selectedParent = parents.find(p => {
                                  const parentValue = p.user_id || p.id;
                                  return parentValue.toString() === contributionFilters.parent_id;
                                });
                                if (selectedParent) {
                                  setStudentFeesParentSearchText(selectedParent.full_name || selectedParent.fullName || '');
                                } else {
                                  setStudentFeesParentSearchText('');
                                }
                              } else {
                                setStudentFeesParentSearchText('');
                              }
                            }, 200);
                        }}
                          placeholder={t('allParents') || 'جميع أولياء الأمور'}
                        style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: 'white',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#374151',
                          width: '100%',
                            direction: isRTL ? 'rtl' : 'ltr'
                          }}
                        />
                        {showStudentFeesParentDropdown && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            marginTop: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}>
                            <div
                              onMouseDown={(e) => {
                                e.preventDefault(); // Empêcher le onBlur de fermer le dropdown
                              }}
                              onClick={() => {
                                setContributionFilters({ ...contributionFilters, parent_id: '' });
                                setStudentFeesParentSearchText('');
                                setShowStudentFeesParentDropdown(false);
                              }}
                              style={{
                                padding: '10px 16px',
                                cursor: 'pointer',
                                backgroundColor: contributionFilters.parent_id === '' ? '#f3f4f6' : 'white',
                                direction: isRTL ? 'rtl' : 'ltr',
                                fontSize: '14px',
                                borderBottom: '1px solid #e5e7eb'
                              }}
                              onMouseEnter={(e) => {
                                if (contributionFilters.parent_id !== '') {
                                  e.currentTarget.style.backgroundColor = '#f9fafb';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (contributionFilters.parent_id !== '') {
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                            >
                              {t('allParents') || 'جميع أولياء الأمور'}
                            </div>
                            {parents
                              .filter(parent => {
                                if (!studentFeesParentSearchText) return true;
                                const name = (parent.full_name || parent.fullName || '').toLowerCase();
                                return name.includes(studentFeesParentSearchText.toLowerCase());
                              })
                              .map(parent => {
                          const parentValue = parent.user_id || parent.id;
                          return (
                                  <div
                                    key={parent.id || parent.user_id}
                                    onMouseDown={(e) => {
                                      e.preventDefault(); // Empêcher le onBlur de fermer le dropdown
                                    }}
                                    onClick={() => {
                                      const parentName = parent.full_name || parent.fullName || '';
                                      setContributionFilters({ ...contributionFilters, parent_id: parentValue.toString() });
                                      setStudentFeesParentSearchText(parentName);
                                      setShowStudentFeesParentDropdown(false);
                                    }}
                                    style={{
                                      padding: '10px 16px',
                                      cursor: 'pointer',
                                      backgroundColor: contributionFilters.parent_id === parentValue.toString() ? '#f3f4f6' : 'white',
                                      direction: isRTL ? 'rtl' : 'ltr',
                                      fontSize: '14px'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (contributionFilters.parent_id !== parentValue.toString()) {
                                        e.currentTarget.style.backgroundColor = '#f9fafb';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (contributionFilters.parent_id !== parentValue.toString()) {
                                        e.currentTarget.style.backgroundColor = 'white';
                                      }
                                    }}
                                  >
                              {parent.full_name || parent.fullName}
                                  </div>
                          );
                        })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        {t('status')}
                      </label>
                      <select
                        value={contributionFilters.status}
                        onChange={(e) => setContributionFilters({ ...contributionFilters, status: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">{t('all') || 'الكل'}</option>
                        <option value="paid">{t('paid')}</option>
                        <option value="unpaid">{t('unpaid')}</option>
                        <option value="partial">{t('partial')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Tableau des contributions */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: '1px solid #f0f0f0',
                  overflow: 'hidden'
                }}>
                  {loadingContributions ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                      <div style={{ fontSize: '18px', marginBottom: '8px' }}>⏳</div>
                      <div>{t('loading')}</div>
                    </div>
                  ) : filteredContributions.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#9e9e9e' }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>💳</div>
                      <div style={{ fontSize: '18px' }}>{t('noRecords') || 'لا توجد سجلات'}</div>
                      <div style={{ fontSize: '14px', marginTop: '8px' }}>{t('noContributions')}</div>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        minWidth: '1400px'
                      }}>
                        <thead style={{ backgroundColor: '#f5f5f5' }}>
                          <tr>
                            <th style={{
                              padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                              textAlign: isRTL ? 'right' : 'left',
                              fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                              fontWeight: '600',
                              color: '#757575',
                              position: 'sticky',
                              left: isRTL ? 'auto' : 0,
                              right: isRTL ? 0 : 'auto',
                              backgroundColor: '#f5f5f5',
                              zIndex: 15,
                              minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                              boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                            }}>
                              {t('students')}
                            </th>
                            <th style={{
                              padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                              textAlign: isRTL ? 'right' : 'left',
                              fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                              fontWeight: '600',
                              color: '#757575',
                              position: windowWidth >= 1024 ? 'sticky' : 'static',
                              left: windowWidth >= 1024 ? '120px' : 'auto',
                              backgroundColor: '#f5f5f5',
                              zIndex: windowWidth >= 1024 ? 10 : 1
                            }}>
                              {t('parents')}
                            </th>
                            <th style={{
                              padding: windowWidth < 768 ? '10px 6px' : windowWidth < 1024 ? '11px 8px' : '12px 10px',
                              textAlign: 'center',
                              fontSize: windowWidth < 768 ? '9px' : windowWidth < 1024 ? '10px' : '11px',
                              fontWeight: '600',
                              color: '#757575',
                              minWidth: windowWidth < 768 ? '60px' : '70px'
                            }}>
                              {t('annualEnrollment')}
                            </th>
                            {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => (
                              <th key={month} style={{
                                padding: windowWidth < 768 ? '10px 6px' : windowWidth < 1024 ? '11px 8px' : '12px 10px',
                                textAlign: 'center',
                                fontSize: windowWidth < 768 ? '9px' : windowWidth < 1024 ? '10px' : '11px',
                                fontWeight: '600',
                                color: '#757575',
                                minWidth: windowWidth < 768 ? '60px' : '70px'
                              }}>
                                {t(month)}
                              </th>
                            ))}
                            {canEditFinances && (
                              <th style={{
                                padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                                textAlign: 'center',
                                fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                                fontWeight: '600',
                                color: '#757575'
                              }}>
                                {t('actions') || 'الإجراءات'}
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredContributions.map((contribution, index) => (
                            <tr key={contribution.id} style={{
                              borderBottom: '1px solid #f0f0f0',
                              backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                            }}>
                              <td style={{
                                padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                                position: 'sticky',
                                left: isRTL ? 'auto' : 0,
                                right: isRTL ? 0 : 'auto',
                                backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                zIndex: 10,
                                minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                                boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                              }}>
                                <div style={{
                                  fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                  fontWeight: '500',
                                  color: '#424242'
                                }}>
                                  {contribution.student_name || 'غير محدد'}
                                </div>
                              </td>
                              <td style={{
                                padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                                position: windowWidth >= 1024 ? 'sticky' : 'static',
                                left: windowWidth >= 1024 ? '120px' : 'auto',
                                backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                zIndex: windowWidth >= 1024 ? 5 : 1
                              }}>
                                <div style={{
                                  fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                  color: '#374151'
                                }}>
                                  {contribution.parent_name || 'غير محدد'}
                                </div>
                                {contribution.parent_phone && (
                                  <div style={{
                                    fontSize: windowWidth < 768 ? '10px' : '11px',
                                    color: '#6b7280'
                                  }}>
                                    {contribution.parent_phone}
                                  </div>
                                )}
                              </td>
                              <td style={{
                                padding: windowWidth < 768 ? '10px 6px' : windowWidth < 1024 ? '11px 8px' : '12px 10px',
                                textAlign: 'center',
                                fontSize: windowWidth < 768 ? '11px' : '12px',
                                color: (contribution.annual_membership || contribution.annualMembership) ? '#059669' : '#9ca3af',
                                fontWeight: (contribution.annual_membership || contribution.annualMembership) ? '600' : '400'
                              }}>
                                {(contribution.annual_membership || contribution.annualMembership) 
                                  ? `${parseFloat(contribution.annual_membership || contribution.annualMembership || 0).toFixed(2)}` 
                                  : '-'}
                              </td>
                              {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => {
                                const amount = contribution[month] || 0;
                                return (
                                  <td key={month} style={{
                                    padding: windowWidth < 768 ? '10px 6px' : windowWidth < 1024 ? '11px 8px' : '12px 10px',
                                    textAlign: 'center',
                                    fontSize: windowWidth < 768 ? '11px' : '12px',
                                    color: amount > 0 ? '#059669' : '#9ca3af',
                                    fontWeight: amount > 0 ? '600' : '400'
                                  }}>
                                    {amount > 0 ? `${parseFloat(amount).toFixed(2)}` : '-'}
                                  </td>
                                );
                              })}
                              {canEditFinances && (
                                <td style={{
                                  padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                                  textAlign: 'center'
                                }}>
                                  <button
                                    onClick={() => handleEditContribution(contribution)}
                                    style={{
                                      ...GLOBAL_STYLES.editButton,
                                      padding: windowWidth < 768 ? '6px 10px' : '8px 12px',
                                      fontSize: windowWidth < 768 ? '11px' : '13px'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}>
                                    <EditIcon size={windowWidth < 768 ? 14 : 16} />
                                    <span>{windowWidth < 768 ? '' : t('edit')}</span>
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : financialSubSection === 'teacherSalaries' ? (
              <>
                <div style={{
                  display: 'flex',
                  flexDirection: windowWidth < 768 ? 'column' : 'row',
                  justifyContent: windowWidth < 768 ? 'flex-start' : 'space-between',
                  alignItems: windowWidth < 768 ? 'flex-start' : 'center',
                  marginBottom: windowWidth < 768 ? '20px' : '30px',
                  gap: windowWidth < 768 ? '16px' : '0'
                }}>
                  <h1 style={{ 
                    color: '#424242', 
                    fontSize: windowWidth < 768 ? '22px' : windowWidth < 1024 ? '24px' : '28px',
                    fontWeight: '600',
                    margin: 0
                  }}>{t('teacherSalaries')}</h1>
                  <div style={{ display: 'flex', gap: windowWidth < 768 ? '8px' : '12px', flexWrap: 'wrap', width: windowWidth < 768 ? '100%' : 'auto' }}>
                    <button
                      onClick={async () => {
                        setLoadingSalaries(true);
                        try {
                          const response = await apiService.getTeacherSalaries({});
                          if (response.success) {
                            setTeacherSalaries(response.data || []);
                          } else {
                            alert(response.error || 'Erreur lors du chargement des salaires');
                          }
                        } catch (error) {
                          console.error('Erreur:', error);
                          alert('Erreur lors du chargement des salaires');
                        } finally {
                          setLoadingSalaries(false);
                        }
                      }}
                      disabled={loadingSalaries}
                      style={{
                        backgroundColor: loadingSalaries ? '#9ca3af' : '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: windowWidth < 768 ? '10px 14px' : '12px 24px',
                        fontSize: windowWidth < 768 ? '13px' : '14px',
                        cursor: loadingSalaries ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flex: windowWidth < 768 ? '1' : 'none',
                        justifyContent: 'center'
                      }}
                    >
                      <RefreshIcon size={windowWidth < 768 ? 14 : 16} style={{ marginLeft: '4px' }} />
                      {t('refresh')}
                    </button>
                  </div>
                </div>

                {/* Tableau des salaires */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: '1px solid #f0f0f0',
                  overflow: 'hidden'
                }}>
                  {loadingSalaries ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                      <div style={{ fontSize: '18px', marginBottom: '8px' }}>⏳</div>
                      <div>{t('loading')}</div>
                    </div>
                  ) : teacherSalaries.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#9e9e9e' }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>💼</div>
                      <div style={{ fontSize: '18px' }}>{t('noRecords') || 'لا توجد سجلات'}</div>
                      <div style={{ fontSize: '14px', marginTop: '8px' }}>{t('noSalaries')}</div>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        minWidth: '1200px'
                      }}>
                        <thead style={{ backgroundColor: '#f5f5f5' }}>
                          <tr>
                            <th style={{
                              padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                              textAlign: isRTL ? 'right' : 'left',
                              fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                              fontWeight: '600',
                              color: '#757575',
                              position: 'sticky',
                              left: isRTL ? 'auto' : 0,
                              right: isRTL ? 0 : 'auto',
                              backgroundColor: '#f5f5f5',
                              zIndex: 10,
                              minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                              boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                            }}>
                              {t('teachers')}
                            </th>
                            {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => (
                              <th key={month} style={{
                                padding: windowWidth < 768 ? '10px 6px' : windowWidth < 1024 ? '11px 8px' : '12px 10px',
                                textAlign: 'center',
                                fontSize: windowWidth < 768 ? '9px' : windowWidth < 1024 ? '10px' : '11px',
                                fontWeight: '600',
                                color: '#757575',
                                minWidth: windowWidth < 768 ? '60px' : '70px'
                              }}>
                                {t(month)}
                              </th>
                            ))}
                            <th style={{
                              padding: windowWidth < 768 ? '10px 6px' : windowWidth < 1024 ? '11px 8px' : '12px 10px',
                              textAlign: 'center',
                              fontSize: windowWidth < 768 ? '9px' : windowWidth < 1024 ? '10px' : '11px',
                              fontWeight: '600',
                              color: '#757575',
                              minWidth: windowWidth < 768 ? '60px' : '70px'
                            }}>
                              {t('total') || 'المجموع'}
                            </th>
                            {canEditFinances && (
                              <th style={{
                                padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                                textAlign: 'center',
                                fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px',
                                fontWeight: '600',
                                color: '#757575'
                              }}>
                                {t('actions') || 'الإجراءات'}
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {teacherSalaries.map((salary, index) => (
                            <tr key={salary.id} style={{
                              borderBottom: '1px solid #f0f0f0',
                              backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                            }}>
                              <td style={{
                                padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                                position: 'sticky',
                                left: isRTL ? 'auto' : 0,
                                right: isRTL ? 0 : 'auto',
                                backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                zIndex: 5,
                                minWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '150px' : '180px',
                                boxShadow: isRTL ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)'
                              }}>
                                <div style={{
                                  fontSize: windowWidth < 768 ? '12px' : windowWidth < 1024 ? '13px' : '14px',
                                  fontWeight: '500',
                                  color: '#424242'
                                }}>
                                  {salary.teacher_name || 'غير محدد'}
                                </div>
                                {salary.specialization && (
                                  <div style={{
                                    fontSize: windowWidth < 768 ? '10px' : '11px',
                                    color: '#6b7280'
                                  }}>
                                    {salary.specialization}
                                  </div>
                                )}
                              </td>
                              {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => {
                                const amount = salary[month] || 0;
                                return (
                                  <td key={month} style={{
                                    padding: windowWidth < 768 ? '10px 6px' : windowWidth < 1024 ? '11px 8px' : '12px 10px',
                                    textAlign: 'center',
                                    fontSize: windowWidth < 768 ? '11px' : '12px',
                                    color: amount > 0 ? '#059669' : '#9ca3af',
                                    fontWeight: amount > 0 ? '600' : '400'
                                  }}>
                                    {amount > 0 ? `${parseFloat(amount).toFixed(2)}` : '-'}
                                  </td>
                                );
                              })}
                              <td style={{
                                padding: windowWidth < 768 ? '10px 6px' : windowWidth < 1024 ? '11px 8px' : '12px 10px',
                                textAlign: 'center',
                                fontSize: windowWidth < 768 ? '11px' : '12px',
                                color: (salary.total_amount || 0) > 0 ? '#059669' : '#9ca3af',
                                fontWeight: (salary.total_amount || 0) > 0 ? '600' : '400'
                              }}>
                                {(salary.total_amount || 0) > 0 ? `${parseFloat(salary.total_amount || 0).toFixed(2)}` : '-'}
                              </td>
                              {canEditFinances && (
                                <td style={{
                                  padding: windowWidth < 768 ? '10px 8px' : windowWidth < 1024 ? '11px 12px' : '12px 16px',
                                  textAlign: 'center'
                                }}>
                                  <button
                                    onClick={() => handleEditSalary(salary)}
                                    style={{
                                      ...GLOBAL_STYLES.editButton,
                                      padding: windowWidth < 768 ? '6px 10px' : '8px 12px',
                                      fontSize: windowWidth < 768 ? '11px' : '13px'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}>
                                    <EditIcon size={windowWidth < 768 ? 14 : 16} />
                                    <span>{windowWidth < 768 ? '' : t('edit')}</span>
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        );

      case 'teachers':
        return (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: windowWidth < 768 ? '0 15px' : windowWidth < 1024 ? '0 20px' : '0 30px'
          }}>

            {teachersSubSection === 'manage' ? (
              <>

            {/* Tableau des enseignants */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0',
              overflow: 'hidden',
              padding: windowWidth < 768 ? '0' : '0'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px', fontWeight: '600', color: '#6b7280' }}>{t('teacher')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px', fontWeight: '600', color: '#6b7280', display: windowWidth < 768 ? 'none' : 'table-cell' }}>{t('username')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px', fontWeight: '600', color: '#6b7280', display: windowWidth < 768 ? 'none' : 'table-cell' }}>{t('gender')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px', fontWeight: '600', color: '#6b7280', display: windowWidth < 768 ? 'none' : 'table-cell' }}>{t('teacherSpecialization')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px', fontWeight: '600', color: '#6b7280', display: windowWidth < 768 ? 'none' : 'table-cell' }}>{t('teacherAssignedHalqas')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px', fontWeight: '600', color: '#6b7280', display: windowWidth < 768 ? 'none' : 'table-cell' }}>{t('createdDate')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px', fontWeight: '600', color: '#6b7280' }}>{t('status')}</th>
                      <th style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', fontSize: windowWidth < 768 ? '10px' : windowWidth < 1024 ? '11px' : '12px', fontWeight: '600', color: '#6b7280' }}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTeachers.length === 0 ? (
                      <tr>
                        <td colSpan={8} style={{ padding: windowWidth < 768 ? '30px 16px' : '40px', textAlign: 'center', color: '#757575', fontSize: windowWidth < 768 ? '14px' : '16px' }}>
                          {teachers.length === 0 ? t('noTeachersRegistered') : t('noActiveTeachers')}
                        </td>
                      </tr>
                    ) : (
                      visibleTeachers.map((teacher) => (
                        <tr key={teacher.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left' }}>
                            <div>
                              <div dir="auto" style={{ fontWeight: '500', color: '#1f2937', fontSize: windowWidth < 768 ? '13px' : windowWidth < 1024 ? '14px' : '16px' }}>
                                {teacher.full_name || teacher.username || `${t('teacher')} ${teacher.id}` || t('notSpecified')}
                              </div>
                              <div style={{ fontSize: windowWidth < 768 ? '11px' : '12px', color: '#6b7280', direction: 'ltr' }}>
                                {teacher.phone || t('notSpecified')}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', color: '#374151', fontSize: windowWidth < 768 ? '12px' : '14px', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                            {teacher.username || t('notSpecified')}
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', color: '#374151', fontSize: windowWidth < 768 ? '12px' : '14px', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                            {teacher.gender === 'Female' ? t('female') : t('male')}
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', color: '#374151', fontSize: windowWidth < 768 ? '12px' : '14px', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                            {teacher.specialization || t('notSpecified')}
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', color: '#374151', fontSize: windowWidth < 768 ? '12px' : '14px', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                            {(() => {
                              //console.log('🔍 DEBUG HALQAS - Teacher ID:', teacher.id);
                              //console.log('🔍 DEBUG HALQAS - assigned_halqas:', teacher.assigned_halqas);
                              //console.log('🔍 DEBUG HALQAS - type:', typeof teacher.assigned_halqas);
                              //console.log('🔍 DEBUG HALQAS - teacher object:', teacher);
                              
                              // Gérer les deux cas : string ou array
                              if (teacher.assigned_halqas) {
                                if (typeof teacher.assigned_halqas === 'string' && teacher.assigned_halqas.trim()) {
                                  //console.log('✅ DEBUG HALQAS - Utilisation assigned_halqas (string):', teacher.assigned_halqas);
                                  return teacher.assigned_halqas;
                                } else if (Array.isArray(teacher.assigned_halqas) && teacher.assigned_halqas.length > 0) {
                                  //console.log('✅ DEBUG HALQAS - Utilisation assigned_halqas (array):', teacher.assigned_halqas);
                                  return teacher.assigned_halqas.join(', ');
                                }
                              }
                              
                              //console.log('⚠️ DEBUG HALQAS - Aucune donnée trouvée, fallback');
                              return t('notSpecified');
                            })()}
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left', color: '#6b7280', fontSize: windowWidth < 768 ? '11px' : '13px', display: windowWidth < 768 ? 'none' : 'table-cell' }}>
                            {teacher.created_date ? GLOBAL_UTILS.formatDate(teacher.created_date) : t('notSpecified')}
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left' }}>
                            <span style={GLOBAL_STYLES.statusBadge(teacher.status === 'active')}>
                              {getDisplayStatus(teacher.status)}
                            </span>
                          </td>
                          <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left' }}>
                            <div style={{ display: 'flex', gap: windowWidth < 768 ? '4px' : '8px', justifyContent: isRTL ? 'flex-end' : 'flex-start', flexWrap: windowWidth < 768 ? 'wrap' : 'nowrap' }}>
                              {canModify('teachers') && (
                                <button
                                  onClick={() => {
                                    handleEditTeacher(teacher);
                                  }}
                                  style={{
                                    ...GLOBAL_STYLES.editButton,
                                    padding: windowWidth < 768 ? '6px 10px' : '8px 12px',
                                    fontSize: windowWidth < 768 ? '11px' : '13px'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}>
                                  <EditIcon size={windowWidth < 768 ? 14 : 16} />
                                  <span>{windowWidth < 768 ? '' : t('edit')}</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
              </>
            ) : teachersSubSection === 'quranTracking' ? (
              // Section متابعة القرآن
              <QuranTrackingSection
                  hideTitle={true}
                  hideHeader={false}
                  teachers={teachers}
                  selectedTeacherFilter={selectedTeacherFilter}
                  onTeacherFilterChange={setSelectedTeacherFilter}
                  selectedStudentFilter={selectedStudentFilter}
                  onStudentFilterChange={setSelectedStudentFilter}
                  students={filteredStudentsForQuran}
                  quranRecords={filteredQuranRecords}
                  onRefreshQuranRecords={refreshQuranRecords}
                  onRefreshStats={handleRefreshStats}
                  onGetStudents={handleGetStudents}
                  onGetQuranRecordById={async (id) => await apiService.getQuranRecordById(id)}
                  onAddQuranRecord={handleAddQuranRecord}
                  onUpdateQuranRecord={handleUpdateQuranRecord}
                  onUpdateRepeatNewHifz={handleUpdateRepeatNewHifz}
                  onCreateOrUpdateNextSession={handleCreateOrUpdateNextSession}
                  onGetNextSessionForStudent={handleGetNextSessionForStudent}
                  onGetStudentIdsByTeacher={handleGetStudentIdsByTeacher}
                  t={t}
                  currentLanguage={currentLanguage}
                  windowWidth={windowWidth}
                  styles={GLOBAL_STYLES.dashboard}
                  canModify={canModify('teachers')}
                  onRecordAdded={async () => {
                    // Rafraîchir les enregistrements
                    await refreshQuranRecords();
                  }}
                  onRecordUpdated={async () => {
                    // Rafraîchir les enregistrements
                    await refreshQuranRecords();
                  }}
                />
            ) : teachersSubSection === 'studentProgress' ? (
              // Section تقدم الطلاب
              <div style={{ width: '100%', overflow: 'hidden', boxSizing: 'border-box', maxWidth: '100%' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: windowWidth < 768 ? '20px' : '32px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', flex: 1 }}>
                    {/* Filtre par professeur */}
                    {teachers && teachers.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          fontSize: '14px',
                          color: '#374151',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }}>
                          {t('teacher')}:
                        </span>
                        <select
                          value={selectedTeacherFilter}
                          onChange={(e) => setSelectedTeacherFilter(e.target.value)}
                          style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: 'white',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#374151',
                            cursor: 'pointer',
                            minWidth: '160px',
                            height: 'fit-content'
                          }}
                        >
                          <option value="all">{t('allTeachersOption')}</option>
                          {teachers.filter(teacher => teacher.status === 'active' || (teacher.status as string) === 'نشط').map(teacher => {
                            const teacherUserId = (teacher as any).user_id || teacher.id;
                            return (
                              <option key={teacher.id} value={teacherUserId.toString()}>
                                {teacher.full_name || teacher.username || `${t('teacher')} ${teacher.id}`}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}
                    {/* Filtre par étudiant */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }}>
                        {GLOBAL_UTILS.getStudentLabel()}: {/* Format inclusif par défaut pour le filtre */}
                      </span>
                      <div style={{ position: 'relative', minWidth: '200px' }}>
                        <input
                          type="text"
                          value={studentProgressStudentSearchText}
                          onChange={(e) => {
                            const searchText = e.target.value;
                            setStudentProgressStudentSearchText(searchText);
                            setShowStudentProgressDropdown(true);
                            
                            // Si le champ est vide, sélectionner "all"
                            if (searchText === '') {
                              setSelectedStudentFilter('all');
                            }
                          }}
                          onFocus={() => {
                            setShowStudentProgressDropdown(true);
                            // Si un étudiant est sélectionné, afficher son nom dans le champ de recherche
                            if (selectedStudentFilter !== 'all') {
                              // Filtrer les étudiants selon le professeur sélectionné
                              let availableStudents = students;
                              if (selectedTeacherFilter !== 'all') {
                                const teacherId = parseInt(selectedTeacherFilter);
                                const studentIdsWithRecords = new Set(
                                  quranRecords
                                    .filter(record => {
                                      const recordTeacherId = record.teacher_id || record.teacherId;
                                      return recordTeacherId === teacherId || parseInt(String(recordTeacherId)) === teacherId;
                                    })
                                    .map(record => record.student_id || record.studentId)
                                    .filter(id => id !== undefined && id !== null)
                                );
                                availableStudents = students.filter(student => studentIdsWithRecords.has(student.id));
                              }
                              const selectedStudent = availableStudents.find(s => s.id.toString() === selectedStudentFilter);
                              if (selectedStudent) {
                                setStudentProgressStudentSearchText(selectedStudent.student_name || '');
                              }
                            }
                          }}
                          onBlur={() => {
                            // Délai pour permettre le clic sur un élément de la liste
                            setTimeout(() => {
                              setShowStudentProgressDropdown(false);
                              // Si un étudiant est sélectionné, afficher son nom dans le champ
                              if (selectedStudentFilter !== 'all') {
                                // Filtrer les étudiants selon le professeur sélectionné
                                let availableStudents = students;
                                if (selectedTeacherFilter !== 'all') {
                                  const teacherId = parseInt(selectedTeacherFilter);
                                  const studentIdsWithRecords = new Set(
                                    quranRecords
                                      .filter(record => {
                                        const recordTeacherId = record.teacher_id || record.teacherId;
                                        return recordTeacherId === teacherId || parseInt(String(recordTeacherId)) === teacherId;
                                      })
                                      .map(record => record.student_id || record.studentId)
                                      .filter(id => id !== undefined && id !== null)
                                  );
                                  availableStudents = students.filter(student => studentIdsWithRecords.has(student.id));
                                }
                                const selectedStudent = availableStudents.find(s => s.id.toString() === selectedStudentFilter);
                                if (selectedStudent) {
                                  setStudentProgressStudentSearchText(selectedStudent.student_name || '');
                                } else {
                                  setStudentProgressStudentSearchText('');
                                }
                              } else {
                                setStudentProgressStudentSearchText('');
                              }
                            }, 200);
                          }}
                          placeholder={t('allStudents')}
                          style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: 'white',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#374151',
                            width: '100%',
                            direction: isRTL ? 'rtl' : 'ltr'
                          }}
                        />
                        {showStudentProgressDropdown && (() => {
                          // Filtrer les étudiants selon le professeur sélectionné
                          let availableStudents = students;
                          if (selectedTeacherFilter !== 'all') {
                            const teacherId = parseInt(selectedTeacherFilter);
                            const studentIdsWithRecords = new Set(
                              quranRecords
                                .filter(record => {
                                  const recordTeacherId = record.teacher_id || record.teacherId;
                                  return recordTeacherId === teacherId || parseInt(String(recordTeacherId)) === teacherId;
                                })
                                .map(record => record.student_id || record.studentId)
                                .filter(id => id !== undefined && id !== null)
                            );
                            availableStudents = students.filter(student => studentIdsWithRecords.has(student.id));
                          }
                          
                          // Filtrer par texte de recherche
                          const filteredStudents = availableStudents.filter(student => {
                            if (!studentProgressStudentSearchText) return true;
                            const name = (student.student_name || '').toLowerCase();
                            return name.includes(studentProgressStudentSearchText.toLowerCase());
                          });
                          
                          return (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              marginTop: '4px',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              zIndex: 1000,
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                            }}>
                              <div
                                onMouseDown={(e) => {
                                  e.preventDefault(); // Empêcher le onBlur de fermer le dropdown
                                }}
                                onClick={() => {
                                  setSelectedStudentFilter('all');
                                  setStudentProgressStudentSearchText('');
                                  setShowStudentProgressDropdown(false);
                                }}
                                style={{
                                  padding: '10px 16px',
                                  cursor: 'pointer',
                                  backgroundColor: selectedStudentFilter === 'all' ? '#f3f4f6' : 'white',
                                  direction: isRTL ? 'rtl' : 'ltr',
                                  fontSize: '14px',
                                  borderBottom: '1px solid #e5e7eb'
                                }}
                                onMouseEnter={(e) => {
                                  if (selectedStudentFilter !== 'all') {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (selectedStudentFilter !== 'all') {
                                    e.currentTarget.style.backgroundColor = 'white';
                                  }
                                }}
                              >
                                {t('allStudents')}
                              </div>
                              {filteredStudents.map(student => (
                                <div
                                  key={student.id}
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // Empêcher le onBlur de fermer le dropdown
                                  }}
                                  onClick={() => {
                                    const studentName = student.student_name || '';
                                    setSelectedStudentFilter(student.id.toString());
                                    setStudentProgressStudentSearchText(studentName);
                                    setShowStudentProgressDropdown(false);
                                  }}
                                  style={{
                                    padding: '10px 16px',
                                    cursor: 'pointer',
                                    backgroundColor: selectedStudentFilter === student.id.toString() ? '#f3f4f6' : 'white',
                                    direction: isRTL ? 'rtl' : 'ltr',
                                    fontSize: '14px'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (selectedStudentFilter !== student.id.toString()) {
                                      e.currentTarget.style.backgroundColor = '#f9fafb';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (selectedStudentFilter !== student.id.toString()) {
                                      e.currentTarget.style.backgroundColor = 'white';
                                    }
                                  }}
                                >
                                  {student.student_name || `${t('student')} ${student.id}`}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      await refreshQuranRecords();
                      const studentsData = await apiService.getStudents();
                      if (studentsData.success && Array.isArray(studentsData.data)) {
                        setStudents(studentsData.data);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: windowWidth < 768 ? '10px 16px' : '12px 24px',
                      backgroundColor: '#9333ea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: windowWidth < 768 ? '13px' : '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#7e22ce';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#9333ea';
                    }}
                  >
                    <RefreshIcon size={windowWidth < 768 ? 14 : 16} />
                    <span>{t('refresh')}</span>
                  </button>
                </div>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: windowWidth < 768 ? '1fr' : windowWidth < 1024 ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
                  gap: windowWidth < 768 ? '16px' : '24px'
                }}>
                  {/* Progress Cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', boxSizing: 'border-box' }}>
                    {(() => {
                      // Filtrer les étudiants selon les filtres sélectionnés
                      let filteredStudents = students;
                      
                      // Filtrer par professeur
                      if (selectedTeacherFilter !== 'all') {
                        const teacherId = parseInt(selectedTeacherFilter);
                        const studentIdsWithRecords = new Set(
                          quranRecords
                            .filter(record => {
                              const recordTeacherId = record.teacher_id || record.teacherId;
                              return recordTeacherId === teacherId || parseInt(String(recordTeacherId)) === teacherId;
                            })
                            .map(record => record.student_id || record.studentId)
                            .filter(id => id !== undefined && id !== null)
                        );
                        filteredStudents = filteredStudents.filter(student => studentIdsWithRecords.has(student.id));
                      }
                      
                      // Filtrer par étudiant
                      if (selectedStudentFilter !== 'all') {
                        const studentId = parseInt(selectedStudentFilter);
                        filteredStudents = filteredStudents.filter(student => student.id === studentId);
                      }
                      
                      return filteredStudents.length > 0 ? (
                        filteredStudents.map((student) => {
                        // Calculer le total_hizb depuis les enregistrements Coran
                        const studentRecords = quranRecords.filter(r => 
                          (r.student_id === student.id) || 
                          (r.studentId === student.id) ||
                          (r.student_name === student.student_name)
                        );
                        
                        // Trier par date décroissante pour obtenir le dernier enregistrement
                        const sortedRecords = studentRecords.sort((a, b) => {
                          const dateA = new Date(a.date || a.created_date || 0).getTime();
                          const dateB = new Date(b.date || b.created_date || 0).getTime();
                          if (dateB !== dateA) return dateB - dateA;
                          const createdA = a.created_date ? new Date(a.created_date).getTime() : 0;
                          const createdB = b.created_date ? new Date(b.created_date).getTime() : 0;
                          return createdB - createdA;
                        });
                        
                        const latestRecord = sortedRecords[0];
                        // Utiliser le total_hizb du dernier enregistrement (cumulatif)
                        let totalHizb = 0;
                        if (latestRecord) {
                          const hizbValue = latestRecord.total_hizb ?? latestRecord.totalHizb;
                          if (typeof hizbValue === 'string') {
                            totalHizb = parseFloat(hizbValue) || 0;
                          } else if (typeof hizbValue === 'number') {
                            totalHizb = hizbValue;
                          }
                        }
                        
                        return (
                          <div key={student.id} style={{
                            ...GLOBAL_STYLES.dashboard.card,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}>
                              <div>
                                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                                  {student.student_name || t('notSpecified')}
                                </h3>
                                <p style={{ color: '#6b7280', margin: 0 }}>{student.level || ''}</p>
                              </div>
                              <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
                                <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#9333ea', margin: 0 }}>
                                  {totalHizb > 0 ? totalHizb.toFixed(2) : '0'}
                                </p>
                                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>{t('memorizedHizb')}</p>
                              </div>
                            </div>
                            <div style={{ marginTop: '16px' }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '14px',
                                color: '#6b7280'
                              }}>
                                <span>{t('averageTestScores')}</span>
                                <span>{(parseFloat(String(student.average || 0)) || 0).toFixed(2)}/20</span>
                              </div>
                            </div>
                          </div>
                        );
                        })
                      ) : (
                        <div style={{
                          ...GLOBAL_STYLES.dashboard.card,
                          textAlign: 'center',
                          padding: '40px',
                          color: '#9ca3af'
                        }}>
                          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                          <div style={{ fontSize: '16px' }}>
                            {students.length === 0 ? t('noStudentsRegistered') : t('noStudentsMatchFilter')}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Progress Chart */}
                  <div style={GLOBAL_STYLES.dashboard.card}>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                      {t('memorizationStatistics')}
                    </h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ color: '#6b7280' }}>{t('memorizationLevelAdvanced')}</span>
                        <span style={{ fontWeight: 'bold', color: '#10b981' }}>
                          {(() => {
                            // Filtrer les étudiants selon les filtres sélectionnés
                            let filteredStudents = students;
                            
                            if (selectedTeacherFilter !== 'all') {
                              const teacherId = parseInt(selectedTeacherFilter);
                              const studentIdsWithRecords = new Set(
                                quranRecords
                                  .filter(record => {
                                    const recordTeacherId = record.teacher_id || record.teacherId;
                                    return recordTeacherId === teacherId || parseInt(String(recordTeacherId)) === teacherId;
                                  })
                                  .map(record => record.student_id || record.studentId)
                                  .filter(id => id !== undefined && id !== null)
                              );
                              filteredStudents = filteredStudents.filter(student => studentIdsWithRecords.has(student.id));
                            }
                            
                            if (selectedStudentFilter !== 'all') {
                              const studentId = parseInt(selectedStudentFilter);
                              filteredStudents = filteredStudents.filter(student => student.id === studentId);
                            }
                            
                            // Calculer le nombre d'étudiants avancés (total_hizb > 20)
                            return filteredStudents.filter(s => {
                              const studentRecords = quranRecords.filter(r => 
                                (r.student_id === s.id) || (r.studentId === s.id) || (r.student_name === s.student_name)
                              );
                              const sortedRecords = studentRecords.sort((a, b) => {
                                const dateA = new Date(a.date || a.created_date || 0).getTime();
                                const dateB = new Date(b.date || b.created_date || 0).getTime();
                                return dateB - dateA;
                              });
                              const latestRecord = sortedRecords[0];
                              if (latestRecord) {
                                const hizbValue = latestRecord.total_hizb ?? latestRecord.totalHizb;
                                const totalHizb = typeof hizbValue === 'string' ? parseFloat(hizbValue) || 0 : (typeof hizbValue === 'number' ? hizbValue : 0);
                                return totalHizb > 20;
                              }
                              return false;
                            }).length;
                          })()}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ color: '#6b7280' }}>{t('memorizationLevelIntermediate')}</span>
                        <span style={{ fontWeight: 'bold', color: '#3b82f6' }}>
                          {(() => {
                            // Filtrer les étudiants selon les filtres sélectionnés
                            let filteredStudents = students;
                            
                            if (selectedTeacherFilter !== 'all') {
                              const teacherId = parseInt(selectedTeacherFilter);
                              const studentIdsWithRecords = new Set(
                                quranRecords
                                  .filter(record => {
                                    const recordTeacherId = record.teacher_id || record.teacherId;
                                    return recordTeacherId === teacherId || parseInt(String(recordTeacherId)) === teacherId;
                                  })
                                  .map(record => record.student_id || record.studentId)
                                  .filter(id => id !== undefined && id !== null)
                              );
                              filteredStudents = filteredStudents.filter(student => studentIdsWithRecords.has(student.id));
                            }
                            
                            if (selectedStudentFilter !== 'all') {
                              const studentId = parseInt(selectedStudentFilter);
                              filteredStudents = filteredStudents.filter(student => student.id === studentId);
                            }
                            
                            // Calculer le nombre d'étudiants intermédiaires (5 < total_hizb <= 20)
                            return filteredStudents.filter(s => {
                              const studentRecords = quranRecords.filter(r => 
                                (r.student_id === s.id) || (r.studentId === s.id) || (r.student_name === s.student_name)
                              );
                              const sortedRecords = studentRecords.sort((a, b) => {
                                const dateA = new Date(a.date || a.created_date || 0).getTime();
                                const dateB = new Date(b.date || b.created_date || 0).getTime();
                                return dateB - dateA;
                              });
                              const latestRecord = sortedRecords[0];
                              if (latestRecord) {
                                const hizbValue = latestRecord.total_hizb ?? latestRecord.totalHizb;
                                const totalHizb = typeof hizbValue === 'string' ? parseFloat(hizbValue) || 0 : (typeof hizbValue === 'number' ? hizbValue : 0);
                                return totalHizb > 5 && totalHizb <= 20;
                              }
                              return false;
                            }).length;
                          })()}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ color: '#6b7280' }}>{t('memorizationLevelBeginner')}</span>
                        <span style={{ fontWeight: 'bold', color: '#f59e0b' }}>
                          {(() => {
                            // Filtrer les étudiants selon les filtres sélectionnés
                            let filteredStudents = students;
                            
                            if (selectedTeacherFilter !== 'all') {
                              const teacherId = parseInt(selectedTeacherFilter);
                              const studentIdsWithRecords = new Set(
                                quranRecords
                                  .filter(record => {
                                    const recordTeacherId = record.teacher_id || record.teacherId;
                                    return recordTeacherId === teacherId || parseInt(String(recordTeacherId)) === teacherId;
                                  })
                                  .map(record => record.student_id || record.studentId)
                                  .filter(id => id !== undefined && id !== null)
                              );
                              filteredStudents = filteredStudents.filter(student => studentIdsWithRecords.has(student.id));
                            }
                            
                            if (selectedStudentFilter !== 'all') {
                              const studentId = parseInt(selectedStudentFilter);
                              filteredStudents = filteredStudents.filter(student => student.id === studentId);
                            }
                            
                            // Calculer le nombre d'étudiants débutants (total_hizb <= 5)
                            return filteredStudents.filter(s => {
                              const studentRecords = quranRecords.filter(r => 
                                (r.student_id === s.id) || (r.studentId === s.id) || (r.student_name === s.student_name)
                              );
                              const sortedRecords = studentRecords.sort((a, b) => {
                                const dateA = new Date(a.date || a.created_date || 0).getTime();
                                const dateB = new Date(b.date || b.created_date || 0).getTime();
                                return dateB - dateA;
                              });
                              const latestRecord = sortedRecords[0];
                              if (latestRecord) {
                                const hizbValue = latestRecord.total_hizb ?? latestRecord.totalHizb;
                                const totalHizb = typeof hizbValue === 'string' ? parseFloat(hizbValue) || 0 : (typeof hizbValue === 'number' ? hizbValue : 0);
                                return totalHizb <= 5;
                              }
                              return true; // Si pas d'enregistrement, considérer comme débutant
                            }).length;
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        );

      case 'association':
        if (!canViewSection('admins')) {
          return (
            <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
              <p>{t('noAccessPermission')}</p>
            </div>
          );
        }
        return (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: windowWidth < 768 ? '0 15px' : '0 30px' }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0', overflow: 'hidden' }}>
              <AssociationSettings
                currentLanguage={currentLanguage}
                isRTL={isRTL}
                canEdit={canModify('admins')}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Cairo, sans-serif',
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      {renderNavigation()}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: windowWidth < 768 ? '12px 0' : '20px 0'
      }}>
        {renderBreadcrumb()}
        {renderMainContent()}
      </div>

      {/* Formulaire d'ajout d'étudiant */}
      {showAddStudentForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            maxWidth: '700px',
            width: '85%',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              paddingBottom: '12px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{
                color: '#424242',
                fontSize: '20px',
                fontWeight: '600',
                margin: 0
              }}>
                {t('addNewStudent')}
              </h2>
              <button
                onClick={() => {
                  setShowAddStudentForm(false);
                  setFormError(null);
                  setFieldErrors({});
                }}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '35px',
                  height: '35px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>


            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('studentName')} *
                </label>
                <input
                  type="text"
                  value={newStudent.student_name}
                  onChange={(e) => {
                    setNewStudent({...newStudent, student_name: e.target.value});
                    if (fieldErrors.student_name) {
                      setFieldErrors({...fieldErrors, student_name: ''});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: fieldErrors.student_name ? '1px solid #f44336' : '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                  placeholder={t('enterStudentName')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
                {fieldErrors.student_name && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.student_name}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('studentParent')} *
                </label>
                <SearchableSelect
                  value={newStudent.parent_id}
                  onChange={(value) => {
                    setNewStudent({...newStudent, parent_id: value});
                    if (fieldErrors.parent_id) {
                      setFieldErrors({...fieldErrors, parent_id: ''});
                    }
                  }}
                  options={parents.map((parent, index) => ({
                    value: parent.user_id || parent.id,
                    label: (parent.full_name || parent.fullName || t('notSpecified')) as string
                  }))}
                  placeholder={t('selectParent')}
                />
                {parents.length === 0 && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#ffebee',
                    borderRadius: '4px',
                    border: '1px solid #ffcdd2',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}>
                    ⚠️ {t('noParentsAvailable')}
                  </div>
                )}
                {fieldErrors.parent_id && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.parent_id}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('studentLevel')}
                </label>
                <select
                  value={newStudent.level}
                  onChange={(e) => {
                    setNewStudent({...newStudent, level: e.target.value});
                    if (fieldErrors.level) {
                      setFieldErrors({...fieldErrors, level: ''});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: fieldErrors.level ? '1px solid #f44336' : '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">{t('selectLevel')}</option>
                  <option value="الروض">الروض</option>
                  <option value="الأول">الأول</option>
                  <option value="الثاني">الثاني</option>
                  <option value="الثالث">الثالث</option>
                  <option value="الرابع">الرابع</option>
                  <option value="الخامس">الخامس</option>
                  <option value="السادس">السادس</option>
                  <option value="الإعدادي">الإعدادي</option>
                  <option value="الثانوي">الثانوي</option>
                </select>
                {fieldErrors.level && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.level}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('studentHalqa')} *
                </label>
                <select
                  value={newStudent.halqa}
                  onChange={(e) => {
                    setNewStudent({...newStudent, halqa: e.target.value});
                    if (fieldErrors.halqa) {
                      setFieldErrors({...fieldErrors, halqa: ''});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: fieldErrors.halqa ? '1px solid #f44336' : '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">{t('selectHalqa')}</option>
                  <option value="حلقة الحفظ بالمصحف">حلقة الحفظ بالمصحف</option>
                  <option value="حلقة الحفظ بالسمع">حلقة الحفظ بالسمع</option>
                </select>
                {fieldErrors.halqa && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.halqa}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('gender')} *
                </label>
                <select
                  value={newStudent.gender || 'Male'}
                  onChange={(e) => {
                    setNewStudent({...newStudent, gender: e.target.value as 'Female' | 'Male'});
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                  required
                >
                  <option value="Male">{t('male')}</option>
                  <option value="Female">{t('female')}</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('teacher')}
                </label>
                <select
                  value={newStudent.teacher_id}
                  onChange={(e) => {
                    setNewStudent({...newStudent, teacher_id: e.target.value});
                    if (fieldErrors.teacher_id) {
                      setFieldErrors({...fieldErrors, teacher_id: ''});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: fieldErrors.teacher_id ? '1px solid #f44336' : '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">{t('selectTeacher')}</option>
                  {teachers
                    .filter(teacher => {
                      const status = teacher.status as string;
                      return status === 'active' || status === 'نشط' || status === '1';
                    })
                    .map(teacher => (
                      <option key={teacher.id || teacher.user_id} value={teacher.user_id || teacher.id}>
                        {teacher.full_name || teacher.username || `Enseignant ${teacher.id}` || t('notSpecified')}
                      </option>
                    ))}
                </select>
                {fieldErrors.teacher_id && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.teacher_id}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  السنة الدراسية *
                </label>
                <select
                  value={newStudent.school_year_id}
                  onChange={(e) => setNewStudent({...newStudent, school_year_id: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">— اختر السنة الدراسية —</option>
                  {years.map(y => (
                    <option key={y.id} value={y.id}>
                      {y.label}{y.is_current ? ' ★' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>


            {/* Champ notes */}
            <div style={{ marginTop: '10px' }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: '500',
                color: '#424242',
                fontSize: '14px'
              }}>
                {t('notes')}
              </label>
              <textarea
                value={newStudent.notes}
                onChange={(e) => setNewStudent({...newStudent, notes: e.target.value})}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  minHeight: '70px',
                  resize: 'vertical'
                }}
                placeholder={t('enterNotes')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            {/* Message d'erreur en bas du formulaire */}
            {formError && (
              <div style={{
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '6px',
                padding: '12px',
                marginTop: '15px',
                color: '#d32f2f',
                fontSize: '13px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                  fontWeight: '600'
                }}>
                  <span style={{ fontSize: '16px' }}>⚠️</span>
                  <span>{t('errorAddingStudent')}</span>
                </div>
                <div style={{
                  whiteSpace: 'pre-line',
                  lineHeight: '1.6',
                  fontSize: '13px',
                  color: '#666'
                }}>
                  {formError}
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              marginTop: '15px'
            }}>
              <button
                onClick={() => {
                  setShowAddStudentForm(false);
                  setFormError(null);
                  setFieldErrors({});
                }}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              
              <button
                onClick={handleAddStudent}
                disabled={isSubmitting}
                style={{
                  backgroundColor: isSubmitting ? '#ccc' : '#4caf50',
                  color: 'white',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? t('saving') : t('saveStudent')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire de modification d'étudiant */}
      {showEditStudentForm && editingStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            width: '85%',
            maxWidth: '700px',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              paddingBottom: '12px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                {t('editStudentTitle')}
              </h2>
              <button
                onClick={() => {
                  setShowEditStudentForm(false);
                  setEditingStudent(null);
                  setCurrentStudentTeacherRelation(null);
                  setFormError(null);
                  setFieldErrors({});
                }}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '35px',
                  height: '35px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Message d'erreur en haut du formulaire */}
            {formError && (
              <div style={{
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '15px',
                color: '#d32f2f',
                fontSize: '13px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '10px',
                  fontWeight: '600'
                }}>
                  <span style={{ fontSize: '18px' }}>⚠️</span>
                  <span>{t('errorEditingStudent')}</span>
                </div>
                <div style={{
                  whiteSpace: 'pre-line',
                  lineHeight: '1.6',
                  fontSize: '13px',
                  color: '#666'
                }}>
                  {formError}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('studentName')} *
                </label>
                <input
                  type="text"
                  value={newStudent.student_name}
                  onChange={(e) => {
                    setNewStudent({...newStudent, student_name: e.target.value});
                    if (fieldErrors.student_name) {
                      setFieldErrors({...fieldErrors, student_name: ''});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: fieldErrors.student_name ? '1px solid #f44336' : '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                  placeholder={t('enterStudentName')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
                {fieldErrors.student_name && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.student_name}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('studentParent')} *
                </label>
                <SearchableSelect
                  value={newStudent.parent_id}
                  onChange={(value) => {
                    setNewStudent({...newStudent, parent_id: value});
                    if (fieldErrors.parent_id) {
                      setFieldErrors({...fieldErrors, parent_id: ''});
                    }
                  }}
                  options={parents.map((parent, index) => ({
                    value: parent.user_id || parent.id,
                    label: (parent.full_name || parent.fullName || t('notSpecified')) as string
                  }))}
                  placeholder={t('selectParent')}
                />
                {parents.length === 0 && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#ffebee',
                    borderRadius: '4px',
                    border: '1px solid #ffcdd2',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}>
                    ⚠️ لا توجد أولياء أمور مسجلين. يجب إضافة ولي أمر أولاً قبل تعديل الطالب.
                  </div>
                )}
                {fieldErrors.parent_id && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.parent_id}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('studentLevel')}
                </label>
                <select
                  value={newStudent.level}
                  onChange={(e) => {
                    setNewStudent({...newStudent, level: e.target.value});
                    if (fieldErrors.level) {
                      setFieldErrors({...fieldErrors, level: ''});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: fieldErrors.level ? '1px solid #f44336' : '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">{t('selectLevel')}</option>
                  <option value="الروض">الروض</option>
                  <option value="الأول">الأول</option>
                  <option value="الثاني">الثاني</option>
                  <option value="الثالث">الثالث</option>
                  <option value="الرابع">الرابع</option>
                  <option value="الخامس">الخامس</option>
                  <option value="السادس">السادس</option>
                  <option value="الإعدادي">الإعدادي</option>
                  <option value="الثانوي">الثانوي</option>
                </select>
                {fieldErrors.level && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.level}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('studentHalqa')} *
                </label>
                <select
                  value={newStudent.halqa}
                  onChange={(e) => {
                    setNewStudent({...newStudent, halqa: e.target.value});
                    if (fieldErrors.halqa) {
                      setFieldErrors({...fieldErrors, halqa: ''});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: fieldErrors.halqa ? '1px solid #f44336' : '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">{t('selectHalqa')}</option>
                  <option value="حلقة الحفظ بالمصحف">حلقة الحفظ بالمصحف</option>
                  <option value="حلقة الحفظ بالسمع">حلقة الحفظ بالسمع</option>
                </select>
                {fieldErrors.halqa && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.halqa}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('gender')} *
                </label>
                <select
                  value={newStudent.gender || 'Male'}
                  onChange={(e) => {
                    setNewStudent({...newStudent, gender: e.target.value as 'Female' | 'Male'});
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                  required
                >
                  <option value="Male">{t('male')}</option>
                  <option value="Female">{t('female')}</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('teacher')}
                </label>
                <select
                  value={newStudent.teacher_id}
                  onChange={(e) => {
                    setNewStudent({...newStudent, teacher_id: e.target.value});
                    if (fieldErrors.teacher_id) {
                      setFieldErrors({...fieldErrors, teacher_id: ''});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: fieldErrors.teacher_id ? '1px solid #f44336' : '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">{t('selectTeacher')}</option>
                  {teachers
                    .filter(teacher => {
                      const status = teacher.status as string;
                      return status === 'active' || status === 'نشط' || status === '1';
                    })
                    .map(teacher => (
                      <option key={teacher.id || teacher.user_id} value={teacher.user_id || teacher.id}>
                        {teacher.full_name || teacher.username || `Enseignant ${teacher.id}` || t('notSpecified')}
                      </option>
                    ))}
                </select>
                {fieldErrors.teacher_id && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.teacher_id}
                  </div>
                )}
              </div>

              {/* Champ الحالة (Status) */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  {t('status')} *
                </label>
                <select
                  value={newStudent.status}
                  onChange={(e) => {
                    setNewStudent({...newStudent, status: e.target.value});
                    if (fieldErrors.status) {
                      setFieldErrors({...fieldErrors, status: ''});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: fieldErrors.status ? '1px solid #f44336' : '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">{t('selectOption')}</option>
                  <option value="active">{t('active')}</option>
                  <option value="inactive">{t('inactive')}</option>
                </select>
                {fieldErrors.status && (
                  <div style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>
                    {fieldErrors.status}
                  </div>
                )}
              </div>

              {/* Réinscription : choisir l'année scolaire */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  color: '#424242',
                  fontSize: '14px'
                }}>
                  السنة الدراسية (إعادة التسجيل)
                </label>
                <select
                  value={newStudent.school_year_id}
                  onChange={(e) => setNewStudent({...newStudent, school_year_id: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #2196f3',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: '#f0f7ff',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">— اختر السنة الدراسية —</option>
                  {years.map(y => (
                    <option key={y.id} value={y.id}>
                      {y.label}{y.is_current ? ' ★' : ''}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                  تغيير السنة الدراسية يُنشئ تلقائياً سجل مساهمات جديد للسنة المختارة
                </div>
              </div>
            </div>

            {/* Champ notes */}
            <div style={{ marginTop: '10px' }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: '500',
                color: '#424242',
                fontSize: '14px'
              }}>
                {t('notes')}
              </label>
              <textarea
                value={newStudent.notes}
                onChange={(e) => setNewStudent({...newStudent, notes: e.target.value})}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  minHeight: '70px',
                  resize: 'vertical'
                }}
                placeholder={t('enterNotes')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            {/* Message d'erreur en bas du formulaire */}
            {formError && (
              <div style={{
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '6px',
                padding: '12px',
                marginTop: '15px',
                color: '#d32f2f',
                fontSize: '13px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                  fontWeight: '600'
                }}>
                  <span style={{ fontSize: '16px' }}>⚠️</span>
                  <span>{t('errorEditingStudent')}</span>
                </div>
                <div style={{
                  whiteSpace: 'pre-line',
                  lineHeight: '1.6',
                  fontSize: '13px',
                  color: '#666'
                }}>
                  {formError}
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '10px',
              marginTop: '15px'
            }}>
              <button
                onClick={() => {
                  const student = editingStudent;
                  setShowEditStudentForm(false);
                  setEditingStudent(null);
                  setCurrentStudentTeacherRelation(null);
                  setFormError(null);
                  setFieldErrors({});
                  if (student) handleDeleteStudent(student.id);
                }}
                style={{
                  backgroundColor: '#fff',
                  color: '#DC2626',
                  padding: '10px 20px',
                  border: '1px solid #FECACA',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                🗑️ حذف
              </button>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    setShowEditStudentForm(false);
                    setEditingStudent(null);
                    setCurrentStudentTeacherRelation(null);
                    setFormError(null);
                    setFieldErrors({});
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleUpdateStudent}
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: isSubmitting ? '#ccc' : '#4caf50',
                    color: 'white',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSubmitting ? t('saving') : t('saveChanges')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section نقط الإختبارات */}
      {currentSection === 'grades' && (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: windowWidth < 768 ? '0 15px' : windowWidth < 1024 ? '0 20px' : '0 20px'
        }}>

          {/* Tableau des grades */}
          <ExamScoresTable
            testScores={testScores}
            students={students}
            subjects={subjects}
            windowWidth={windowWidth}
            isRTL={isRTL}
            showActions={canModify('grades')}
            onDeleteTestScore={handleDeleteTestScore}
            formatDate={formatDate}
            t={t}
          />
        </div>
      )}

      {/* Modale d'ajout de grade */}
      {showAddTestScoreForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '30px',
              paddingBottom: '20px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
              {t('addTestScore')}
              </h2>
              <button
                onClick={() => {
                  setShowAddTestScoreForm(false);
                  setTestScoreError(null);
                  setNewTestScore({
                    studentId: '',
                    subjectId: '',
                    grade: '',
                    date: todayInTz(timezone)
                  });
                }}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '35px',
                  height: '35px',
                  fontSize: '18px',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>

            {testScoreError && (
              <div style={{
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '6px',
                padding: '15px',
                marginBottom: '20px',
                color: '#d32f2f',
                fontSize: '14px'
              }}>
                {testScoreError}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  {t('student')} *
                </label>
                <SearchableSelect
                  value={newTestScore.studentId}
                  onChange={(value) => setNewTestScore({...newTestScore, studentId: value})}
                  options={students
                    .filter(s => {
                      const yid = selectedYear?.id ?? currentYear?.id ?? null;
                      return !yid || Number(s.school_year_id) === yid;
                    })
                    .map(student => ({
                      value: student.id,
                      label: (student.student_name || `طالب ${student.id}`) as string
                    }))}
                  placeholder={t('selectStudent') || t('student')}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  {t('subject')} *
                </label>
                <select
                  value={newTestScore.subjectId}
                  onChange={(e) => setNewTestScore({...newTestScore, subjectId: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px'
                  }}
                >
                  <option value="">{t('selectSubject')}</option>
                  {subjects.length > 0 ? (
                    subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      {loading ? t('loading') : t('noSubjectsAvailable')}
                    </option>
                  )}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  {t('gradeOutOf20')} *
                </label>
                <input
                  type="number"
                  min="0"
                  max="20"
                  step="0.5"
                  value={newTestScore.grade}
                  onChange={(e) => setNewTestScore({...newTestScore, grade: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px'
                  }}
                  placeholder={t('enterGrade')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  {t('date')} *
                </label>
                <input
                  type="date"
                  value={newTestScore.date}
                  onChange={(e) => setNewTestScore({...newTestScore, date: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '25px'
            }}>
              <button
                onClick={() => {
                  setShowAddTestScoreForm(false);
                  setTestScoreError(null);
                  setNewTestScore({
                    studentId: '',
                    subjectId: '',
                    grade: '',
                    date: todayInTz(timezone)
                  });
                }}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleAddTestScore}
                disabled={isSubmittingTestScore}
                style={{
                  backgroundColor: isSubmittingTestScore ? '#ccc' : '#9c27b0',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: isSubmittingTestScore ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmittingTestScore ? t('saving') : t('add')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'envoi de message */}
      {showSendMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#424242',
              marginBottom: '24px'
            }}>
              {messageToReplyTo ? 'رد على الرسالة' : 'إرسال رسالة'}
            </h2>

            {messageError && (
              <div style={{
                backgroundColor: '#ffebee',
                color: '#c62828',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {messageError}
              </div>
            )}

            {!isReplyMode && (
              <div style={{ display: 'flex', gap: '20px' }}>
                {/* Colonne gauche - Recipients */}
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '12px',
                    fontWeight: '500',
                    color: '#424242'
                  }}>
                    المستقبلين *
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="radio"
                        id="sendToAll"
                        name="recipientType"
                        value="all"
                        checked={newMessage.recipientType === 'all'}
                        onChange={(e) => {
                          setNewMessage({...newMessage, recipientType: e.target.value});
                          setShowSpecificUsers(false);
                          setShowClassSelection(false);
                        }}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <label htmlFor="sendToAll" style={{
                        fontSize: '14px',
                        color: '#424242',
                        cursor: 'pointer'
                      }}>
                        جميع المستخدمين النشطين
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="radio"
                        id="sendToAdmin"
                        name="recipientType"
                        value="admin"
                        checked={newMessage.recipientType === 'admin'}
                        onChange={(e) => {
                          setNewMessage({...newMessage, recipientType: e.target.value});
                          setShowSpecificUsers(false);
                          setShowClassSelection(false);
                          // Ne pas réinitialiser selectedUsers pour permettre la sélection de destinataires supplémentaires
                        }}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <label htmlFor="sendToAdmin" style={{
                        fontSize: '14px',
                        color: '#424242',
                        cursor: 'pointer'
                      }}>
                        الإدارة
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="radio"
                        id="sendToSpecific"
                        name="recipientType"
                        value="specific"
                        checked={newMessage.recipientType === 'specific'}
                        onChange={(e) => {
                          setNewMessage({...newMessage, recipientType: e.target.value});
                          setShowSpecificUsers(true);
                          setShowClassSelection(false);
                        }}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <label htmlFor="sendToSpecific" style={{
                        fontSize: '14px',
                        color: '#424242',
                        cursor: 'pointer'
                      }}>
                        مستخدمين محددين
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="radio"
                        id="sendToClass"
                        name="recipientType"
                        value="class"
                        checked={newMessage.recipientType === 'class'}
                        onChange={(e) => {
                          setNewMessage({...newMessage, recipientType: e.target.value});
                          setShowSpecificUsers(false);
                          setShowClassSelection(true);
                        }}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <label htmlFor="sendToClass" style={{
                        fontSize: '14px',
                        color: '#424242',
                        cursor: 'pointer'
                      }}>
                        أولياء أمور حلقة معينة
                      </label>
                    </div>
                  </div>
                </div>

                {/* Colonne droite - Quick Templates */}
                {!isReplyMode && (
                  <div style={{ flex: 1 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '12px',
                      fontWeight: '500',
                      color: '#424242'
                    }}>
                      قوالب سريعة
                    </label>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '8px'
                    }}>
                      <button
                        type="button"
                        onClick={() => applyTemplate('meeting')}
                        style={{
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                          border: '1px solid #bbdefb',
                          padding: '12px 8px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textAlign: 'center'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#bbdefb'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                      >
                        📅 دعوة اجتماع
                      </button>
                      <button
                        type="button"
                        onClick={() => applyTemplate('absence')}
                        style={{
                          backgroundColor: '#ffebee',
                          color: '#c62828',
                          border: '1px solid #ffcdd2',
                          padding: '12px 8px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textAlign: 'center'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#ffcdd2'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffebee'}
                      >
                        <XCircleIcon size={16} style={{ marginLeft: '4px' }} />
                        إشعار غياب
                      </button>
                      <button
                        type="button"
                        onClick={() => applyTemplate('announcement')}
                        style={{
                          backgroundColor: '#fff3e0',
                          color: '#ef6c00',
                          border: '1px solid #ffcc02',
                          padding: '12px 8px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textAlign: 'center'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#ffcc02'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff3e0'}
                      >
                        📢 إعلان
                      </button>
                      <button
                        type="button"
                        onClick={() => applyTemplate('congratulation')}
                        style={{
                          backgroundColor: '#f3e5f5',
                          color: '#7b1fa2',
                          border: '1px solid #ce93d8',
                          padding: '12px 8px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textAlign: 'center'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#ce93d8'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3e5f5'}
                      >
                        🎉 تهنئة
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* User Selection */}
            {(showSpecificUsers || isReplyMode) && (
              <div style={{ marginTop: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  {isReplyMode ? 'المستلم (المرسل الأصلي)' : 'اختر المستخدمين'}
                </label>
                <div style={{
                  maxHeight: '150px',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  padding: '8px',
                  backgroundColor: isReplyMode ? '#f5f5f5' : 'white'
                }}>
                  {isReplyMode ? (
                    // En mode réponse, afficher uniquement l'expéditeur (lecture seule)
                    messageToReplyTo && (() => {
                      const senderType = messageToReplyTo.sender_type?.toLowerCase() || '';
                      if (senderType === 'parent' || senderType === 'ولي الأمر') {
                        // Pour un parent, trouver l'étudiant correspondant
                        const student = messageToReplyTo.student_id 
                          ? students.find(s => s.id === messageToReplyTo.student_id)
                          : null;
                        if (student) {
                          return (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px',
                              backgroundColor: '#e3f2fd',
                              borderRadius: '4px',
                              border: '1px solid #90caf9'
                            }}>
                              <input
                                type="checkbox"
                                checked={true}
                                disabled={true}
                                style={{ width: '16px', height: '16px', cursor: 'not-allowed' }}
                              />
                              <label style={{
                                fontSize: '14px',
                                color: '#424242',
                                fontWeight: '500'
                              }}>
                                {student.student_name} - {student.parent_name || messageToReplyTo.sender_name || 'غير محدد'}
                              </label>
                            </div>
                          );
                        }
                        // Si pas d'étudiant trouvé, continuer avec le retour par défaut
                      } else if (senderType === 'teacher' || senderType === 'معلم') {
                        // Pour un enseignant, afficher le nom de l'enseignant
                        return (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px',
                            backgroundColor: '#e3f2fd',
                            borderRadius: '4px',
                            border: '1px solid #90caf9'
                          }}>
                            <input
                              type="checkbox"
                              checked={true}
                              disabled={true}
                              style={{ width: '16px', height: '16px', cursor: 'not-allowed' }}
                            />
                            <label style={{
                              fontSize: '14px',
                              color: '#424242',
                              fontWeight: '500'
                            }}>
                              {messageToReplyTo.sender_name || 'معلم'}
                            </label>
                          </div>
                        );
                      }
                      // Par défaut, afficher le nom de l'expéditeur
                      return (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          backgroundColor: '#e3f2fd',
                          borderRadius: '4px',
                          border: '1px solid #90caf9'
                        }}>
                          <input
                            type="checkbox"
                            checked={true}
                            disabled={true}
                            style={{ width: '16px', height: '16px', cursor: 'not-allowed' }}
                          />
                          <label style={{
                            fontSize: '14px',
                            color: '#424242',
                            fontWeight: '500'
                          }}>
                            {messageToReplyTo.sender_name || 'غير معروف'}
                          </label>
                        </div>
                      );
                    })()
                  ) : (
                    // Mode normal : afficher tous les étudiants avec checkboxes
                    students.map(student => (
                      <div key={student.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <input
                          type="checkbox"
                          id={`user_${student.id}`}
                          checked={selectedUsers.includes(student.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers([...selectedUsers, student.id]);
                            } else {
                              setSelectedUsers(selectedUsers.filter(id => id !== student.id));
                            }
                          }}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <label htmlFor={`user_${student.id}`} style={{
                          fontSize: '14px',
                          color: '#424242',
                          cursor: 'pointer'
                        }}>
                          {student.student_name} - {student.parent_name || 'غير محدد'}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Admin Additional Recipients (Optional) */}
            {newMessage.recipientType === 'admin' && !isReplyMode && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#424242'
                    }}>
                      مستلمين إضافيين (اختياري)
                    </label>
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      marginBottom: '8px'
                    }}>
                      سيتم إرسال الرسالة إلى جميع المديرين تلقائياً. يمكنك إضافة مستلمين إضافيين (أولياء أمور أو أساتذة) إذا لزم الأمر.
                    </div>
                    <div style={{
                      maxHeight: '150px',
                      overflowY: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      padding: '8px',
                      backgroundColor: 'white'
                    }}>
                      {students.map(student => (
                        <div key={student.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="checkbox"
                            id={`admin_extra_${student.id}`}
                            checked={selectedUsers.includes(student.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsers([...selectedUsers, student.id]);
                              } else {
                                setSelectedUsers(selectedUsers.filter(id => id !== student.id));
                              }
                            }}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <label htmlFor={`admin_extra_${student.id}`} style={{
                            fontSize: '14px',
                            color: '#424242',
                            cursor: 'pointer'
                          }}>
                            {student.student_name} - {student.parent_name || 'غير محدد'}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

            {/* Admin Additional Recipients (Optional) */}
            {newMessage.recipientType === 'admin' && !isReplyMode && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  مستلمين إضافيين (اختياري)
                </label>
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginBottom: '8px'
                }}>
                  سيتم إرسال الرسالة إلى جميع المديرين تلقائياً. يمكنك إضافة مستلمين إضافيين (أولياء أمور أو أساتذة) إذا لزم الأمر.
                </div>
                <div style={{
                  maxHeight: '150px',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  padding: '8px',
                  backgroundColor: 'white'
                }}>
                  {students.map(student => (
                    <div key={student.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="checkbox"
                        id={`admin_extra_${student.id}`}
                        checked={selectedUsers.includes(student.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers([...selectedUsers, student.id]);
                          } else {
                            setSelectedUsers(selectedUsers.filter(id => id !== student.id));
                          }
                        }}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <label htmlFor={`admin_extra_${student.id}`} style={{
                        fontSize: '14px',
                        color: '#424242',
                        cursor: 'pointer'
                      }}>
                        {student.student_name} - {student.parent_name || 'غير محدد'}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Class Selection */}
            {showClassSelection && !isReplyMode && (
              <div style={{ marginTop: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  اختر الحلقة
                </label>
                <select
                  value={newMessage.selectedClass}
                  onChange={(e) => setNewMessage({...newMessage, selectedClass: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px'
                  }}
                >
                  <option value="">اختر حلقة</option>
                  <option value="حلقة الحفظ بالمصحف">حلقة الحفظ بالمصحف</option>
                  <option value="حلقة الحفظ بالسمع">حلقة الحفظ بالسمع</option>
                </select>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px' }}>
              {/* Subject */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  الموضوع *
                </label>
                <input
                  type="text"
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px'
                  }}
                  placeholder={t('enterMessageSubject')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              {/* Content */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  المحتوى *
                </label>
                <textarea
                  value={newMessage.content}
                  onChange={(e) => setNewMessage({...newMessage, content: e.target.value})}
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px',
                    resize: 'vertical'
                  }}
                  placeholder={t('enterMessageContent')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              {/* Priority */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  أولوية الرسالة
                </label>
                <select
                  value={newMessage.priority}
                  onChange={(e) => setNewMessage({...newMessage, priority: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px'
                  }}
                >
                  <option value="normal">عادية</option>
                  <option value="high">عالية</option>
                  <option value="urgent">عاجلة</option>
                </select>
              </div>
            </div>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowSendMessageModal(false);
                  setMessageToReplyTo(null);
                  setIsReplyMode(false);
                  setNewMessage({
                    recipientType: 'all',
                    subject: '',
                    content: '',
                    priority: 'normal',
                    selectedClass: ''
                  });
                  setSelectedUsers([]);
                  setShowSpecificUsers(false);
                  setShowClassSelection(false);
                  setMessageError(null);
                }}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSendMessage}
                disabled={isSubmittingMessage}
                style={{
                  backgroundColor: isSubmittingMessage ? '#ccc' : '#4A90E2',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: isSubmittingMessage ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmittingMessage ? 'جاري الإرسال...' : 'إرسال'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* ── Modal de confirmation suppression PARENT ── */}
      {deleteParentModal && (() => {
        const { parent, childrenCount } = deleteParentModal;
        const nameToType = parent.full_name || parent.fullName || '';
        const confirmed = deleteParentInput.trim() === nameToType.trim();
        return (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>🗑️</div>
                <div>
                  <div style={{ fontSize: '17px', fontWeight: '700', color: '#111827' }}>حذف ولي الأمر</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>هذا الإجراء لا يمكن التراجع عنه</div>
                </div>
              </div>

              <div style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400E', marginBottom: '6px' }}>⚠️ معلومات ولي الأمر</div>
                <div style={{ fontSize: '14px', color: '#1F2937', fontWeight: '600' }}>{nameToType}</div>
                {childrenCount > 0 ? (
                  <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#FEE2E2', borderRadius: '8px', fontSize: '13px', color: '#991B1B', fontWeight: '600' }}>
                    🚫 يمتلك {childrenCount} طالب(ين) مسجلين — سيتم رفض الحذف من الخادم
                  </div>
                ) : (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#059669' }}>✅ لا يوجد أبناء مرتبطون بهذا الحساب</div>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', color: '#374151', marginBottom: '8px' }}>
                  لتأكيد الحذف، اكتب اسم ولي الأمر بالضبط:
                </div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#DC2626', backgroundColor: '#FEF2F2', padding: '6px 10px', borderRadius: '6px', marginBottom: '8px', fontFamily: 'monospace' }}>
                  {nameToType}
                </div>
                <input
                  value={deleteParentInput}
                  onChange={e => setDeleteParentInput(e.target.value)}
                  placeholder="اكتب الاسم هنا..."
                  style={{ width: '100%', padding: '10px 12px', border: `2px solid ${confirmed ? '#10B981' : '#E5E7EB'}`, borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                  autoFocus
                />
                {deleteParentInput && !confirmed && (
                  <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '4px' }}>الاسم غير مطابق</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setDeleteParentModal(null); setDeleteParentInput(''); }}
                  style={{ flex: 1, minWidth: '90px', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', backgroundColor: '#fff', color: '#374151' }}>
                  إلغاء
                </button>
                {childrenCount > 0 && (
                  <button
                    onClick={handleSetParentInactive}
                    disabled={deleteParentLoading}
                    style={{ flex: 2, minWidth: '160px', padding: '10px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: deleteParentLoading ? 'not-allowed' : 'pointer', backgroundColor: '#F59E0B', color: '#fff', opacity: deleteParentLoading ? 0.7 : 1 }}>
                    {deleteParentLoading ? '⏳...' : '⏸️ تغيير الحالة إلى غير نشط'}
                  </button>
                )}
                {childrenCount === 0 && (
                  <button
                    onClick={confirmDeleteParent}
                    disabled={!confirmed || deleteParentLoading}
                    style={{ flex: 1, minWidth: '100px', padding: '10px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: confirmed && !deleteParentLoading ? 'pointer' : 'not-allowed', backgroundColor: confirmed ? '#DC2626' : '#FCA5A5', color: '#fff', transition: 'background-color 0.2s', opacity: deleteParentLoading ? 0.7 : 1 }}>
                    {deleteParentLoading ? '⏳ جاري الحذف...' : '🗑️ حذف نهائي'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal de confirmation suppression ÉLÈVE ── */}
      {deleteStudentModal && (() => {
        const { student } = deleteStudentModal;
        const nameToType = student.student_name || '';
        const canDelete = deleteStudentCheck?.can_delete ?? false;
        const confirmed = deleteStudentInput.trim() === nameToType.trim();
        const checks: Array<{ type: string; count: number; blocks: boolean; label_ar: string }> = deleteStudentCheck?.checks || [];
        const blockingChecks = checks.filter((c: any) => c.blocks && c.count > 0);
        const cascadeChecks = checks.filter((c: any) => !c.blocks && c.count > 0);
        const iconMap: Record<string, string> = {
          grades: '📝', quran_records: '📖', contributions: '💰',
          quran_next: '🕌', student_classes: '🏫', student_teachers: '👨‍🏫', monthly_contrib_empty: '📋'
        };
        return (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '520px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto' }}>

              {/* En-tête */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>🗑️</div>
                <div>
                  <div style={{ fontSize: '17px', fontWeight: '700', color: '#111827' }}>حذف الطالب</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>هذا الإجراء لا يمكن التراجع عنه</div>
                </div>
              </div>

              {/* Infos élève */}
              <div style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>👤 معلومات الطالب</div>
                <div style={{ fontSize: '15px', color: '#1F2937', fontWeight: '700' }}>{nameToType}</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                  {student.level && <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#E0E7FF', color: '#3730A3', fontWeight: '500' }}>📚 {student.level}</span>}
                  {student.halqa && <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#F3F4F6', color: '#374151', fontWeight: '500' }}>🕌 {student.halqa}</span>}
                  {student.parent_name && <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#DBEAFE', color: '#1D4ED8', fontWeight: '500' }}>👨‍👧 {student.parent_name}</span>}
                </div>
              </div>

              {/* Chargement des données */}
              {deleteStudentCheckLoading && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280', fontSize: '14px' }}>⏳ جاري التحقق من البيانات...</div>
              )}

              {/* Données chargées */}
              {!deleteStudentCheckLoading && deleteStudentCheck && (
                <>
                  {/* Données bloquantes */}
                  {blockingChecks.length > 0 && (
                    <div style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#991B1B', marginBottom: '10px' }}>🚫 بيانات تمنع الحذف</div>
                      {blockingChecks.map((c: any, i: number) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '6px', marginBottom: i < blockingChecks.length - 1 ? '6px' : '0' }}>
                          <span style={{ fontSize: '13px', color: '#1F2937' }}>{iconMap[c.type] || '⚠️'} {c.label_ar}</span>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#DC2626', backgroundColor: '#FEE2E2', padding: '2px 8px', borderRadius: '10px' }}>{c.count}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Données qui seront supprimées en cascade */}
                  {cascadeChecks.length > 0 && (
                    <div style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400E', marginBottom: '10px' }}>⚠️ بيانات ستُحذف تلقائياً</div>
                      {cascadeChecks.map((c: any, i: number) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '6px', marginBottom: i < cascadeChecks.length - 1 ? '6px' : '0' }}>
                          <span style={{ fontSize: '13px', color: '#1F2937' }}>{iconMap[c.type] || '📋'} {c.label_ar}</span>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#92400E', backgroundColor: '#FEF3C7', padding: '2px 8px', borderRadius: '10px' }}>{c.count}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Aucune donnée → suppression libre */}
                  {blockingChecks.length === 0 && cascadeChecks.length === 0 && (
                    <div style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '12px', marginBottom: '12px', fontSize: '13px', color: '#065F46', fontWeight: '600' }}>
                      ✅ لا توجد بيانات مرتبطة بهذا الطالب. يمكن حذفه بأمان.
                    </div>
                  )}
                </>
              )}

              {/* Champ de confirmation par nom (seulement si suppression possible) */}
              {!deleteStudentCheckLoading && canDelete && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', color: '#374151', marginBottom: '8px' }}>لتأكيد الحذف، اكتب اسم الطالب بالضبط:</div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#DC2626', backgroundColor: '#FEF2F2', padding: '6px 10px', borderRadius: '6px', marginBottom: '8px', fontFamily: 'monospace' }}>
                    {nameToType}
                  </div>
                  <input
                    value={deleteStudentInput}
                    onChange={e => setDeleteStudentInput(e.target.value)}
                    placeholder="اكتب اسم الطالب هنا..."
                    style={{ width: '100%', padding: '10px 12px', border: `2px solid ${confirmed ? '#10B981' : '#E5E7EB'}`, borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                    autoFocus
                  />
                  {deleteStudentInput && !confirmed && (
                    <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '4px' }}>الاسم غير مطابق</div>
                  )}
                </div>
              )}

              {/* Boutons */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setDeleteStudentModal(null); setDeleteStudentInput(''); setDeleteStudentCheck(null); }}
                  style={{ flex: 1, minWidth: '100px', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', backgroundColor: '#fff', color: '#374151' }}>
                  إلغاء
                </button>
                {!deleteStudentCheckLoading && !canDelete && deleteStudentCheck && (
                  <button
                    onClick={handleSetStudentInactive}
                    disabled={deleteStudentLoading}
                    style={{ flex: 2, minWidth: '160px', padding: '10px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: deleteStudentLoading ? 'not-allowed' : 'pointer', backgroundColor: '#F59E0B', color: '#fff', opacity: deleteStudentLoading ? 0.7 : 1 }}>
                    {deleteStudentLoading ? '⏳...' : '⏸️ تغيير الحالة إلى غير نشط'}
                  </button>
                )}
                {!deleteStudentCheckLoading && canDelete && (
                  <button
                    onClick={confirmDeleteStudent}
                    disabled={!confirmed || deleteStudentLoading}
                    style={{ flex: 1, minWidth: '100px', padding: '10px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: confirmed && !deleteStudentLoading ? 'pointer' : 'not-allowed', backgroundColor: confirmed ? '#DC2626' : '#FCA5A5', color: '#fff', transition: 'background-color 0.2s', opacity: deleteStudentLoading ? 0.7 : 1 }}>
                    {deleteStudentLoading ? '⏳ جاري الحذف...' : '🗑️ حذف نهائي'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal d'ajout d'enseignant */}
      {showAddTeacherForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              إضافة أستاذ جديد
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('teacherName')} *
                </label>
                <input
                  type="text"
                  value={newTeacher.fullName}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, fullName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                  placeholder={t('enterTeacherFullName')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('teacherUsername')} *
                </label>
                <input
                  type="text"
                  value={newTeacher.username}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, username: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                  placeholder={t('username')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('teacherPassword') || t('password')} *
                </label>
                <PasswordInput
                  value={newTeacher.password}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, password: e.target.value }))}
                  placeholder={t('password')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                  required
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('confirmPassword')} *
                </label>
                <PasswordInput
                  value={newTeacher.confirmPassword}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder={t('confirmPassword')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('studentPhone')} *
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', flexDirection: isRTL ? 'row-reverse' : 'row', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden' }}>
                    <button type="button" onClick={() => { setFormPhonePickerOpen(formPhonePickerOpen === 'teacher' ? null : 'teacher'); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 10px', backgroundColor: '#f9fafb', border: 'none', borderRight: isRTL ? 'none' : '1px solid #e5e7eb', borderLeft: isRTL ? '1px solid #e5e7eb' : 'none', cursor: 'pointer', flexShrink: 0, direction: 'ltr' }}>
                      <span>{teacherPhoneCountry.flag}</span>
                      <span style={{ fontSize: '12px', color: '#059669', fontWeight: '700' }}>+{teacherPhoneCountry.dialCode}</span>
                      <span style={{ fontSize: '9px', color: '#9ca3af' }}>▼</span>
                    </button>
                    <input type="tel" value={newTeacher.phone} onChange={(e) => setNewTeacher(prev => ({ ...prev, phone: e.target.value }))} style={{ flex: 1, padding: '8px', border: 'none', outline: 'none', fontSize: '13px', direction: 'ltr', textAlign: 'left' }} placeholder={t('enterPhone')} />
                  </div>
                  {formPhonePickerOpen === 'teacher' && <><div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => { setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} /><div style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 0, top: 'calc(100% + 4px)', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: '280px', maxHeight: '280px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 999 }}><div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#fafafa' }}><span>🔍</span><input autoFocus value={formPhoneSearch} onChange={e => setFormPhoneSearch(e.target.value)} placeholder={currentLanguage === 'ar' ? 'بحث...' : 'Rechercher...'} style={{ flex: 1, border: 'none', outline: 'none', fontSize: '12px', backgroundColor: 'transparent' }} /></div><div style={{ overflowY: 'auto', flex: 1 }}>{COUNTRIES.filter(c => !formPhoneSearch.trim() || c.name.ar.includes(formPhoneSearch) || c.name.fr.toLowerCase().includes(formPhoneSearch.toLowerCase()) || c.dialCode.includes(formPhoneSearch)).map(c => { const sel = c.code === teacherPhoneCountry.code; return (<div key={c.code} onClick={() => { setTeacherPhoneCountry(c); setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f9fafb', backgroundColor: sel ? '#f0fdf4' : 'transparent' }} onMouseEnter={e => { if (!sel) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = sel ? '#f0fdf4' : 'transparent'; }}><span>{c.flag}</span><span style={{ flex: 1, fontSize: '12px', color: sel ? '#059669' : '#374151', fontWeight: sel ? '600' : '400' }}>{c.name[currentLanguage as 'ar' | 'fr' | 'en'] || c.name.ar}</span><span style={{ fontSize: '11px', color: '#9ca3af', minWidth: '40px', textAlign: 'right' }}>+{c.dialCode}</span>{sel && <span style={{ color: '#059669' }}>✓</span>}</div>); })}</div></div></>}
                </div>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('parentEmail')} *
                </label>
                <input
                  type="email"
                  required
                  value={newTeacher.email}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                  placeholder={t('enterEmail')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('gender')} *
                </label>
                <select
                  value={newTeacher.gender || 'Male'}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, gender: e.target.value as 'Female' | 'Male' }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                  required
                >
                  <option value="Male">{t('male')}</option>
                  <option value="Female">{t('female')}</option>
                </select>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('teacherSpecialization')} *
                </label>
                <select
                  value={newTeacher.specialization}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, specialization: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                >
                  <option value="">اختر التخصص</option>
                  <option value="القرآن الكريم">القرآن الكريم</option>
                  <option value="التفسير">التفسير</option>
                  <option value="التجويد">التجويد</option>
                  <option value="الحديث الشريف">الحديث الشريف</option>
                  <option value="الفقه">الفقه</option>
                  <option value="العقيدة">العقيدة</option>
                  <option value="السيرة النبوية">السيرة النبوية</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#374151'
              }}>
                الحلقات المسؤول عنها *
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '8px',
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                {GLOBAL_CONSTANTS.MANAGED_CIRCLES.map((halqa) => (
                  <label key={halqa} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      value={halqa}
                      checked={newTeacher.assigned_halqas ? newTeacher.assigned_halqas.split(',').map(s => s.trim()).includes(halqa) : false}
                      onChange={(e) => {
                        const currentHalqas = newTeacher.assigned_halqas ? newTeacher.assigned_halqas.split(',').map(s => s.trim()).filter(s => s) : [];
                        if (e.target.checked) {
                          const newHalqas = [...currentHalqas, halqa];
                          setNewTeacher(prev => ({
                            ...prev,
                            assigned_halqas: newHalqas.join(', ')
                          }));
                        } else {
                          const newHalqas = currentHalqas.filter(h => h !== halqa);
                          setNewTeacher(prev => ({
                            ...prev,
                            assigned_halqas: newHalqas.join(', ')
                          }));
                        }
                      }}
                      style={{ margin: 0 }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>{halqa}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Toggles de notification */}
            {(newTeacher.phone.trim().length > 0 || newTeacher.email.trim().length > 0) && (
              <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>{t('sendCredentials')}</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {newTeacher.phone.trim().length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <button type="button" onClick={() => setTeacherSendSms(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${teacherSendSms ? '#6b4eff' : '#d1d5db'}`, backgroundColor: teacherSendSms ? '#eef2ff' : '#fff', color: teacherSendSms ? '#6b4eff' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                        💬 SMS
                      </button>
                      {teacherSendSms && (
                        <span style={{ fontSize: '10px', color: '#f59e0b', paddingRight: '4px' }}>
                          ⚠️ يتطلب فتح تطبيق الرسائل على الهاتف
                        </span>
                      )}
                    </div>
                  )}
                  {newTeacher.phone.trim().length > 0 && (
                    <button type="button" onClick={() => setTeacherSendWhatsApp(v => !v)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${teacherSendWhatsApp ? '#25d366' : '#d1d5db'}`, backgroundColor: teacherSendWhatsApp ? '#f0fdf4' : '#fff', color: teacherSendWhatsApp ? '#16a34a' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                      📱 WhatsApp
                    </button>
                  )}
                  {newTeacher.email.trim().length > 0 && (
                    <button type="button" onClick={() => setTeacherSendEmail(v => !v)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${teacherSendEmail ? '#6b4eff' : '#d1d5db'}`, backgroundColor: teacherSendEmail ? '#eef2ff' : '#fff', color: teacherSendEmail ? '#6b4eff' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                      ✉️ Email
                    </button>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                {t('notes')}
              </label>
              <textarea
                value={newTeacher.notes}
                onChange={(e) => setNewTeacher(prev => ({ ...prev, notes: e.target.value }))}
                rows={1}
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }}
                placeholder={t('teacherNotesOptional')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            {teacherError && (
              <div style={{
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {teacherError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowAddTeacherForm(false);
                  setNewTeacher({
                    fullName: '',
                    username: '',
                    password: '',
                    confirmPassword: '',
                    phone: '',
                    email: '',
                    specialization: '',
                    assigned_halqas: '',
                    experience_years: 0,
                    certification_date: '',
                    notes: '',
                    status: 'active',
                    gender: 'Male'
                  });
                  setTeacherSendEmail(true); setTeacherSendSms(false); setTeacherSendWhatsApp(false);
                  setTeacherError(null);
                }}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleAddTeacher}
                disabled={isSubmittingTeacher}
                style={{
                  padding: '12px 24px',
                  backgroundColor: isSubmittingTeacher ? '#9ca3af' : '#14b8a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: isSubmittingTeacher ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmittingTeacher ? 'جاري الإضافة...' : 'إضافة الأستاذ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de modification d'enseignant */}
      {showEditTeacherForm && editingTeacher && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              تعديل بيانات الأستاذ
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('teacherName')} *
                </label>
                <input
                  type="text"
                  value={newTeacher.fullName}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, fullName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('teacherUsername')} *
                </label>
                <input
                  type="text"
                  value={newTeacher.username}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, username: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  كلمة المرور (اتركها فارغة للاحتفاظ بالحالية)
                </label>
                <PasswordInput
                  value={newTeacher.password}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, password: e.target.value }))}
                  placeholder={t('newPasswordOptional')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('confirmPassword')}
                </label>
                <PasswordInput
                  value={newTeacher.confirmPassword}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder={t('confirmPassword')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('studentPhone')} *
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', flexDirection: isRTL ? 'row-reverse' : 'row', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden' }}>
                    <button type="button" onClick={() => { setFormPhonePickerOpen(formPhonePickerOpen === 'teacher' ? null : 'teacher'); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 10px', backgroundColor: '#f9fafb', border: 'none', borderRight: isRTL ? 'none' : '1px solid #e5e7eb', borderLeft: isRTL ? '1px solid #e5e7eb' : 'none', cursor: 'pointer', flexShrink: 0, direction: 'ltr' }}>
                      <span>{teacherPhoneCountry.flag}</span>
                      <span style={{ fontSize: '12px', color: '#059669', fontWeight: '700' }}>+{teacherPhoneCountry.dialCode}</span>
                      <span style={{ fontSize: '9px', color: '#9ca3af' }}>▼</span>
                    </button>
                    <input type="tel" value={newTeacher.phone} onChange={(e) => setNewTeacher(prev => ({ ...prev, phone: e.target.value }))} style={{ flex: 1, padding: '8px', border: 'none', outline: 'none', fontSize: '13px', direction: 'ltr', textAlign: 'left' }} placeholder={t('enterPhone')} />
                  </div>
                  {formPhonePickerOpen === 'teacher' && <><div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => { setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} /><div style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 0, top: 'calc(100% + 4px)', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: '280px', maxHeight: '280px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 999 }}><div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#fafafa' }}><span>🔍</span><input autoFocus value={formPhoneSearch} onChange={e => setFormPhoneSearch(e.target.value)} placeholder={currentLanguage === 'ar' ? 'بحث...' : 'Rechercher...'} style={{ flex: 1, border: 'none', outline: 'none', fontSize: '12px', backgroundColor: 'transparent' }} /></div><div style={{ overflowY: 'auto', flex: 1 }}>{COUNTRIES.filter(c => !formPhoneSearch.trim() || c.name.ar.includes(formPhoneSearch) || c.name.fr.toLowerCase().includes(formPhoneSearch.toLowerCase()) || c.dialCode.includes(formPhoneSearch)).map(c => { const sel = c.code === teacherPhoneCountry.code; return (<div key={c.code} onClick={() => { setTeacherPhoneCountry(c); setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f9fafb', backgroundColor: sel ? '#f0fdf4' : 'transparent' }} onMouseEnter={e => { if (!sel) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = sel ? '#f0fdf4' : 'transparent'; }}><span>{c.flag}</span><span style={{ flex: 1, fontSize: '12px', color: sel ? '#059669' : '#374151', fontWeight: sel ? '600' : '400' }}>{c.name[currentLanguage as 'ar' | 'fr' | 'en'] || c.name.ar}</span><span style={{ fontSize: '11px', color: '#9ca3af', minWidth: '40px', textAlign: 'right' }}>+{c.dialCode}</span>{sel && <span style={{ color: '#059669' }}>✓</span>}</div>); })}</div></div></>}
                </div>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('parentEmail')}
                </label>
                <input
                  type="email"
                  value={newTeacher.email}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                  placeholder={t('enterEmail')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('gender')} *
                </label>
                <select
                  value={newTeacher.gender || 'Male'}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, gender: e.target.value as 'Female' | 'Male' }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                  required
                >
                  <option value="Male">{t('male')}</option>
                  <option value="Female">{t('female')}</option>
                </select>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('teacherSpecialization')} *
                </label>
                <select
                  value={newTeacher.specialization}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, specialization: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                >
                  <option value="">اختر التخصص</option>
                  <option value="القرآن الكريم">القرآن الكريم</option>
                  <option value="التفسير">التفسير</option>
                  <option value="التجويد">التجويد</option>
                  <option value="الحديث الشريف">الحديث الشريف</option>
                  <option value="الفقه">الفقه</option>
                  <option value="العقيدة">العقيدة</option>
                  <option value="السيرة النبوية">السيرة النبوية</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#374151'
              }}>
                الحلقات المسؤول عنها *
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '8px',
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                {GLOBAL_CONSTANTS.MANAGED_CIRCLES.map((halqa) => (
                  <label key={halqa} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      value={halqa}
                      checked={newTeacher.assigned_halqas ? newTeacher.assigned_halqas.split(',').map(s => s.trim()).includes(halqa) : false}
                      onChange={(e) => {
                        const currentHalqas = newTeacher.assigned_halqas ? newTeacher.assigned_halqas.split(',').map(s => s.trim()).filter(s => s) : [];
                        if (e.target.checked) {
                          const newHalqas = [...currentHalqas, halqa];
                          setNewTeacher(prev => ({
                            ...prev,
                            assigned_halqas: newHalqas.join(', ')
                          }));
                        } else {
                          const newHalqas = currentHalqas.filter(h => h !== halqa);
                          setNewTeacher(prev => ({
                            ...prev,
                            assigned_halqas: newHalqas.join(', ')
                          }));
                        }
                      }}
                      style={{ margin: 0 }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>{halqa}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#374151',
                fontSize: '14px'
              }}>
                الحالة *
              </label>
              <select
                value={newTeacher.status}
                onChange={(e) => setNewTeacher(prev => ({ ...prev, status: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
                required
              >
                <option value="active">{t('active')}</option>
                <option value="inactive">{t('inactive')}</option>
              </select>
            </div>

            {/* Toggles de notification */}
            {(newTeacher.phone.trim().length > 0 || newTeacher.email.trim().length > 0) && (
              <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>{t('sendCredentials')}</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {newTeacher.phone.trim().length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <button type="button" onClick={() => setTeacherSendSms(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${teacherSendSms ? '#6b4eff' : '#d1d5db'}`, backgroundColor: teacherSendSms ? '#eef2ff' : '#fff', color: teacherSendSms ? '#6b4eff' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                        💬 SMS
                      </button>
                      {teacherSendSms && (
                        <span style={{ fontSize: '10px', color: '#f59e0b', paddingRight: '4px' }}>
                          ⚠️ يتطلب فتح تطبيق الرسائل على الهاتف
                        </span>
                      )}
                    </div>
                  )}
                  {newTeacher.phone.trim().length > 0 && (
                    <button type="button" onClick={() => setTeacherSendWhatsApp(v => !v)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${teacherSendWhatsApp ? '#25d366' : '#d1d5db'}`, backgroundColor: teacherSendWhatsApp ? '#f0fdf4' : '#fff', color: teacherSendWhatsApp ? '#16a34a' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                      📱 WhatsApp
                    </button>
                  )}
                  {newTeacher.email.trim().length > 0 && (
                    <button type="button" onClick={() => setTeacherSendEmail(v => !v)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${teacherSendEmail ? '#6b4eff' : '#d1d5db'}`, backgroundColor: teacherSendEmail ? '#eef2ff' : '#fff', color: teacherSendEmail ? '#6b4eff' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                      ✉️ Email
                    </button>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                {t('notes')}
              </label>
              <textarea
                value={newTeacher.notes}
                onChange={(e) => setNewTeacher(prev => ({ ...prev, notes: e.target.value }))}
                rows={1}
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }}
                placeholder={t('teacherNotesOptional')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            {teacherError && (
              <div style={{
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {teacherError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowEditTeacherForm(false);
                  setEditingTeacher(null);
                  setNewTeacher({
                    fullName: '',
                    username: '',
                    password: '',
                    confirmPassword: '',
                    phone: '',
                    email: '',
                    specialization: '',
                    assigned_halqas: '',
                    experience_years: 0,
                    certification_date: '',
                    notes: '',
                    status: 'active',
                    gender: 'Male'
                  });
                  setTeacherSendEmail(true); setTeacherSendSms(false); setTeacherSendWhatsApp(false);
                  setTeacherError(null);
                }}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleUpdateTeacher}
                disabled={isSubmittingTeacher}
                style={{
                  padding: '12px 24px',
                  backgroundColor: isSubmittingTeacher ? '#9ca3af' : '#14b8a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: isSubmittingTeacher ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmittingTeacher ? 'جاري التحديث...' : 'حفظ التعديلات'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section Parents */}
      {currentSection === 'parents' && (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: windowWidth < 768 ? '0 15px' : windowWidth < 1024 ? '0 20px' : '0 30px'
        }}>

          {/* Filtres mobile pour les parents */}
          {windowWidth < 768 && (
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '12px',
                direction: isRTL ? 'rtl' : 'ltr'
              }}>
                {t('filters')}
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  marginBottom: '4px',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}>
                  {t('studentParent')}
                </label>
                <SearchableSelect
                  value={parentFilter}
                  onChange={(value) => setParentFilter(value as string)}
                  options={[
                    { value: '', label: t('all') },
                    ...parents.map(parent => ({
                      value: (parent.id || parent.user_id || '').toString(),
                      label: parent.full_name || parent.fullName || t('notSpecified')
                    }))
                  ]}
                  placeholder={t('all')}
                />
              </div>
            </div>
          )}

          {/* Affichage des parents */}
          {(() => {
            const isMobile = windowWidth < 768;
            const parentsHasDetail = !isMobile && !!selectedParentDetail;
            const renderParentDetailContent = () => {
              if (!selectedParentDetail) return null;
              return (
                <>
                  {/* En-tête du panneau */}
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f9fafb' }}>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '17px', color: '#111827', direction: isRTL ? 'rtl' : 'ltr' }}>
                        {selectedParentDetail.full_name || selectedParentDetail.fullName || selectedParentDetail.username}
                      </div>
                      <span style={{ fontSize: '12px', padding: '2px 10px', borderRadius: '12px', fontWeight: '600', backgroundColor: selectedParentDetail.status === 'active' ? '#D1FAE5' : '#FEF3C7', color: selectedParentDetail.status === 'active' ? '#065F46' : '#92400E' }}>
                        {getDisplayStatus(selectedParentDetail.status)}
                      </span>
                    </div>
                    <button onClick={() => { setSelectedParentDetail(null); setParentDetailChildren([]); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', color: '#6b7280', fontSize: '20px', lineHeight: 1 }}>✕</button>
                  </div>
                  {/* Corps du panneau - scrollable */}
                  <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
                    {/* Infos principales */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#059669', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', direction: isRTL ? 'rtl' : 'ltr' }}>{t('personalInfoSection')}</div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {[
                          { label: t('username'), value: selectedParentDetail.username },
                          { label: t('gender'), value: selectedParentDetail.gender ? (selectedParentDetail.gender === 'Female' ? t('female') : t('male')) : null },
                          { label: t('phone'), value: selectedParentDetail.phone },
                          { label: t('email'), value: selectedParentDetail.email },
                          { label: t('address'), value: selectedParentDetail.address },
                          { label: t('createdDate'), value: selectedParentDetail.created_date ? formatDate(selectedParentDetail.created_date) : null },
                        ].filter(row => !!row.value).map((row, i) => (
                          <div key={row.label} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            direction: isRTL ? 'rtl' : 'ltr',
                            padding: '7px 8px',
                            borderRadius: '6px',
                            backgroundColor: i % 2 === 0 ? '#f9fafb' : 'transparent',
                          }}>
                            <span style={{ color: '#9ca3af', fontSize: '12px', whiteSpace: 'nowrap' }}>{row.label}</span>
                            <span style={{ fontWeight: '500', color: '#111827', fontSize: '13px' }}>{row.value}</span>
                          </div>
                        ))}
                        {selectedParentDetail.notes && (
                          <div style={{ paddingTop: '10px', direction: isRTL ? 'rtl' : 'ltr' }}>
                            <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '5px' }}>{t('notes')}</div>
                            <div style={{ color: '#374151', backgroundColor: '#f9fafb', borderRadius: '6px', padding: '8px 10px', fontSize: '13px', border: '1px solid #e5e7eb' }}>{selectedParentDetail.notes}</div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Section enfants */}
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '14px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#059669', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', direction: isRTL ? 'rtl' : 'ltr' }}>{t('childrenSection')}</div>
                      {parentDetailLoading ? (
                        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '16px' }}>{t('loading')}</div>
                      ) : parentDetailChildren.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '16px', direction: isRTL ? 'rtl' : 'ltr' }}>{t('noChildrenRegistered')}</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {parentDetailChildren.map((child: any, idx: number) => {
                            const isRegistered = isEnrolledForYear(child, currentYear?.id);
                            return (
                              <div key={child.id || idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', overflowX: 'auto', backgroundColor: '#f0fdf4', borderRadius: '8px', padding: '10px 12px', border: '1px solid #d1fae5', direction: isRTL ? 'rtl' : 'ltr' }}>
                                {parentReenrollMode && !isRegistered && (
                                  <input
                                    type="checkbox"
                                    checked={parentReenrollSelection.includes(child.id)}
                                    onChange={(e) => {
                                      setParentReenrollSelection(prev =>
                                        e.target.checked ? [...prev, child.id] : prev.filter(id2 => id2 !== child.id)
                                      );
                                    }}
                                    style={{ cursor: 'pointer', flexShrink: 0 }}
                                  />
                                )}
                                <span style={{ fontWeight: '600', fontSize: '14px', color: '#111827', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                  {child.student_name || child.full_name || child.username || '—'}
                                </span>
                                {child.gender && (
                                  <span style={{ fontSize: '12px', color: child.gender === 'male' || child.gender === 'Male' ? '#3b82f6' : '#ec4899', flexShrink: 0 }}>
                                    {child.gender === 'male' || child.gender === 'Male' ? '♂' : '♀'}
                                  </span>
                                )}
                                {(child.halqa_name || child.halqa) && (
                                  <span style={{ fontSize: '11px', padding: '2px 8px', backgroundColor: '#dbeafe', color: '#1d4ed8', borderRadius: '10px', whiteSpace: 'nowrap', flexShrink: 0 }}>📚 {child.halqa_name || child.halqa}</span>
                                )}
                                {child.level && (
                                  <span style={{ fontSize: '11px', padding: '2px 8px', backgroundColor: '#ede9fe', color: '#5b21b6', borderRadius: '10px', whiteSpace: 'nowrap', flexShrink: 0 }}>🎓 {child.level}</span>
                                )}
                                {child.status && (
                                  <span style={{ fontSize: '11px', padding: '2px 8px', backgroundColor: child.status === 'نشط' ? '#d1fae5' : '#fef3c7', color: child.status === 'نشط' ? '#065f46' : '#92400e', borderRadius: '10px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {child.status}
                                  </span>
                                )}
                                {inscriptionYear?.label && (
                                  <span style={{ fontSize: '10px', color: '#6b7280', whiteSpace: 'nowrap', flexShrink: 0 }}>{inscriptionYear.label}</span>
                                )}
                                {!isRegistered && (
                                  <span style={{ marginLeft: 'auto', fontSize: '11px', padding: '2px 8px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '10px', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {t('notRegistered')}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {/* Boutons d'action */}
                    {canModify('parents') && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #e5e7eb', justifyContent: 'flex-end' }}>
                        {!parentReenrollMode && parentDetailChildren.some((c: any) => !isEnrolledForYear(c, currentYear?.id)) && (
                          <button
                            onClick={() => { setParentReenrollMode(true); setParentReenrollSelection([]); }}
                            style={{ ...GLOBAL_STYLES.editButton, padding: '8px 16px', fontSize: '13px' }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}
                          >
                            <span>🔄</span>
                            <span>{t('reEnroll')}</span>
                          </button>
                        )}
                        {parentReenrollMode && (
                          <button
                            onClick={handleParentReenroll}
                            disabled={parentReenrollSelection.length === 0}
                            style={{ ...GLOBAL_STYLES.editButton, padding: '8px 16px', fontSize: '13px', cursor: parentReenrollSelection.length === 0 ? 'not-allowed' : 'pointer', opacity: parentReenrollSelection.length === 0 ? 0.5 : 1 }}
                            onMouseOver={(e) => { if (parentReenrollSelection.length > 0) e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover; }}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}
                          >
                            <span>✔</span>
                            <span>{t('confirm')}</span>
                          </button>
                        )}
                        {parentReenrollMode && (
                          <button
                            onClick={() => { setParentReenrollMode(false); setParentReenrollSelection([]); }}
                            style={{ ...GLOBAL_STYLES.editButton, padding: '8px 16px', fontSize: '13px' }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}
                          >
                            <span>✖</span>
                            <span>{t('cancel')}</span>
                          </button>
                        )}
                        <button
                          onClick={() => { handleEditParent(selectedParentDetail); setSelectedParentDetail(null); }}
                          style={{ ...GLOBAL_STYLES.editButton, padding: '8px 16px', fontSize: '13px' }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}
                        >
                          <EditIcon size={15} />
                          <span>{t('edit')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              );
            };
            return (
          <div style={{ display: 'flex', gap: '16px', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            padding: windowWidth < 768 && parentMobileViewMode === 'cards' ? '12px' : '0',
            flex: parentsHasDetail ? '0 0 50%' : undefined,
            minWidth: 0,
            width: parentsHasDetail ? undefined : '100%',
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: '75vh'
          }}>
            {/* Vue mobile - Cartes */}
            {windowWidth < 768 && parentMobileViewMode === 'cards' ? (
              (() => {
                const filteredParents = parents.filter(parent => {
                  if (!parentFilter) return true;
                  const parentId = (parent.id || parent.user_id || '').toString();
                  return parentId === parentFilter;
                });

                if (parents.length === 0) {
                  return (
                    <div style={{
                      padding: '40px 16px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '14px'
                    }}>
                      {t('noParentsRegistered')}
                    </div>
                  );
                }

                if (filteredParents.length === 0) {
                  return (
                    <div style={{
                      padding: '40px 16px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '14px'
                    }}>
                      {t('noFilterResultsParents')}
                    </div>
                  );
                }

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '70vh', overflowY: 'auto' }}>
                    {filteredParents.map((parent, index) => (
                      <div
                        key={parent.id || parent.user_id || `parent-${index}`}
                        style={{
                          backgroundColor: '#f9fafb',
                          borderRadius: '8px',
                          padding: '16px',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                      >
                        {/* Nom du parent - En-tête de la carte */}
                        <div dir="auto" style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '12px',
                          paddingBottom: '12px',
                          borderBottom: '2px solid #e5e7eb',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }}>
                          {parent.full_name || parent.fullName || t('notSpecified')}
                        </div>
                        
                        {/* Informations du parent */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', direction: isRTL ? 'rtl' : 'ltr' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('username')}:</span>
                            <span style={{ fontSize: '13px', color: '#374151' }}>{parent.username || t('notSpecified')}</span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('phone')}:</span>
                            <span style={{ fontSize: '13px', color: '#374151', direction: 'ltr' }}>{parent.phone || t('notSpecified')}</span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('gender')}:</span>
                            <span style={{ fontSize: '13px', color: '#374151' }}>{parent.gender === 'Female' ? t('female') : t('male')}</span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('address')}:</span>
                            <span style={{ fontSize: '13px', color: '#374151' }}>{parent.address || t('notSpecified')}</span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('createdDate')}:</span>
                            <span style={{ fontSize: '13px', color: '#374151' }}>{formatDate(parent.created_date)}</span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{t('status')}:</span>
                            <span style={GLOBAL_STYLES.statusBadge(parent.status === 'active')}>
                              {getDisplayStatus(parent.status)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        {canModify('parents') && (
                          <div style={{
                            display: 'flex',
                            gap: '8px',
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: '1px solid #e5e7eb',
                            direction: isRTL ? 'rtl' : 'ltr'
                          }}>
                            <button
                              onClick={() => {
                                setEditingParent(parent);
                                setNewParent({
                                  fullName: parent.full_name || parent.fullName || '',
                                  username: parent.username || '',
                                  password: '',
                                  confirmPassword: '',
                                  phone: parent.phone || '',
                                  email: parent.email || '',
                                  address: parent.address || '',
                                  notes: parent.notes || '',
                                  status: parent.status || 'active',
                                  gender: parent.gender || 'Male'
                                });
                                setParentSendEmail(true); setParentSendSms(false); setParentSendWhatsApp(false);
                                setShowEditParentForm(true);
                              }}
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                              }}
                            >
                              <EditIcon size={14} />
                              <span>{t('edit')}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()
            ) : (
              /* Vue desktop - Tableau */
              <div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{
                      padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px',
                      textAlign: isRTL ? 'right' : 'left',
                      fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                      fontWeight: '500',
                      color: '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      width: '1%',
                      whiteSpace: 'nowrap'
                    }}>
                      {windowWidth < 768 ? (
                        <span>{t('studentParent')}</span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span>{t('studentParent')}</span>
                          <SearchableSelect
                            value={parentFilter}
                            onChange={(value) => setParentFilter(value as string)}
                            options={[
                              { value: '', label: t('all') },
                              ...parents.map(parent => ({
                                value: (parent.id || parent.user_id || '').toString(),
                                label: parent.full_name || parent.fullName || t('notSpecified')
                              }))
                            ]}
                            placeholder={t('all')}
                            compact={true}
                          />
                        </div>
                      )}
                    </th>
                    <th style={{
                      padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px',
                      textAlign: isRTL ? 'right' : 'left',
                      fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                      fontWeight: '500',
                      color: '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      display: selectedParentDetail ? 'none' : 'table-cell'
                    }}>{t('username')}</th>
                    <th style={{
                      padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px',
                      textAlign: isRTL ? 'right' : 'left',
                      fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                      fontWeight: '500',
                      color: '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      display: selectedParentDetail || windowWidth < 768 ? 'none' : 'table-cell'
                    }}>{t('gender')}</th>
                    <th style={{
                      padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px',
                      textAlign: isRTL ? 'right' : 'left',
                      fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                      fontWeight: '500',
                      color: '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      display: 'none'
                    }}>{t('address')}</th>
                    <th style={{
                      padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px',
                      textAlign: isRTL ? 'right' : 'left',
                      fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                      fontWeight: '500',
                      color: '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      display: 'none'
                    }}>{t('createdDate')}</th>
                    <th style={{
                      padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px',
                      textAlign: isRTL ? 'right' : 'left',
                      fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                      fontWeight: '500',
                      color: '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb'
                    }}>{t('status')}</th>
                    {canModify('parents') && (
                      <th style={{
                        padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px',
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: windowWidth < 768 ? '11px' : windowWidth < 1024 ? '12px' : '14px',
                        fontWeight: '500',
                        color: '#6b7280',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e5e7eb',
                        display: selectedParentDetail ? 'none' : 'table-cell'
                      }}>{t('actions')}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filteredParents = parents.filter(parent => {
                      if (!parentFilter) return true;
                      const parentId = (parent.id || parent.user_id || '').toString();
                      return parentId === parentFilter;
                    });

                    if (parents.length === 0) {
                      return (
                        <tr>
                          <td colSpan={7} style={{
                            padding: windowWidth < 768 ? '30px 16px' : '40px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: windowWidth < 768 ? '14px' : '16px'
                          }}>
                            {t('noParentsRegistered')}
                          </td>
                        </tr>
                      );
                    }

                    if (filteredParents.length === 0) {
                      return (
                        <tr>
                          <td colSpan={7} style={{
                            padding: windowWidth < 768 ? '30px 16px' : '40px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: windowWidth < 768 ? '14px' : '16px'
                          }}>
                            {t('noFilterResultsParents')}
                          </td>
                        </tr>
                      );
                    }

                    return filteredParents.map((parent, index) => {
                      const isSelected = selectedParentDetail && (selectedParentDetail.id || selectedParentDetail.user_id) === (parent.id || parent.user_id);
                      return (
                      <React.Fragment key={parent.id || parent.user_id || `parent-${index}`}>
                      <tr
                        onClick={() => handleParentDetailOpen(parent)}
                        style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer', backgroundColor: isSelected ? '#f0fdf4' : 'transparent', transition: 'background-color 0.15s' }}
                        onMouseOver={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = '#f9fafb'; }}
                        onMouseOut={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                      >
                        <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left' }}>
                          <div>
                            <div dir="auto" style={{ fontWeight: '500', color: '#111827', fontSize: windowWidth < 768 ? '13px' : windowWidth < 1024 ? '14px' : '16px' }}>
                              {parent.full_name || parent.fullName || t('notSpecified')}
                            </div>
                            <div style={{ fontSize: windowWidth < 768 ? '11px' : '14px', color: '#6b7280', direction: 'ltr' }}>
                              {parent.phone || t('notSpecified')}
                            </div>
                          </div>
                        </td>
                        <td dir="auto" style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', color: '#374151', fontSize: windowWidth < 768 ? '12px' : '14px', textAlign: isRTL ? 'right' : 'left', display: selectedParentDetail ? 'none' : 'table-cell' }}>
                          {parent.username || t('notSpecified')}
                        </td>
                        <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', color: '#374151', fontSize: windowWidth < 768 ? '12px' : '14px', textAlign: isRTL ? 'right' : 'left', display: selectedParentDetail || windowWidth < 768 ? 'none' : 'table-cell' }}>
                          {parent.gender === 'Female' ? t('female') : t('male')}
                        </td>
                        <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', color: '#374151', fontSize: windowWidth < 768 ? '12px' : '14px', textAlign: isRTL ? 'right' : 'left', display: 'none' }}>
                          {parent.address || t('notSpecified')}
                        </td>
                        <td style={{ display: 'none' }}>
                          {formatDate(parent.created_date)}
                        </td>
                        <td style={{ padding: windowWidth < 768 ? '12px 8px' : windowWidth < 1024 ? '14px 12px' : '16px', textAlign: isRTL ? 'right' : 'left' }}>
                          <span style={GLOBAL_STYLES.statusBadge(parent.status === 'active')}>
                            {getDisplayStatus(parent.status)}
                          </span>
                        </td>
                        {canModify('parents') && (
                          <td style={{ padding: windowWidth < 768 ? '8px' : windowWidth < 1024 ? '10px 12px' : '12px 16px', textAlign: isRTL ? 'right' : 'left', display: selectedParentDetail ? 'none' : 'table-cell' }}>
                            <div style={{ display: 'flex', gap: '6px', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleEditParent(parent); }}
                                style={{ ...GLOBAL_STYLES.editButton, padding: windowWidth < 768 ? '5px 8px' : '7px 12px', fontSize: windowWidth < 768 ? '11px' : '13px' }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButtonHover}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = GLOBAL_STYLES.editButton.backgroundColor}>
                                <EditIcon size={windowWidth < 768 ? 13 : 15} />
                                {windowWidth >= 768 && <span>{t('edit')}</span>}
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                      {isMobile && isSelected && selectedParentDetail && (
                        <tr>
                          <td colSpan={6} style={{ padding: 0, border: '1px solid #3b82f6', borderTop: 'none', backgroundColor: 'white' }}>
                            {renderParentDetailContent()}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
            )}
          </div>

          {/* Panneau de détail du parent sélectionné - desktop uniquement */}
          {!isMobile && selectedParentDetail && (
            <div style={{
              flex: '0 0 50%',
              minWidth: 0,
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {renderParentDetailContent()}
            </div>
          )}
          </div>
          );
          })()}
        </div>
      )}

      {/* Formulaire d'ajout de parent */}
      {showAddParentForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              {t('addNewParent')}
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('parentName')} *
                </label>
                <input
                  type="text"
                  value={newParent.fullName}
                  onChange={(e) => setNewParent(prev => ({ ...prev, fullName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('parentUsername')} *
                </label>
                <input
                  type="text"
                  value={newParent.username}
                  onChange={(e) => setNewParent(prev => ({ ...prev, username: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                  {t('password')} *
                </label>
                <PasswordInput
                  value={newParent.password}
                  onChange={(e) => setNewParent(prev => ({ ...prev, password: e.target.value }))}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                  {t('confirmPassword')} *
                </label>
                <PasswordInput
                  value={newParent.confirmPassword}
                  onChange={(e) => setNewParent(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('studentPhone')} *
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', flexDirection: isRTL ? 'row-reverse' : 'row', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden' }}>
                    <button type="button" onClick={() => { setFormPhonePickerOpen(formPhonePickerOpen === 'parent' ? null : 'parent'); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 10px', backgroundColor: '#f9fafb', border: 'none', borderRight: isRTL ? 'none' : '1px solid #e5e7eb', borderLeft: isRTL ? '1px solid #e5e7eb' : 'none', cursor: 'pointer', flexShrink: 0, direction: 'ltr' }}>
                      <span>{parentPhoneCountry.flag}</span>
                      <span style={{ fontSize: '12px', color: '#059669', fontWeight: '700' }}>+{parentPhoneCountry.dialCode}</span>
                      <span style={{ fontSize: '9px', color: '#9ca3af' }}>▼</span>
                    </button>
                    <input type="tel" value={newParent.phone} onChange={(e) => setNewParent(prev => ({ ...prev, phone: e.target.value }))} style={{ flex: 1, padding: '8px', border: 'none', outline: 'none', fontSize: '13px', direction: 'ltr', textAlign: 'left' }} placeholder={t('enterPhone')} />
                  </div>
                  {formPhonePickerOpen === 'parent' && <><div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => { setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} /><div style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 0, top: 'calc(100% + 4px)', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: '280px', maxHeight: '280px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 999 }}><div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#fafafa' }}><span>🔍</span><input autoFocus value={formPhoneSearch} onChange={e => setFormPhoneSearch(e.target.value)} placeholder={currentLanguage === 'ar' ? 'بحث...' : 'Rechercher...'} style={{ flex: 1, border: 'none', outline: 'none', fontSize: '12px', backgroundColor: 'transparent' }} /></div><div style={{ overflowY: 'auto', flex: 1 }}>{COUNTRIES.filter(c => !formPhoneSearch.trim() || c.name.ar.includes(formPhoneSearch) || c.name.fr.toLowerCase().includes(formPhoneSearch.toLowerCase()) || c.dialCode.includes(formPhoneSearch)).map(c => { const sel = c.code === parentPhoneCountry.code; return (<div key={c.code} onClick={() => { setParentPhoneCountry(c); setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f9fafb', backgroundColor: sel ? '#f0fdf4' : 'transparent' }} onMouseEnter={e => { if (!sel) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = sel ? '#f0fdf4' : 'transparent'; }}><span>{c.flag}</span><span style={{ flex: 1, fontSize: '12px', color: sel ? '#059669' : '#374151', fontWeight: sel ? '600' : '400' }}>{c.name[currentLanguage as 'ar' | 'fr' | 'en'] || c.name.ar}</span><span style={{ fontSize: '11px', color: '#9ca3af', minWidth: '40px', textAlign: 'right' }}>+{c.dialCode}</span>{sel && <span style={{ color: '#059669' }}>✓</span>}</div>); })}</div></div></>}
                </div>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('parentEmail')}
                </label>
                <input
                  type="email"
                  value={newParent.email}
                  onChange={(e) => setNewParent(prev => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('gender')} *
                </label>
                <select
                  value={newParent.gender || 'Male'}
                  onChange={(e) => setNewParent(prev => ({ ...prev, gender: e.target.value as 'Female' | 'Male' }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                  required
                >
                  <option value="Male">{t('male')}</option>
                  <option value="Female">{t('female')}</option>
                </select>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('studentAddress')}
                </label>
                <input
                  type="text"
                  value={newParent.address}
                  onChange={(e) => setNewParent(prev => ({ ...prev, address: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>

            {/* Toggles de notification */}
            {(newParent.phone.trim().length > 0 || newParent.email.trim().length > 0) && (
              <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>{t('sendCredentials')}</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {newParent.phone.trim().length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <button type="button" onClick={() => setParentSendSms(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${parentSendSms ? '#6b4eff' : '#d1d5db'}`, backgroundColor: parentSendSms ? '#eef2ff' : '#fff', color: parentSendSms ? '#6b4eff' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                        💬 SMS
                      </button>
                      {parentSendSms && (
                        <span style={{ fontSize: '10px', color: '#f59e0b', paddingRight: '4px' }}>
                          ⚠️ يتطلب فتح تطبيق الرسائل على الهاتف
                        </span>
                      )}
                    </div>
                  )}
                  {newParent.phone.trim().length > 0 && (
                    <button type="button" onClick={() => setParentSendWhatsApp(v => !v)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${parentSendWhatsApp ? '#25d366' : '#d1d5db'}`, backgroundColor: parentSendWhatsApp ? '#f0fdf4' : '#fff', color: parentSendWhatsApp ? '#16a34a' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                      📱 WhatsApp
                    </button>
                  )}
                  {newParent.email.trim().length > 0 && (
                    <button type="button" onClick={() => setParentSendEmail(v => !v)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${parentSendEmail ? '#6b4eff' : '#d1d5db'}`, backgroundColor: parentSendEmail ? '#eef2ff' : '#fff', color: parentSendEmail ? '#6b4eff' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                      ✉️ Email
                    </button>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                {t('studentNotes')}
              </label>
              <textarea
                value={newParent.notes}
                onChange={(e) => setNewParent(prev => ({ ...prev, notes: e.target.value }))}
                rows={1}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddParentForm(false);
                  setNewParent({ fullName: '', username: '', password: '', confirmPassword: '', phone: '', email: '', address: '', notes: '', status: 'active', gender: 'Male' });
                  setParentSendEmail(true); setParentSendSms(false); setParentSendWhatsApp(false);
                }}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleAddParent}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? t('saving') : t('saveParent')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire de modification de parent */}
      {showEditParentForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '95vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            position: 'relative'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              {t('editParentTitle')}
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('parentName')} *
                </label>
                <input
                  type="text"
                  value={newParent.fullName}
                  onChange={(e) => setNewParent(prev => ({ ...prev, fullName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('parentUsername')} *
                </label>
                <input
                  type="text"
                  value={newParent.username}
                  onChange={(e) => setNewParent(prev => ({ ...prev, username: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                  {t('newPassword')}{' '}
                  <span style={{ color: '#9ca3af', fontSize: '11px', fontWeight: '400', fontStyle: 'italic' }}>
                    {t('optional')}
                  </span>
                </label>
                <PasswordInput
                  value={newParent.password}
                  onChange={(e) => setNewParent(prev => ({ ...prev, password: e.target.value }))}
                  placeholder={t('newPasswordOptional')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                  {t('confirmPassword')}
                </label>
                <PasswordInput
                  value={newParent.confirmPassword}
                  onChange={(e) => setNewParent(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder={t('confirmPassword')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('studentPhone')} *
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', flexDirection: isRTL ? 'row-reverse' : 'row', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden' }}>
                    <button type="button" onClick={() => { setFormPhonePickerOpen(formPhonePickerOpen === 'parent' ? null : 'parent'); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 10px', backgroundColor: '#f9fafb', border: 'none', borderRight: isRTL ? 'none' : '1px solid #e5e7eb', borderLeft: isRTL ? '1px solid #e5e7eb' : 'none', cursor: 'pointer', flexShrink: 0, direction: 'ltr' }}>
                      <span>{parentPhoneCountry.flag}</span>
                      <span style={{ fontSize: '12px', color: '#059669', fontWeight: '700' }}>+{parentPhoneCountry.dialCode}</span>
                      <span style={{ fontSize: '9px', color: '#9ca3af' }}>▼</span>
                    </button>
                    <input type="tel" value={newParent.phone} onChange={(e) => setNewParent(prev => ({ ...prev, phone: e.target.value }))} style={{ flex: 1, padding: '8px', border: 'none', outline: 'none', fontSize: '13px', direction: 'ltr', textAlign: 'left' }} placeholder={t('enterPhone')} />
                  </div>
                  {formPhonePickerOpen === 'parent' && <><div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => { setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} /><div style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 0, top: 'calc(100% + 4px)', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: '280px', maxHeight: '280px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 999 }}><div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#fafafa' }}><span>🔍</span><input autoFocus value={formPhoneSearch} onChange={e => setFormPhoneSearch(e.target.value)} placeholder={currentLanguage === 'ar' ? 'بحث...' : 'Rechercher...'} style={{ flex: 1, border: 'none', outline: 'none', fontSize: '12px', backgroundColor: 'transparent' }} /></div><div style={{ overflowY: 'auto', flex: 1 }}>{COUNTRIES.filter(c => !formPhoneSearch.trim() || c.name.ar.includes(formPhoneSearch) || c.name.fr.toLowerCase().includes(formPhoneSearch.toLowerCase()) || c.dialCode.includes(formPhoneSearch)).map(c => { const sel = c.code === parentPhoneCountry.code; return (<div key={c.code} onClick={() => { setParentPhoneCountry(c); setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f9fafb', backgroundColor: sel ? '#f0fdf4' : 'transparent' }} onMouseEnter={e => { if (!sel) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = sel ? '#f0fdf4' : 'transparent'; }}><span>{c.flag}</span><span style={{ flex: 1, fontSize: '12px', color: sel ? '#059669' : '#374151', fontWeight: sel ? '600' : '400' }}>{c.name[currentLanguage as 'ar' | 'fr' | 'en'] || c.name.ar}</span><span style={{ fontSize: '11px', color: '#9ca3af', minWidth: '40px', textAlign: 'right' }}>+{c.dialCode}</span>{sel && <span style={{ color: '#059669' }}>✓</span>}</div>); })}</div></div></>}
                </div>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('parentEmail')}
                </label>
                <input
                  type="email"
                  required
                  value={newParent.email}
                  onChange={(e) => setNewParent(prev => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('gender')} *
                </label>
                <select
                  value={newParent.gender || 'Male'}
                  onChange={(e) => setNewParent(prev => ({ ...prev, gender: e.target.value as 'Female' | 'Male' }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                  required
                >
                  <option value="Male">{t('male')}</option>
                  <option value="Female">{t('female')}</option>
                </select>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('studentAddress')}
                </label>
                <input
                  type="text"
                  value={newParent.address}
                  onChange={(e) => setNewParent(prev => ({ ...prev, address: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>

            {/* Champ الحالة (Status) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '13px'
                }}>
                  {t('status')} *
                </label>
                <select
                  value={newParent.status || 'active'}
                  onChange={(e) => setNewParent(prev => ({ ...prev, status: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="active">{t('active')}</option>
                  <option value="inactive">{t('inactive')}</option>
                </select>
              </div>
            </div>

            {/* Toggles de notification */}
            {(newParent.phone.trim().length > 0 || newParent.email.trim().length > 0) && (
              <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>{t('sendCredentials')}</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {newParent.phone.trim().length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <button type="button" onClick={() => setParentSendSms(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${parentSendSms ? '#6b4eff' : '#d1d5db'}`, backgroundColor: parentSendSms ? '#eef2ff' : '#fff', color: parentSendSms ? '#6b4eff' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                        💬 SMS
                      </button>
                      {parentSendSms && (
                        <span style={{ fontSize: '10px', color: '#f59e0b', paddingRight: '4px' }}>
                          ⚠️ يتطلب فتح تطبيق الرسائل على الهاتف
                        </span>
                      )}
                    </div>
                  )}
                  {newParent.phone.trim().length > 0 && (
                    <button type="button" onClick={() => setParentSendWhatsApp(v => !v)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${parentSendWhatsApp ? '#25d366' : '#d1d5db'}`, backgroundColor: parentSendWhatsApp ? '#f0fdf4' : '#fff', color: parentSendWhatsApp ? '#16a34a' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                      📱 WhatsApp
                    </button>
                  )}
                  {newParent.email.trim().length > 0 && (
                    <button type="button" onClick={() => setParentSendEmail(v => !v)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '16px', border: `1px solid ${parentSendEmail ? '#6b4eff' : '#d1d5db'}`, backgroundColor: parentSendEmail ? '#eef2ff' : '#fff', color: parentSendEmail ? '#6b4eff' : '#9ca3af', fontSize: '12px', cursor: 'pointer' }}>
                      ✉️ Email
                    </button>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                {t('studentNotes')}
              </label>
              <textarea
                value={newParent.notes}
                onChange={(e) => setNewParent(prev => ({ ...prev, notes: e.target.value }))}
                rows={1}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <button
                onClick={() => {
                  const parent = editingParent;
                  setShowEditParentForm(false);
                  setEditingParent(null);
                  setNewParent({ fullName: '', username: '', password: '', confirmPassword: '', phone: '', email: '', address: '', notes: '', status: 'active', gender: 'Male' });
                  setParentSendEmail(true); setParentSendSms(false); setParentSendWhatsApp(false);
                  if (parent) handleDeleteParent(parent.id);
                }}
                style={{
                  padding: '12px 20px',
                  border: '1px solid #FECACA',
                  borderRadius: '8px',
                  backgroundColor: '#FFF',
                  color: '#DC2626',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                🗑️ حذف
              </button>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setShowEditParentForm(false);
                    setEditingParent(null);
                    setNewParent({ fullName: '', username: '', password: '', confirmPassword: '', phone: '', email: '', address: '', notes: '', status: 'active', gender: 'Male' });
                    setParentSendEmail(true); setParentSendSms(false); setParentSendWhatsApp(false);
                  }}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleUpdateParent}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? t('saving') : t('saveChanges')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire d'ajout d'administrateur */}
      {showAddAdminForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              إضافة مدير جديد
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  {t('teacherName')} *
                </label>
                <input
                  type="text"
                  value={newAdmin.fullName}
                  onChange={(e) => setNewAdmin({ ...newAdmin, fullName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder={t('enterAdminFullName')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  {t('teacherUsername')} *
                </label>
                <input
                  type="text"
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder={t('username')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  {t('teacherPassword') || t('password')} *
                </label>
                <PasswordInput
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  placeholder={t('password')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  {t('confirmPassword')} *
                </label>
                <PasswordInput
                  value={adminPasswordConfirm}
                  onChange={(e) => setAdminPasswordConfirm(e.target.value)}
                  placeholder={t('confirmPassword')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    padding: '12px',
                    border: `1px solid ${newAdmin.password && adminPasswordConfirm && newAdmin.password !== adminPasswordConfirm ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: newAdmin.password && adminPasswordConfirm && newAdmin.password !== adminPasswordConfirm ? '#fef2f2' : 'white'
                  }}
                  required
                />
                {newAdmin.password && adminPasswordConfirm && newAdmin.password !== adminPasswordConfirm && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                    كلمة المرور وتأكيدها غير متطابقتين
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  رقم الهاتف
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', flexDirection: isRTL ? 'row-reverse' : 'row', border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden' }}>
                    <button type="button" onClick={() => { setFormPhonePickerOpen(formPhonePickerOpen === 'admin' ? null : 'admin'); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '10px 12px', backgroundColor: '#f9fafb', border: 'none', borderRight: isRTL ? 'none' : '1px solid #e5e7eb', borderLeft: isRTL ? '1px solid #e5e7eb' : 'none', cursor: 'pointer', flexShrink: 0, direction: 'ltr' }}>
                      <span>{adminPhoneCountry.flag}</span>
                      <span style={{ fontSize: '13px', color: '#059669', fontWeight: '700' }}>+{adminPhoneCountry.dialCode}</span>
                      <span style={{ fontSize: '9px', color: '#9ca3af' }}>▼</span>
                    </button>
                    <input type="tel" value={newAdmin.phone} onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })} style={{ flex: 1, padding: '12px', border: 'none', outline: 'none', fontSize: '14px', direction: 'ltr', textAlign: 'left' }} placeholder={t('phoneFormat')} />
                  </div>
                  {formPhonePickerOpen === 'admin' && <><div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => { setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} /><div style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 0, top: 'calc(100% + 4px)', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: '280px', maxHeight: '280px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 999 }}><div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#fafafa' }}><span>🔍</span><input autoFocus value={formPhoneSearch} onChange={e => setFormPhoneSearch(e.target.value)} placeholder={currentLanguage === 'ar' ? 'بحث...' : 'Rechercher...'} style={{ flex: 1, border: 'none', outline: 'none', fontSize: '12px', backgroundColor: 'transparent' }} /></div><div style={{ overflowY: 'auto', flex: 1 }}>{COUNTRIES.filter(c => !formPhoneSearch.trim() || c.name.ar.includes(formPhoneSearch) || c.name.fr.toLowerCase().includes(formPhoneSearch.toLowerCase()) || c.dialCode.includes(formPhoneSearch)).map(c => { const sel = c.code === adminPhoneCountry.code; return (<div key={c.code} onClick={() => { setAdminPhoneCountry(c); setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f9fafb', backgroundColor: sel ? '#f0fdf4' : 'transparent' }} onMouseEnter={e => { if (!sel) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = sel ? '#f0fdf4' : 'transparent'; }}><span>{c.flag}</span><span style={{ flex: 1, fontSize: '12px', color: sel ? '#059669' : '#374151', fontWeight: sel ? '600' : '400' }}>{c.name[currentLanguage as 'ar' | 'fr' | 'en'] || c.name.ar}</span><span style={{ fontSize: '11px', color: '#9ca3af', minWidth: '40px', textAlign: 'right' }}>+{c.dialCode}</span>{sel && <span style={{ color: '#059669' }}>✓</span>}</div>); })}</div></div></>}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  البريد الإلكتروني
                </label>
                <input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  {t('gender')} *
                </label>
                <select
                  value={newAdmin.gender || 'Male'}
                  onChange={(e) => setNewAdmin({ ...newAdmin, gender: e.target.value as 'Female' | 'Male' })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  required
                >
                  <option value="Male">{t('male')}</option>
                  <option value="Female">{t('female')}</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                الصلاحيات *
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', padding: '16px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
                {AVAILABLE_PERMISSIONS.map((permission: PermissionOption) => (
                  <label key={permission.key} style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151' }}>
                    <input
                      type="checkbox"
                      checked={newAdmin.permissions.includes(permission.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewAdmin({ ...newAdmin, permissions: [...newAdmin.permissions, permission.key] });
                        } else {
                          setNewAdmin({ ...newAdmin, permissions: newAdmin.permissions.filter((p: string) => p !== permission.key) });
                        }
                      }}
                      style={{ marginLeft: '8px' }}
                    />
                    {currentLanguage === 'ar' ? permission.labelAr : currentLanguage === 'fr' ? permission.labelFr : permission.labelEn}
                  </label>
                ))}
              </div>
            </div>

            {/* Toggles de notification */}
            <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                {t('sendCredentials')}
              </label>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={adminSendSms}
                      onChange={(e) => setAdminSendSms(e.target.checked)}
                      disabled={!newAdmin.phone}
                    />
                    <span style={{ fontSize: '14px', color: !newAdmin.phone ? '#9ca3af' : '#374151' }}>SMS</span>
                  </label>
                  {adminSendSms && (
                    <span style={{ fontSize: '10px', color: '#f59e0b', paddingRight: '4px' }}>
                      ⚠️ يتطلب فتح تطبيق الرسائل على الهاتف
                    </span>
                  )}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={adminSendWhatsApp}
                    onChange={(e) => setAdminSendWhatsApp(e.target.checked)}
                    disabled={!newAdmin.phone}
                  />
                  <span style={{ fontSize: '14px', color: !newAdmin.phone ? '#9ca3af' : '#374151' }}>WhatsApp</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={adminSendEmail}
                    onChange={(e) => setAdminSendEmail(e.target.checked)}
                    disabled={!newAdmin.email}
                  />
                  <span style={{ fontSize: '14px', color: !newAdmin.email ? '#9ca3af' : '#374151' }}>Email</span>
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                {t('notes')}
              </label>
              <textarea
                value={newAdmin.notes}
                onChange={(e) => setNewAdmin({ ...newAdmin, notes: e.target.value })}
                rows={1}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                placeholder={t('teacherNotesOptional')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddAdminForm(false);
                  setAdminSendEmail(true);
                  setAdminSendSms(false);
                  setAdminSendWhatsApp(false);
                  setNewAdmin({
                    fullName: '',
                    username: '',
                    password: '',
                    phone: '',
                    email: '',
                    permissions: ['students', 'teachers', 'parents', 'admins', 'communication', 'grades', 'finances'],
                    notes: '',
                    status: 'active',
                    gender: 'Male'
                  });
                }}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleAddAdmin}
                disabled={isSubmittingAdmin}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: isSubmittingAdmin ? 'not-allowed' : 'pointer',
                  opacity: isSubmittingAdmin ? 0.6 : 1
                }}
              >
                {isSubmittingAdmin ? 'جاري الإضافة...' : 'إضافة المدير'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire de modification d'administrateur */}
      {showEditAdminForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              تعديل بيانات المدير
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  {t('teacherName')} *
                </label>
                <input
                  type="text"
                  value={newAdmin.fullName}
                  onChange={(e) => setNewAdmin({ ...newAdmin, fullName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  {t('teacherUsername')} *
                </label>
                <input
                  type="text"
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                    (اتركها فارغة للاحتفاظ بالحالية)
                </label>
                <PasswordInput
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  placeholder={t('newPasswordOptional')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  {t('confirmPassword')}
                </label>
                <PasswordInput
                  value={adminPasswordConfirm}
                  onChange={(e) => setAdminPasswordConfirm(e.target.value)}
                  placeholder={t('confirmPassword')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    padding: '12px',
                    border: `1px solid ${newAdmin.password && adminPasswordConfirm && newAdmin.password !== adminPasswordConfirm ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: newAdmin.password && adminPasswordConfirm && newAdmin.password !== adminPasswordConfirm ? '#fef2f2' : 'white'
                  }}
                />
                {newAdmin.password && adminPasswordConfirm && newAdmin.password !== adminPasswordConfirm && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                    كلمة المرور وتأكيدها غير متطابقتين
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  رقم الهاتف *
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', flexDirection: isRTL ? 'row-reverse' : 'row', border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden' }}>
                    <button type="button" onClick={() => { setFormPhonePickerOpen(formPhonePickerOpen === 'admin' ? null : 'admin'); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '10px 12px', backgroundColor: '#f9fafb', border: 'none', borderRight: isRTL ? 'none' : '1px solid #e5e7eb', borderLeft: isRTL ? '1px solid #e5e7eb' : 'none', cursor: 'pointer', flexShrink: 0, direction: 'ltr' }}>
                      <span>{adminPhoneCountry.flag}</span>
                      <span style={{ fontSize: '13px', color: '#059669', fontWeight: '700' }}>+{adminPhoneCountry.dialCode}</span>
                      <span style={{ fontSize: '9px', color: '#9ca3af' }}>▼</span>
                    </button>
                    <input type="tel" value={newAdmin.phone} onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })} style={{ flex: 1, padding: '12px', border: 'none', outline: 'none', fontSize: '14px', direction: 'ltr', textAlign: 'left' }} placeholder={t('phoneFormat')} />
                  </div>
                  {formPhonePickerOpen === 'admin' && <><div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => { setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} /><div style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 0, top: 'calc(100% + 4px)', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: '280px', maxHeight: '280px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 999 }}><div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#fafafa' }}><span>🔍</span><input autoFocus value={formPhoneSearch} onChange={e => setFormPhoneSearch(e.target.value)} placeholder={currentLanguage === 'ar' ? 'بحث...' : 'Rechercher...'} style={{ flex: 1, border: 'none', outline: 'none', fontSize: '12px', backgroundColor: 'transparent' }} /></div><div style={{ overflowY: 'auto', flex: 1 }}>{COUNTRIES.filter(c => !formPhoneSearch.trim() || c.name.ar.includes(formPhoneSearch) || c.name.fr.toLowerCase().includes(formPhoneSearch.toLowerCase()) || c.dialCode.includes(formPhoneSearch)).map(c => { const sel = c.code === adminPhoneCountry.code; return (<div key={c.code} onClick={() => { setAdminPhoneCountry(c); setFormPhonePickerOpen(null); setFormPhoneSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f9fafb', backgroundColor: sel ? '#f0fdf4' : 'transparent' }} onMouseEnter={e => { if (!sel) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = sel ? '#f0fdf4' : 'transparent'; }}><span>{c.flag}</span><span style={{ flex: 1, fontSize: '12px', color: sel ? '#059669' : '#374151', fontWeight: sel ? '600' : '400' }}>{c.name[currentLanguage as 'ar' | 'fr' | 'en'] || c.name.ar}</span><span style={{ fontSize: '11px', color: '#9ca3af', minWidth: '40px', textAlign: 'right' }}>+{c.dialCode}</span>{sel && <span style={{ color: '#059669' }}>✓</span>}</div>); })}</div></div></>}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  البريد الإلكتروني *
                </label>
                <input
                  type="email"
                  required
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  {t('gender')} *
                </label>
                <select
                  value={newAdmin.gender || 'Male'}
                  onChange={(e) => setNewAdmin({ ...newAdmin, gender: e.target.value as 'Female' | 'Male' })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  required
                >
                  <option value="Male">{t('male')}</option>
                  <option value="Female">{t('female')}</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                الصلاحيات *
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', padding: '16px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
                {AVAILABLE_PERMISSIONS.map((permission: PermissionOption) => (
                  <label key={permission.key} style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151' }}>
                    <input
                      type="checkbox"
                      checked={newAdmin.permissions.includes(permission.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewAdmin({ ...newAdmin, permissions: [...newAdmin.permissions, permission.key] });
                        } else {
                          setNewAdmin({ ...newAdmin, permissions: newAdmin.permissions.filter((p: string) => p !== permission.key) });
                        }
                      }}
                      style={{ marginLeft: '8px' }}
                    />
                    {currentLanguage === 'ar' ? permission.labelAr : currentLanguage === 'fr' ? permission.labelFr : permission.labelEn}
                  </label>
                ))}
              </div>
            </div>

            {/* Champ الحالة (Status) */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                {t('status')} *
              </label>
              <select
                value={newAdmin.status || 'active'}
                onChange={(e) => setNewAdmin({ ...newAdmin, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              >
                <option value="active">{t('active')}</option>
                <option value="inactive">{t('inactive')}</option>
              </select>
            </div>

            {/* Toggles de notification */}
            <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                {t('sendCredentials')}
              </label>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={adminSendSms}
                      onChange={(e) => setAdminSendSms(e.target.checked)}
                      disabled={!newAdmin.phone}
                    />
                    <span style={{ fontSize: '14px', color: !newAdmin.phone ? '#9ca3af' : '#374151' }}>SMS</span>
                  </label>
                  {adminSendSms && (
                    <span style={{ fontSize: '10px', color: '#f59e0b', paddingRight: '4px' }}>
                      ⚠️ يتطلب فتح تطبيق الرسائل على الهاتف
                    </span>
                  )}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={adminSendWhatsApp}
                    onChange={(e) => setAdminSendWhatsApp(e.target.checked)}
                    disabled={!newAdmin.phone}
                  />
                  <span style={{ fontSize: '14px', color: !newAdmin.phone ? '#9ca3af' : '#374151' }}>WhatsApp</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={adminSendEmail}
                    onChange={(e) => setAdminSendEmail(e.target.checked)}
                    disabled={!newAdmin.email}
                  />
                  <span style={{ fontSize: '14px', color: !newAdmin.email ? '#9ca3af' : '#374151' }}>Email</span>
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                {t('notes')}
              </label>
              <textarea
                value={newAdmin.notes}
                onChange={(e) => setNewAdmin({ ...newAdmin, notes: e.target.value })}
                rows={1}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                placeholder={t('teacherNotesOptional')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowEditAdminForm(false);
                  setEditingAdmin(null);
                  setAdminSendEmail(true);
                  setAdminSendSms(false);
                  setAdminSendWhatsApp(false);
                  setNewAdmin({
                    fullName: '',
                    username: '',
                    password: '',
                    phone: '',
                    email: '',
                    permissions: ['students', 'teachers', 'parents', 'admins', 'communication', 'grades', 'finances'],
                    notes: '',
                    status: 'active',
                    gender: 'Male'
                  });
                  setAdminPasswordConfirm(''); // Réinitialiser le champ de confirmation
                }}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleUpdateAdmin}
                disabled={isSubmittingAdmin}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: isSubmittingAdmin ? 'not-allowed' : 'pointer',
                  opacity: isSubmittingAdmin ? 0.6 : 1
                }}
              >
                {isSubmittingAdmin ? 'جاري التحديث...' : 'حفظ التعديلات'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire de modification de contribution mensuelle */}
      {showEditContributionForm && editingContribution && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              تعديل رسوم الطالب: {editingContribution.student_name || 'غير محدد'}
            </h2>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                {t('annualEnrollment')} *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={editingContributionValues.annual_membership}
                onChange={(e) => setEditingContributionValues({ ...editingContributionValues, annual_membership: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="0.00"
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '12px' }}>
                الرسوم الشهرية
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => (
                  <div key={month}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#6b7280', marginBottom: '6px' }}>
                      {t(month)}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingContributionValues[month as keyof typeof editingContributionValues]}
                      onChange={(e) => setEditingContributionValues({ ...editingContributionValues, [month]: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="0.00"
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowEditContributionForm(false);
                  setEditingContribution(null);
                  setEditingContributionValues({
                    annual_membership: '',
                    september: '',
                    october: '',
                    november: '',
                    december: '',
                    january: '',
                    february: '',
                    march: '',
                    april: '',
                    may: '',
                    june: '',
                    july: '',
                    august: ''
                  });
                }}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleUpdateContribution}
                disabled={isSubmittingContribution}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#5e35b1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: isSubmittingContribution ? 'not-allowed' : 'pointer',
                  opacity: isSubmittingContribution ? 0.6 : 1
                }}
              >
                {isSubmittingContribution ? 'جاري الحفظ...' : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire de modification de salaire d'enseignant */}
      {showEditSalaryForm && editingSalary && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              تعديل راتب الأستاذ: {editingSalary.teacher_name || 'غير محدد'}
            </h2>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '12px' }}>
                الرواتب الشهرية
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => (
                  <div key={month}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#6b7280', marginBottom: '6px' }}>
                      {t(month)}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingSalaryValues[month as keyof typeof editingSalaryValues]}
                      onChange={(e) => setEditingSalaryValues({ ...editingSalaryValues, [month]: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="0.00"
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowEditSalaryForm(false);
                  setEditingSalary(null);
                  setEditingSalaryValues({
                    september: '',
                    october: '',
                    november: '',
                    december: '',
                    january: '',
                    february: '',
                    march: '',
                    april: '',
                    may: '',
                    june: '',
                    july: '',
                    august: ''
                  });
                }}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleUpdateSalary}
                disabled={isSubmittingSalary}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#5e35b1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: isSubmittingSalary ? 'not-allowed' : 'pointer',
                  opacity: isSubmittingSalary ? 0.6 : 1
                }}
              >
                {isSubmittingSalary ? 'جاري الحفظ...' : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire unifié pour ajout et modification d'enregistrement financier */}
      {(showAddFinancialRecordForm || (showEditFinancialRecordForm && editingFinancialRecord)) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '20px'
            }}>
              {showEditFinancialRecordForm && editingFinancialRecord ? 'تعديل مدخول أو مصروف' : 'إضافة مدخول أو مصروف'}
            </h2>

            {editingFinancialRecord && isCalculatedRecord(editingFinancialRecord) && (
              <div style={{
                padding: '10px',
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '6px',
                marginBottom: '20px',
                color: '#92400e',
                fontSize: '13px'
              }}>
                ⚠️ هذا السجل محسوب تلقائياً ولا يمكن تعديله
              </div>
            )}

            <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  {t('category')} *
                </label>
                <input
                  type="text"
                  value={showEditFinancialRecordForm && editingFinancialRecord ? editingFinancialRecordValues.category_name : newFinancialRecord.category_name}
                  onChange={(e) => {
                    if (showEditFinancialRecordForm && editingFinancialRecord) {
                      setEditingFinancialRecordValues({ ...editingFinancialRecordValues, category_name: e.target.value });
                    } else {
                      setNewFinancialRecord({ ...newFinancialRecord, category_name: e.target.value });
                    }
                  }}
                  disabled={editingFinancialRecord && isCalculatedRecord(editingFinancialRecord)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                    backgroundColor: editingFinancialRecord && isCalculatedRecord(editingFinancialRecord) ? '#f3f4f6' : 'white',
                    cursor: editingFinancialRecord && isCalculatedRecord(editingFinancialRecord) ? 'not-allowed' : 'text'
                  }}
                  placeholder={t('category')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  {t('type')} *
                </label>
                <select
                  value={showEditFinancialRecordForm && editingFinancialRecord ? editingFinancialRecordValues.category_type : newFinancialRecord.category_type}
                  onChange={(e) => {
                    if (showEditFinancialRecordForm && editingFinancialRecord) {
                      setEditingFinancialRecordValues({ ...editingFinancialRecordValues, category_type: e.target.value as 'revenue' | 'expense' | 'total_revenue' | 'total_expense' });
                    } else {
                      setNewFinancialRecord({ ...newFinancialRecord, category_type: e.target.value as 'revenue' | 'expense' });
                    }
                  }}
                  disabled={editingFinancialRecord && (editingFinancialRecord.category_type === 'total_revenue' || editingFinancialRecord.category_type === 'total_expense')}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                    backgroundColor: editingFinancialRecord && (editingFinancialRecord.category_type === 'total_revenue' || editingFinancialRecord.category_type === 'total_expense') ? '#f3f4f6' : 'white',
                    cursor: editingFinancialRecord && (editingFinancialRecord.category_type === 'total_revenue' || editingFinancialRecord.category_type === 'total_expense') ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="revenue">{t('income')}</option>
                  <option value="expense">{t('expense')}</option>
                  {editingFinancialRecord && isCalculatedRecord(editingFinancialRecord) && (
                    <>
                      <option value="total_revenue">Total Revenues (محسوب تلقائياً)</option>
                      <option value="total_expense">Total Expenses (محسوب تلقائياً)</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  ترتيب العرض *
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={showEditFinancialRecordForm && editingFinancialRecord ? editingFinancialRecordValues.display_order : newFinancialRecord.display_order}
                  onChange={(e) => {
                    if (showEditFinancialRecordForm && editingFinancialRecord) {
                      setEditingFinancialRecordValues({ ...editingFinancialRecordValues, display_order: parseInt(e.target.value) || 1 });
                    } else {
                      setNewFinancialRecord({ ...newFinancialRecord, display_order: parseInt(e.target.value) || 1 });
                    }
                  }}
                  disabled={editingFinancialRecord && isCalculatedRecord(editingFinancialRecord)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                    backgroundColor: editingFinancialRecord && isCalculatedRecord(editingFinancialRecord) ? '#f3f4f6' : 'white',
                    cursor: editingFinancialRecord && isCalculatedRecord(editingFinancialRecord) ? 'not-allowed' : 'text'
                  }}
                  placeholder="1"
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '-16px', marginBottom: '20px', paddingLeft: '0' }}>
              رقم صحيح يحدد ترتيب عرض هذا السجل (يجب أن يكون فريداً لكل نوع)
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '10px' }}>
                المبالغ الشهرية
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                {['september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august'].map(month => (
                  <div key={month}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#6b7280', marginBottom: '3px' }}>
                      {t(month)}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={showEditFinancialRecordForm && editingFinancialRecord ? (editingFinancialRecordValues[month as keyof typeof editingFinancialRecordValues] || '') : (newFinancialRecord[month as keyof typeof newFinancialRecord] || '')}
                      onChange={(e) => {
                        if (showEditFinancialRecordForm && editingFinancialRecord) {
                          setEditingFinancialRecordValues({ ...editingFinancialRecordValues, [month]: e.target.value });
                        } else {
                          setNewFinancialRecord({ ...newFinancialRecord, [month]: e.target.value });
                        }
                      }}
                      disabled={editingFinancialRecord && (editingFinancialRecord.category_type === 'total_revenue' || editingFinancialRecord.category_type === 'total_expense')}
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #d1d5db',
                        borderRadius: '5px',
                        fontSize: '12px',
                        boxSizing: 'border-box',
                        backgroundColor: editingFinancialRecord && (editingFinancialRecord.category_type === 'total_revenue' || editingFinancialRecord.category_type === 'total_expense') ? '#f3f4f6' : 'white',
                        cursor: editingFinancialRecord && (editingFinancialRecord.category_type === 'total_revenue' || editingFinancialRecord.category_type === 'total_expense') ? 'not-allowed' : 'text'
                      }}
                      placeholder="0.00"
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => {
                  if (showEditFinancialRecordForm && editingFinancialRecord) {
                    setShowEditFinancialRecordForm(false);
                    setEditingFinancialRecord(null);
                  } else {
                    setShowAddFinancialRecordForm(false);
                    setNewFinancialRecord({
                      category_name: '',
                      category_type: 'revenue',
                      display_order: 1,
                      september: '',
                      october: '',
                      november: '',
                      december: '',
                      january: '',
                      february: '',
                      march: '',
                      april: '',
                      may: '',
                      june: '',
                      july: '',
                      august: ''
                    });
                  }
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={showEditFinancialRecordForm && editingFinancialRecord ? handleUpdateFinancialRecord : handleAddFinancialRecord}
                disabled={isSubmittingFinancialRecord || (editingFinancialRecord && isCalculatedRecord(editingFinancialRecord))}
                style={{
                  padding: '10px 20px',
                  backgroundColor: (editingFinancialRecord && isCalculatedRecord(editingFinancialRecord)) ? '#9ca3af' : (showEditFinancialRecordForm && editingFinancialRecord ? '#3b82f6' : '#10b981'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: (isSubmittingFinancialRecord || (editingFinancialRecord && isCalculatedRecord(editingFinancialRecord))) ? 'not-allowed' : 'pointer',
                  opacity: (isSubmittingFinancialRecord || (editingFinancialRecord && isCalculatedRecord(editingFinancialRecord))) ? 0.6 : 1
                }}
              >
                {isSubmittingFinancialRecord ? t('actSaving') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard: React.FC<AdminDashboardProps> = (props) => (
  <SchoolYearProvider>
    <AdminDashboardContent {...props} />
  </SchoolYearProvider>
);

export default AdminDashboard;
